import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  ShoppingCart, Search, LogOut, Plus, Minus, Trash2, X, Check, AlertCircle, Loader2,
  Send, Utensils, QrCode, Download, Edit3, Save,
  Clock, User, Users, Receipt, Timer, CheckCircle, XCircle,
  RefreshCw, Eye, EyeOff, Settings as SettingsIcon, Archive, History as HistoryIcon,
  Tag, ChevronDown, Coffee, Hash, Bike, Lock,
  Volume2, VolumeX, MessageSquare, Banknote, CreditCard, Printer, Wallet,
  Monitor, Tv, Copy, ExternalLink, Power, Palette, ToggleLeft, ToggleRight
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

// ══════════════════════════════════════════════════
// API BASE (same logic as POS/Kitchen apps)
// ══════════════════════════════════════════════════
const API_BASE = (() => {
  const h = window.location.hostname, p = window.location.port, pr = window.location.protocol;
  if (pr === "file:") return "https://efis.pro/api";
  if (h === "localhost" && p) return "http://localhost/fiscalization/api";
  if (h === "localhost" && !p) return "http://localhost/fiscalization/api";
  return "https://efis.pro/api";
})();

// ══════════════════════════════════════════════════
// API helper — Bearer token from localStorage
// ══════════════════════════════════════════════════
// Small helper: is this request safe to auto-retry? Only GET (or no method,
// which defaults to GET). POST/PUT/DELETE are NOT retried automatically to
// avoid double-submitting an order.
function _isRetryable(opts) {
  const m = (opts.method || "GET").toUpperCase();
  return m === "GET";
}

function _sleep(ms) { return new Promise(res => setTimeout(res, ms)); }

// ── Smart polling config (v1.7) ─────────────────────────────────────────────
// Shared by all recurring pollers below. See makeSmartPoller().
//   POLL_JITTER  — up to +Nms random per tick so multiple screens never fire
//                  on the same instant (smooths traffic vs synchronized bursts).
//   POLL_MAX     — max backoff interval on repeated failures (e.g. a 403 burst
//                  from the host firewall); prevents a block from snowballing.
const POLL_JITTER = 2000;
const POLL_MAX = 60000;

// ── TEMPORARY MAINTENANCE SWITCH ────────────────────────────────────────────
// Set to true to take the ENTIRE SELF app offline (shows a "back soon" screen
// and — importantly — renders BEFORE any poller mounts, so it also stops all
// SELF API traffic). Flip back to false to restore everything unchanged.
const SELF_MAINTENANCE = false;
const SELF_MAINTENANCE_MSG = "Nazad za 1-3 dana"; // "Back in 1-3 days"

// makeSmartPoller: wraps a fetch fn with jitter + pause-when-hidden + backoff.
// Returns a cleanup function. `fn` should return a Promise; it is treated as
// "ok" unless it throws or returns false.
//
// SINGLETON GUARD: pass opts.key to guarantee only ONE active poller for that
// key exists at a time — even if React re-mounts the component, runs the effect
// twice (StrictMode), or a parent re-renders. Any new poller with the same key
// first tears down the previous one. This makes overlapping/stacked pollers
// (the cause of "too many requests") impossible.
const _activePollers = (typeof window !== "undefined")
  ? (window.__efisPollers = window.__efisPollers || {})
  : {};

function makeSmartPoller(fn, baseMs, opts = {}) {
  const pauseHidden = opts.pauseHidden !== false; // default: pause when hidden
  const key = opts.key || null;

  // If a poller with this key already exists, stop it before starting a new one.
  if (key && _activePollers[key]) {
    try { _activePollers[key](); } catch (e) {}
    delete _activePollers[key];
  }

  let timer = null, stopped = false, inFlight = false, backoff = baseMs;

  const schedule = (ms) => {
    if (stopped) return;
    if (pauseHidden && typeof document !== "undefined" && document.hidden) return;
    if (timer) { clearTimeout(timer); timer = null; }
    timer = setTimeout(run, ms + Math.floor(Math.random() * POLL_JITTER));
  };
  const run = async () => {
    if (stopped || inFlight) return;
    inFlight = true;
    let ok = true;
    try { const r = await fn(); if (r === false) ok = false; }
    catch (e) { ok = false; }
    finally { inFlight = false; }
    backoff = ok ? baseMs : Math.min(backoff * 2, POLL_MAX);
    schedule(backoff);
  };
  const onVis = () => {
    if (stopped) return;
    if (document.hidden) { if (timer) { clearTimeout(timer); timer = null; } }
    else if (!timer && !inFlight) { backoff = baseMs; run(); }
  };

  const cleanup = () => {
    stopped = true;
    if (timer) clearTimeout(timer);
    if (pauseHidden && typeof document !== "undefined") document.removeEventListener("visibilitychange", onVis);
    if (key && _activePollers[key] === cleanup) delete _activePollers[key];
  };

  if (key) _activePollers[key] = cleanup;
  run();
  if (pauseHidden && typeof document !== "undefined") document.addEventListener("visibilitychange", onVis);
  return cleanup;
}

async function apiAuth(path, opts = {}) {
  const token = localStorage.getItem("self_token");
  const h = { "Content-Type": "application/json" };
  if (token) h["Authorization"] = `Bearer ${token}`;
  const maxTries = _isRetryable(opts) ? 2 : 1; // one retry for reads only
  let lastErr = null;
  for (let attempt = 0; attempt < maxTries; attempt++) {
    try {
      const r = await fetch(`${API_BASE}${path}`, { headers: h, ...opts });
      if (r.status === 401) {
        localStorage.removeItem("self_token");
        localStorage.removeItem("self_user");
        window.location.reload();
        return { success: false };
      }
      // 429/5xx on a retryable read → wait briefly and try once more.
      if ((r.status === 429 || r.status >= 500) && attempt < maxTries - 1) {
        await _sleep(700);
        continue;
      }
      return await r.json();
    } catch (e) {
      lastErr = e;
      if (attempt < maxTries - 1) { await _sleep(700); continue; }
    }
  }
  return { success: false, message: "Network error", _network: true };
}

// Public API (no auth) — for customer endpoints
async function apiPublic(path, opts = {}) {
  const maxTries = _isRetryable(opts) ? 2 : 1;
  for (let attempt = 0; attempt < maxTries; attempt++) {
    try {
      const r = await fetch(`${API_BASE}${path}`, {
        headers: { "Content-Type": "application/json" },
        ...opts,
      });
      if ((r.status === 429 || r.status >= 500) && attempt < maxTries - 1) {
        await _sleep(700);
        continue;
      }
      return await r.json();
    } catch (e) {
      if (attempt < maxTries - 1) { await _sleep(700); continue; }
    }
  }
  return { success: false, message: "Network error", _network: true };
}

// ══════════════════════════════════════════════════
// PIN SYSTEM (same pattern as POS, separate localStorage key)
// SCOPED BY company_id — each company has fully isolated PIN data on the same device.
// ══════════════════════════════════════════════════
const PIN_ENCRYPT_KEY = "efis_pro_2026_pin_lock";

function getPinKey(companyId) {
  return `self_pin_system_${companyId || "0"}`;
}

function pinEncrypt(text) {
  const key = PIN_ENCRYPT_KEY;
  let result = "";
  for (let i = 0; i < text.length; i++) result += String.fromCharCode(text.charCodeAt(i) ^ key.charCodeAt(i % key.length));
  return btoa(result);
}
function pinDecrypt(encoded) {
  try {
    const key = PIN_ENCRYPT_KEY;
    const text = atob(encoded);
    let result = "";
    for (let i = 0; i < text.length; i++) result += String.fromCharCode(text.charCodeAt(i) ^ key.charCodeAt(i % key.length));
    return result;
  } catch { return null; }
}

async function hashPin(pin) {
  const data = new TextEncoder().encode(pin + "_efis_salt_2026");
  const hash = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, "0")).join("");
}

function loadPinData(companyId) {
  if (!companyId) return null;
  try {
    const raw = localStorage.getItem(getPinKey(companyId));
    if (!raw) return null;
    const decrypted = pinDecrypt(raw);
    if (!decrypted) return null;
    return JSON.parse(decrypted);
  } catch { return null; }
}
function savePinData(data, companyId) {
  if (!companyId) return;
  try {
    const json = JSON.stringify(data);
    localStorage.setItem(getPinKey(companyId), pinEncrypt(json));
  } catch { /* ignore */ }
}

// One-time migration of legacy unscoped keys to scoped keys.
// Called once on AdminApp mount when companyId is known.
function migrateLegacyKeys(companyId) {
  if (!companyId) return;
  try {
    // PIN
    const oldPin = localStorage.getItem("self_pin_system");
    if (oldPin) {
      const newKey = getPinKey(companyId);
      if (!localStorage.getItem(newKey)) {
        localStorage.setItem(newKey, oldPin);
      }
      localStorage.removeItem("self_pin_system");
    }
    // TCR
    const oldTcr = localStorage.getItem("self_tcr");
    if (oldTcr) {
      const newKey = getTcrKey(companyId);
      if (!localStorage.getItem(newKey)) {
        localStorage.setItem(newKey, oldTcr);
      }
      localStorage.removeItem("self_tcr");
    }
  } catch { /* ignore */ }
}

// SELF-specific permissions (12 total, grouped)
const ALL_PERMISSIONS = [
  // Operacije — view permissions per sub-tab + action permissions
  { key: "ops_view_new",      label: "Operacije: tab Nove",         group: "Operacije" },
  { key: "ops_view_active",   label: "Operacije: tab Aktivne",      group: "Operacije" },
  { key: "ops_view_history",  label: "Operacije: tab Istorija 24h", group: "Operacije" },
  { key: "ops_create_sale",   label: "Nova prodaja (KASA)",         group: "Operacije" },
  { key: "accept_orders",     label: "Prihvati / odbij narudžbe",   group: "Operacije" },
  { key: "change_status",     label: "Status (spreman, završi)",    group: "Operacije" },
  { key: "fiscalize",         label: "Fiskalizuj (kreiraj račun)",  group: "Fiskalizacija" },
  { key: "view_invoices",     label: "Pregled računa",              group: "Fiskalizacija" },
  { key: "reprint_invoice",   label: "Reprint računa",              group: "Fiskalizacija" },
  { key: "edit_menu",         label: "Uređivanje menija",           group: "Konfiguracija" },
  { key: "manage_categories", label: "Upravljanje kategorijama",    group: "Konfiguracija" },
  { key: "manage_tables",     label: "Stolovi & QR",                group: "Konfiguracija" },
  { key: "view_history",      label: "Istorija narudžbi",           group: "Pregled" },
  { key: "access_settings",   label: "Podešavanja",                 group: "Sistem" },
  { key: "manage_pins",       label: "Upravljanje PIN-ovima",       group: "Sistem" },
];

const PERMISSION_PRESETS = {
  full: { label: "Pun pristup", permissions: ALL_PERMISSIONS.map(p => p.key) },
  manager: { label: "Menadžer", permissions: ALL_PERMISSIONS.map(p => p.key).filter(k => k !== "access_settings" && k !== "manage_pins") },
  cashier: { label: "Konobar", permissions: ["ops_view_new", "ops_view_active", "ops_view_history", "ops_create_sale", "accept_orders", "change_status", "fiscalize", "view_invoices", "reprint_invoice"] },
  viewer: { label: "Pregled", permissions: ["ops_view_active", "ops_view_history", "view_invoices", "view_history"] },
  custom: { label: "Prilagođeno", permissions: [] },
};

function hasPermission(activeOperator, perm) {
  if (!activeOperator) return true; // no PIN system = full access
  if (activeOperator.is_main_admin) return true;
  return (activeOperator.permissions || []).includes(perm);
}

// Operacije nav tab is visible if operator can see at least one sub-tab.
function canSeeOperationsTab(activeOperator) {
  return hasPermission(activeOperator, "ops_view_new")
      || hasPermission(activeOperator, "ops_view_active")
      || hasPermission(activeOperator, "ops_view_history")
      || hasPermission(activeOperator, "ops_create_sale");
}


// ══════════════════════════════════════════════════
// TCR helpers (mirrors POS pattern, separate localStorage key)
// ══════════════════════════════════════════════════
function getTcrKey(companyId) { return `self_tcr_${companyId || "0"}`; }
function getTcr(companyId) {
  if (!companyId) return "";
  return localStorage.getItem(getTcrKey(companyId)) || "";
}
function setTcr(code, companyId) {
  if (!companyId) return;
  if (code) localStorage.setItem(getTcrKey(companyId), code);
  else localStorage.removeItem(getTcrKey(companyId));
}

// Detect if running in Electron wrapper (same check POS uses)
const isElectron = typeof window !== "undefined" && (
  (window.process && window.process.type) ||
  (window.navigator?.userAgent || "").toLowerCase().indexOf(" electron/") > -1
);

// Read an HTTP response body as a binary string preserving bytes 0x00-0xFF.
// Use this when the response contains raw ESC/POS bytes (including the QR
// raster bitmap embedded by the PHP backend's escposQrBitmap()). The default
// response.text() would UTF-8-decode the body and corrupt any byte ≥ 0x80,
// turning the bitmap into garbage and the printer into a glyph-spewing mess.
//
// Output is a JS string where charCodeAt(i) === byte i. Pass directly to
// electronAPI.printText() — Electron main.js does Buffer.from(s, "binary")
// which reverses this and gets the original bytes back, byte-perfect.
//
// HISTORY: ported from POS App.js (2026-05-10) after discovering that
// SELF's printReceipt(invoiceId) had the same UTF-8 corruption bug:
// fetch().text() mangled the QR raster bitmap before it ever reached
// the printer, regardless of whether the print backend was WinUSB or
// the Windows spooler. With this helper, the bytes arrive intact and
// the printer renders the raster QR correctly via either pipeline.
async function readResponseAsBinaryString(r) {
  const buf = await r.arrayBuffer();
  const u8 = new Uint8Array(buf);
  // String.fromCharCode.apply has an arg-count limit on big buffers,
  // and concatenation in a tight loop is slow. Build in 8KB chunks for
  // speed and safety. 8KB stays well under apply()'s limit and keeps
  // memory pressure low even for ~10KB+ payloads with QR bitmap inside.
  let s = "";
  const chunkSize = 8192;
  for (let i = 0; i < u8.length; i += chunkSize) {
    s += String.fromCharCode.apply(null, u8.subarray(i, i + chunkSize));
  }
  return s;
}

// ══════════════════════════════════════════════════
// PRINT RECEIPT — identical algorithm to POS printReceipt()
// Method 1: Electron IPC — direct ESC/POS thermal print (Windows/Mac/Linux)
// Method 2: Network printer (if self_printer_type="network")
// Method 3: HTML popup with browser print dialog (universal fallback)
// ══════════════════════════════════════════════════
async function printReceipt(invoiceId) {
  const token = localStorage.getItem("self_token");

  // Method 1: Electron — native ESC/POS via IPC (same as POS desktop app)
  if (isElectron && window.electronAPI) {
    try {
      // When WinUSB direct-print is enabled, ask the backend for native QR
      // (GS(k command) instead of raster bitmap. The raster freezes cheap
      // thermal printers when sent via libusb — native QR sends only ~200
      // bytes total and the printer renders the QR itself. For other modes
      // (spooler / CUPS) the raster works fine and looks slightly better,
      // so default stays as raster. Mirrors POS behavior since 2026-05-10.
      let qrModeParam = "";
      try {
        const s = await window.electronAPI.getSettings();
        if (s && s.useWinUsb) qrModeParam = "&qrMode=native";
      } catch {}
      const r = await fetch(`${API_BASE}/invoices/pdf/${invoiceId}?type=text${qrModeParam}`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (r.ok) {
        // CRITICAL: use binary-safe read, not r.text(). r.text() UTF-8-decodes
        // the response which corrupts the QR raster bitmap bytes.
        const text = await readResponseAsBinaryString(r);
        const result = await window.electronAPI.printText(text);
        return result && result.success;
      }
    } catch (e) { console.log("Electron print failed:", e); }
  }

  const printerType = localStorage.getItem("self_printer_type");
  const printerAddr = localStorage.getItem("self_printer_addr");

  // Method 2: Network print server
  if (printerType === "network" && printerAddr) {
    try {
      const r = await fetch(`${API_BASE}/invoices/pdf/${invoiceId}?type=text`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (r.ok) {
        // Same binary-safe read — even though we JSON.stringify the text right
        // after, the string we send to the print server must preserve the QR
        // raster bytes. Network print servers typically Buffer.from(s,"binary")
        // on receive, matching this encoding.
        const text = await readResponseAsBinaryString(r);
        const printUrl = printerAddr.endsWith("/") ? printerAddr.slice(0,-1) + "/print/text" : printerAddr + "/print/text";
        const pr = await fetch(printUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text })
        });
        const result = await pr.json();
        return result.success;
      }
    } catch (e) { console.log("Network print failed:", e); }
  }

  // Method 3: HTML popup fallback
  return printReceiptHTML(invoiceId);
}

async function printReceiptHTML(invoiceId) {
  const token = localStorage.getItem("self_token");
  try {
    const r = await fetch(`${API_BASE}/invoices/pdf/${invoiceId}?type=html`, {
      headers: { "Authorization": `Bearer ${token}` }
    });
    if (!r.ok) return false;
    const html = await r.text();

    // Electron fallback: printHTML if available
    if (isElectron && window.electronAPI?.printHTML) {
      await window.electronAPI.printHTML(html);
      return true;
    }

    const width = parseInt(localStorage.getItem("self_paper_width") || "80");
    const printWidth = width <= 58 ? "58mm" : "80mm";
    const win = window.open("", "print_receipt", `width=350,height=600`);
    if (win) {
      // eslint-disable-next-line no-useless-concat
      win.document.write(`<html><head><style>@page{size:${printWidth} auto;margin:2mm;}body{font-family:monospace;font-size:12px;margin:0;padding:4mm;width:${printWidth};}@media print{body{width:${printWidth};}}</style></head><body>${html}<scr` + `ipt>setTimeout(function(){window.print();},300);</scr` + `ipt></body></html>`);
      win.document.close();
    }
    return true;
  } catch (e) { return false; }
}

// ══════════════════════════════════════════════════
// DESIGN TOKENS — Admin
// ══════════════════════════════════════════════════
const S = {
  font: `"DM Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`,
  bg: "#f5f6f8",
  card: "#ffffff",
  dark: "#0f172a",
  muted: "#64748b",
  lightMuted: "#94a3b8",
  border: "#e2e8f0",
  accent: "#3b82f6",
  green: "#16a34a",
  yellow: "#eab308",
  red: "#dc2626",
  purple: "#8b5cf6",
  orange: "#f97316",
  text: "#1e293b",
  input: {
    width: "100%",
    padding: "10px 12px",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    fontSize: 14,
    outline: "none",
    background: "#fff",
    color: "#1e293b",
  },
  btn: (bg = "#3b82f6", color = "#fff") => ({
    background: bg,
    color,
    border: "none",
    borderRadius: 8,
    padding: "10px 16px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    transition: "transform .1s",
  }),
  btnSm: (bg = "#f3f4f6", color = "#64748b") => ({
    background: bg,
    color,
    border: "none",
    borderRadius: 6,
    padding: "6px 10px",
    fontSize: 12,
    fontWeight: 500,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontFamily: `"DM Sans", sans-serif`,
  }),
};

// ══════════════════════════════════════════════════
// TRANSLATIONS (customer-facing, 4 languages)
// ══════════════════════════════════════════════════
const T = {
  cg: {
    menu: "Meni", cart: "Korpa", empty_cart: "Vaša korpa je prazna",
    add_to_cart: "Dodaj u korpu", place_order: "Naruči",
    total: "Ukupno", subtotal: "Osnovica", vat: "PDV",
    name_optional: "Ime (nije obavezno)", note_optional: "Napomena (nije obavezna)",
    qty: "Količina", remove: "Ukloni", close: "Zatvori", back: "Nazad",
    your_order: "Vaša narudžba", order_number: "Broj narudžbe",
    status_pending: "Čeka prihvatanje", status_preparing: "U pripremi",
    pay_notice_title: "Platite na kasi",
    pay_notice_body: "Molimo idite na kasu i platite narudžbu kako bi bila prihvaćena.",
    pay_notice_ok: "U redu",
    pay_counter_banner: "Platite na kasi da bi vaša narudžba bila prihvaćena",
    status_ready_pickup: "Spremno — dođite po narudžbu",
    status_ready_table: "Spremno — konobar donosi",
    status_finished: "Završeno", status_rejected: "Odbijeno",
    status_cancelled: "Otkazano",
    cancel_order: "Otkaži narudžbu",
    place_new: "Nova narudžba", table: "Sto", terminal: "Terminal", takeaway: "Ponesi",
    follow_screen_title: "Pratite TV ekran",
    follow_screen_body: "Pratite TV ekran za status vaše trenutne narudžbe. Broj narudžbe je",
    welcome: "Dobrodošli", restaurant_closed: "Restoran je zatvoren",
    configure_tables: "Sto nije konfigurisan",
    restaurant_not_found: "Restoran nije pronađen",
    tags: "Dodaci", search_menu: "Pretraži meni",
    no_items: "Nema artikala",
    confirm_cancel: "Da li ste sigurni da želite otkazati narudžbu?",
    placed_at: "Naručeno", ready_at: "Spremno",
    estimated: "Procijenjeno",
    out_of_stock: "Nedostupno",
    unavailable_now: "Nedostupno trenutno",
    connecting: "Povezivanje...",
    items: "artikala",
    please_wait: "Molimo sačekajte",
    // Terminal kiosk
    kiosk_pair_title: "Uparite uređaj",
    kiosk_pair_intro: "Unesite 6-cifreni kod sa /admin → Podešavanja → Terminali",
    kiosk_pair_btn: "Upari",
    kiosk_pair_invalid: "Pogrešan ili istekao kod",
    kiosk_landing_tap: "Dodirnite za naručivanje",
    kiosk_starting_order: "Pokretanje narudžbe...",
    kiosk_loading_menu: "Učitavanje menija...",
    kiosk_added: "Dodato u korpu",
    kiosk_choose_lang: "Izaberite jezik",
    kiosk_categories_all: "Sve",
    kiosk_customize: "Prilagodi",
    kiosk_special_note: "Posebna napomena",
    kiosk_special_note_ph: "npr. bez luka, ekstra meso",
    kiosk_review_order: "Pregled narudžbe",
    kiosk_pay_at_counter: "Plati na kasi",
    kiosk_order_placed: "Narudžba primljena!",
    kiosk_your_order_no: "Vaš broj narudžbe",
    kiosk_scan_qr: "Skenirajte QR za status narudžbe",
    kiosk_returning_in: "Povratak za",
    kiosk_estimated_time: "Procijenjeno vrijeme",
    kiosk_minutes_short: "min",
    kiosk_seconds_short: "sek",
    kiosk_placing: "Slanje narudžbe...",
    kiosk_place_failed: "Greška pri slanju narudžbe",
    kiosk_try_again: "Pokušaj ponovo",
    kiosk_printing: "Štampanje računa...",
    kiosk_continue: "Nastavi naručivanje",
    // Order status page
    order_status_title: "Status narudžbe",
    order_status_loading: "Učitavanje...",
    order_status_not_found: "Narudžba nije pronađena",
    order_waiting_label: "Čekamo prihvatanje od restorana",
    order_preparing_label: "Vaša narudžba se priprema",
    order_ready_pickup_label: "Spremno — dođite po narudžbu",
    order_ready_table_label: "Spremno — konobar dolazi",
    order_finished_label: "Narudžba završena. Hvala!",
    order_rejected_label: "Narudžba je odbijena",
    order_cancelled_label: "Narudžba je otkazana",
    order_eta_about: "Procijenjeno",
    order_open_kiosk: "Vratite se na terminal za novu narudžbu",
    // Live display
    live_pending: "Čeka odobrenje",
    live_preparing: "U pripremi",
    live_ready: "Spremno",
    live_no_orders: "Trenutno nema narudžbi",
    live_reconnecting: "Povezivanje...",
    // Kiosk owner settings (hidden — long-press logo to open)
    kset_title: "Podešavanja terminala",
    kset_subtitle: "Vlasnička podešavanja — sakrijte od kupaca",
    kset_close: "Zatvori",
    kset_terminal_info: "Informacije o terminalu",
    kset_terminal_number: "Broj terminala",
    kset_device_label: "Oznaka uređaja",
    kset_save_label: "Sačuvaj oznaku",
    kset_label_saved: "Sačuvano",
    kset_unpair: "Odspoji terminal",
    kset_unpair_confirm: "Sigurno želite odspojiti? Trebaće novi kod za uparivanje.",
    kset_printer: "Štampač",
    kset_printer_type: "Tip štampača",
    kset_printer_interface: "Konekcija",
    kset_printer_iface_ph: "tcp://192.168.1.100:9100 ili printer:USB001",
    kset_printer_charset: "Charset",
    kset_printer_width: "Širina (znakova)",
    kset_printer_save: "Sačuvaj podešavanja",
    kset_printer_test: "Test konekcije",
    kset_printer_test_print: "Štampaj test",
    kset_printer_saved: "Podešavanja sačuvana",
    kset_printer_test_ok: "Konekcija uspješna",
    kset_printer_test_fail: "Greška",
    kset_printer_no_electron: "Funkcija dostupna samo u .exe verziji",
    kset_long_press_hint: "Vlasnik: dugi pritisak na logo otvara podešavanja",
  },
  en: {
    menu: "Menu", cart: "Cart", empty_cart: "Your cart is empty",
    add_to_cart: "Add to cart", place_order: "Place order",
    total: "Total", subtotal: "Subtotal", vat: "VAT",
    name_optional: "Name (optional)", note_optional: "Note (optional)",
    qty: "Quantity", remove: "Remove", close: "Close", back: "Back",
    your_order: "Your order", order_number: "Order number",
    status_pending: "Waiting for approval", status_preparing: "Preparing",
    pay_notice_title: "Pay at the counter",
    pay_notice_body: "Please go to the counter and pay so your order can be accepted.",
    pay_notice_ok: "OK",
    pay_counter_banner: "Go to the counter to pay so your order gets accepted",
    status_ready_pickup: "Ready — come pick it up",
    status_ready_table: "Ready — waiter is bringing it",
    status_finished: "Completed", status_rejected: "Rejected",
    status_cancelled: "Cancelled",
    cancel_order: "Cancel order",
    place_new: "New order", table: "Table", terminal: "Terminal", takeaway: "Takeaway",
    follow_screen_title: "Follow the TV screen",
    follow_screen_body: "Follow the TV screen for the status of your current order. Your order number is",
    welcome: "Welcome", restaurant_closed: "Restaurant closed",
    configure_tables: "Table not configured",
    restaurant_not_found: "Restaurant not found",
    tags: "Extras", search_menu: "Search menu",
    no_items: "No items",
    confirm_cancel: "Are you sure you want to cancel this order?",
    placed_at: "Placed", ready_at: "Ready at",
    estimated: "Estimated",
    out_of_stock: "Out of stock",
    unavailable_now: "Currently unavailable",
    connecting: "Connecting...",
    items: "items",
    please_wait: "Please wait",
    // Terminal kiosk
    kiosk_pair_title: "Pair this device",
    kiosk_pair_intro: "Enter the 6-digit code from /admin → Settings → Terminals",
    kiosk_pair_btn: "Pair",
    kiosk_pair_invalid: "Invalid or expired code",
    kiosk_landing_tap: "Tap to order",
    kiosk_starting_order: "Starting order...",
    kiosk_loading_menu: "Loading menu...",
    kiosk_added: "Added to cart",
    kiosk_choose_lang: "Choose language",
    kiosk_categories_all: "All",
    kiosk_customize: "Customize",
    kiosk_special_note: "Special note",
    kiosk_special_note_ph: "e.g. no onions, extra meat",
    kiosk_review_order: "Review order",
    kiosk_pay_at_counter: "Pay at counter",
    kiosk_order_placed: "Order placed!",
    kiosk_your_order_no: "Your order number",
    kiosk_scan_qr: "Scan QR for order status",
    kiosk_returning_in: "Returning in",
    kiosk_estimated_time: "Estimated time",
    kiosk_minutes_short: "min",
    kiosk_seconds_short: "sec",
    kiosk_placing: "Placing your order...",
    kiosk_place_failed: "Failed to place order",
    kiosk_try_again: "Try again",
    kiosk_printing: "Printing receipt...",
    kiosk_continue: "Continue ordering",
    // Order status page
    order_status_title: "Order status",
    order_status_loading: "Loading...",
    order_status_not_found: "Order not found",
    order_waiting_label: "Waiting for restaurant to accept",
    order_preparing_label: "Your order is being prepared",
    order_ready_pickup_label: "Ready — please come pick up",
    order_ready_table_label: "Ready — waiter is bringing it",
    order_finished_label: "Order completed. Thank you!",
    order_rejected_label: "Order was rejected",
    order_cancelled_label: "Order was cancelled",
    order_eta_about: "Estimated",
    order_open_kiosk: "Return to the kiosk for a new order",
    // Live display
    live_pending: "Awaiting",
    live_preparing: "Preparing",
    live_ready: "Ready",
    live_no_orders: "No orders right now",
    live_reconnecting: "Reconnecting...",
    // Kiosk owner settings
    kset_title: "Terminal Settings",
    kset_subtitle: "Owner settings — hide from customers",
    kset_close: "Close",
    kset_terminal_info: "Terminal Info",
    kset_terminal_number: "Terminal Number",
    kset_device_label: "Device Label",
    kset_save_label: "Save Label",
    kset_label_saved: "Saved",
    kset_unpair: "Unpair Terminal",
    kset_unpair_confirm: "Are you sure? You'll need a new pairing code.",
    kset_printer: "Printer",
    kset_printer_type: "Printer Type",
    kset_printer_interface: "Connection",
    kset_printer_iface_ph: "tcp://192.168.1.100:9100 or printer:USB001",
    kset_printer_charset: "Charset",
    kset_printer_width: "Width (chars)",
    kset_printer_save: "Save Settings",
    kset_printer_test: "Test Connection",
    kset_printer_test_print: "Test Print",
    kset_printer_saved: "Settings saved",
    kset_printer_test_ok: "Connection OK",
    kset_printer_test_fail: "Error",
    kset_printer_no_electron: "Available only in .exe version",
    kset_long_press_hint: "Owner: long-press logo to open settings",
  },
  al: {
    menu: "Menu", cart: "Shporta", empty_cart: "Shporta është bosh",
    add_to_cart: "Shto në shportë", place_order: "Porosit",
    total: "Totali", subtotal: "Nëntotali", vat: "TVSH",
    name_optional: "Emri (opsional)", note_optional: "Shënim (opsional)",
    qty: "Sasia", remove: "Hiq", close: "Mbyll", back: "Kthehu",
    your_order: "Porosia juaj", order_number: "Numri i porosisë",
    status_pending: "Në pritje të pranimit", status_preparing: "Duke u përgatitur",
    pay_notice_title: "Paguani në arkë",
    pay_notice_body: "Ju lutemi shkoni te arka dhe paguani porosinë që të pranohet.",
    pay_notice_ok: "Në rregull",
    pay_counter_banner: "Shkoni te arka për të paguar që porosia juaj të pranohet",
    status_ready_pickup: "Gati — ejani ta merrni",
    status_ready_table: "Gati — kamarjeri po ju sjell",
    status_finished: "Përfunduar", status_rejected: "Refuzuar",
    status_cancelled: "Anuluar",
    cancel_order: "Anulo porosinë",
    place_new: "Porosi e re", table: "Tavolina", terminal: "Terminali", takeaway: "Merr me vete",
    follow_screen_title: "Ndiqni ekranin TV",
    follow_screen_body: "Ndiqni ekranin TV për statusin e porosisë suaj aktuale. Numri i porosisë është",
    welcome: "Mirë se vini", restaurant_closed: "Restoranti është mbyllur",
    configure_tables: "Tavolina nuk është konfiguruar",
    restaurant_not_found: "Restoranti nuk u gjet",
    tags: "Shtesa", search_menu: "Kërko në menu",
    no_items: "Nuk ka artikuj",
    confirm_cancel: "A jeni i sigurt që doni të anuloni porosinë?",
    placed_at: "Porositur", ready_at: "Gati në",
    estimated: "E vlerësuar",
    out_of_stock: "Jo në stok",
    unavailable_now: "Momentalisht e padisponueshme",
    connecting: "Duke u lidhur...",
    items: "artikuj",
    please_wait: "Ju lutemi prisni",
    // Terminal kiosk
    kiosk_pair_title: "Lidh këtë pajisje",
    kiosk_pair_intro: "Vendosni kodin 6-shifror nga /admin → Cilësimet → Terminalet",
    kiosk_pair_btn: "Lidh",
    kiosk_pair_invalid: "Kodi është i pavlefshëm ose ka skaduar",
    kiosk_landing_tap: "Prekni për të porositur",
    kiosk_starting_order: "Po fillon porosinë...",
    kiosk_loading_menu: "Po ngarkon menunë...",
    kiosk_added: "Shtuar në shportë",
    kiosk_choose_lang: "Zgjidhni gjuhën",
    kiosk_categories_all: "Të gjitha",
    kiosk_customize: "Personalizo",
    kiosk_special_note: "Shënim i veçantë",
    kiosk_special_note_ph: "p.sh. pa qepë, mish ekstra",
    kiosk_review_order: "Rishiko porosinë",
    kiosk_pay_at_counter: "Paguaj në arkë",
    kiosk_order_placed: "Porosia u dërgua!",
    kiosk_your_order_no: "Numri juaj i porosisë",
    kiosk_scan_qr: "Skanoni QR për statusin e porosisë",
    kiosk_returning_in: "Kthim pas",
    kiosk_estimated_time: "Koha e parashikuar",
    kiosk_minutes_short: "min",
    kiosk_seconds_short: "sek",
    kiosk_placing: "Po dërgon porosinë...",
    kiosk_place_failed: "Gabim gjatë dërgimit",
    kiosk_try_again: "Provo përsëri",
    kiosk_printing: "Po printon faturën...",
    kiosk_continue: "Vazhdo porositë",
    // Order status page
    order_status_title: "Statusi i porosisë",
    order_status_loading: "Po ngarkohet...",
    order_status_not_found: "Porosia nuk u gjet",
    order_waiting_label: "Duke pritur pranimin nga restoranti",
    order_preparing_label: "Porosia juaj po përgatitet",
    order_ready_pickup_label: "Gati — ejani ta merrni",
    order_ready_table_label: "Gati — kamarjeri po vjen",
    order_finished_label: "Porosia u përfundua. Faleminderit!",
    order_rejected_label: "Porosia u refuzua",
    order_cancelled_label: "Porosia u anulua",
    order_eta_about: "Parashikohet",
    order_open_kiosk: "Kthehu te terminali për porosi të re",
    // Live display
    live_pending: "Në pritje",
    live_preparing: "Duke u përgatitur",
    live_ready: "Gati",
    live_no_orders: "Nuk ka porosi tani",
    live_reconnecting: "Po lidhet...",
    // Kiosk owner settings
    kset_title: "Cilësimet e terminalit",
    kset_subtitle: "Cilësimet e pronarit — fshihni nga klientët",
    kset_close: "Mbyll",
    kset_terminal_info: "Informacioni i terminalit",
    kset_terminal_number: "Numri i terminalit",
    kset_device_label: "Etiketa e pajisjes",
    kset_save_label: "Ruaj etiketën",
    kset_label_saved: "Ruajtur",
    kset_unpair: "Shkëput terminalin",
    kset_unpair_confirm: "A jeni i sigurt? Do të nevojitet një kod i ri.",
    kset_printer: "Printer",
    kset_printer_type: "Lloji i printerit",
    kset_printer_interface: "Lidhja",
    kset_printer_iface_ph: "tcp://192.168.1.100:9100 ose printer:USB001",
    kset_printer_charset: "Charset",
    kset_printer_width: "Gjerësia (karaktere)",
    kset_printer_save: "Ruaj cilësimet",
    kset_printer_test: "Testo lidhjen",
    kset_printer_test_print: "Printo test",
    kset_printer_saved: "Cilësimet u ruajtën",
    kset_printer_test_ok: "Lidhja OK",
    kset_printer_test_fail: "Gabim",
    kset_printer_no_electron: "I disponueshëm vetëm në versionin .exe",
    kset_long_press_hint: "Pronari: shtypje e gjatë mbi logo për cilësime",
  },
};

const LANG_NAMES = { cg: "Crnogorski", en: "English", al: "Shqip" };
const LANG_FLAGS = { cg: "🇲🇪", en: "🇬🇧", al: "🇦🇱" };

function t(lang, key) { return (T[lang] && T[lang][key]) || T.cg[key] || key; }
function eur(n) { return `${Number(n || 0).toFixed(2)} €`; }

// Extract the human invoice ordinal from a Montenegrin invoice_number.
// Format is {seller}/{ordinal}/{year}/{tcr}, e.g.
//   "qn217pg243/3/2026/uk240sx285" → "3"
// Falls back to the DB id (prefixed #) only if no invoice_number is present.
function invoiceOrdinal(invoiceNumber, fallbackId) {
  if (invoiceNumber && typeof invoiceNumber === "string" && invoiceNumber.includes("/")) {
    const parts = invoiceNumber.split("/");
    if (parts.length >= 2 && parts[1]) return parts[1];
  }
  return fallbackId ? `#${fallbackId}` : "—";
}

// Short source label for admin order cards. Takeaway orders have no table
// number, so they read "Ponesi"; terminal/table show their number.
function orderSourceLabel(order) {
  if (order.source_type === "takeaway") return "Ponesi";
  if (order.source_type === "kasa") return "Kasa";
  if (order.source_type === "waiter") return `K-Sto ${order.table_number}`;
  const base = order.source_type === "terminal" ? "Terminal" : "Sto";
  return `${base} ${order.table_number}`;
}
// Bruto = Neto × (1 + vat/100). Used wherever the customer-facing price
// needs to be shown (Meni list, Nova prodaja tile, cart line). The fiscal
// layer always works in neto, so storage stays in neto and we convert on
// render only.
function netoToBruto(neto, vatRate) {
  const n = Number(neto);
  const r = Number(vatRate);
  if (!isFinite(n) || !isFinite(r)) return 0;
  return Math.round(n * (1 + r / 100) * 100) / 100;
}
// fmtQty — backend stores qty as DECIMAL so values arrive like "1.0000".
// If the value is whole, drop all decimals ("1"). Otherwise keep up to
// 3 decimals and strip trailing zeros ("0.5" not "0.500").
function fmtQty(q) {
  const n = Number(q);
  if (!isFinite(n)) return String(q ?? "");
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(3).replace(/\.?0+$/, "");
}

// pickT — return the translation for `lang` if present, otherwise fall back
// to `base`. Used for product names, descriptions, and tag labels.
// `translations` may be undefined, null, an empty object, or a real map.
// Whitespace-only strings count as "no translation" so we don't render gaps.
function pickT(translations, lang, base) {
  if (!translations || typeof translations !== "object") return base;
  const v = translations[lang];
  if (typeof v !== "string" || !v.trim()) return base;
  return v;
}

// ─── useReadyAlarm ───────────────────────────────────────────────────
// Plays a 3-tone chime + vibrates the device when the order's status
// transitions INTO ready_pickup or ready_table. Caller passes the
// current status; the hook tracks the previous value internally and
// only fires on the rising edge (not on every poll cycle).
//
// WebAudio is used so we don't need a static asset. The first call
// after mount may be silent if the user hasn't interacted with the
// page yet (browser autoplay policy) — that's fine, by the time the
// order becomes ready the customer has tapped at least once on this
// session.
function useReadyAlarm(status) {
  const prevStatusRef = useRef(status);
  const [flash, setFlash] = useState(false);

  useEffect(() => {
    const prev = prevStatusRef.current;
    const isReady = status === "ready_pickup" || status === "ready_table";
    const wasReady = prev === "ready_pickup" || prev === "ready_table";
    const transitionedToReady = isReady && !wasReady;
    prevStatusRef.current = status;

    if (!transitionedToReady) return;

    // 1) Visual flash
    setFlash(true);
    const flashTimer = setTimeout(() => setFlash(false), 6000);

    // 2) Vibrate (mobile only — silently no-ops elsewhere)
    if (navigator.vibrate) {
      try { navigator.vibrate([300, 120, 300, 120, 600]); } catch {}
    }

    // 3) Chime — three ascending tones (C5 → E5 → G5)
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const tones = [
        { freq: 523.25, start: 0.00, dur: 0.18 },
        { freq: 659.25, start: 0.20, dur: 0.18 },
        { freq: 783.99, start: 0.40, dur: 0.32 },
      ];
      tones.forEach(t => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = t.freq;
        osc.connect(gain);
        gain.connect(ctx.destination);
        const t0 = ctx.currentTime + t.start;
        gain.gain.setValueAtTime(0.0001, t0);
        gain.gain.exponentialRampToValueAtTime(0.45, t0 + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, t0 + t.dur);
        osc.start(t0);
        osc.stop(t0 + t.dur + 0.05);
      });
      // Repeat once after a short pause for emphasis
      setTimeout(() => {
        try {
          const ctx2 = new Ctx();
          tones.forEach(t => {
            const osc = ctx2.createOscillator();
            const gain = ctx2.createGain();
            osc.type = "sine";
            osc.frequency.value = t.freq;
            osc.connect(gain);
            gain.connect(ctx2.destination);
            const t0 = ctx2.currentTime + t.start;
            gain.gain.setValueAtTime(0.0001, t0);
            gain.gain.exponentialRampToValueAtTime(0.45, t0 + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.0001, t0 + t.dur);
            osc.start(t0);
            osc.stop(t0 + t.dur + 0.05);
          });
        } catch {}
      }, 1100);
    } catch (e) {
      // WebAudio unavailable or blocked — flash + vibrate still happen
    }

    return () => clearTimeout(flashTimer);
  }, [status]);

  return flash;
}
function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso.replace(" ", "T"));
  return d.toLocaleTimeString("sr-Latn-ME", { hour: "2-digit", minute: "2-digit" });
}
function fmtDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso.replace(" ", "T"));
  return d.toLocaleString("sr-Latn-ME", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}
function minsAgo(iso) {
  if (!iso) return 0;
  const d = new Date(iso.replace(" ", "T"));
  return Math.floor((Date.now() - d.getTime()) / 60000);
}

// Resolve category display name for a given language (falls back to base name)
function catName(cat, lang) {
  if (!cat) return "";
  if (lang === "cg" || !lang) return cat.name || "";
  const t = cat.translations && cat.translations[lang];
  return (t && t.trim()) ? t : (cat.name || "");
}

// ══════════════════════════════════════════════════
// URL MODE DETECTION
//   /admin                          → admin
//   /:slug/table/:n                 → customer (table)
//   /:slug/terminal/:n              → customer (terminal)
//   /                               → landing (redirect help)
// ══════════════════════════════════════════════════
function detectMode() {
  const path = window.location.pathname.replace(/^\//, "").replace(/\/$/, "");
  const parts = path.split("/").filter(Boolean);

  if (parts[0] === "admin") return { mode: "admin" };

  // NEW: standalone terminal kiosk app at /terminal (no slug, no number).
  // Device identifies itself via Bearer terminal_token from localStorage.
  // Mounted by the EFIS SELF Terminal .exe but also runs in any browser.
  if (parts[0] === "terminal" && parts.length === 1) return { mode: "kiosk" };

  // NEW: customer order status page — opened from receipt QR code scan.
  // /order/{publicToken} — public read-only, polls every 4s.
  if (parts[0] === "order" && parts.length === 2 && parts[1]) {
    return { mode: "orderStatus", publicToken: parts[1] };
  }

  // NEW: public live display for wall TV — /live/{slug}.
  // Shows order numbers in 3 columns. No login. Polls every 3s.
  if (parts[0] === "live" && parts.length === 2 && parts[1]) {
    return { mode: "liveDisplay", slug: parts[1] };
  }

  if (parts.length >= 3 && (parts[1] === "table" || parts[1] === "terminal")) {
    const slug = parts[0];
    const kind = parts[1];
    const number = parseInt(parts[2], 10);
    if (slug && number > 0) return { mode: "customer", slug, kind, number };
  }

  // NEW: waiter (konobar) app — /{slug}/konobar.
  // Mobile-first browser view. Device pairs once with a 6-digit code,
  // then keeps a permanent waiter_token in localStorage.
  if (parts.length === 2 && parts[1] === "konobar" && parts[0]) {
    return { mode: "konobar", slug: parts[0] };
  }

  // NEW: universal/takeaway QR — /{slug} with no table number.
  // Permanent link for phone customers without a table (counter/pickup).
  // Backend serves the menu only if takeaway_enabled is on for this company.
  // Guard against known reserved first-segments handled above.
  const reserved = ["admin", "terminal", "order", "live"];
  if (parts.length === 1 && parts[0] && !reserved.includes(parts[0])) {
    return { mode: "customer", slug: parts[0], kind: "takeaway", number: 0 };
  }

  return { mode: "landing" };
}

// ══════════════════════════════════════════════════
// UI: Spinner + Toast + Modal wrapper
// ══════════════════════════════════════════════════
function Spinner({ size = 20, color = "#3b82f6" }) {
  return (
    <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
      <Loader2 size={size} color={color} style={{ animation: "spin 1s linear infinite" }} />
    </div>
  );
}

function Toast({ msg, type = "info", onClose }) {
  useEffect(() => {
    if (!msg) return;
    const t = setTimeout(onClose, 3000);
    return () => clearTimeout(t);
  }, [msg, onClose]);
  if (!msg) return null;
  const bg = type === "error" ? "#dc2626" : type === "success" ? "#16a34a" : "#1e293b";
  // On mobile (≤640px) toast appears at top so it doesn't conflict with bottom action buttons / cart bar.
  // On desktop, keep at bottom (admin panel doesn't have bottom buttons that matter).
  const isMobile = typeof window !== "undefined" && window.innerWidth <= 640;
  const positionStyle = isMobile
    ? { top: "calc(env(safe-area-inset-top, 0px) + 16px)", bottom: "auto" }
    : { bottom: 24, top: "auto" };
  return (
    <div style={{
      position: "fixed", left: "50%", transform: "translateX(-50%)",
      ...positionStyle,
      background: bg, color: "#fff", padding: "12px 18px", borderRadius: 10,
      boxShadow: "0 8px 24px rgba(0,0,0,0.2)", zIndex: 9999, fontSize: 14, fontWeight: 500,
      maxWidth: "90vw", animation: "fade-in 0.2s ease",
    }}>{msg}</div>
  );
}

function Modal({ open, onClose, children, maxWidth = 480 }) {
  if (!open) return null;
  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", zIndex: 1000,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 12,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: "#fff", borderRadius: 14, maxWidth, width: "100%",
        maxHeight: "92vh", overflow: "auto", boxShadow: "0 24px 48px rgba(0,0,0,0.3)",
      }}>{children}</div>
    </div>
  );
}

// ══════════════════════════════════════════════════
// NUMPAD INPUT — touchscreen-friendly numeric input
// Wraps a regular number input. Click the input or 🔢 button to open numpad modal.
// Used wherever admin enters numbers on a touchscreen kiosk without a keyboard.
// ══════════════════════════════════════════════════
function NumpadInput({ value, onChange, placeholder, decimal = true, style = {}, ...rest }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <div style={{ display: "flex", alignItems: "stretch", gap: 0, ...style }}>
        <input
          type="text"
          inputMode={decimal ? "decimal" : "numeric"}
          value={value}
          onChange={e => onChange(e.target.value)}
          onFocus={e => { e.target.blur(); setOpen(true); }}
          placeholder={placeholder}
          readOnly
          style={{ ...S.input, flex: 1, borderTopRightRadius: 0, borderBottomRightRadius: 0, borderRight: "none", cursor: "pointer" }}
          {...rest}
        />
        <button
          type="button"
          onClick={() => setOpen(true)}
          style={{
            padding: "0 14px", background: S.accent, color: "#fff", border: "none",
            borderTopRightRadius: 8, borderBottomRightRadius: 8, cursor: "pointer",
            fontSize: 18, fontWeight: 700, display: "flex", alignItems: "center",
            justifyContent: "center", minWidth: 50,
          }}
          tabIndex={-1} aria-label="Numpad"
        >🔢</button>
      </div>
      {open && (
        <NumpadModal
          initial={value} decimal={decimal} placeholder={placeholder}
          onClose={() => setOpen(false)}
          onConfirm={(v) => { onChange(v); setOpen(false); }}
        />
      )}
    </>
  );
}

function NumpadModal({ initial, decimal, placeholder, onConfirm, onClose }) {
  const [val, setVal] = useState(String(initial ?? ""));

  const press = (d) => {
    if (d === ".") {
      if (!decimal) return;
      if (val.includes(".")) return;
      setVal(v => (v === "" ? "0." : v + "."));
      return;
    }
    setVal(v => v + d);
  };
  const back = () => setVal(v => v.slice(0, -1));
  const clear = () => setVal("");

  const Btn = ({ children, onClick, color = "#fff", textColor = S.dark, span = 1 }) => (
    <button type="button" onClick={onClick} style={{
      padding: 0, height: 60, gridColumn: `span ${span}`,
      background: color, color: textColor,
      border: `1px solid ${S.border}`, borderRadius: 10,
      fontSize: 22, fontWeight: 700, cursor: "pointer",
    }}>{children}</button>
  );

  return (
    <Modal open onClose={onClose} maxWidth={360}>
      <div style={{ padding: 18 }}>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: S.muted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>
            Unesite vrijednost
          </div>
          <div style={{
            background: "#f8fafc", border: `2px solid ${S.border}`,
            borderRadius: 10, padding: "14px 16px", fontSize: 26, fontWeight: 700,
            fontFamily: "monospace", textAlign: "right", minHeight: 32,
            color: val ? S.dark : S.muted,
          }}>
            {val || placeholder || "0"}
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
          <Btn onClick={() => press("7")}>7</Btn>
          <Btn onClick={() => press("8")}>8</Btn>
          <Btn onClick={() => press("9")}>9</Btn>
          <Btn onClick={() => press("4")}>4</Btn>
          <Btn onClick={() => press("5")}>5</Btn>
          <Btn onClick={() => press("6")}>6</Btn>
          <Btn onClick={() => press("1")}>1</Btn>
          <Btn onClick={() => press("2")}>2</Btn>
          <Btn onClick={() => press("3")}>3</Btn>
          {decimal ? <Btn onClick={() => press(".")}>.</Btn> : <div />}
          <Btn onClick={() => press("0")}>0</Btn>
          <Btn onClick={back} color="#fef3c7" textColor="#92400e">⌫</Btn>
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <button onClick={clear} style={{ ...S.btn("#fff", S.muted), border: `1px solid ${S.border}`, flex: 1, justifyContent: "center", padding: "12px" }}>
            Obriši
          </button>
          <button onClick={onClose} style={{ ...S.btn("#fff", S.text), border: `1px solid ${S.border}`, flex: 1, justifyContent: "center", padding: "12px" }}>
            Otkaži
          </button>
          <button onClick={() => onConfirm(val)} style={{ ...S.btn(S.green), flex: 1.5, justifyContent: "center", padding: "12px", fontSize: 15 }}>
            <Check size={16} /> OK
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ══════════════════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════════════════
//                          CUSTOMER APP
// ══════════════════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════════════════

function CustomerApp({ slug, kind, number }) {
  const sessionKey = `self_session_${slug}_${kind}_${number}`;
  const langKey = `self_lang_${slug}`;

  const [lang, setLang] = useState(() => localStorage.getItem(langKey) || "cg");
  const [menuData, setMenuData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);

  const [cart, setCart] = useState([]); // [{productId, name, price, qty, tags[], note, vatRate, unit}]
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [productDetail, setProductDetail] = useState(null);

  const [customerName, setCustomerName] = useState("");
  const [customerNote, setCustomerNote] = useState("");
  const [placing, setPlacing] = useState(false);

  const [currentSession, setCurrentSession] = useState(() => localStorage.getItem(sessionKey) || null);
  const [toast, setToast] = useState(null);
  // One-time "pay at the counter" notice, shown right after placing an order
  // from a table/universal QR (not terminal — kiosks have their own flow).
  const [showPayNotice, setShowPayNotice] = useState(false);

  // ─── Load menu ───
  const loadMenu = useCallback(async () => {
    setLoading(true);
    // Takeaway (universal QR) has no table number — call menu without ?table.
    // Table/terminal flows pass the number as before.
    const url = kind === "takeaway"
      ? `/self/menu/${encodeURIComponent(slug)}`
      : `/self/menu/${encodeURIComponent(slug)}?table=${number}`;
    const r = await apiPublic(url);
    if (!r.success) {
      setErrorMsg(r.message || t(lang, "restaurant_not_found"));
      setLoading(false);
      return;
    }
    setMenuData(r.data);
    setErrorMsg(null);
    setLoading(false);
    // Apply branding color to theme
    if (r.data?.settings?.branding_color) {
      document.documentElement.style.setProperty("--brand", r.data.settings.branding_color);
    }
  }, [slug, number, lang]);

  useEffect(() => { loadMenu(); }, [loadMenu]);
  useEffect(() => { localStorage.setItem(langKey, lang); }, [lang, langKey]);

  // ─── If there's an active session, show status ───
  if (currentSession) {
    return (
      <OrderStatusView
        sessionToken={currentSession}
        lang={lang}
        setLang={setLang}
        companyName={menuData?.company?.name}
        brandingColor={menuData?.settings?.branding_color || "#3b82f6"}
        payNotice={showPayNotice}
        onDismissPayNotice={() => setShowPayNotice(false)}
        pageBg={(() => {
          const c = menuData?.settings?.bg_color;
          return c && /^#[0-9a-fA-F]{3,8}$/.test(c) ? c : S.bg;
        })()}
        onNewOrder={() => {
          localStorage.removeItem(sessionKey);
          setCurrentSession(null);
          setCart([]);
          setCustomerName("");
          setCustomerNote("");
        }}
      />
    );
  }

  // ─── Error states ───
  if (loading) return <CustomerLoadingScreen lang={lang} />;
  if (errorMsg) {
    return (
      <div style={{ minHeight: "100vh", background: S.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
        <div style={{ background: "#fff", padding: 28, borderRadius: 14, maxWidth: 420, width: "100%", textAlign: "center" }}>
          <AlertCircle size={40} color={S.red} style={{ marginBottom: 12 }} />
          <h2 style={{ margin: "0 0 6px", fontSize: 18, color: S.dark }}>{errorMsg}</h2>
          <p style={{ color: S.muted, fontSize: 14, margin: 0 }}>{slug} · {t(lang, "table")} {number}</p>
        </div>
      </div>
    );
  }

  const company = menuData.company;
  const settings = menuData.settings;
  const categories = menuData.categories || [];
  const products = menuData.products || [];
  const brandingColor = settings.branding_color || "#3b82f6";
  // Customer-page background — admin-configurable. Empty/missing falls
  // back to S.bg (the existing light-gray default). All inner panels
  // stay white so legibility holds regardless of the chosen bg.
  const pageBg = settings.bg_color && /^#[0-9a-fA-F]{3,8}$/.test(settings.bg_color)
    ? settings.bg_color
    : S.bg;
  const availableLangs = (settings.languages || "cg").split(",").map(x => x.trim()).filter(Boolean);

  // ─── Cart operations ───
  const cartCount = cart.reduce((s, it) => s + it.qty, 0);
  // Customer-facing total is BRUTO (price + VAT). Each cart line stored
  // its NETO price (matching DB / fiscal flow), so we convert per-line.
  const cartTotal = cart.reduce((s, it) => s + netoToBruto(it.price, it.vatRate) * it.qty, 0);

  function addToCart(product, qty, tags, note) {
    setCart(prev => [...prev, {
      productId: product.id,
      name: product.name,
      price: product.price,
      vatRate: product.vat_rate,
      unit: product.unit,
      qty, tags: tags || [], note: note || "",
      _key: Date.now() + Math.random(),
    }]);
    setProductDetail(null);
    setToast({ type: "success", msg: `${product.name} → ${t(lang, "cart")}` });
  }

  function updateQty(key, delta) {
    setCart(prev => prev.map(it => it._key === key ? { ...it, qty: Math.max(1, it.qty + delta) } : it));
  }
  function removeItem(key) {
    setCart(prev => prev.filter(it => it._key !== key));
  }

  async function placeOrder() {
    if (cart.length === 0) return;
    setPlacing(true);
    const r = await apiPublic("/self/order", {
      method: "POST",
      body: JSON.stringify({
        slug,
        tableNumber: number,
        sourceType: kind === "terminal" ? "terminal" : (kind === "takeaway" ? "takeaway" : "table"),
        customerName: customerName.trim(),
        note: customerNote.trim(),
        language: lang,
        items: cart.map(it => ({
          productId: it.productId,
          qty: it.qty,
          tags: it.tags,
          note: it.note,
        })),
      }),
    });
    setPlacing(false);
    if (r.success) {
      localStorage.setItem(sessionKey, r.data.sessionToken);
      setCurrentSession(r.data.sessionToken);
      setCart([]);
      setCheckoutOpen(false);
      // Table / universal QR orders are paid at the counter — show the notice.
      if (kind !== "terminal") setShowPayNotice(true);
    } else {
      setToast({ type: "error", msg: r.message || "Error" });
    }
  }

  // ─── Render ───
  return (
    <div style={{ minHeight: "100vh", background: pageBg, paddingBottom: cartCount > 0 ? 80 : 20 }}>
      {/* Header */}
      <div style={{ background: brandingColor, color: "#fff", padding: "14px 16px", paddingTop: "max(14px, env(safe-area-inset-top))" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{company.name}</div>
            <div style={{ fontSize: 12, opacity: 0.9, marginTop: 2 }}>
              {kind === "takeaway"
                ? t(lang, "takeaway")
                : `${kind === "terminal" ? t(lang, "terminal") : t(lang, "table")} · ${number}`}
            </div>
          </div>
          <LangPicker lang={lang} setLang={setLang} options={availableLangs.length > 0 ? availableLangs : ["cg", "en", "al"]} />
        </div>
        {settings.welcome_text && (
          <div style={{ fontSize: 13, marginTop: 10, lineHeight: 1.4, opacity: 0.95 }}>
            {settings.welcome_text}
          </div>
        )}
        {!settings.is_open && (
          <div style={{ marginTop: 10, background: "rgba(0,0,0,0.2)", padding: "8px 12px", borderRadius: 8, fontSize: 13 }}>
            {t(lang, "restaurant_closed")}
          </div>
        )}
      </div>

      {/* Menu */}
      <CustomerMenuView
        categories={categories}
        products={products}
        lang={lang}
        brandingColor={brandingColor}
        pageBg={pageBg}
        onSelectProduct={setProductDetail}
      />

      {/* Cart floating button */}
      {cartCount > 0 && (
        <div style={{
          position: "fixed", left: 12, right: 12, bottom: "max(12px, env(safe-area-inset-bottom))",
          zIndex: 50,
        }}>
          <button onClick={() => setCartOpen(true)} style={{
            width: "100%", background: brandingColor, color: "#fff", border: "none",
            borderRadius: 12, padding: "14px 16px", fontSize: 16, fontWeight: 700,
            display: "flex", justifyContent: "space-between", alignItems: "center",
            boxShadow: "0 10px 24px rgba(0,0,0,0.2)", cursor: "pointer",
          }}>
            <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <ShoppingCart size={20} />
              {cartCount} {t(lang, "items")}
            </span>
            <span>{eur(cartTotal)}</span>
          </button>
        </div>
      )}

      {/* Product detail modal */}
      {productDetail && (
        <ProductDetailModal
          product={productDetail}
          lang={lang}
          brandingColor={brandingColor}
          onClose={() => setProductDetail(null)}
          onAdd={addToCart}
        />
      )}

      {/* Cart drawer */}
      {cartOpen && (
        <CartDrawer
          cart={cart}
          lang={lang}
          brandingColor={brandingColor}
          onUpdateQty={updateQty}
          onRemove={removeItem}
          onClose={() => setCartOpen(false)}
          onCheckout={() => { setCartOpen(false); setCheckoutOpen(true); }}
        />
      )}

      {/* Checkout sheet */}
      {checkoutOpen && (
        <CheckoutSheet
          cart={cart}
          lang={lang}
          brandingColor={brandingColor}
          customerName={customerName} setCustomerName={setCustomerName}
          customerNote={customerNote} setCustomerNote={setCustomerNote}
          placing={placing}
          onClose={() => setCheckoutOpen(false)}
          onPlace={placeOrder}
        />
      )}

      <Toast msg={toast?.msg} type={toast?.type} onClose={() => setToast(null)} />
    </div>
  );
}

function CustomerLoadingScreen({ lang }) {
  return (
    <div style={{ minHeight: "100vh", background: S.bg, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 14 }}>
      <Spinner size={36} />
      <div style={{ color: S.muted, fontSize: 14 }}>{t(lang, "connecting")}</div>
    </div>
  );
}

function LangPicker({ lang, setLang, options }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <button onClick={() => setOpen(o => !o)} style={{
        background: "rgba(255,255,255,0.18)", color: "#fff", border: "none",
        borderRadius: 8, padding: "6px 10px", fontSize: 13, fontWeight: 600,
        cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
      }}>
        {LANG_FLAGS[lang]} {lang.toUpperCase()}
        <ChevronDown size={14} />
      </button>
      {open && (
        <div style={{
          position: "absolute", top: "100%", right: 0, marginTop: 4,
          background: "#fff", borderRadius: 8, minWidth: 140, zIndex: 100,
          boxShadow: "0 8px 24px rgba(0,0,0,0.15)", overflow: "hidden",
        }}>
          {options.map(l => (
            <button key={l} onClick={() => { setLang(l); setOpen(false); }} style={{
              width: "100%", padding: "10px 12px", border: "none", background: l === lang ? "#f1f5f9" : "#fff",
              color: S.text, fontSize: 14, fontWeight: 600, cursor: "pointer",
              display: "flex", alignItems: "center", gap: 8, textAlign: "left",
            }}>
              <span>{LANG_FLAGS[l]}</span>
              <span>{LANG_NAMES[l]}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function CustomerMenuView({ categories, products, lang, brandingColor, pageBg, onSelectProduct }) {
  const [activeCat, setActiveCat] = useState(null); // null = all
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    let list = products;
    if (activeCat !== null) list = list.filter(p => p.category_id === activeCat);
    if (search) {
      const s = search.toLowerCase();
      list = list.filter(p => p.name.toLowerCase().includes(s) || (p.description || "").toLowerCase().includes(s));
    }
    return list;
  }, [products, activeCat, search]);

  const grouped = useMemo(() => {
    if (activeCat !== null || search) return [{ id: null, name: "", products: filtered }];
    // Group products by category
    const cats = [...categories];
    const result = [];
    for (const c of cats) {
      const ps = products.filter(p => p.category_id === c.id);
      if (ps.length > 0) result.push({ id: c.id, name: catName(c, lang), products: ps });
    }
    const uncategorized = products.filter(p => !p.category_id || !cats.find(c => c.id === p.category_id));
    if (uncategorized.length > 0) result.push({ id: null, name: "—", products: uncategorized });
    return result;
  }, [categories, products, activeCat, filtered, search, lang]);

  return (
    <>
      {/* Search */}
      <div style={{ padding: "12px 12px 8px", background: pageBg || S.bg, position: "sticky", top: 0, zIndex: 10 }}>
        <div style={{ position: "relative" }}>
          <Search size={18} color={S.muted} style={{ position: "absolute", left: 12, top: 11 }} />
          <input
            type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder={t(lang, "search_menu")}
            style={{
              width: "100%", padding: "10px 12px 10px 38px", border: `1px solid ${S.border}`,
              borderRadius: 10, fontSize: 14, outline: "none", background: "#fff",
            }}
          />
        </div>
      </div>

      {/* Category chips */}
      {categories.length > 0 && !search && (
        <div style={{ padding: "4px 12px 10px", display: "flex", gap: 8, overflowX: "auto", whiteSpace: "nowrap" }}>
          <CatChip active={activeCat === null} color={brandingColor} onClick={() => setActiveCat(null)}>
            {t(lang, "menu")}
          </CatChip>
          {categories.map(c => (
            <CatChip key={c.id} active={activeCat === c.id} color={brandingColor} onClick={() => setActiveCat(c.id)}>
              {catName(c, lang)}
            </CatChip>
          ))}
        </div>
      )}

      {/* Product list by groups */}
      <div style={{ padding: "4px 12px 20px" }}>
        {grouped.length === 0 && (
          <div style={{ textAlign: "center", padding: 40, color: S.muted, fontSize: 14 }}>
            {t(lang, "no_items")}
          </div>
        )}
        {grouped.map(group => (
          <div key={group.id ?? "all"} style={{ marginBottom: 16 }}>
            {group.name && (
              <h3 style={{ fontSize: 14, fontWeight: 700, color: S.muted, textTransform: "uppercase", letterSpacing: 1, margin: "10px 4px 8px" }}>
                {group.name}
              </h3>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {group.products.map(p => (
                <ProductCard key={p.id} product={p} lang={lang} onSelect={onSelectProduct} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function CatChip({ active, color, children, onClick }) {
  return (
    <button onClick={onClick} style={{
      background: active ? color : "#fff",
      color: active ? "#fff" : S.text,
      border: `1px solid ${active ? color : S.border}`,
      borderRadius: 20, padding: "7px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer",
      whiteSpace: "nowrap",
    }}>{children}</button>
  );
}

function ProductCard({ product, lang, onSelect }) {
  const unavailable = !product.available;
  return (
    <button onClick={() => !unavailable && onSelect(product)} disabled={unavailable} style={{
      display: "flex", gap: 10, background: "#fff", border: `1px solid ${S.border}`,
      borderRadius: 12, padding: 10, textAlign: "left", cursor: unavailable ? "default" : "pointer",
      opacity: unavailable ? 0.55 : 1, width: "100%", alignItems: "stretch",
    }}>
      {product.photo_url ? (
        <img src={product.photo_url} alt="" style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 8, flexShrink: 0 }} />
      ) : (
        <div style={{ width: 72, height: 72, background: "#f1f5f9", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Coffee size={28} color={S.lightMuted} />
        </div>
      )}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between", minWidth: 0 }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 15, color: S.dark, lineHeight: 1.3 }}>
            {pickT(product.name_translations, lang, product.name)}
          </div>
          {(() => {
            const desc = pickT(product.description_translations, lang, product.description);
            return desc ? (
              <div style={{ fontSize: 12, color: S.muted, marginTop: 2, lineHeight: 1.3, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                {desc}
              </div>
            ) : null;
          })()}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4 }}>
          {/* Customer-facing price is BRUTO (price + VAT). Storage and POS
              still use neto, conversion happens here on render. */}
          <div style={{ fontWeight: 700, color: S.dark, fontSize: 14 }}>
            {eur(netoToBruto(product.price, product.vat_rate))}
          </div>
          {unavailable ? (
            <span style={{ color: S.red, fontSize: 12, fontWeight: 600 }}>{t(lang, "unavailable_now")}</span>
          ) : (
            <Plus size={18} color={S.accent} />
          )}
        </div>
      </div>
    </button>
  );
}

function ProductDetailModal({ product, lang, brandingColor, onClose, onAdd }) {
  const [qty, setQty] = useState(1);
  const [selectedTags, setSelectedTags] = useState([]);
  const [note, setNote] = useState("");

  const toggleTag = (tag) => {
    setSelectedTags(p => p.includes(tag) ? p.filter(t => t !== tag) : [...p, tag]);
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 900, display: "flex", alignItems: "flex-end", animation: "fade-in 0.2s" }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: "#fff", width: "100%", maxHeight: "90vh", overflowY: "auto",
        borderRadius: "18px 18px 0 0", animation: "slide-up 0.3s ease",
      }}>
        {product.photo_url && (
          <img src={product.photo_url} alt="" style={{ width: "100%", height: 200, objectFit: "cover" }} />
        )}
        <div style={{ padding: "18px 16px 16px", paddingBottom: "max(16px, env(safe-area-inset-bottom))" }}>
          <button onClick={onClose} style={{
            position: "absolute", top: 12, right: 12,
            background: "rgba(0,0,0,0.4)", border: "none", borderRadius: "50%",
            width: 34, height: 34, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
          }}><X size={18} color="#fff" /></button>

          <h2 style={{ fontSize: 20, fontWeight: 700, margin: "0 0 6px", color: S.dark }}>
            {pickT(product.name_translations, lang, product.name)}
          </h2>
          {(() => {
            const desc = pickT(product.description_translations, lang, product.description);
            return desc ? (
              <p style={{ color: S.muted, fontSize: 14, margin: "0 0 14px", lineHeight: 1.5 }}>{desc}</p>
            ) : null;
          })()}
          <div style={{ fontSize: 22, fontWeight: 700, color: S.dark, marginBottom: 14 }}>
            {eur(netoToBruto(product.price, product.vat_rate))}
          </div>

          {/* Tags (extras) — uses tags_rich (with visibility + translations)
              when available, falls back to tags_preset (raw strings, all
              visible). Hidden tags are filtered out. The order line stores
              the BASE label so kitchen always sees the local language. */}
          {(() => {
            const richSource = Array.isArray(product.tags_rich) && product.tags_rich.length > 0
              ? product.tags_rich
              : (product.tags_preset || []).map(s => ({ base: s, visible: true, translations: {} }));
            const visible = richSource.filter(t => t.visible !== false && t.base);
            if (!product.tags_enabled || visible.length === 0) return null;
            return (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: S.muted, marginBottom: 8, textTransform: "uppercase", letterSpacing: 1 }}>
                  {t(lang, "tags")}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {visible.map(tagObj => {
                    const label = pickT(tagObj.translations, lang, tagObj.base);
                    const selected = selectedTags.includes(tagObj.base);
                    return (
                      <button key={tagObj.base} onClick={() => toggleTag(tagObj.base)} style={{
                        padding: "6px 12px", borderRadius: 18, fontSize: 13, fontWeight: 500,
                        border: `1px solid ${selected ? brandingColor : S.border}`,
                        background: selected ? brandingColor : "#fff",
                        color: selected ? "#fff" : S.text, cursor: "pointer",
                      }}>{label}</button>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Note */}
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: S.muted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6, display: "block" }}>
              {t(lang, "note_optional")}
            </label>
            <textarea value={note} onChange={e => setNote(e.target.value)} maxLength={200}
              placeholder="..."
              style={{ ...S.input, minHeight: 60, fontFamily: "inherit", resize: "vertical" }}
            />
          </div>

          {/* Qty + Add */}
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", border: `1px solid ${S.border}`, borderRadius: 10, overflow: "hidden" }}>
              <button onClick={() => setQty(q => Math.max(1, q - 1))} style={{ padding: "12px 14px", border: "none", background: "#fff", cursor: "pointer" }}>
                <Minus size={18} color={S.muted} />
              </button>
              <div style={{ padding: "12px 18px", fontSize: 16, fontWeight: 700, minWidth: 50, textAlign: "center" }}>{qty}</div>
              <button onClick={() => setQty(q => q + 1)} style={{ padding: "12px 14px", border: "none", background: "#fff", cursor: "pointer" }}>
                <Plus size={18} color={S.muted} />
              </button>
            </div>
            <button onClick={() => onAdd(product, qty, selectedTags, note)} style={{
              flex: 1, background: brandingColor, color: "#fff", border: "none", borderRadius: 10,
              padding: "12px", fontSize: 15, fontWeight: 700, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}>
              <Plus size={18} /> {t(lang, "add_to_cart")} · {eur(netoToBruto(product.price, product.vat_rate) * qty)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CartDrawer({ cart, lang, brandingColor, onUpdateQty, onRemove, onClose, onCheckout }) {
  // Cart shows BRUTO totals — that's what the customer pays
  const total = cart.reduce((s, it) => s + netoToBruto(it.price, it.vatRate) * it.qty, 0);
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 900, display: "flex", alignItems: "flex-end" }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: S.bg, width: "100%", maxHeight: "90vh",
        borderRadius: "18px 18px 0 0", display: "flex", flexDirection: "column",
        animation: "slide-up 0.3s ease",
      }}>
        <div style={{ padding: "16px", borderBottom: `1px solid ${S.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", background: "#fff", borderRadius: "18px 18px 0 0" }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{t(lang, "cart")}</h2>
          <button onClick={onClose} style={{ background: "transparent", border: "none", cursor: "pointer" }}><X size={22} color={S.muted} /></button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: 12 }}>
          {cart.length === 0 ? (
            <div style={{ textAlign: "center", padding: 40, color: S.muted }}>{t(lang, "empty_cart")}</div>
          ) : (
            cart.map(it => (
              <div key={it._key} style={{ background: "#fff", borderRadius: 10, padding: 12, marginBottom: 8, border: `1px solid ${S.border}` }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: S.dark }}>{it.name}</div>
                    {it.tags.length > 0 && (
                      <div style={{ fontSize: 12, color: S.muted, marginTop: 2 }}>
                        + {it.tags.join(", ")}
                      </div>
                    )}
                    {it.note && (
                      <div style={{ fontSize: 12, color: S.muted, fontStyle: "italic", marginTop: 2 }}>
                        "{it.note}"
                      </div>
                    )}
                  </div>
                  <button onClick={() => onRemove(it._key)} style={{ background: "transparent", border: "none", cursor: "pointer", padding: 4 }}>
                    <Trash2 size={16} color={S.red} />
                  </button>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", border: `1px solid ${S.border}`, borderRadius: 8 }}>
                    <button onClick={() => onUpdateQty(it._key, -1)} style={{ padding: "6px 10px", border: "none", background: "transparent", cursor: "pointer" }}><Minus size={14} /></button>
                    <span style={{ padding: "0 12px", fontWeight: 700 }}>{it.qty}</span>
                    <button onClick={() => onUpdateQty(it._key, +1)} style={{ padding: "6px 10px", border: "none", background: "transparent", cursor: "pointer" }}><Plus size={14} /></button>
                  </div>
                  <div style={{ fontWeight: 700 }}>{eur(netoToBruto(it.price, it.vatRate) * it.qty)}</div>
                </div>
              </div>
            ))
          )}
        </div>

        {cart.length > 0 && (
          <div style={{ padding: 12, paddingBottom: "max(12px, env(safe-area-inset-bottom))", background: "#fff", borderTop: `1px solid ${S.border}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12, fontSize: 16 }}>
              <span style={{ color: S.muted, fontWeight: 600 }}>{t(lang, "total")}</span>
              <span style={{ fontWeight: 800, color: S.dark }}>{eur(total)}</span>
            </div>
            <button onClick={onCheckout} style={{
              width: "100%", background: brandingColor, color: "#fff", border: "none",
              borderRadius: 10, padding: "14px", fontSize: 16, fontWeight: 700, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}><Send size={18} /> {t(lang, "place_order")}</button>
          </div>
        )}
      </div>
    </div>
  );
}

function CheckoutSheet({ cart, lang, brandingColor, customerName, setCustomerName, customerNote, setCustomerNote, placing, onClose, onPlace }) {
  const total = cart.reduce((s, it) => s + netoToBruto(it.price, it.vatRate) * it.qty, 0);
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 950, display: "flex", alignItems: "flex-end" }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", width: "100%", maxHeight: "90vh", overflowY: "auto", borderRadius: "18px 18px 0 0", animation: "slide-up 0.3s ease" }}>
        <div style={{ padding: "16px", borderBottom: `1px solid ${S.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{t(lang, "place_order")}</h2>
          <button onClick={onClose} disabled={placing} style={{ background: "transparent", border: "none", cursor: "pointer" }}><X size={22} color={S.muted} /></button>
        </div>
        <div style={{ padding: 16, paddingBottom: "max(16px, env(safe-area-inset-bottom))" }}>
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, fontWeight: 700, color: S.muted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6, display: "block" }}>
              {t(lang, "name_optional")}
            </label>
            <input value={customerName} onChange={e => setCustomerName(e.target.value)} maxLength={100} style={S.input} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, fontWeight: 700, color: S.muted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6, display: "block" }}>
              {t(lang, "note_optional")}
            </label>
            <textarea value={customerNote} onChange={e => setCustomerNote(e.target.value)} maxLength={300} style={{ ...S.input, minHeight: 60, resize: "vertical", fontFamily: "inherit" }} />
          </div>
          <div style={{ background: S.bg, borderRadius: 10, padding: 12, marginBottom: 14 }}>
            {cart.map(it => (
              <div key={it._key} style={{ display: "flex", justifyContent: "space-between", fontSize: 14, marginBottom: 4 }}>
                <span>{fmtQty(it.qty)}× {it.name}</span>
                <span style={{ fontWeight: 600 }}>{eur(netoToBruto(it.price, it.vatRate) * it.qty)}</span>
              </div>
            ))}
            <div style={{ borderTop: `1px dashed ${S.border}`, marginTop: 8, paddingTop: 8, display: "flex", justifyContent: "space-between", fontWeight: 800, fontSize: 15 }}>
              <span>{t(lang, "total")}</span>
              <span>{eur(total)}</span>
            </div>
          </div>
          <button onClick={onPlace} disabled={placing} style={{
            width: "100%", background: brandingColor, color: "#fff", border: "none",
            borderRadius: 10, padding: "14px", fontSize: 16, fontWeight: 700,
            cursor: placing ? "default" : "pointer", opacity: placing ? 0.7 : 1,
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          }}>
            {placing ? <><Spinner color="#fff" size={18} /> {t(lang, "please_wait")}</> : <><Check size={20} /> {t(lang, "place_order")}</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function OrderStatusView({ sessionToken, lang, setLang, companyName, brandingColor, pageBg, onNewOrder, payNotice, onDismissPayNotice }) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [newOrderWarn, setNewOrderWarn] = useState(false);

  const orderStatusRef = useRef(null);
  const load = useCallback(async () => {
    const r = await apiPublic(`/self/status/${encodeURIComponent(sessionToken)}`);
    if (r.success) { setOrder(r.data); orderStatusRef.current = r.data?.status; }
    setLoading(false);
    return r.success;
  }, [sessionToken]);

  // Poll with jitter/pause/backoff. We intentionally depend ONLY on [load]
  // (stable) — NOT on `order` — so the poller sets up ONCE and does not restart
  // every 5s (which would double the request rate and reset backoff). The
  // terminal-status stop is checked via a ref inside the poll instead.
  // (No separate initial-load effect — the smart poller does the first load.)
  useEffect(() => {
    let stop = null;
    const guarded = async () => {
      if (["finished", "rejected", "cancelled"].includes(orderStatusRef.current)) {
        if (stop) { stop(); stop = null; }   // terminal → stop polling
        return true;
      }
      return await load();
    };
    stop = makeSmartPoller(guarded, 5000, { key: `self-status-${sessionToken}` });
    return () => { if (stop) stop(); };
  }, [load]);

  const cancel = async () => {
    const r = await apiPublic(`/self/cancel/${encodeURIComponent(sessionToken)}`, { method: "POST" });
    if (r.success) load();
    setCancelConfirm(false);
  };

  // Trigger chime + vibrate + visual flash when status flips to ready_*.
  // Hook is internally edge-triggered so polling won't re-fire it.
  // Must be called BEFORE any conditional return (Rules of Hooks).
  const readyFlash = useReadyAlarm(order?.status);

  if (loading || !order) return <CustomerLoadingScreen lang={lang} />;

  const statusColor = {
    pending: S.yellow, preparing: S.accent, ready_pickup: S.green,
    ready_table: S.green, finished: S.muted, rejected: S.red, cancelled: S.muted,
  }[order.status] || S.muted;

  const statusIcon = {
    pending: <Clock size={40} color={statusColor} />,
    preparing: <Utensils size={40} color={statusColor} />,
    ready_pickup: <CheckCircle size={40} color={statusColor} />,
    ready_table: <Bike size={40} color={statusColor} />,
    finished: <CheckCircle size={40} color={statusColor} />,
    rejected: <XCircle size={40} color={statusColor} />,
    cancelled: <XCircle size={40} color={statusColor} />,
  }[order.status] || <Clock size={40} color={statusColor} />;

  const canCancel = order.status === "pending";
  const isFinal = ["finished", "rejected", "cancelled"].includes(order.status);

  return (
    <div style={{ minHeight: "100vh", background: pageBg || S.bg }}>
      {/* Pulsing green banner overlays the top while order just became ready —
          impossible to miss even if the customer is looking away. */}
      <style>{`
        @keyframes ready-pulse-banner {
          0%, 100% { transform: scale(1); box-shadow: 0 4px 18px rgba(22,163,74,0.55); }
          50% { transform: scale(1.025); box-shadow: 0 4px 28px rgba(22,163,74,0.95); }
        }
        @keyframes ready-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(22,163,74,0.45); }
          50% { box-shadow: 0 0 0 12px rgba(22,163,74,0.0); }
        }
        @keyframes pay-pulse {
          0%, 100% { transform: scale(1); box-shadow: 0 3px 14px rgba(245,158,11,0.45); }
          50% { transform: scale(1.03); box-shadow: 0 3px 24px rgba(245,158,11,0.85); }
        }
        @keyframes pay-blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.55; }
        }
      `}</style>
      {readyFlash && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 1500,
          background: "#16a34a", color: "#fff",
          padding: "16px 16px",
          paddingTop: "max(16px, env(safe-area-inset-top))",
          textAlign: "center", fontWeight: 800, fontSize: 18,
          boxShadow: "0 4px 18px rgba(22,163,74,0.55)",
          animation: "ready-pulse-banner 1.0s ease-in-out infinite",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
        }}>
          <CheckCircle size={26} />
          {order.status === "ready_pickup"
            ? t(lang, "status_ready_pickup")
            : t(lang, "status_ready_table")}
        </div>
      )}
      <div style={{ background: brandingColor, color: "#fff", padding: "14px 16px", paddingTop: "max(14px, env(safe-area-inset-top))", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{companyName || "—"}</div>
          <div style={{ fontSize: 12, opacity: 0.9 }}>
            {order.sourceType === "terminal" ? t(lang, "terminal") : t(lang, "table")} · {order.tableNumber}
          </div>
        </div>
        <LangPicker lang={lang} setLang={setLang} options={Object.keys(T)} />
      </div>

      <div style={{ padding: 16, maxWidth: 500, margin: "0 auto" }}>
        {/* Status card */}
        <div style={{
          background: "#fff", borderRadius: 14, padding: "26px 20px",
          textAlign: "center", boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
          border: `2px solid ${statusColor}22`,
        }}>
          <div style={{ marginBottom: 10 }}>{statusIcon}</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: S.dark, marginBottom: 4 }}>
            {t(lang, `status_${order.status}`)}
          </div>
          <div style={{ fontSize: 14, color: S.muted }}>
            {t(lang, "order_number")}: <b style={{ color: S.dark }}>{order.orderNumber}</b>
          </div>
          {/* Flashy "pay at the counter" reminder — visible the whole time the
              order waits for approval. Table/universal-QR orders only (kiosk
              and waiter flows pay differently). */}
          {order.status === "pending" && ["table", "takeaway"].includes(order.sourceType) && (
            <div style={{
              marginTop: 14,
              background: "#f59e0b", color: "#fff",
              borderRadius: 12, padding: "12px 14px",
              fontSize: 14.5, fontWeight: 800, lineHeight: 1.35,
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              animation: "pay-pulse 1.1s ease-in-out infinite",
            }}>
              <CreditCard size={20} style={{ flexShrink: 0, animation: "pay-blink 1.1s ease-in-out infinite" }} />
              {t(lang, "pay_counter_banner")}
            </div>
          )}
          {order.status === "preparing" && !isFinal && (
            <div style={{ marginTop: 14, display: "inline-flex", alignItems: "center", gap: 6, color: statusColor, fontSize: 13, fontWeight: 600 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: statusColor, animation: "pulse 1.5s infinite" }} />
              {t(lang, "please_wait")}
            </div>
          )}
          {order.rejectReason && (
            <div style={{ marginTop: 10, color: S.red, fontSize: 13 }}>{order.rejectReason}</div>
          )}
        </div>

        {/* Timings */}
        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <div style={{ flex: 1, background: "#fff", borderRadius: 10, padding: 10, textAlign: "center" }}>
            <div style={{ fontSize: 11, color: S.muted, textTransform: "uppercase", letterSpacing: 1 }}>{t(lang, "placed_at")}</div>
            <div style={{ fontWeight: 700, marginTop: 2 }}>{fmtTime(order.createdAt)}</div>
          </div>
          {order.readyAt && (
            <div style={{ flex: 1, background: "#fff", borderRadius: 10, padding: 10, textAlign: "center" }}>
              <div style={{ fontSize: 11, color: S.muted, textTransform: "uppercase", letterSpacing: 1 }}>{t(lang, "ready_at")}</div>
              <div style={{ fontWeight: 700, marginTop: 2 }}>{fmtTime(order.readyAt)}</div>
            </div>
          )}
        </div>

        {/* Items */}
        <div style={{ background: "#fff", borderRadius: 12, padding: 14, marginTop: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: S.muted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>
            {t(lang, "your_order")}
          </div>
          {order.items.map((it, idx) => {
            const isDone = it.kitchen_status === "done";
            const isPending = it.kitchen_status === "pending";
            return (
              <div key={idx} style={{ paddingBottom: 10, marginBottom: 10, borderBottom: idx < order.items.length - 1 ? `1px dashed ${S.border}` : "none", opacity: isDone ? 0.65 : 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
                    {isDone && <CheckCircle size={15} color={S.green} style={{ flexShrink: 0 }} />}
                    {isPending && <Clock size={15} color={S.yellow} style={{ flexShrink: 0 }} />}
                    <span style={{ textDecoration: isDone ? "line-through" : "none" }}>
                      {fmtQty(it.qty)}× {it.product_name}
                    </span>
                  </span>
                  <span style={{ fontWeight: 700 }}>{eur(it.line_total)}</span>
                </div>
                {it.tags?.length > 0 && (
                  <div style={{ fontSize: 12, color: S.muted, marginTop: 3 }}>+ {it.tags.join(", ")}</div>
                )}
                {it.note && (
                  <div style={{ fontSize: 12, color: S.muted, fontStyle: "italic", marginTop: 2 }}>"{it.note}"</div>
                )}
              </div>
            );
          })}
          <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 8, borderTop: `1px solid ${S.border}`, fontWeight: 800, fontSize: 16 }}>
            <span>{t(lang, "total")}</span>
            <span>{eur(order.total)}</span>
          </div>
        </div>

        {/* Actions */}
        <div style={{ marginTop: 16 }}>
          {canCancel && !cancelConfirm && (
            <button onClick={() => setCancelConfirm(true)} style={{ ...S.btn("#fff", S.red), width: "100%", padding: 12, border: `1px solid ${S.red}`, justifyContent: "center" }}>
              <X size={18} /> {t(lang, "cancel_order")}
            </button>
          )}
          {canCancel && cancelConfirm && (
            <div style={{ background: "#fef2f2", border: `1px solid #fecaca`, borderRadius: 10, padding: 12 }}>
              <div style={{ fontSize: 14, color: S.red, marginBottom: 10 }}>{t(lang, "confirm_cancel")}</div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => setCancelConfirm(false)} style={{ ...S.btn("#fff", S.text), flex: 1, border: `1px solid ${S.border}`, justifyContent: "center" }}>
                  {t(lang, "back")}
                </button>
                <button onClick={cancel} style={{ ...S.btn(S.red), flex: 1, justifyContent: "center" }}>
                  <Check size={18} /> {t(lang, "cancel_order")}
                </button>
              </div>
            </div>
          )}
          {isFinal && (
            <button onClick={onNewOrder} style={{ ...S.btn(brandingColor), width: "100%", padding: 12, justifyContent: "center" }}>
              <RefreshCw size={18} /> {t(lang, "place_new")}
            </button>
          )}

          {/* New-order button while the current order is still active.
              Once accepted (preparing/ready), the customer can start a fresh
              order — but we warn them first to follow the TV screen for the
              CURRENT order number, since they'll move off this screen. */}
          {!isFinal && !canCancel && (
            <>
              {!newOrderWarn ? (
                <button onClick={() => setNewOrderWarn(true)} style={{ ...S.btn("#fff", brandingColor), width: "100%", padding: 12, border: `1.5px solid ${brandingColor}`, justifyContent: "center", marginTop: 10 }}>
                  <Plus size={18} /> {t(lang, "place_new")}
                </button>
              ) : (
                <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, padding: 14, marginTop: 10 }}>
                  <div style={{ fontSize: 14, color: "#78350f", marginBottom: 4, fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
                    <Tv size={16} /> {t(lang, "follow_screen_title")}
                  </div>
                  <div style={{ fontSize: 13, color: "#78350f", marginBottom: 12, lineHeight: 1.5 }}>
                    {t(lang, "follow_screen_body")} <b>{order.orderNumber}</b>.
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => setNewOrderWarn(false)} style={{ ...S.btn("#fff", S.text), flex: 1, border: `1px solid ${S.border}`, justifyContent: "center" }}>
                      {t(lang, "back")}
                    </button>
                    <button onClick={onNewOrder} style={{ ...S.btn(brandingColor), flex: 1, justifyContent: "center" }}>
                      <Check size={18} /> {t(lang, "place_new")}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* One-time "pay at the counter" notice, shown right after placing the
          order. Dismissed with OK — the pulsing banner in the status card
          keeps reminding until the order is accepted. */}
      {payNotice && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 2000,
          background: "rgba(0,0,0,0.55)",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: 24, animation: "fade-in 0.2s ease",
        }}>
          <div style={{
            background: "#fff", borderRadius: 18, padding: "28px 22px",
            width: "100%", maxWidth: 360, textAlign: "center",
            boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
          }}>
            <div style={{
              width: 64, height: 64, borderRadius: 18, margin: "0 auto 14px",
              background: "#fef3c7", display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <CreditCard size={32} color="#f59e0b" />
            </div>
            <div style={{ fontSize: 19, fontWeight: 800, color: S.dark, marginBottom: 8 }}>
              {t(lang, "pay_notice_title")}
            </div>
            <div style={{ fontSize: 14.5, color: S.muted, lineHeight: 1.5, marginBottom: 20 }}>
              {t(lang, "pay_notice_body")}
            </div>
            <button
              onClick={onDismissPayNotice}
              style={{
                width: "100%", padding: "14px", borderRadius: 12, border: "none",
                background: brandingColor || "#3b82f6", color: "#fff",
                fontSize: 16, fontWeight: 800, cursor: "pointer", fontFamily: S.font,
              }}>
              {t(lang, "pay_notice_ok")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════════════════
//                              ADMIN APP
// ══════════════════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════════════════

function AdminLogin({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e?.preventDefault();
    setLoading(true); setError("");
    const r = await apiPublic("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password, app: "pos" }),
    });
    setLoading(false);
    if (r.success) {
      localStorage.setItem("self_token", r.data.token);
      localStorage.setItem("self_user", JSON.stringify(r.data.operator || r.data.user || {}));
      onLogin(r.data.token, r.data.operator || r.data.user);
    } else {
      setError(r.message || "Login failed");
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f0f2f5", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, fontFamily: S.font }}>
      <div style={{ width: 420, maxWidth: "100%", background: "#fff", borderRadius: 16, boxShadow: "0 12px 40px rgba(0,0,0,0.08)", padding: 32, border: `1px solid ${S.border}` }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ display: "inline-flex", width: 56, height: 56, borderRadius: 14, background: S.accent, alignItems: "center", justifyContent: "center", marginBottom: 12 }}>
            <Utensils size={28} color="#fff" />
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0, color: S.dark }}>
            EFIS.PRO <span style={{ color: S.accent }}>SELF</span>
          </h1>
          <p style={{ color: S.muted, fontSize: 14, margin: "6px 0 0" }}>Admin prijava</p>
        </div>
        <form onSubmit={submit}>
          <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: S.muted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Email</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="operator@company.me" style={{ ...S.input, marginBottom: 14 }} autoFocus />
          <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: S.muted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Lozinka</label>
          <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" style={{ ...S.input, marginBottom: 18 }} />
          {error && (
            <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 12px", marginBottom: 14, display: "flex", gap: 6, alignItems: "center", color: S.red, fontSize: 14 }}>
              <AlertCircle size={16} /> {error}
            </div>
          )}
          <button type="submit" disabled={loading} style={{ ...S.btn(S.dark), width: "100%", padding: 14, fontSize: 15, justifyContent: "center", opacity: loading ? 0.7 : 1 }}>
            {loading ? <><Spinner color="#fff" size={18} /> Prijava...</> : "Prijavi se"}
          </button>
        </form>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════
// PIN SETUP SCREEN — first-time flow, creates main admin
// ══════════════════════════════════════════════════
function PinSetupScreen({ user, onComplete }) {
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [step, setStep] = useState(1);
  const [error, setError] = useState("");

  const handleDigit = async (d) => {
    if (step === 1) {
      const next = pin + d;
      setPin(next);
      if (next.length === 4) setTimeout(() => setStep(2), 300);
    } else {
      const next = confirmPin + d;
      setConfirmPin(next);
      if (next.length === 4) {
        if (next !== pin) {
          setError("PIN-ovi se ne poklapaju");
          setTimeout(() => { setPin(""); setConfirmPin(""); setStep(1); setError(""); }, 1200);
        } else {
          const h = await hashPin(next);
          const data = {
            operators: [{
              id: user.id,
              name: user.full_name || user.name || user.email,
              operator_code: user.operator_code || "",
              pin_hash: h,
              permissions: ALL_PERMISSIONS.map(p => p.key),
              is_main_admin: true,
            }],
          };
          savePinData(data, user.company_id);
          onComplete(data.operators[0]);
        }
      }
    }
  };
  const handleBackspace = () => {
    if (step === 1) setPin(p => p.slice(0, -1));
    else setConfirmPin(p => p.slice(0, -1));
    setError("");
  };

  const currentPin = step === 1 ? pin : confirmPin;

  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "linear-gradient(135deg, #0a0a14 0%, #0d1525 40%, #0f1d35 100%)", fontFamily: S.font }}>
      <style>{`@keyframes dotPop { 0% { transform: scale(0); } 50% { transform: scale(1.3); } 100% { transform: scale(1); } }`}</style>

      <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 20, padding: "36px 40px", textAlign: "center", backdropFilter: "blur(20px)", maxWidth: 360 }}>
        <div style={{ width: 52, height: 52, borderRadius: 14, background: "rgba(59,130,246,0.15)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
          <Users size={24} color="#60a5fa" />
        </div>
        <h2 style={{ color: "white", fontSize: 20, fontWeight: 700, margin: "0 0 6px" }}>Postavi Admin PIN</h2>
        <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 14, margin: "0 0 8px" }}>
          {step === 1 ? "Unesite 4-cifreni PIN za vaš nalog" : "Potvrdite PIN ponovo"}
        </p>
        <p style={{ color: "rgba(59,130,246,0.6)", fontSize: 12, margin: "0 0 24px" }}>{user.full_name || user.email}</p>

        <div style={{ display: "flex", gap: 16, marginBottom: 8, justifyContent: "center" }}>
          {[0,1,2,3].map(i => (
            <div key={i} style={{ width: 18, height: 18, borderRadius: "50%", border: currentPin.length > i ? "none" : "2px solid rgba(255,255,255,0.2)", background: currentPin.length > i ? "#3b82f6" : "transparent", transition: "all 0.15s ease", animation: currentPin.length === i + 1 ? "dotPop 0.2s ease" : "none", boxShadow: currentPin.length > i ? `0 0 12px rgba(59,130,246,0.4)` : "none" }} />
          ))}
        </div>

        <div style={{ height: 24, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 12 }}>
          {error && <div style={{ color: "#f87171", fontSize: 13 }}>{error}</div>}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 70px)", gap: 10, justifyContent: "center" }}>
          {[1,2,3,4,5,6,7,8,9].map(n => (
            <PinKey key={n} onClick={() => handleDigit(String(n))}>{n}</PinKey>
          ))}
          <div />
          <PinKey onClick={() => handleDigit("0")}>0</PinKey>
          <PinKey onClick={handleBackspace} subtle>⌫</PinKey>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════
// PIN LOCK SCREEN — shown when locked, no auto-lock per Mhmd
// ══════════════════════════════════════════════════
function PinLockScreen({ onUnlock, pinData, companyName, onForgotPin }) {
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [shake, setShake] = useState(false);
  const [time, setTime] = useState(new Date());
  const [checking, setChecking] = useState(false);

  useEffect(() => { const iv = setInterval(() => setTime(new Date()), 1000); return () => clearInterval(iv); }, []);

  const handleDigit = async (d) => {
    if (checking) return;
    const next = pin + d;
    setPin(next);
    setError("");
    if (next.length === 4) {
      setChecking(true);
      const h = await hashPin(next);
      const found = (pinData.operators || []).find(op => op.pin_hash === h);
      if (found) {
        onUnlock(found);
      } else {
        setShake(true);
        setError("Pogrešan PIN");
        setTimeout(() => { setPin(""); setShake(false); setChecking(false); }, 600);
        return;
      }
      setChecking(false);
    }
  };
  const handleBackspace = () => { if (!checking) { setPin(p => p.slice(0, -1)); setError(""); } };

  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "linear-gradient(135deg, #0a0a14 0%, #0d1525 40%, #0f1d35 100%)", position: "relative", overflow: "hidden", fontFamily: S.font }}>
      <style>{`
        @keyframes shakeAnim { 0%,100% { transform: translateX(0); } 20%,60% { transform: translateX(-12px); } 40%,80% { transform: translateX(12px); } }
        @keyframes dotPop { 0% { transform: scale(0); } 50% { transform: scale(1.3); } 100% { transform: scale(1); } }
        @keyframes shimmer { 0% { background-position: -200% center; } 100% { background-position: 200% center; } }
      `}</style>

      <div style={{ marginBottom: 20, display: "inline-flex", width: 64, height: 64, borderRadius: 16, background: "rgba(59,130,246,0.15)", alignItems: "center", justifyContent: "center" }}>
        <Utensils size={30} color="#60a5fa" />
      </div>

      <div style={{ textAlign: "center", marginBottom: 28 }}>
        <div style={{ color: "white", fontSize: 42, fontWeight: 200, letterSpacing: 3, fontFamily: "'JetBrains Mono', monospace", background: "linear-gradient(90deg, #60a5fa, #a78bfa, #60a5fa)", backgroundSize: "200% auto", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", animation: "shimmer 4s linear infinite" }}>
          {time.toLocaleTimeString("sr-Latn-ME", { hour: "2-digit", minute: "2-digit" })}
        </div>
        <div style={{ color: "rgba(255,255,255,0.25)", fontSize: 13, marginTop: 4 }}>
          {time.toLocaleDateString("sr-Latn-ME", { weekday: "long", day: "numeric", month: "long" })}
        </div>
      </div>

      <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 13, letterSpacing: 2, textTransform: "uppercase", marginBottom: 16 }}>Unesite PIN</div>

      <div style={{ display: "flex", gap: 16, marginBottom: 8, animation: shake ? "shakeAnim 0.5s ease" : "none" }}>
        {[0,1,2,3].map(i => (
          <div key={i} style={{ width: 18, height: 18, borderRadius: "50%", border: pin.length > i ? "none" : "2px solid rgba(255,255,255,0.2)", background: pin.length > i ? (error ? "#ef4444" : "#3b82f6") : "transparent", transition: "all 0.15s ease", animation: pin.length === i + 1 && !error ? "dotPop 0.2s ease" : "none", boxShadow: pin.length > i ? `0 0 12px ${error ? "rgba(239,68,68,0.4)" : "rgba(59,130,246,0.4)"}` : "none" }} />
        ))}
      </div>

      <div style={{ height: 24, display: "flex", alignItems: "center", marginBottom: 12 }}>
        {error && <div style={{ color: "#f87171", fontSize: 14, fontWeight: 500 }}>{error}</div>}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 72px)", gap: 12 }}>
        {[1,2,3,4,5,6,7,8,9].map(n => (
          <PinKey key={n} onClick={() => handleDigit(String(n))}>{n}</PinKey>
        ))}
        <div />
        <PinKey onClick={() => handleDigit("0")}>0</PinKey>
        <PinKey onClick={handleBackspace} subtle>⌫</PinKey>
      </div>

      {/* Forgot PIN link */}
      {onForgotPin && (
        <button onClick={onForgotPin} style={{
          position: "absolute", bottom: 60, background: "transparent", border: "none",
          color: "rgba(255,255,255,0.4)", fontSize: 12, cursor: "pointer",
          textDecoration: "underline", fontFamily: S.font,
        }}>
          Zaboravili ste PIN? Odjavite se
        </button>
      )}

      <div style={{ position: "absolute", bottom: 24, color: "rgba(255,255,255,0.15)", fontSize: 12, letterSpacing: 1 }}>{companyName || "EFIS.PRO SELF"}</div>
    </div>
  );
}

function PinKey({ children, onClick, subtle }) {
  return (
    <button onClick={onClick} style={{
      width: 72, height: 72, borderRadius: 20,
      border: subtle ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(255,255,255,0.1)",
      background: subtle ? "transparent" : "rgba(255,255,255,0.04)",
      color: subtle ? "rgba(255,255,255,0.4)" : "white",
      fontSize: subtle ? 18 : 26, fontWeight: 500, cursor: "pointer",
      transition: "all 0.15s ease", backdropFilter: "blur(10px)",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}
      onMouseDown={e => { e.currentTarget.style.background = "rgba(59,130,246,0.2)"; e.currentTarget.style.transform = "scale(0.94)"; }}
      onMouseUp={e => { e.currentTarget.style.background = subtle ? "transparent" : "rgba(255,255,255,0.04)"; e.currentTarget.style.transform = "scale(1)"; }}
      onMouseLeave={e => { e.currentTarget.style.background = subtle ? "transparent" : "rgba(255,255,255,0.04)"; e.currentTarget.style.transform = "scale(1)"; }}
    >{children}</button>
  );
}

function AdminApp({ user, onLogout }) {
  const companyId = user?.company_id;
  const [tab, setTab] = useState("operations");
  const [toast, setToast] = useState(null);
  const showToast = (msg, type = "info") => setToast({ msg, type });

  // ─── Admin app background — per-device personalization ──────────────
  // Stored in localStorage (NOT in DB) because:
  //   1. It's a per-device visual preference, not a business setting.
  //   2. Lets admin distinguish SELF from POS when running both side-by-side.
  //   3. Different devices can use different colors without conflict.
  // Empty string / invalid hex = use default S.bg.
  const ADMIN_BG_KEY = "self_admin_bg_color";
  const [adminBg, setAdminBg] = useState(() => {
    try {
      const v = localStorage.getItem(ADMIN_BG_KEY) || "";
      return /^#[0-9a-fA-F]{3,8}$/.test(v) ? v : "";
    } catch { return ""; }
  });
  const [showBgPicker, setShowBgPicker] = useState(false);
  useEffect(() => {
    try {
      if (adminBg) localStorage.setItem(ADMIN_BG_KEY, adminBg);
      else localStorage.removeItem(ADMIN_BG_KEY);
    } catch {}
  }, [adminBg]);
  // Effective background for the admin shell — falls back to default light gray.
  const effectiveAdminBg = (adminBg && /^#[0-9a-fA-F]{3,8}$/.test(adminBg)) ? adminBg : S.bg;

  // CRITICAL: One-time migration of legacy unscoped localStorage keys to per-company keys.
  // Runs synchronously before the first PIN read. If we don't migrate first, an existing
  // user's PIN data (under old "self_pin_system" key) would appear to be missing under
  // the new scoped key, forcing them to set up PIN again.
  // Also: runs only once per AdminApp mount; safe if called repeatedly (idempotent).
  const migratedRef = useRef(false);
  if (companyId && !migratedRef.current) {
    migrateLegacyKeys(companyId);
    migratedRef.current = true;
  }

  // PIN system state — initial load AFTER migration completes
  const [pinData, setPinData] = useState(() => loadPinData(companyId));
  const [activeOperator, setActiveOperator] = useState(null);
  const [showPinSetup, setShowPinSetup] = useState(false);
  const [showResetOffer, setShowResetOffer] = useState(() => {
    // After re-login with "forgot PIN" flag set, offer reset
    const flag = sessionStorage.getItem("self_pin_reset_offer");
    if (flag === "1") {
      sessionStorage.removeItem("self_pin_reset_offer");
      return true;
    }
    return false;
  });

  const canAccess = (perm) => hasPermission(activeOperator, perm);

  // Synthesize virtual operator from logged-in user when PIN system is OFF.
  // This mirrors the POS pattern: activeOperator is ALWAYS populated when the
  // app is rendered, so fiscal calls always have a real operator_code (the one
  // returned by the auth endpoint, registered with Poreska Uprava). Without
  // this, activeOperator would be null when PIN is off, and we'd have to fall
  // back to user.operator_code or — worse — a hardcoded "ADMIN" string that
  // is not a valid Poreska Uprava code.
  // The _synthesized marker lets us tell apart this virtual operator from
  // a real PIN-unlocked one (used in handleLock to decide whether to keep it).
  useEffect(() => {
    const noPinSystem = !pinData || !pinData.operators || pinData.operators.length === 0;
    if (noPinSystem && (!activeOperator || !activeOperator._synthesized)) {
      setActiveOperator({
        name: user?.full_name || user?.name || user?.email || "Admin",
        operator_code: user?.operator_code || "",
        is_main_admin: true,
        permissions: ALL_PERMISSIONS.map(p => p.key),
        _synthesized: true,
      });
    }
  }, [pinData, user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select TCR if none stored (per-company)
  useEffect(() => {
    if (!companyId) return;
    if (getTcr(companyId)) return;
    apiAuth("/auth/company-config").then(r => {
      if (r.success) {
        const allTcrs = r.data?.all_tcrs || [];
        const active = allTcrs.filter(t => t.status === "active");
        if (active.length > 0) setTcr(active[0].tcr_code, companyId);
      }
    });
  }, [companyId]);

  // Lock only locks PIN-unlocked operators. If PIN is off (synthesized
  // operator), this is a no-op — the lock button won't be visible anyway
  // because of the (pinData && activeOperator) guard in the top bar.
  const handleLock = () => {
    if (activeOperator && !activeOperator._synthesized) setActiveOperator(null);
  };

  // Build tab list filtered by permissions (main admin sees all)
  const allTabs = [
    { key: "operations", label: "Operacije", icon: Utensils, permCheck: canSeeOperationsTab },
    { key: "invoices", label: "Računi", icon: Receipt, perm: "view_invoices" },
    { key: "menu", label: "Meni", icon: Coffee, perm: "edit_menu" },
    { key: "categories", label: "Kategorije", icon: Tag, perm: "manage_categories" },
    { key: "tables", label: "Stolovi & QR", icon: QrCode, perm: "manage_tables" },
    { key: "history", label: "Istorija", icon: HistoryIcon, perm: "view_history" },
    { key: "operators", label: "Operateri", icon: Users, perm: "manage_pins" },
    { key: "settings", label: "Podešavanja", icon: SettingsIcon, perm: "access_settings" },
  ];
  // Two gating modes — single-perm via `perm`, or arbitrary via `permCheck`
  const visibleTabs = allTabs.filter(t =>
    t.permCheck ? t.permCheck(activeOperator) : canAccess(t.perm)
  );

  // Auto-switch if current tab is no longer visible
  useEffect(() => {
    if (visibleTabs.length > 0 && !visibleTabs.find(t => t.key === tab)) {
      setTab(visibleTabs[0].key);
    }
  }, [activeOperator]); // eslint-disable-line react-hooks/exhaustive-deps

  // Forgot PIN handler: set a flag in sessionStorage and log out
  // After re-login, showResetOffer will be true → modal offers to reset
  const handleForgotPin = () => {
    sessionStorage.setItem("self_pin_reset_offer", "1");
    onLogout();
  };

  const handleResetPinSystem = () => {
    if (!window.confirm("Ovo će obrisati sve PIN-ove i operatere. Nastaviti?")) return;
    if (companyId) localStorage.removeItem(getPinKey(companyId));
    sessionStorage.removeItem("self_pin_reset_offer");
    showToast("PIN sistem resetovan.", "success");
    // Full reload — synthesizes fresh activeOperator from user, ensures clean state
    setTimeout(() => window.location.reload(), 500);
  };

  // If admin just re-logged-in after forgot-PIN, offer reset
  if (showResetOffer && pinData && pinData.operators?.length > 0) {
    return (
      <div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: S.bg, padding: 20 }}>
        <div style={{ background: "#fff", borderRadius: 16, padding: 30, maxWidth: 440, width: "100%", boxShadow: "0 10px 30px rgba(0,0,0,0.1)" }}>
          <div style={{ display: "inline-flex", width: 56, height: 56, borderRadius: 14, background: "rgba(245,158,11,0.1)", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
            <AlertCircle size={28} color={S.yellow} />
          </div>
          <h2 style={{ margin: "0 0 8px", fontSize: 20 }}>PIN sistem je već aktivan</h2>
          <p style={{ color: S.muted, fontSize: 14, lineHeight: 1.5, marginBottom: 20 }}>
            Možete nastaviti unosom PIN-a, ili ako ste zaboravili PIN — resetovati cijeli sistem. Reset briše sve operatere i PIN-ove. Nakon reseta postavićete novi Admin PIN.
          </p>
          <div style={{ display: "flex", gap: 8, flexDirection: "column" }}>
            <button onClick={() => setShowResetOffer(false)} style={{ ...S.btn(S.accent), justifyContent: "center", padding: "12px" }}>
              <Lock size={16} /> Nastavi — imam PIN
            </button>
            <button onClick={handleResetPinSystem} style={{ ...S.btn("#fff", S.red), border: `1px solid ${S.red}`, justifyContent: "center", padding: "12px" }}>
              <Trash2 size={16} /> Resetuj PIN sistem
            </button>
          </div>
          <div style={{ fontSize: 11, color: S.muted, marginTop: 14, textAlign: "center" }}>
            Ova opcija je dostupna jer ste se upravo prijavili sa email-om i lozinkom (dokaz identiteta).
          </div>
        </div>
      </div>
    );
  }

  // If PIN setup needed (explicit trigger from Operators tab first-use)
  if (showPinSetup) {
    return <PinSetupScreen user={user} onComplete={(op) => {
      setPinData(loadPinData(companyId));
      setActiveOperator(op);
      setShowPinSetup(false);
      showToast("PIN sistem aktiviran", "success");
    }} />;
  }

  // If PIN data exists but nobody unlocked → show lock screen
  if (pinData && pinData.operators?.length > 0 && !activeOperator) {
    return <PinLockScreen
      pinData={pinData}
      companyName={user?.company_name || "EFIS.PRO SELF"}
      onUnlock={(op) => setActiveOperator(op)}
      onForgotPin={handleForgotPin}
    />;
  }

  return (
    <div style={{ minHeight: "100vh", background: effectiveAdminBg, fontFamily: S.font }}>
      {/* Top bar */}
      <div style={{ background: S.dark, color: "#fff", padding: "10px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Utensils size={22} />
          <div>
            <div style={{ fontWeight: 800, fontSize: 15 }}>EFIS.PRO <span style={{ color: "#93c5fd" }}>SELF</span></div>
            <div style={{ fontSize: 11, color: "#94a3b8" }}>Admin Panel</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ fontSize: 13, color: "#cbd5e1", display: "flex", alignItems: "center", gap: 6 }}>
            <User size={15} />
            {activeOperator?.name || user?.full_name || user?.email || "—"}
            {activeOperator?.is_main_admin && <span style={{ fontSize: 10, color: "#93c5fd", fontWeight: 700, marginLeft: 4 }}>ADMIN</span>}
          </div>

          {/* Theme picker — opens a small popover with 6 presets + custom color.
              Stored per-device in localStorage. Useful when running SELF
              admin and POS admin side-by-side to avoid mixing them up. */}
          <div style={{ position: "relative" }}>
            <button
              onClick={() => setShowBgPicker(s => !s)}
              title="Pozadina admin panela"
              style={{
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.2)",
                color: "#fff", borderRadius: 8, padding: "6px 10px",
                cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
                fontSize: 13,
              }}>
              <Palette size={14} />
              <span style={{
                width: 12, height: 12, borderRadius: 3,
                background: effectiveAdminBg, border: "1px solid rgba(255,255,255,0.3)",
                display: "inline-block",
              }} />
            </button>

            {showBgPicker && (
              <>
                {/* Click-outside catcher */}
                <div
                  onClick={() => setShowBgPicker(false)}
                  style={{ position: "fixed", inset: 0, zIndex: 100 }}
                />
                <div style={{
                  position: "absolute", top: "calc(100% + 8px)", right: 0,
                  background: "#fff", borderRadius: 10,
                  boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
                  border: `1px solid ${S.border}`,
                  padding: 12, zIndex: 101, width: 240,
                  fontFamily: S.font, color: S.dark,
                }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: S.muted, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>
                    Pozadina admin panela
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6, marginBottom: 10 }}>
                    {[
                      { v: "",        lbl: "Default",   swatch: "#f5f6f8" },
                      { v: "#fef3c7", lbl: "Žuta",      swatch: "#fef3c7" },
                      { v: "#dcfce7", lbl: "Zelena",    swatch: "#dcfce7" },
                      { v: "#dbeafe", lbl: "Plava",     swatch: "#dbeafe" },
                      { v: "#fce7f3", lbl: "Roze",      swatch: "#fce7f3" },
                      { v: "#1f2937", lbl: "Tamna",     swatch: "#1f2937", darkText: true },
                    ].map(p => {
                      const active = (adminBg || "") === p.v;
                      return (
                        <button key={p.v || "default"}
                          onClick={() => setAdminBg(p.v)}
                          style={{
                            padding: 8, borderRadius: 8,
                            border: `2px solid ${active ? S.accent : S.border}`,
                            background: p.swatch,
                            color: p.darkText ? "#fff" : S.dark,
                            fontSize: 11, fontWeight: 600,
                            cursor: "pointer", fontFamily: S.font,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            minHeight: 38,
                          }}>
                          {p.lbl}
                        </button>
                      );
                    })}
                  </div>
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <input type="color"
                      value={adminBg || "#f5f6f8"}
                      onChange={e => setAdminBg(e.target.value)}
                      style={{ width: 32, height: 30, border: "none", cursor: "pointer", padding: 0 }} />
                    <input
                      value={adminBg || ""}
                      onChange={e => {
                        const v = e.target.value;
                        // Only apply if it looks like a valid hex (or empty)
                        if (v === "" || /^#[0-9a-fA-F]{0,8}$/.test(v)) setAdminBg(v);
                      }}
                      placeholder="prazno = default"
                      style={{
                        flex: 1, padding: "5px 8px", fontSize: 12,
                        border: `1px solid ${S.border}`, borderRadius: 6,
                        fontFamily: S.font, color: S.dark, outline: "none",
                      }} />
                  </div>
                  <div style={{ fontSize: 10, color: S.muted, marginTop: 8, lineHeight: 1.4 }}>
                    Lokalno na ovom uređaju (ne dijeli se sa drugim admin uređajima).
                  </div>
                </div>
              </>
            )}
          </div>

          {pinData && activeOperator && (
            <button onClick={handleLock} title="Zaključaj" style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.2)", color: "#fff", borderRadius: 8, padding: "6px 10px", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
              <Lock size={14} /> Zaključaj
            </button>
          )}
          <button onClick={onLogout} style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.2)", color: "#fff", borderRadius: 8, padding: "6px 10px", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
            <LogOut size={14} /> Odjava
          </button>
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ background: "#fff", borderBottom: `1px solid ${S.border}`, display: "flex", overflowX: "auto", padding: "0 4px" }}>
        {visibleTabs.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)} style={{
            padding: "12px 16px", border: "none", background: "transparent",
            borderBottom: `3px solid ${tab === key ? S.accent : "transparent"}`,
            color: tab === key ? S.accent : S.muted,
            fontWeight: tab === key ? 700 : 500, cursor: "pointer", display: "flex",
            alignItems: "center", gap: 6, fontSize: 14, whiteSpace: "nowrap",
          }}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      <div style={{ padding: "16px", maxWidth: 1400, margin: "0 auto" }}>
        {tab === "operations" && <OperationsTab showToast={showToast} activeOperator={activeOperator} />}
        {tab === "invoices" && <InvoicesTab showToast={showToast} activeOperator={activeOperator} />}
        {tab === "menu" && <MenuSettingsTab showToast={showToast} />}
        {tab === "categories" && <CategoriesTab showToast={showToast} />}
        {tab === "tables" && <TablesQRTab showToast={showToast} />}
        {tab === "history" && <HistoryTab showToast={showToast} />}
        {tab === "operators" && <OperatorsTab
          showToast={showToast}
          user={user}
          pinData={pinData}
          setPinData={setPinData}
          activeOperator={activeOperator}
          setActiveOperator={setActiveOperator}
          onTriggerSetup={() => setShowPinSetup(true)}
        />}
        {tab === "settings" && <SettingsTab showToast={showToast} />}
      </div>

      <Toast msg={toast?.msg} type={toast?.type} onClose={() => setToast(null)} />
    </div>
  );
}

// ══════════════════════════════════════════════════
// OPERATIONS TAB — the critical daily-use view
// ══════════════════════════════════════════════════
function OperationsTab({ showToast, activeOperator }) {
  const canAccess = (p) => hasPermission(activeOperator, p);

  // Sub-tab visibility — operator may have access to a subset of [Nove,
  // Aktivne, Istorija, Nova prodaja]. Pick the first allowed one as default.
  const subTabsVisible = useMemo(() => ({
    new:          canAccess("ops_view_new"),
    active:       canAccess("ops_view_active"),
    history24:    canAccess("ops_view_history"),
    nova_prodaja: canAccess("ops_create_sale"),
  }), [activeOperator]); // eslint-disable-line react-hooks/exhaustive-deps
  const initialSub = subTabsVisible.new ? "new"
                   : subTabsVisible.active ? "active"
                   : subTabsVisible.history24 ? "history24"
                   : subTabsVisible.nova_prodaja ? "nova_prodaja"
                   : "new"; // fallback (shouldn't render — Operacije nav tab would be hidden)

  const [sub, setSub] = useState(initialSub);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("self_sound") !== "false");
  const [acceptModal, setAcceptModal] = useState(null);
  const [rejectModal, setRejectModal] = useState(null);
  const [fiscalizeModal, setFiscalizeModal] = useState(null);
  const [activeOrderModal, setActiveOrderModal] = useState(null); // order being viewed in detail modal
  const lastPendingCountRef = useRef(0);
  const audioRef = useRef(null);
  const soundOnRef = useRef(soundOn);
  useEffect(() => { soundOnRef.current = soundOn; }, [soundOn]);

  // Auto-switch sub-tab if current one becomes inaccessible
  useEffect(() => {
    if (!subTabsVisible[sub]) {
      const fallback = subTabsVisible.new ? "new"
                     : subTabsVisible.active ? "active"
                     : subTabsVisible.history24 ? "history24"
                     : subTabsVisible.nova_prodaja ? "nova_prodaja"
                     : null;
      if (fallback) setSub(fallback);
    }
  }, [subTabsVisible, sub]);

  const load = useCallback(async () => {
    if (sub === "nova_prodaja") return; // sales screen has no orders list
    const path = sub === "history24" ? "/self-admin/history" : "/self-admin/orders";
    const r = await apiAuth(path);
    if (r.success) {
      setOrders(r.data?.orders || []);
      setLoading(false);

      // Sound on new pending (soundOn read from ref → does NOT churn `load`)
      if (sub === "new") {
        const pendingNow = (r.data?.orders || []).filter(o => o.status === "pending").length;
        if (pendingNow > lastPendingCountRef.current && soundOnRef.current && audioRef.current) {
          audioRef.current.play().catch(() => {});
        }
        lastPendingCountRef.current = pendingNow;
      }
    }
    return r.success;
  }, [sub]);

  // Operacije dashboard: smart poller only (jitter/pause/backoff), 5s → 7s.
  // `load` depends ONLY on [sub] now, so the poller sets up once per tab and
  // never restarts on re-render. The smart poller does the initial load itself,
  // so there is NO separate initial-load effect (which would double requests).
  useEffect(() => {
    if (sub === "nova_prodaja") return;
    return makeSmartPoller(load, 7000, { key: `ops-orders-${sub}` });
  }, [load, sub]);

  useEffect(() => { localStorage.setItem("self_sound", soundOn ? "true" : "false"); }, [soundOn]);

  const filtered = useMemo(() => {
    if (sub === "new") return orders.filter(o => o.status === "pending");
    if (sub === "active") return orders.filter(o => ["preparing", "ready_pickup", "ready_table"].includes(o.status));
    return orders; // history24 already filtered server-side
  }, [orders, sub]);

  const counts = useMemo(() => ({
    pending: orders.filter(o => o.status === "pending").length,
    active: orders.filter(o => ["preparing", "ready_pickup", "ready_table"].includes(o.status)).length,
  }), [orders]);

  // accept(id, mode='draft', linkedInvoiceId, opts)
  // opts.chainFiscalize=true → after accept succeeds, immediately open
  // FiscalizeModal for the same order. Lets the operator do "Prihvati i
  // fiskalizuj" in one click.
  const accept = async (id, mode, linkedInvoiceId, opts = {}) => {
    const r = await apiAuth(`/self-admin/accept/${id}`, {
      method: "POST",
      body: JSON.stringify({ mode, linkedInvoiceId }),
    });
    if (r.success) {
      showToast("Narudžba prihvaćena", "success");
      setAcceptModal(null);
      // Reload first; then open the fiscalize modal using fresh order data
      const fresh = await apiAuth(`/self-admin/orders`);
      const reloaded = fresh.success ? (fresh.data?.orders || []) : [];
      setOrders(reloaded);
      setLoading(false);
      if (opts.chainFiscalize) {
        const justAccepted = reloaded.find(o => o.id === id);
        if (justAccepted) setFiscalizeModal(justAccepted);
      }
    } else {
      showToast(r.message || "Error", "error");
    }
  };

  const reject = async (id, reason) => {
    const r = await apiAuth(`/self-admin/reject/${id}`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    });
    if (r.success) {
      showToast("Narudžba odbijena", "success");
      setRejectModal(null);
      load();
    } else {
      showToast(r.message || "Error", "error");
    }
  };

  const setStatus = async (id, status) => {
    const r = await apiAuth(`/self-admin/setStatus/${id}`, {
      method: "POST",
      body: JSON.stringify({ status }),
    });
    if (r.success) { showToast("Status ažuriran", "success"); load(); }
    else showToast(r.message || "Error", "error");
  };

  const finish = async (id) => {
    const r = await apiAuth(`/self-admin/finish/${id}`, { method: "POST" });
    if (r.success) { showToast("Narudžba završena", "success"); load(); }
    else showToast(r.message || "Error", "error");
  };

  const unfinish = async (id) => {
    const r = await apiAuth(`/self-admin/unfinish/${id}`, { method: "POST" });
    if (r.success) { showToast("Narudžba reaktivirana", "success"); load(); }
    else showToast(r.message || "Error", "error");
  };

  return (
    <div>
      {/* Sub-tabs + sound */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, alignItems: "center" }}>
        {subTabsVisible.new          && <SubTab active={sub === "new"} onClick={() => setSub("new")} badge={counts.pending} badgeColor={S.yellow}>Nove</SubTab>}
        {subTabsVisible.active       && <SubTab active={sub === "active"} onClick={() => setSub("active")} badge={counts.active} badgeColor={S.accent}>Aktivne</SubTab>}
        {subTabsVisible.history24    && <SubTab active={sub === "history24"} onClick={() => setSub("history24")}>Istorija 24h</SubTab>}
        {subTabsVisible.nova_prodaja && <SubTab active={sub === "nova_prodaja"} onClick={() => setSub("nova_prodaja")}>+ Nova prodaja</SubTab>}
        <div style={{ flex: 1 }} />
        {/* Sound toggle is for order alerts — hide when on Nova prodaja since
            no incoming-order alert is relevant on the sales screen. */}
        {sub !== "nova_prodaja" && (
          <button onClick={() => setSoundOn(s => !s)} style={{
            background: "transparent", border: `1px solid ${S.border}`, borderRadius: 8,
            padding: "8px 10px", cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
            color: soundOn ? S.green : S.muted, fontSize: 13,
          }}>
            {soundOn ? <Volume2 size={16} /> : <VolumeX size={16} />} {soundOn ? "Zvuk" : "Utišano"}
          </button>
        )}
      </div>

      {sub === "nova_prodaja" ? (
        <NovaProdajaTab
          showToast={showToast}
          activeOperator={activeOperator}
          onCreated={() => {
            // After successful KASA sale: switch back to Aktivne so the
            // operator can see the order they just created.
            if (subTabsVisible.active) {
              setSub("active");
              load();
            }
          }}
        />
      ) : loading ? (
        <div style={{ textAlign: "center", padding: 60 }}><Spinner /></div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: 60, color: S.muted }}>
          Nema narudžbi
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 12 }}>
          {filtered.map(o => (
            <AdminOrderCard key={o.id} order={o}
              onAccept={() => setAcceptModal(o)}
              onAcceptAndFiscalize={() => accept(o.id, "draft", null, { chainFiscalize: true })}
              onReject={() => setRejectModal(o)}
              onSetStatus={setStatus}
              onFinish={finish}
              onUnfinish={unfinish}
              onFiscalize={() => setFiscalizeModal(o)}
              onCardClick={() => {
                // Active cards open the detail modal. Pending/finished cards
                // do nothing on click — their actions are already on buttons.
                if (["preparing", "ready_pickup", "ready_table"].includes(o.status)) {
                  setActiveOrderModal(o);
                }
              }}
              canAccept={canAccess("accept_orders")}
              canStatus={canAccess("change_status")}
              canFiscalize={canAccess("fiscalize")}
            />
          ))}
        </div>
      )}

      {acceptModal && (
        <AcceptOrderModal
          order={acceptModal}
          onClose={() => setAcceptModal(null)}
          onAccept={accept}
        />
      )}
      {rejectModal && (
        <RejectOrderModal
          order={rejectModal}
          onClose={() => setRejectModal(null)}
          onReject={reject}
        />
      )}
      {fiscalizeModal && (
        <FiscalizeModal
          order={fiscalizeModal}
          onClose={() => setFiscalizeModal(null)}
          onSuccess={() => { setFiscalizeModal(null); load(); }}
          showToast={showToast}
          activeOperator={activeOperator}
        />
      )}

      {activeOrderModal && (
        <ActiveOrderModal
          order={activeOrderModal}
          onClose={() => setActiveOrderModal(null)}
          onChanged={load}
          showToast={showToast}
          canStatus={canAccess("change_status")}
          canFiscalize={canAccess("fiscalize")}
          onSetStatus={setStatus}
          onFinish={finish}
          onFiscalize={() => { setActiveOrderModal(null); setFiscalizeModal(activeOrderModal); }}
        />
      )}

      {/* Notification sound */}
      <audio ref={audioRef} preload="auto">
        <source src="data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAGAACAgICAgICBgYGBgYKCgoKCg4ODg4OEhISEhIWFhYaGhoeHh4iIiImJiYqKiouLjIyNjY6Oj4+QkJGRkpOTlJWVlpeXmJmZmpucnJ2en5+goaGio6OkpaanqKmqq6ytrq+wsrO0tba3uLq7vL2+wMHDxMXHyMnLzM7P0dLU1dfY2tvc3t/h4uTl5+jp6+zt7/Dx8vT19vj5+vv8/f7+/v39/Pv6+fj39vX08/Lx8O/u7ezr6uno5+bl5OPi4eDf3t3c29rZ2NfW1dTT0tHQz87NzMvKycjHxsXEw8LBwL++vby7urm4t7a1tLOysbCvrq2sq6qpqKempaSjoqGgoJ+enZybmpmYl5aVlJOSkZCPjo2MjIuKiYiHhoWEhIOCgYGAgICAgA==" type="audio/wav" />
      </audio>
    </div>
  );
}

function SubTab({ active, onClick, badge, badgeColor, children }) {
  return (
    <button onClick={onClick} style={{
      padding: "8px 14px", border: `1px solid ${active ? S.accent : S.border}`,
      background: active ? S.accent : "#fff",
      color: active ? "#fff" : S.text,
      borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: "pointer",
      display: "flex", alignItems: "center", gap: 6,
    }}>
      {children}
      {badge > 0 && (
        <span style={{
          background: active ? "rgba(255,255,255,0.25)" : (badgeColor || S.yellow),
          color: active ? "#fff" : "#fff",
          borderRadius: 10, padding: "1px 7px", fontSize: 11, fontWeight: 700, minWidth: 20, textAlign: "center",
        }}>{badge}</span>
      )}
    </button>
  );
}

// ════════════════════════════════════════════════════════════════════
// ════════════════════════════════════════════════════════════════════
//  NOVA PRODAJA TAB — KASA direct sale screen
//
//  Mirrors POS layout (left product grid + right cart pane). Differences:
//   - Products come from /api/self-settings/products (only kitchen-enabled
//     items configured for SELF). is_visible is NOT a filter — admin can
//     sell hidden items too.
//   - On submit: chains 3 calls
//       1. POST /api/self-admin/direct-sale  → creates self_order + kitchen
//       2. POST /api/invoices                → fiscalizes (cash/card/split)
//       3. POST /api/self-admin/link-invoice → binds fiscal invoice back
//     If step 1 succeeds and step 2 fails, the order sits in Aktivne
//     unfiscalized — admin can fiscalize from there. No data lost.
//   - Auto-prints fiscal receipt via window.electronAPI.printReceipt
//     (uses SELF admin .exe printer config — different from POS app)
//   - Layout settings: just "kartice / tabela" toggle + card size.
//     Stored per-device in localStorage. No images/photos toggle (kept
//     simple per design intent — that's exclusive to POS app).
// ════════════════════════════════════════════════════════════════════
// ════════════════════════════════════════════════════════════════════

const NP_LAYOUT_KEY = "self_nova_prodaja_layout"; // { style, cardSize }
const NP_CARD_SIZES = {
  malo:    { px: 110, label: "Malo" },
  srednje: { px: 150, label: "Srednje" },
  veliko:  { px: 200, label: "Veliko" },
};

function loadNpLayout() {
  try {
    const raw = localStorage.getItem(NP_LAYOUT_KEY);
    if (!raw) return { style: "kartice", cardSize: "srednje" };
    const obj = JSON.parse(raw);
    return {
      style: obj.style === "tabela" ? "tabela" : "kartice",
      cardSize: NP_CARD_SIZES[obj.cardSize] ? obj.cardSize : "srednje",
    };
  } catch {
    return { style: "kartice", cardSize: "srednje" };
  }
}
function saveNpLayout(layout) {
  try { localStorage.setItem(NP_LAYOUT_KEY, JSON.stringify(layout)); } catch {}
}

function NovaProdajaTab({ showToast, activeOperator, onCreated }) {
  // ─── Data ─────────────────────────────────────────────────────────
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]); // SELF categories (from settings)
  const [loadingProducts, setLoadingProducts] = useState(true);

  // ─── UI state ─────────────────────────────────────────────────────
  // Filtering uses the SELF categories directly (same ones the customer
  // sees). No POS master-category bridging — it was unreliable (matched
  // SELF↔POS categories by name, which broke whenever names differed).
  const [activeCategory, setActiveCategory] = useState("all"); // self_category_id | "all"
  const [search, setSearch] = useState("");
  const [layout, setLayout] = useState(loadNpLayout);
  const [showLayoutPanel, setShowLayoutPanel] = useState(false);

  // ─── Cart ─────────────────────────────────────────────────────────
  // Each cart line:
  //   { id, name, price, vat_rate, quantity, custom_price?, kitchen_tags_preset, kitchen_tags }
  const [cart, setCart] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [customerName, setCustomerName] = useState("");

  // ─── Fiscalization state ──────────────────────────────────────────
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [splitCash, setSplitCash] = useState("");
  const [splitCard, setSplitCard] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // ─── Permission gates ─────────────────────────────────────────────
  const canFiscalize = hasPermission(activeOperator, "fiscalize");

  // ─── Initial load ─────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      setLoadingProducts(true);
      // Products + SELF categories — that's all we need. Filter chips are
      // the SELF categories (same as the customer menu).
      const [pRes, cRes] = await Promise.all([
        apiAuth("/self-settings/products"),
        apiAuth("/self-settings/categories"),
      ]);
      if (pRes.success) setProducts(pRes.data?.products || []);
      if (cRes.success) setCategories(cRes.data?.categories || []);
      setLoadingProducts(false);
    })();
  }, []);

  // ─── Save layout on change ────────────────────────────────────────
  useEffect(() => { saveNpLayout(layout); }, [layout]);

  // ─── Reset card-partner-style fields when payment method changes ──
  useEffect(() => {
    if (paymentMethod !== "split") {
      setSplitCash("");
      setSplitCard("");
    }
  }, [paymentMethod]);

  // ─── Filter products by category + search ─────────────────────────
  // Products carry self_category_id (set in admin Meni). Chips are the
  // SELF categories. "Sve" = no category filter. Products with no
  // self_category_id only show under "Sve".
  const filteredProducts = useMemo(() => {
    let list = products;
    // Category filter
    if (activeCategory !== "all") {
      list = list.filter(p => Number(p.self_category_id) === Number(activeCategory));
    }
    // Search filter
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(p =>
        (p.custom_name || p.name || "").toLowerCase().includes(q) ||
        (p.sku || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [products, activeCategory, search]);

  // ─── Cart operations ──────────────────────────────────────────────
  const addToCart = (product) => {
    const price = product.custom_price !== null ? product.custom_price : product.pos_price;
    const name = product.custom_name || product.name;
    setCart(prev => {
      const existing = prev.find(c => c.id === product.id);
      if (existing) {
        return prev.map(c => c.id === product.id ? { ...c, quantity: c.quantity + 1 } : c);
      }
      return [...prev, {
        id: product.id,
        name,
        price: parseFloat(price),
        vat_rate: parseFloat(product.vat_rate || 21),
        quantity: 1,
        custom_price: null,
        kitchen_tags_preset: Array.isArray(product.tags_preset) ? product.tags_preset : [],
        kitchen_tags: [],
        note: "",
      }];
    });
  };

  const updateCartItem = (id, patch) => {
    setCart(prev => prev.map(c => c.id === id ? { ...c, ...patch } : c));
  };
  const removeFromCart = (id) => setCart(prev => prev.filter(c => c.id !== id));
  const clearCart = () => {
    setCart([]);
    setEditingItem(null);
    setCustomerName("");
    setPaymentMethod("cash");
    setSplitCash("");
    setSplitCard("");
    setError(null);
  };

  // ─── Cart calculations (per-VAT-rate breakdown like POS) ──────────
  const cartCalc = useMemo(() => {
    let netTotal = 0;
    const vatGroups = {};
    cart.forEach(item => {
      const up = item.custom_price !== null ? item.custom_price : item.price;
      const lineNet = item.quantity * up;
      const rate = item.vat_rate;
      if (!vatGroups[rate]) vatGroups[rate] = { base: 0, vat: 0 };
      vatGroups[rate].base += lineNet;
      vatGroups[rate].vat += lineNet * (rate / 100);
      netTotal += lineNet;
    });
    let vatAmount = 0;
    Object.values(vatGroups).forEach(g => { vatAmount += g.vat; });
    const total = netTotal + vatAmount;
    return { netTotal, vatAmount, total, vatGroups };
  }, [cart]);

  // ─── Submit: chain create → fiscalize → link → auto-print ─────────
  const handleSubmit = async () => {
    if (cart.length === 0) return;
    setShowConfirm(false);
    setError(null);

    // Operator code guard (same as FiscalizeModal)
    if (!activeOperator?.operator_code) {
      setError("Operater nije konfiguriran. Postavke → Operateri za podešavanje.");
      return;
    }
    // TCR
    const user = (() => { try { return JSON.parse(localStorage.getItem("self_user") || "{}"); } catch { return {}; } })();
    const tcr = localStorage.getItem("pos_tcr_" + (user.company_id || ""));
    if (!tcr) {
      setError("TCR nije podešen. Otvorite POS → Podešavanja → Fiskalizacija.");
      return;
    }
    // Split validation
    if (paymentMethod === "split") {
      const sc = parseFloat(splitCash) || 0;
      const sd = parseFloat(splitCard) || 0;
      if (Math.abs(sc + sd - cartCalc.total) > 0.01) {
        setError(`Podjela se ne slaže: keš ${eur(sc)} + kartica ${eur(sd)} = ${eur(sc+sd)}, a iznos je ${eur(cartCalc.total)}`);
        return;
      }
    }

    setSubmitting(true);

    // Internal day check (best-effort, non-fatal failure)
    try {
      const idCheck = await apiAuth("/internalday/settings");
      if (idCheck.success && idCheck.data?.internal_day_enabled) {
        const idStatus = await apiAuth(`/internalday/status/${tcr}`);
        if (idStatus.success && !idStatus.data?.day) {
          setSubmitting(false);
          setError("Interni dan nije otvoren! Otvorite dan u POS → Blagajna.");
          return;
        }
      }
    } catch (e) { /* ignore */ }

    // ─── Step 1: create SELF order (status=preparing, source=kasa) ──
    const directSalePayload = {
      customerName: customerName.trim(),
      note: "",
      items: cart.map(it => ({
        productId: it.id,
        qty: it.quantity,
        tags: it.kitchen_tags || [],
        note: it.note || "",
      })),
    };
    const createRes = await apiAuth("/self-admin/direct-sale", {
      method: "POST",
      body: JSON.stringify(directSalePayload),
    });
    if (!createRes.success) {
      setSubmitting(false);
      setError(createRes.message || "Greška pri kreiranju narudžbe");
      return;
    }
    const { orderId, orderNumber } = createRes.data;

    // ─── Step 2: fiscalize via /api/invoices ─────────────────────────
    const invoicePayload = {
      location_id: user.location_id || 1,
      tcr_code: tcr,
      operator_code: activeOperator.operator_code,
      payment_method: paymentMethod,
      fiscalize_now: true,
      discount_type: "none",
      discount_value: 0,
      ...(paymentMethod === "split" && {
        split_cash: parseFloat(splitCash) || 0,
        split_card: parseFloat(splitCard) || 0,
      }),
      items: cart.map(item => ({
        product_id: item.id,
        name: item.name,
        quantity: item.quantity,
        unit_price: item.custom_price !== null ? item.custom_price : item.price,
        vat_rate: item.vat_rate,
        unit: "kom",
        discount_type: "none",
        discount_value: 0,
      })),
    };
    const fiscRes = await apiAuth("/invoices", {
      method: "POST",
      body: JSON.stringify(invoicePayload),
    });
    if (!fiscRes.success) {
      setSubmitting(false);
      setError(`Narudžba kreirana (${orderNumber}), ali fiskalizacija nije uspjela: ${fiscRes.message || "greška"}. Možete fiskalizovati ručno iz Aktivne tab.`);
      // Don't clear cart yet — operator might want to retry, but the SELF order
      // exists either way. We do bounce them back to Aktivne so they see the
      // unfiscalized order and can retry from there.
      onCreated && onCreated();
      return;
    }
    const fiscalInvoiceId = fiscRes.data?.invoice?.id;
    const fiscalInvoiceNumber = fiscRes.data?.invoice?.invoice_number;

    // ─── Step 3: link the fiscal invoice to the SELF order ───────────
    if (fiscalInvoiceId) {
      try {
        await apiAuth(`/self-admin/link-invoice/${orderId}`, {
          method: "POST",
          body: JSON.stringify({ invoiceId: fiscalInvoiceId }),
        });
      } catch (e) {
        // Non-fatal — admin can manually link from Aktivne tab if it failed
        console.warn("Link-invoice failed:", e);
      }
    }

    // ─── Step 4: auto-print fiscal receipt (fire-and-forget) ─────────
    if (fiscalInvoiceId && window.electronAPI?.printReceipt) {
      try {
        // Print using SELF admin's printer (different from POS). Format similar
        // to ActiveOrderModal's printPickupTicket but with FIC/IIC included
        // since this IS a fiscal receipt now.
        const company = {
          name: (user.company_name || "EFIS.PRO").toUpperCase(),
          address: [user.address, user.city].filter(Boolean).join(", "),
          pib: user.pib || "",
        };
        const data = {
          company_name: company.name,
          address: company.address,
          pib: company.pib,
          title: "RAČUN",
          invoice_number: fiscalInvoiceNumber || orderNumber,
          date: new Date().toLocaleString("sr-Latn-ME", {
            day: "2-digit", month: "2-digit", year: "numeric",
            hour: "2-digit", minute: "2-digit",
          }),
          items: cart.map(it => {
            const up = it.custom_price !== null ? it.custom_price : it.price;
            const tagsTxt = (it.kitchen_tags || []).length > 0
              ? ` (${it.kitchen_tags.join(", ")})`
              : "";
            return {
              name: it.name + tagsTxt,
              qty: it.quantity,
              price: up,
              total: it.quantity * up * (1 + it.vat_rate / 100),
            };
          }),
          total: cartCalc.total,
          payment_method: paymentMethod === "cash" ? "Gotovina"
                        : paymentMethod === "card" ? "Kartica"
                        : "Podjela",
          iic: fiscRes.data?.invoice?.iic || "",
          fic: fiscRes.data?.invoice?.fic || "",
          // Fiscal QR — can be added if backend returns the verification URL.
          // Without it, kept empty and printer skips QR rendering.
          qr_url: fiscRes.data?.invoice?.verify_url || "",
        };
        window.electronAPI.printReceipt(data).catch(() => {});
      } catch (e) {
        console.warn("Auto-print failed:", e);
      }
    }

    setSubmitting(false);
    showToast(`Račun ${fiscalInvoiceNumber || orderNumber} kreiran i fiskalizovan`, "success");
    clearCart();
    onCreated && onCreated();
  };

  // ─── Layout helpers ───────────────────────────────────────────────
  const sizeInfo = NP_CARD_SIZES[layout.cardSize] || NP_CARD_SIZES.srednje;
  const cellPx = sizeInfo.px;
  const isTabela = layout.style === "tabela";
  const cardHeight = isTabela ? cellPx : Math.round(cellPx * 0.71);

  return (
    <div style={{
      display: "flex",
      // 78vh works across ~1280×800 and 1920×1080 with the admin nav header
      height: "calc(100vh - 220px)",
      minHeight: 520,
      overflow: "hidden",
      background: "#fff",
      borderRadius: 12,
      border: `1px solid ${S.border}`,
    }}>
      {/* ═══ LEFT: PRODUCT GRID ═══ */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", borderRight: `1px solid ${S.border}`, minWidth: 0 }}>
        {/* Top bar — search + layout toggle. Category filtering is via the
            chip row below (SELF categories). */}
        <div style={{ padding: "10px 14px", borderBottom: `1px solid ${S.border}`, display: "flex", gap: 8, alignItems: "center" }}>
          <div style={{ flex: 1, position: "relative" }}>
            <Search size={16} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#9ca3af" }} />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Pretraži proizvode..."
              style={{
                width: "100%", padding: "10px 12px 10px 36px",
                border: `1px solid ${S.border}`, borderRadius: 8,
                fontSize: 14, fontFamily: S.font, color: S.dark, outline: "none",
                boxSizing: "border-box",
              }}
            />
          </div>
          <button
            onClick={() => setShowLayoutPanel(s => !s)}
            title="Izgled"
            style={{
              padding: 10, background: showLayoutPanel ? S.accent : "#f3f4f6",
              color: showLayoutPanel ? "#fff" : S.muted,
              border: `1px solid ${showLayoutPanel ? S.accent : S.border}`, borderRadius: 8,
              cursor: "pointer", display: "flex", alignItems: "center",
            }}>
            <SettingsIcon size={16} />
          </button>
        </div>

        {/* Layout panel (collapsible) */}
        {showLayoutPanel && (
          <div style={{ padding: "10px 14px", borderBottom: `1px solid ${S.border}`, background: "#f8fafc", display: "flex", gap: 16, alignItems: "center" }}>
            <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
              <span style={{ fontSize: 12, color: S.muted, fontWeight: 700, marginRight: 4, textTransform: "uppercase", letterSpacing: 0.4 }}>Prikaz:</span>
              {[
                { v: "kartice", lbl: "Kartice" },
                { v: "tabela",  lbl: "Tabela" },
              ].map(o => (
                <button key={o.v} onClick={() => setLayout(l => ({ ...l, style: o.v }))}
                  style={{
                    padding: "6px 12px", borderRadius: 7,
                    border: `1px solid ${layout.style === o.v ? S.accent : S.border}`,
                    background: layout.style === o.v ? S.accent : "#fff",
                    color: layout.style === o.v ? "#fff" : S.dark,
                    fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: S.font,
                  }}>
                  {o.lbl}
                </button>
              ))}
            </div>
            <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
              <span style={{ fontSize: 12, color: S.muted, fontWeight: 700, marginRight: 4, textTransform: "uppercase", letterSpacing: 0.4 }}>Veličina:</span>
              {Object.entries(NP_CARD_SIZES).map(([k, v]) => (
                <button key={k} onClick={() => setLayout(l => ({ ...l, cardSize: k }))}
                  style={{
                    padding: "6px 12px", borderRadius: 7,
                    border: `1px solid ${layout.cardSize === k ? S.accent : S.border}`,
                    background: layout.cardSize === k ? S.accent : "#fff",
                    color: layout.cardSize === k ? "#fff" : S.dark,
                    fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: S.font,
                  }}>
                  {v.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Category chips */}
        <div style={{ padding: "8px 14px", borderBottom: `1px solid ${S.border}`, display: "flex", gap: 6, overflowX: "auto", flexShrink: 0, flexWrap: "nowrap" }}>
          <NpCatChip active={activeCategory === "all"} onClick={() => setActiveCategory("all")} label="Sve" />
          {categories.map(c => (
            <NpCatChip key={c.id} active={String(activeCategory) === String(c.id)} onClick={() => setActiveCategory(c.id)} label={c.name} />
          ))}
        </div>

        {/* Product grid */}
        <div style={{ flex: 1, overflowY: "auto", padding: 10, background: "#f1f5f9" }}>
          {loadingProducts ? (
            <div style={{ textAlign: "center", padding: 60 }}><Spinner /></div>
          ) : filteredProducts.length === 0 ? (
            <div style={{ textAlign: "center", padding: 50, color: S.muted, fontSize: 14 }}>
              Nema proizvoda
            </div>
          ) : (
            <div style={{
              display: "grid",
              gridTemplateColumns: `repeat(auto-fill, minmax(${cellPx}px, 1fr))`,
              gridAutoRows: `${cardHeight}px`,
              gap: 8,
            }}>
              {filteredProducts.map(p => (
                <NpProductTile key={p.id} product={p}
                  isTabela={isTabela}
                  onTap={() => addToCart(p)} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ═══ RIGHT: CART ═══ */}
      <div style={{ flex: "0 0 43%", minWidth: 360, display: "flex", flexDirection: "column", background: "#fff" }}>
        {/* Header */}
        <div style={{ padding: "12px 16px", borderBottom: `1px solid ${S.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <ShoppingCart size={18} color={S.dark} />
            <span style={{ fontWeight: 700, fontSize: 16, color: S.dark }}>Račun</span>
            <span style={{ background: "#f3f4f6", padding: "2px 8px", borderRadius: 5, fontSize: 12, fontWeight: 600, color: S.muted }}>{cart.length}</span>
            <span style={{ background: "#fef3c7", color: "#92400e", padding: "2px 8px", borderRadius: 5, fontSize: 11, fontWeight: 700, letterSpacing: 0.5 }}>KASA</span>
          </div>
          {cart.length > 0 && (
            <button onClick={clearCart} style={{
              background: "#fef2f2", color: S.red, border: "none", padding: "6px 10px", borderRadius: 6,
              cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 600, fontFamily: S.font,
            }}>
              <Trash2 size={13} /> Obriši
            </button>
          )}
        </div>

        {/* Customer name (optional) */}
        <div style={{ padding: "10px 14px", borderBottom: `1px solid ${S.border}`, background: "#f8fafc" }}>
          <input
            value={customerName}
            onChange={e => setCustomerName(e.target.value)}
            placeholder="Ime kupca (opcionalno)"
            maxLength={60}
            style={{
              width: "100%", padding: "8px 12px",
              border: `1px solid ${S.border}`, borderRadius: 8,
              fontSize: 14, fontFamily: S.font, color: S.dark, outline: "none",
              boxSizing: "border-box", background: "#fff",
            }}
          />
        </div>

        {/* Cart items list */}
        <div style={{ flex: 1, overflowY: "auto", padding: "6px 14px" }}>
          {cart.length === 0 ? (
            <div style={{ textAlign: "center", padding: "50px 16px", color: S.lightMuted }}>
              <ShoppingCart size={36} strokeWidth={1.5} />
              <p style={{ marginTop: 12, fontSize: 14 }}>Dodajte proizvode</p>
            </div>
          ) : cart.map(item => {
            const up = item.custom_price !== null ? item.custom_price : item.price;
            const lineGross = item.quantity * up * (1 + item.vat_rate / 100);
            return (
              <div key={item.id} style={{ padding: "10px 0", borderBottom: "1px solid #f3f4f6" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: S.dark, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {item.name}
                    </div>
                    <div style={{ fontSize: 12, color: S.muted }}>
                      {/* Show BRUTO unit price — matches what admin sees on
                          the tile they tapped. Admin can still edit neto in
                          the per-line editor below. */}
                      {netoToBruto(up, item.vat_rate).toFixed(2)} × {fmtQty(item.quantity)}
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <button onClick={() => updateCartItem(item.id, { quantity: Math.max(1, item.quantity - 1) })}
                      style={npQtyBtnStyle}>
                      <Minus size={14} />
                    </button>
                    <span style={{ fontSize: 14, fontWeight: 600, minWidth: 24, textAlign: "center" }}>{item.quantity}</span>
                    <button onClick={() => updateCartItem(item.id, { quantity: item.quantity + 1 })}
                      style={npQtyBtnStyle}>
                      <Plus size={14} />
                    </button>
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: S.dark, minWidth: 56, textAlign: "right" }}>
                    {Number(lineGross).toFixed(2)} €
                  </div>
                  <button onClick={() => setEditingItem(item.id === editingItem ? null : item.id)}
                    style={{ padding: 4, background: "none", border: "none", cursor: "pointer", color: editingItem === item.id ? S.accent : S.muted }}>
                    <Edit3 size={14} />
                  </button>
                  <button onClick={() => removeFromCart(item.id)}
                    style={{ padding: 5, background: "#fef2f2", border: "none", borderRadius: 6, cursor: "pointer", color: S.red }}>
                    <X size={14} />
                  </button>
                </div>

                {/* Per-line edit (price, qty, kitchen tags) */}
                {editingItem === item.id && (
                  <div style={{ marginTop: 8, padding: 10, background: "#f9fafb", borderRadius: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <div style={{ flex: "1 1 90px" }}>
                      <label style={{ fontSize: 11, color: S.muted, fontWeight: 700 }}>Cijena (neto)</label>
                      <input type="number" step="0.01"
                        value={item.custom_price !== null ? item.custom_price : item.price}
                        onChange={e => updateCartItem(item.id, { custom_price: parseFloat(e.target.value) || 0 })}
                        style={npInlineInputStyle} />
                      <div style={{ fontSize: 10, color: S.muted, marginTop: 3 }}>
                        Bruto: <strong style={{ color: S.green }}>
                          {netoToBruto(item.custom_price !== null ? item.custom_price : item.price, item.vat_rate).toFixed(2)} €
                        </strong>
                      </div>
                    </div>
                    <div style={{ flex: "1 1 70px" }}>
                      <label style={{ fontSize: 11, color: S.muted, fontWeight: 700 }}>Količina</label>
                      <input type="number" step="1" min="1"
                        value={item.quantity}
                        onChange={e => updateCartItem(item.id, { quantity: Math.max(1, parseInt(e.target.value) || 1) })}
                        style={npInlineInputStyle} />
                    </div>
                    {item.kitchen_tags_preset?.length > 0 && (
                      <div style={{ flex: "1 1 100%", marginTop: 4 }}>
                        <label style={{ fontSize: 11, color: "#d97706", fontWeight: 700, display: "flex", alignItems: "center", gap: 4 }}>
                          🍳 Tagovi za kuhinju
                        </label>
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 4 }}>
                          {item.kitchen_tags_preset.map(tag => {
                            const active = (item.kitchen_tags || []).includes(tag);
                            return (
                              <button key={tag}
                                onClick={() => updateCartItem(item.id, {
                                  kitchen_tags: active
                                    ? (item.kitchen_tags || []).filter(t => t !== tag)
                                    : [...(item.kitchen_tags || []), tag]
                                })}
                                style={{
                                  padding: "4px 10px", borderRadius: 6,
                                  border: `1px solid ${active ? "#f59e0b" : S.border}`,
                                  background: active ? "#fffbeb" : "#fff",
                                  color: active ? "#92400e" : S.muted,
                                  fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: S.font,
                                }}>
                                {tag}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Compact tag display when not editing */}
                {(item.kitchen_tags || []).length > 0 && editingItem !== item.id && (
                  <div style={{ marginTop: 3, fontSize: 11, color: "#92400e", paddingLeft: 2 }}>
                    🍳 {(item.kitchen_tags || []).join(", ")}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Totals + payment + submit */}
        {cart.length > 0 && (
          <div style={{ borderTop: `2px solid ${S.border}`, padding: "12px 16px" }}>
            {/* Totals */}
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, fontSize: 13 }}>
              <span style={{ color: S.muted }}>Neto</span>
              <span>{Number(cartCalc.netTotal).toFixed(2)} €</span>
            </div>
            {Object.entries(cartCalc.vatGroups).map(([rate, g]) => (
              <div key={rate} style={{ display: "flex", justifyContent: "space-between", marginBottom: 3, fontSize: 12 }}>
                <span style={{ color: S.muted }}>PDV {rate}%</span>
                <span>{Number(g.vat).toFixed(2)} €</span>
              </div>
            ))}
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12, paddingTop: 6, borderTop: "1px solid #f3f4f6" }}>
              <span style={{ fontWeight: 700, fontSize: 16 }}>Ukupno</span>
              <span style={{ fontWeight: 700, fontSize: 20, color: S.green }}>{Number(cartCalc.total).toFixed(2)} €</span>
            </div>

            {/* Payment buttons */}
            <div style={{ display: "flex", gap: 5, marginBottom: 10 }}>
              {[
                { id: "cash",  label: "Gotovina", icon: Banknote },
                { id: "card",  label: "Kartica",  icon: CreditCard },
                { id: "split", label: "Podijeli", icon: Wallet },
              ].map(m => (
                <button key={m.id} onClick={() => setPaymentMethod(m.id)}
                  style={{
                    flex: 1, padding: "10px 6px",
                    border: "2px solid",
                    borderColor: paymentMethod === m.id ? S.green : S.border,
                    background: paymentMethod === m.id ? "#f0fdf4" : "#fff",
                    color: paymentMethod === m.id ? S.green : S.muted,
                    borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
                    fontFamily: S.font,
                  }}>
                  <m.icon size={13} /> {m.label}
                </button>
              ))}
            </div>

            {/* Split fields */}
            {paymentMethod === "split" && (
              <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 11, color: S.muted, fontWeight: 700 }}>Gotovina €</label>
                  <input type="number" step="0.01"
                    value={splitCash}
                    onChange={e => {
                      setSplitCash(e.target.value);
                      setSplitCard((cartCalc.total - (parseFloat(e.target.value) || 0)).toFixed(2));
                    }}
                    style={npInlineInputStyle} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 11, color: S.muted, fontWeight: 700 }}>Kartica €</label>
                  <input type="number" step="0.01"
                    value={splitCard}
                    onChange={e => {
                      setSplitCard(e.target.value);
                      setSplitCash((cartCalc.total - (parseFloat(e.target.value) || 0)).toFixed(2));
                    }}
                    style={npInlineInputStyle} />
                </div>
              </div>
            )}

            {/* Error display */}
            {error && (
              <div style={{
                padding: "10px 12px", marginBottom: 10,
                background: "#fee2e2", border: "1px solid #fecaca", borderRadius: 8,
                color: S.red, fontSize: 13, fontWeight: 600,
              }}>
                {error}
              </div>
            )}

            {/* Submit */}
            <button
              onClick={() => {
                if (!canFiscalize) {
                  setError("Nemate dozvolu za fiskalizaciju");
                  return;
                }
                setShowConfirm(true);
              }}
              disabled={submitting}
              style={{
                width: "100%", padding: "14px 16px",
                background: submitting ? S.muted : S.green,
                color: "#fff", border: "none", borderRadius: 10,
                fontSize: 16, fontWeight: 700, cursor: submitting ? "default" : "pointer",
                display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
                fontFamily: S.font, opacity: submitting ? 0.85 : 1,
              }}>
              {submitting ? <><Spinner size={18} color="#fff" /> Obrađivanje...</>
                          : <><Receipt size={18} /> Naruči i Fiskalizuj</>}
            </button>
          </div>
        )}
      </div>

      {/* Confirm modal */}
      {showConfirm && (
        <Modal open onClose={() => setShowConfirm(false)}>
          <div style={{ padding: 22, maxWidth: 420, width: "100%" }}>
            <h3 style={{ margin: "0 0 12px", fontSize: 18, color: S.dark }}>Potvrdi fiskalizaciju</h3>
            <div style={{ background: "#f8fafc", padding: "12px 14px", borderRadius: 10, marginBottom: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, fontSize: 13 }}>
                <span style={{ color: S.muted }}>Stavki:</span>
                <span style={{ fontWeight: 700 }}>{cart.length}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, fontSize: 13 }}>
                <span style={{ color: S.muted }}>Plaćanje:</span>
                <span style={{ fontWeight: 700 }}>
                  {paymentMethod === "cash" ? "Gotovina" : paymentMethod === "card" ? "Kartica" : "Podjela"}
                </span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 6, borderTop: `1px solid ${S.border}`, fontSize: 16 }}>
                <span style={{ fontWeight: 700 }}>Ukupno:</span>
                <span style={{ fontWeight: 800, color: S.green }}>{Number(cartCalc.total).toFixed(2)} €</span>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setShowConfirm(false)}
                style={{ ...S.btn("#fff", S.dark), border: `1px solid ${S.border}`, flex: 1, justifyContent: "center", padding: "12px" }}>
                Otkaži
              </button>
              <button onClick={handleSubmit}
                style={{ ...S.btn(S.green), flex: 1, justifyContent: "center", padding: "12px", fontSize: 14 }}>
                <Check size={16} /> Potvrdi
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── Category chip pill (used in NovaProdajaTab) ─────────────────────
function NpCatChip({ active, onClick, label }) {
  return (
    <button onClick={onClick}
      style={{
        padding: "6px 14px",
        borderRadius: 18,
        border: `1.5px solid ${active ? S.accent : S.border}`,
        background: active ? S.accent : "#fff",
        color: active ? "#fff" : S.dark,
        fontSize: 13, fontWeight: 600,
        cursor: "pointer", whiteSpace: "nowrap",
        flexShrink: 0,
        fontFamily: S.font,
      }}>
      {label}
    </button>
  );
}

// ─── Product tile in NovaProdajaTab grid ─────────────────────────────
function NpProductTile({ product, isTabela, onTap }) {
  const name = product.custom_name || product.name;
  const neto = product.custom_price !== null ? product.custom_price : product.pos_price;
  // Customer-facing price is BRUTO. Convert here on render — neto stays
  // the source of truth in storage (matches POS behavior).
  const bruto = netoToBruto(neto, product.vat_rate);
  const isAvailable = product.available !== 0;
  return (
    <button
      onClick={onTap}
      disabled={!isAvailable}
      style={{
        background: isAvailable ? "#fff" : "#f1f5f9",
        border: `1px solid ${S.border}`,
        borderRadius: 10,
        padding: 8,
        cursor: isAvailable ? "pointer" : "not-allowed",
        opacity: isAvailable ? 1 : 0.55,
        display: "flex", flexDirection: "column", justifyContent: "space-between",
        textAlign: "left", overflow: "hidden",
        fontFamily: S.font,
        transition: "transform .08s",
      }}>
      <div style={{
        fontSize: isTabela ? 13 : 13, fontWeight: 700, color: S.dark, lineHeight: 1.2,
        display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden",
      }}>
        {name}
      </div>
      <div style={{ marginTop: 4 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: S.accent }}>
          {Number(bruto).toFixed(2)} €
        </div>
        <div style={{ fontSize: 10, color: S.lightMuted, marginTop: 1 }}>
          neto {Number(neto).toFixed(2)}
        </div>
      </div>
    </button>
  );
}

const npQtyBtnStyle = {
  width: 30, height: 30, border: `1px solid ${S.border}`, borderRadius: 7,
  background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
  color: S.dark, fontFamily: S.font,
};
const npInlineInputStyle = {
  width: "100%", padding: "6px 10px",
  border: `1px solid ${S.border}`, borderRadius: 7,
  fontSize: 13, fontFamily: S.font, color: S.dark, outline: "none",
  boxSizing: "border-box", background: "#fff",
};

function AdminOrderCard({ order, onAccept, onAcceptAndFiscalize, onReject, onSetStatus, onFinish, onUnfinish, onFiscalize, onCardClick, canAccept = true, canStatus = true, canFiscalize = true }) {
  const age = minsAgo(order.created_at);
  const isPending = order.status === "pending";
  const isActive = ["preparing", "ready_pickup", "ready_table"].includes(order.status);

  const statusColor = {
    pending: S.yellow, preparing: S.accent,
    ready_pickup: S.green, ready_table: S.purple,
    finished: S.muted, rejected: S.red, cancelled: S.muted,
  }[order.status] || S.muted;

  const statusLabel = {
    pending: "Čeka", preparing: "U pripremi",
    ready_pickup: "Spremno — pickup", ready_table: "Spremno — za sto",
    finished: "Završeno", rejected: "Odbijeno", cancelled: "Otkazano",
  }[order.status];

  const needsAttention = isActive && order.kitchen_done === 1 &&
    !["ready_pickup", "ready_table"].includes(order.status);

  // The card is clickable only if onCardClick handler exists AND order is
  // active (so pending or finished cards still work like before — pure
  // button-driven). Buttons stop propagation to avoid double-firing.
  const cardClickable = !!onCardClick && isActive;

  return (
    <div
      onClick={cardClickable ? onCardClick : undefined}
      style={{
        background: "#fff", borderRadius: 12, overflow: "hidden",
        border: `1px solid ${needsAttention ? S.green : S.border}`,
        borderLeft: `5px solid ${needsAttention ? S.green : statusColor}`,
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        display: "flex", flexDirection: "column",
        animation: needsAttention ? "ready-pulse 1.4s ease-in-out infinite" : "none",
        cursor: cardClickable ? "pointer" : "default",
      }}>
      {/* Header */}
      <div style={{ padding: "10px 12px", borderBottom: `1px solid ${S.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Hash size={14} color={S.muted} />
            <span style={{ fontWeight: 800, fontSize: 15, color: S.dark }}>{order.order_number}</span>
            <span style={{
              fontSize: 11, padding: "2px 8px", borderRadius: 10, fontWeight: 600,
              background: statusColor + "22", color: statusColor, textTransform: "uppercase", letterSpacing: 0.5,
            }}>{statusLabel}</span>
          </div>
          <div style={{ fontSize: 12, color: S.muted, marginTop: 3 }}>
            {orderSourceLabel(order)}
            {order.customer_name && <> · <User size={11} style={{ display: "inline", verticalAlign: "middle" }} /> {order.customer_name}</>}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontWeight: 800, fontSize: 16 }}>{eur(order.total)}</div>
          <div style={{ fontSize: 11, color: S.muted, display: "flex", alignItems: "center", gap: 3, justifyContent: "flex-end" }}>
            <Timer size={11} /> {age}min
          </div>
        </div>
      </div>

      {/* Items */}
      <div style={{ padding: "10px 12px", flex: 1 }}>
        {(() => {
          // Per-item progress count (only show if any item has a known kitchen status)
          const withStatus = (order.items || []).filter(i => i.kitchen_status === "pending" || i.kitchen_status === "done");
          const doneCount = withStatus.filter(i => i.kitchen_status === "done").length;
          const totalKnown = withStatus.length;
          if (totalKnown === 0 || !isActive) return null;
          return (
            <div style={{ fontSize: 11, color: S.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
              <span>{doneCount}/{totalKnown} spremno</span>
              <div style={{ flex: 1, height: 4, background: S.border, borderRadius: 2, overflow: "hidden" }}>
                <div style={{ width: `${(doneCount / totalKnown) * 100}%`, height: "100%", background: doneCount === totalKnown ? S.green : S.accent, transition: "width 0.3s" }} />
              </div>
            </div>
          );
        })()}

        {order.items?.map((it, idx) => {
          const isDone = it.kitchen_status === "done";
          const isPending = it.kitchen_status === "pending";
          return (
            <div key={idx} style={{
              fontSize: 13, marginBottom: 4, display: "flex", justifyContent: "space-between",
              opacity: isDone ? 0.55 : 1,
            }}>
              <span style={{ display: "flex", alignItems: "center", gap: 6, flex: 1, minWidth: 0 }}>
                {isDone && <CheckCircle size={13} color={S.green} style={{ flexShrink: 0 }} />}
                {isPending && <Clock size={13} color={S.yellow} style={{ flexShrink: 0 }} />}
                <span style={{ textDecoration: isDone ? "line-through" : "none" }}>
                  <b>{it.qty}×</b> {it.product_name}
                  {it.tags?.length > 0 && <span style={{ color: S.muted, fontSize: 11 }}> ({it.tags.join(", ")})</span>}
                  {it.note && <span style={{ display: "block", color: "#b45309", fontSize: 11, fontStyle: "italic", marginLeft: 16 }}>"{it.note}"</span>}
                </span>
              </span>
              <span style={{ color: S.muted, flexShrink: 0, marginLeft: 6 }}>{eur(it.line_total)}</span>
            </div>
          );
        })}
        {order.customer_note && (
          <div style={{ marginTop: 6, padding: 8, background: "#fef3c7", borderRadius: 6, fontSize: 12, color: "#78350f", fontStyle: "italic" }}>
            <MessageSquare size={11} style={{ display: "inline", verticalAlign: "middle", marginRight: 4 }} />
            "{order.customer_note}"
          </div>
        )}

        {/* Kitchen ready badge */}
        {order.kitchen_done === 1 && isActive && (
          <div style={{ marginTop: 8, padding: "6px 10px", background: S.green + "22", color: S.green, borderRadius: 6, fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", gap: 4 }}>
            <CheckCircle size={13} /> Kuhinja je spremila
          </div>
        )}

        {/* Fiscal info */}
        {order.handled_as && (
          <div style={{ marginTop: 6, fontSize: 11, color: S.muted, display: "flex", alignItems: "center", gap: 4 }}>
            {order.handled_as === "fiscal" ? (
              <><Receipt size={12} color={S.green} /> Fiskalizovano (Račun {invoiceOrdinal(order.invoice_number, order.linked_invoice_id)})</>
            ) : (
              <><AlertCircle size={12} color={S.yellow} /> Nije fiskalizovano</>
            )}
          </div>
        )}

        {order.reject_reason && (
          <div style={{ marginTop: 6, fontSize: 12, color: S.red }}>
            Razlog: {order.reject_reason}
          </div>
        )}
      </div>

      {/* Actions */}
      <div onClick={(e) => e.stopPropagation()} style={{ padding: "8px 10px", borderTop: `1px solid ${S.border}`, display: "flex", gap: 6, flexWrap: "wrap" }}>
        {isPending && canAccept && (
          <>
            <button onClick={onAccept} style={{ ...S.btn(S.green), flex: 1, justifyContent: "center", padding: "8px", fontSize: 13 }}>
              <Check size={16} /> Prihvati
            </button>
            {canFiscalize && (
              <button
                onClick={onAcceptAndFiscalize}
                title="Prihvati narudžbu i odmah otvori fiskalizaciju"
                style={{ ...S.btn(S.accent), flex: 1, justifyContent: "center", padding: "8px", fontSize: 12 }}>
                <Receipt size={15} /> Prihvati + Fiskalizuj
              </button>
            )}
            <button onClick={onReject} style={{ ...S.btn(S.red), justifyContent: "center", padding: "8px 12px", fontSize: 13 }}>
              <X size={16} />
            </button>
          </>
        )}
        {isActive && (
          <>
            {order.status !== "ready_pickup" && canStatus && (
              <button onClick={() => onSetStatus(order.id, "ready_pickup")} style={{ ...S.btn(S.green), fontSize: 12, padding: "7px 10px" }}>
                <CheckCircle size={14} /> Spreman pickup
              </button>
            )}
            {order.status !== "ready_table" && canStatus && (
              <button onClick={() => onSetStatus(order.id, "ready_table")} style={{ ...S.btn(S.purple), fontSize: 12, padding: "7px 10px" }}>
                <Bike size={14} /> Spreman za sto
              </button>
            )}
            {!order.linked_invoice_id && canFiscalize && (
              <button onClick={onFiscalize} style={{ ...S.btn(S.accent), fontSize: 12, padding: "7px 10px" }}>
                <Receipt size={14} /> Fiskalizuj
              </button>
            )}
            {canStatus && (
              <button onClick={() => onFinish(order.id)} style={{ ...S.btn(S.dark), fontSize: 12, padding: "7px 10px", marginLeft: "auto" }}>
                <Archive size={14} /> Završi
              </button>
            )}
          </>
        )}
        {order.status === "finished" && (
          <>
            {!order.linked_invoice_id && canFiscalize && (
              <button onClick={onFiscalize} style={{ ...S.btn(S.accent), fontSize: 12, padding: "7px 10px" }}>
                <Receipt size={14} /> Fiskalizuj
              </button>
            )}
            {canStatus && (
              <button onClick={() => onUnfinish(order.id)} style={{ ...S.btn("#fff", S.muted), fontSize: 12, padding: "7px 10px", border: `1px solid ${S.border}` }}>
                <RefreshCw size={14} /> Vrati
              </button>
            )}
          </>
        )}
      </div>

      {cardClickable && (
        <div style={{ padding: "4px 12px 8px", fontSize: 10, color: S.lightMuted, textAlign: "center", letterSpacing: 0.4, textTransform: "uppercase", fontWeight: 600 }}>
          Klik za detalje
        </div>
      )}
    </div>
  );
}

function AcceptOrderModal({ order, onClose, onAccept }) {
  return (
    <Modal open onClose={onClose}>
      <div style={{ padding: 20 }}>
        <h2 style={{ margin: "0 0 6px", fontSize: 18, fontWeight: 800 }}>Prihvati narudžbu #{order.order_number}</h2>
        <p style={{ color: S.muted, fontSize: 13, margin: "0 0 16px" }}>
          {orderSourceLabel(order)} · <b style={{ color: S.dark }}>{eur(order.total)}</b>
        </p>

        <div style={{ background: "#eff6ff", border: `1px solid #bfdbfe`, padding: 12, borderRadius: 10, marginBottom: 14 }}>
          <div style={{ fontSize: 13, color: "#1e40af", lineHeight: 1.5 }}>
            <b>Tok rada:</b><br />
            1. Prihvati narudžbu — odmah ide u kuhinju<br />
            2. Kada klijent plati, klikni <b>"Fiskalizuj"</b> (keš / kartica / podjela)
          </div>
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={onClose} style={{ ...S.btn("#fff", S.text), flex: 1, border: `1px solid ${S.border}`, justifyContent: "center" }}>Otkaži</button>
          <button onClick={() => onAccept(order.id, "draft")} style={{ ...S.btn(S.green), flex: 1, justifyContent: "center" }}>
            <Check size={16} /> Prihvati i pošalji u kuhinju
          </button>
        </div>
      </div>
    </Modal>
  );
}

function RejectOrderModal({ order, onClose, onReject }) {
  const [reason, setReason] = useState("");
  const preset = ["Artikal nedostupan", "Kuhinja zatvorena", "Predugo čekanje", "Drugo"];
  return (
    <Modal open onClose={onClose}>
      <div style={{ padding: 20 }}>
        <h2 style={{ margin: "0 0 12px", fontSize: 18, fontWeight: 800 }}>Odbij narudžbu #{order.order_number}</h2>
        <p style={{ color: S.muted, fontSize: 13, margin: "0 0 12px" }}>
          Klijent će vidjeti razlog odbijanja.
        </p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
          {preset.map(r => (
            <button key={r} onClick={() => setReason(r)} style={{
              padding: "6px 10px", borderRadius: 16, fontSize: 12,
              border: `1px solid ${reason === r ? S.red : S.border}`,
              background: reason === r ? "#fef2f2" : "#fff",
              color: reason === r ? S.red : S.text, cursor: "pointer", fontWeight: 600,
            }}>{r}</button>
          ))}
        </div>
        <textarea value={reason} onChange={e => setReason(e.target.value)} maxLength={200}
          placeholder="Razlog..." style={{ ...S.input, minHeight: 60, resize: "vertical", fontFamily: "inherit", marginBottom: 14 }} />
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={onClose} style={{ ...S.btn("#fff", S.text), flex: 1, border: `1px solid ${S.border}`, justifyContent: "center" }}>Otkaži</button>
          <button onClick={() => onReject(order.id, reason)} disabled={!reason.trim()} style={{ ...S.btn(S.red), flex: 1, justifyContent: "center", opacity: reason.trim() ? 1 : 0.5 }}>
            <X size={16} /> Odbij narudžbu
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ══════════════════════════════════════════════════
// FISCALIZE MODAL — one-click fiscalization
// Calls existing /api/invoices endpoint (same contract POS uses), then
// /self-admin/linkInvoice, then auto-prints.
// Zero backend changes — we're just a second POS client.
// ══════════════════════════════════════════════════
// ════════════════════════════════════════════════════════════════════
// ActiveOrderModal — open when an active order card is clicked.
//
// Lists items with checkboxes; ticking an item calls /self-admin/
// toggleItem/{kitchenItemId} which marks kitchen_order_items.status as
// done/pending. When all items done, kitchen_orders.status flips to
// 'done' too — the existing kitchen_done flag on self_orders updates,
// so the SELF order card auto-pulses green ("Kuhinja je spremila").
//
// The modal also bundles "Spreman pickup", "Spreman za sto", "Završi",
// "Fiskalizuj" actions in one place. The print button calls Electron
// IPC printReceipt with pickup-ticket data — useful when customer lost
// their original receipt.
// ════════════════════════════════════════════════════════════════════
function ActiveOrderModal({ order, onClose, onChanged, showToast, canStatus, canFiscalize, onSetStatus, onFinish, onFiscalize }) {
  const [items, setItems] = useState(order.items || []);
  const [busyItem, setBusyItem] = useState(null);
  const [printing, setPrinting] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null); // item pending removal confirm
  const [removing, setRemoving] = useState(false);

  // An order that's been fiscalized (linked to an invoice) is locked — its
  // items cannot be removed (the invoice is already with the tax authority).
  const isFiscalized = !!order.linked_invoice_id || order.handled_as === "fiscal";

  // Keep local items in sync if parent reload pushes a fresh order in (e.g.
  // poll updated kitchen_status while modal open). Match by kitchen_item_id
  // when present, fallback to product_id+name for items without one yet.
  useEffect(() => {
    setItems(order.items || []);
  }, [order]);

  const confirmRemove = async () => {
    if (!removeTarget) return;
    setRemoving(true);
    const r = await apiAuth(`/self-admin/removeItem/${order.id}`, {
      method: "POST",
      body: JSON.stringify({ itemId: removeTarget.id }),
    });
    setRemoving(false);
    if (r.success) {
      setItems(r.data?.order?.items || []);
      setRemoveTarget(null);
      showToast(`Uklonjeno: ${r.data?.removedItem || "stavka"}`, "success");
      onChanged && onChanged();
    } else {
      showToast(r.message || "Greška pri uklanjanju", "error");
    }
  };

  const toggleItem = async (item, done) => {
    if (!item.kitchen_item_id) {
      showToast("Stavka nije još poslata u kuhinju", "error");
      return;
    }
    setBusyItem(item.kitchen_item_id);
    // Optimistic update — feels instant
    setItems(prev => prev.map(it =>
      it.kitchen_item_id === item.kitchen_item_id
        ? { ...it, kitchen_status: done ? "done" : "pending" }
        : it
    ));
    const r = await apiAuth(`/self-admin/toggle-item/${item.kitchen_item_id}`, {
      method: "POST",
      body: JSON.stringify({ done }),
    });
    setBusyItem(null);
    if (!r.success) {
      // Revert on failure
      setItems(prev => prev.map(it =>
        it.kitchen_item_id === item.kitchen_item_id
          ? { ...it, kitchen_status: done ? "pending" : "done" }
          : it
      ));
      showToast(r.message || "Greška", "error");
    } else {
      onChanged && onChanged();
    }
  };

  const printPickupTicket = async () => {
    if (!window.electronAPI?.printReceipt) {
      showToast("Štampač dostupan samo u .exe verziji", "error");
      return;
    }
    setPrinting(true);
    try {
      // Get fresh company info from stored user
      const storedUser = (() => {
        try { return JSON.parse(localStorage.getItem("self_user") || "{}"); }
        catch { return {}; }
      })();
      const data = {
        company_name: (storedUser.company_name || "EFIS.PRO").toUpperCase(),
        address: [storedUser.address, storedUser.city].filter(Boolean).join(", "),
        pib: storedUser.pib || "",
        title: "TIKET ZA PREUZIMANJE",
        invoice_number: order.order_number,
        date: new Date(order.created_at?.replace(" ", "T") || Date.now()).toLocaleString("sr-Latn-ME", {
          day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit"
        }),
        items: items.map(it => ({
          name: it.tags?.length > 0 ? `${it.product_name} (${it.tags.join(", ")})` : it.product_name,
          qty: it.qty,
          price: it.price,
          total: it.line_total,
        })),
        total: order.total,
        payment_method: "Plati na kasi",
        iic: "", fic: "",
        qr_url: order.public_token ? `https://self.efis.pro/order/${order.public_token}` : "",
      };
      const r = await window.electronAPI.printReceipt(data);
      if (r?.success) showToast("Tiket štampan", "success");
      else showToast(r?.message || "Greška pri štampi", "error");
    } catch (e) {
      showToast(e.message || "Greška pri štampi", "error");
    }
    setPrinting(false);
  };

  const isReadyPickup = order.status === "ready_pickup";
  const isReadyTable = order.status === "ready_table";

  return (
    <Modal open onClose={onClose}>
      <div style={{ padding: 0, maxWidth: 640, width: "100%" }}>
        {/* Header */}
        <div style={{
          padding: "16px 20px",
          background: S.dark, color: "#fff",
          borderTopLeftRadius: 12, borderTopRightRadius: 12,
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, fontFamily: "JetBrains Mono, monospace" }}>
              #{order.order_number}
            </div>
            <div style={{ fontSize: 12, color: "#cbd5e1", marginTop: 4 }}>
              {orderSourceLabel(order)}
              {order.customer_name && <> · {order.customer_name}</>}
              {" · "}{eur(order.total)}
            </div>
          </div>
          <button onClick={onClose} style={{
            background: "rgba(255,255,255,0.15)", color: "#fff", border: "none",
            borderRadius: 8, padding: "8px 12px", cursor: "pointer",
            display: "inline-flex", alignItems: "center", gap: 4, fontSize: 13,
          }}>
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: 20, maxHeight: "60vh", overflowY: "auto" }}>
          {/* Customer note */}
          {order.customer_note && (
            <div style={{ marginBottom: 14, padding: "10px 12px", background: "#fef3c7", borderRadius: 8, fontSize: 13, color: "#78350f", display: "flex", gap: 8, alignItems: "flex-start" }}>
              <MessageSquare size={14} style={{ marginTop: 2, flexShrink: 0 }} />
              <span style={{ fontStyle: "italic" }}>"{order.customer_note}"</span>
            </div>
          )}

          {/* Items */}
          <div style={{ fontSize: 11, color: S.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
            Stavke — kliknite za označavanje{!isFiscalized ? " · X za uklanjanje" : ""}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
            {items.map((it, idx) => {
              const isDone = it.kitchen_status === "done";
              const isBusy = busyItem === it.kitchen_item_id;
              return (
                <div key={idx} style={{
                  display: "flex", alignItems: "center", gap: 8,
                  background: isDone ? "#f0fdf4" : "#fff",
                  border: `1.5px solid ${isDone ? S.green : S.border}`,
                  borderRadius: 10,
                  overflow: "hidden",
                }}>
                  {/* Clickable area — toggles kitchen-done status */}
                  <button
                    onClick={() => !isBusy && toggleItem(it, !isDone)}
                    style={{
                      flex: 1, minWidth: 0,
                      display: "flex", alignItems: "center", gap: 12,
                      padding: "12px 14px",
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      textAlign: "left",
                      fontFamily: S.font,
                    }}>
                    <div style={{
                      width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                      background: isDone ? S.green : "#fff",
                      border: `2px solid ${isDone ? S.green : S.border}`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: "#fff",
                    }}>
                      {isBusy ? <Spinner size={14} color={isDone ? "#fff" : S.muted} />
                              : isDone ? <Check size={18} strokeWidth={3} /> : null}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: 14, fontWeight: 700, color: S.dark,
                        textDecoration: isDone ? "line-through" : "none",
                        opacity: isDone ? 0.6 : 1,
                      }}>
                        {fmtQty(it.qty)}× {it.product_name}
                      </div>
                      {it.tags?.length > 0 && (
                        <div style={{ fontSize: 12, color: S.muted, marginTop: 2 }}>
                          {it.tags.join(" · ")}
                        </div>
                      )}
                      {it.note && (
                        <div style={{ fontSize: 12, color: S.muted, marginTop: 2, fontStyle: "italic" }}>
                          "{it.note}"
                        </div>
                      )}
                    </div>
                    <div style={{ fontSize: 13, color: S.muted, fontWeight: 600, fontFamily: "JetBrains Mono, monospace", flexShrink: 0 }}>
                      {eur(it.line_total)}
                    </div>
                  </button>
                  {/* Remove (X) — only before fiscalization */}
                  {!isFiscalized && (
                    <button
                      onClick={() => setRemoveTarget(it)}
                      title="Ukloni stavku"
                      style={{
                        flexShrink: 0,
                        width: 40, alignSelf: "stretch",
                        background: "#fef2f2",
                        border: "none",
                        borderLeft: `1px solid ${S.border}`,
                        cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        color: S.red,
                      }}>
                      <X size={18} strokeWidth={2.5} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Totals */}
          <div style={{ padding: "12px 14px", background: "#f8fafc", borderRadius: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontSize: 13, color: S.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Ukupno</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: S.dark, fontFamily: "JetBrains Mono, monospace" }}>{eur(order.total)}</div>
          </div>
        </div>

        {/* Footer actions */}
        <div style={{
          padding: 16, borderTop: `1px solid ${S.border}`,
          display: "flex", gap: 8, flexWrap: "wrap",
          background: "#fafbfc",
          borderBottomLeftRadius: 12, borderBottomRightRadius: 12,
        }}>
          <button onClick={printPickupTicket} disabled={printing}
            style={{ ...S.btn("#fff", S.dark), border: `1px solid ${S.border}`, padding: "10px 14px", fontSize: 13 }}>
            {printing ? <Spinner size={14} /> : <Printer size={14} />} Štampaj tiket
          </button>
          <div style={{ flex: 1 }} />
          {!isReadyPickup && canStatus && (
            <button onClick={() => { onSetStatus(order.id, "ready_pickup"); onClose(); }}
              style={{ ...S.btn(S.green), padding: "10px 14px", fontSize: 13 }}>
              <CheckCircle size={14} /> Spreman pickup
            </button>
          )}
          {!isReadyTable && canStatus && (
            <button onClick={() => { onSetStatus(order.id, "ready_table"); onClose(); }}
              style={{ ...S.btn(S.purple), padding: "10px 14px", fontSize: 13 }}>
              <Bike size={14} /> Spreman za sto
            </button>
          )}
          {!order.linked_invoice_id && canFiscalize && (
            <button onClick={onFiscalize}
              style={{ ...S.btn(S.accent), padding: "10px 14px", fontSize: 13 }}>
              <Receipt size={14} /> Fiskalizuj
            </button>
          )}
          {canStatus && (
            <button onClick={() => { onFinish(order.id); onClose(); }}
              style={{ ...S.btn(S.dark), padding: "10px 14px", fontSize: 13 }}>
              <Archive size={14} /> Završi
            </button>
          )}
        </div>
      </div>

      {/* Remove-item confirm (opened by long-press on an item) */}
      {removeTarget && (
        <Modal open onClose={() => !removing && setRemoveTarget(null)}>
          <div style={{ padding: 22 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <div style={{ width: 42, height: 42, borderRadius: 12, background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Trash2 size={20} color={S.red} />
              </div>
              <h2 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>Ukloniti stavku?</h2>
            </div>
            <p style={{ margin: "0 0 4px", fontSize: 14, color: S.dark }}>
              <b>{fmtQty(removeTarget.qty)}× {removeTarget.product_name}</b>
            </p>
            <p style={{ margin: "0 0 18px", fontSize: 13, color: S.muted, lineHeight: 1.5 }}>
              Stavka će biti uklonjena iz narudžbe, iznos se preračunava, a kupac vidi promjenu na svom ekranu. Ova radnja se ne može poništiti.
            </p>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setRemoveTarget(null)} disabled={removing}
                style={{ ...S.btn("#fff", S.text), flex: 1, border: `1px solid ${S.border}`, justifyContent: "center" }}>
                Otkaži
              </button>
              <button onClick={confirmRemove} disabled={removing}
                style={{ ...S.btn(S.red), flex: 1, justifyContent: "center", opacity: removing ? 0.6 : 1 }}>
                {removing ? <Spinner size={14} color="#fff" /> : <Trash2 size={16} />} Ukloni
              </button>
            </div>
          </div>
        </Modal>
      )}
    </Modal>
  );
}

function FiscalizeModal({ order, onClose, onSuccess, showToast, activeOperator }) {
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [splitCash, setSplitCash] = useState("");
  const [splitCard, setSplitCard] = useState("");
  const [discountType, setDiscountType] = useState("none");
  const [discountValue, setDiscountValue] = useState("");
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  // Read companyId from stored user (set at login). Needed for per-company TCR.
  const storedUser = (() => { try { return JSON.parse(localStorage.getItem("self_user") || "{}"); } catch { return {}; } })();
  const companyId = storedUser?.company_id;
  const tcr = getTcr(companyId);

  // Computed totals with discount preview
  const { subtotal, discountAmount, total } = useMemo(() => {
    const sub = (order.items || []).reduce((s, it) => s + Number(it.line_total), 0);
    let disc = 0;
    const dv = parseFloat(discountValue) || 0;
    if (discountType === "percentage") disc = sub * (dv / 100);
    else if (discountType === "fixed") disc = dv;
    disc = Math.max(0, Math.min(disc, sub));
    return { subtotal: sub, discountAmount: disc, total: sub - disc };
  }, [order, discountType, discountValue]);

  const doFiscalize = async () => {
    if (!tcr) {
      setError("TCR nije podešen. Otvorite Podešavanja → Fiskalizacija.");
      return;
    }

    // Hard guard: operator_code is required for any fiscal operation.
    // This prevents sending invalid codes (like "ADMIN") to Poreska Uprava,
    // which would still succeed at the protocol level but record the wrong
    // operator on every receipt. Mirrors POS behavior — block early, tell
    // the user to fix it in Podešavanja → Operateri.
    if (!activeOperator?.operator_code) {
      setError("Operater nije konfiguriran. Postavke → Operateri za podešavanje.");
      return;
    }

    // Split validation
    if (paymentMethod === "split") {
      const sc = parseFloat(splitCash) || 0;
      const sd = parseFloat(splitCard) || 0;
      if (Math.abs(sc + sd - total) > 0.01) {
        setError(`Podjela se ne slaže: keš ${eur(sc)} + kartica ${eur(sd)} = ${eur(sc+sd)}, a iznos je ${eur(total)}`);
        return;
      }
    }

    setProcessing(true);
    setError(null);

    // Internal day check (same as POS)
    try {
      const idCheck = await apiAuth("/internalday/settings");
      if (idCheck.success && idCheck.data?.internal_day_enabled) {
        const idStatus = await apiAuth(`/internalday/status/${tcr}`);
        if (idStatus.success && !idStatus.data?.day) {
          setProcessing(false);
          setError("Interni dan nije otvoren! Otvorite dan u POS → Blagajna.");
          return;
        }
      }
    } catch(e) { /* non-fatal */ }

    const user = (() => { try { return JSON.parse(localStorage.getItem("self_user") || "{}"); } catch { return {}; } })();

    // Build payload identical to POS buildPayload(true)
    const payload = {
      location_id: user.location_id || 1,
      tcr_code: tcr,
      operator_code: activeOperator.operator_code,
      payment_method: paymentMethod,
      fiscalize_now: true,
      discount_type: discountType,
      discount_value: parseFloat(discountValue) || 0,
      ...(paymentMethod === "split" && {
        split_cash: parseFloat(splitCash) || 0,
        split_card: parseFloat(splitCard) || 0,
      }),
      items: (order.items || []).map(it => ({
        product_id: it.product_id,
        name: it.product_name,
        quantity: Number(it.qty),
        unit_price: Number(it.price),
        vat_rate: Number(it.vat_rate),
        unit: it.unit || "kom",
        discount_type: "none",
        discount_value: 0,
      })),
    };

    const r = await apiAuth("/invoices", {
      method: "POST",
      body: JSON.stringify(payload),
    });

    if (!r.success) {
      setProcessing(false);
      setError(r.message || "Fiskalizacija nije uspjela");
      return;
    }

    const invoiceId = r.data?.invoice?.id;
    if (!invoiceId) {
      setProcessing(false);
      setError("Račun kreiran ali ID nije vraćen");
      return;
    }

    // Link invoice to SELF order
    const lr = await apiAuth(`/self-admin/linkInvoice/${order.id}`, {
      method: "POST",
      body: JSON.stringify({ invoiceId }),
    });
    if (!lr.success) {
      setProcessing(false);
      setError(`Račun #${invoiceId} kreiran ali veza nije uspjela: ${lr.message || ""}`);
      return;
    }

    // Auto-print (non-blocking)
    printReceipt(invoiceId).catch(() => {});

    setProcessing(false);
    showToast(`Fiskalizovano! FIC: ${r.data?.fic || "—"}`, "success");
    onSuccess();
  };

  return (
    <Modal open onClose={processing ? null : onClose} maxWidth={480}>
      <div style={{ padding: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div>
            <h2 style={{ margin: "0 0 4px", fontSize: 18, fontWeight: 800 }}>Fiskalizuj #{order.order_number}</h2>
            <p style={{ margin: 0, fontSize: 13, color: S.muted }}>
              {orderSourceLabel(order)} · TCR: <b style={{ color: tcr ? S.dark : S.red }}>{tcr || "NIJE PODEŠEN"}</b>
            </p>
          </div>
          <button onClick={onClose} disabled={processing} style={{ background: "transparent", border: "none", cursor: processing ? "default" : "pointer" }}>
            <X size={20} color={S.muted} />
          </button>
        </div>

        {/* Payment method */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: S.muted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Način plaćanja</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
            <PayMethodBtn active={paymentMethod === "cash"} onClick={() => setPaymentMethod("cash")} icon={<Banknote size={18} />} label="Keš" color={S.green} />
            <PayMethodBtn active={paymentMethod === "card"} onClick={() => setPaymentMethod("card")} icon={<CreditCard size={18} />} label="Kartica" color={S.accent} />
            <PayMethodBtn active={paymentMethod === "split"} onClick={() => setPaymentMethod("split")} icon={<Hash size={18} />} label="Podjela" color={S.purple} />
          </div>
        </div>

        {/* Split fields */}
        {paymentMethod === "split" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: S.muted, textTransform: "uppercase" }}>Keš (€)</label>
              <NumpadInput value={splitCash} onChange={setSplitCash} placeholder="0.00" style={{ marginTop: 4 }} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: S.muted, textTransform: "uppercase" }}>Kartica (€)</label>
              <NumpadInput value={splitCard} onChange={setSplitCard} placeholder="0.00" style={{ marginTop: 4 }} />
            </div>
          </div>
        )}

        {/* Discount */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: S.muted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Popust (opciono)</div>
          <div style={{ display: "flex", gap: 6 }}>
            <select value={discountType} onChange={e => setDiscountType(e.target.value)} style={{ ...S.input, width: 110 }}>
              <option value="none">Nema</option>
              <option value="percentage">%</option>
              <option value="fixed">€</option>
            </select>
            {discountType !== "none" && (
              <NumpadInput value={discountValue} onChange={setDiscountValue} placeholder={discountType === "percentage" ? "10" : "5.00"} style={{ flex: 1 }} />
            )}
          </div>
        </div>

        {/* Summary */}
        <div style={{ background: "#f8fafc", borderRadius: 10, padding: 12, marginBottom: 14, fontSize: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
            <span style={{ color: S.muted }}>Osnovica</span>
            <span>{eur(subtotal)}</span>
          </div>
          {discountAmount > 0 && (
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, color: S.red }}>
              <span>Popust</span>
              <span>-{eur(discountAmount)}</span>
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "space-between", borderTop: `1px dashed ${S.border}`, paddingTop: 6, marginTop: 6, fontSize: 16, fontWeight: 800 }}>
            <span>Za plaćanje</span>
            <span>{eur(total)}</span>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div style={{ background: "#fef2f2", border: "1px solid #fecaca", padding: 10, borderRadius: 8, marginBottom: 12, color: S.red, fontSize: 13, display: "flex", alignItems: "flex-start", gap: 6 }}>
            <AlertCircle size={16} style={{ marginTop: 1, flexShrink: 0 }} />
            <span>{error}</span>
          </div>
        )}

        {/* Actions */}
        <button onClick={doFiscalize} disabled={processing || !tcr} style={{
          ...S.btn(S.green), width: "100%", padding: "14px", fontSize: 16, justifyContent: "center",
          opacity: (processing || !tcr) ? 0.6 : 1,
        }}>
          {processing ? <><Spinner color="#fff" size={18} /> Fiskalizujem...</> : <><Receipt size={18} /> Fiskalizuj i štampaj · {eur(total)}</>}
        </button>

        {/* Manual fallback link */}
        <div style={{ textAlign: "center", marginTop: 10, fontSize: 11, color: S.muted }}>
          Za storno ili druge radnje otvorite POS aplikaciju.
        </div>
      </div>
    </Modal>
  );
}

function PayMethodBtn({ active, onClick, icon, label, color }) {
  return (
    <button onClick={onClick} style={{
      padding: "12px 6px", border: `1.5px solid ${active ? color : S.border}`,
      background: active ? color : "#fff",
      color: active ? "#fff" : S.text,
      borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: "pointer",
      display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
    }}>
      {icon}
      {label}
    </button>
  );
}


// ══════════════════════════════════════════════════
// MENU SETTINGS TAB — visibility + overrides
// ══════════════════════════════════════════════════
function MenuSettingsTab({ showToast }) {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [p, c] = await Promise.all([
      apiAuth("/self-settings/products"),
      apiAuth("/self-settings/categories"),
    ]);
    if (p.success) setProducts(p.data?.products || []);
    if (c.success) setCategories(c.data?.categories || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateConfig = async (productId, patch) => {
    const r = await apiAuth(`/self-settings/productConfig/${productId}`, {
      method: "POST", body: JSON.stringify(patch),
    });
    if (r.success) {
      setProducts(prev => prev.map(p => p.id === productId ? { ...p, ...patch } : p));
      showToast("Sačuvano", "success");
    } else {
      showToast(r.message || "Error", "error");
    }
  };

  if (loading) return <div style={{ textAlign: "center", padding: 40 }}><Spinner /></div>;

  return (
    <div>
      <div style={{ marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18 }}>Meni za Self Service</h2>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: S.muted }}>
            Prikazani su svi POS artikli sa uključenom kuhinjom. Uključite vidljivost za Self i po potrebi postavite prilagođene vrijednosti.
          </p>
        </div>
      </div>

      {products.length === 0 && (
        <div style={{ background: "#fff", padding: 40, borderRadius: 12, textAlign: "center", color: S.muted }}>
          Nema artikala sa uključenom kuhinjom. Uključite kuhinju za artikle u POS-u.
        </div>
      )}

      <div style={{ background: "#fff", borderRadius: 12, overflow: "hidden", border: `1px solid ${S.border}` }}>
        {products.map((p, idx) => (
          <div key={p.id} style={{ padding: 12, borderBottom: idx < products.length - 1 ? `1px solid ${S.border}` : "none", display: "flex", alignItems: "center", gap: 12 }}>
            <button onClick={() => updateConfig(p.id, { is_visible: p.is_visible ? 0 : 1 })} style={{
              background: "transparent", border: "none", cursor: "pointer", padding: 4,
            }}>
              {p.is_visible ? <Eye size={20} color={S.green} /> : <EyeOff size={20} color={S.lightMuted} />}
            </button>
            {p.photo_url && (
              <img src={p.photo_url} alt="" style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 8 }} />
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, color: S.dark }}>{p.custom_name || p.name}</div>
              <div style={{ fontSize: 12, color: S.muted }}>
                {/* Display price as BRUTO (with VAT) — that's what the customer
                    pays. Neto shown as small hint so admin can compare. */}
                {p.pos_category_name}
                {" · "}
                <strong style={{ color: S.green }}>
                  {eur(netoToBruto(p.custom_price ?? p.pos_price, p.vat_rate))}
                </strong>
                <span style={{ opacity: 0.6 }}> (neto {eur(p.custom_price ?? p.pos_price)})</span>
                {p.custom_name && <span style={{ color: S.accent }}> · prilagođeno</span>}
              </div>
              {/* Tags preview row — shows visible/hidden state + translation
                  indicators (🇬🇧/🇦🇱) at a glance so admin can see which tags
                  still need translating. Tap "Uredi" to fill them in. */}
              {(() => {
                // Prefer custom_tags if set, else fall back to POS preset
                const tagList = (Array.isArray(p.custom_tags) && p.custom_tags.length > 0)
                  ? p.custom_tags
                  : (p.tags_preset || []).map(s => ({ base: s, visible: true, translations: {} }));
                if (tagList.length === 0) return null;
                return (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
                    {tagList.map((t, i) => {
                      const base = typeof t === "string" ? t : t.base;
                      const visible = typeof t === "string" ? true : t.visible !== false;
                      const tr = (typeof t === "string") ? {} : (t.translations || {});
                      const hasEn = !!(tr.en && String(tr.en).trim());
                      const hasAl = !!(tr.al && String(tr.al).trim());
                      return (
                        <span key={i} style={{
                          fontSize: 10, padding: "2px 7px", borderRadius: 8,
                          background: visible ? "#e0f2fe" : "#f1f5f9",
                          color: visible ? "#075985" : S.muted,
                          textDecoration: visible ? "none" : "line-through",
                          fontWeight: 600,
                          display: "inline-flex", alignItems: "center", gap: 3,
                        }}>
                          {visible ? <Eye size={9} /> : <EyeOff size={9} />}
                          {base}
                          {hasEn && <span style={{ opacity: 0.7, marginLeft: 1 }} title={`EN: ${tr.en}`}>🇬🇧</span>}
                          {hasAl && <span style={{ opacity: 0.7, marginLeft: 1 }} title={`AL: ${tr.al}`}>🇦🇱</span>}
                        </span>
                      );
                    })}
                  </div>
                );
              })()}
              {/* Name/description translation indicators — small row right below tags */}
              {(() => {
                const nameTr = p.name_translations || {};
                const descTr = p.description_translations || {};
                const flags = [];
                if (nameTr.en || descTr.en) flags.push({ flag: "🇬🇧", title: `EN: ${[nameTr.en, descTr.en].filter(Boolean).join(" / ")}` });
                if (nameTr.al || descTr.al) flags.push({ flag: "🇦🇱", title: `AL: ${[nameTr.al, descTr.al].filter(Boolean).join(" / ")}` });
                if (flags.length === 0) return null;
                return (
                  <div style={{ marginTop: 4, fontSize: 10, color: S.muted, display: "flex", gap: 6 }}>
                    <span>Prijevodi:</span>
                    {flags.map((f, i) => (
                      <span key={i} title={f.title}>{f.flag}</span>
                    ))}
                  </div>
                );
              })()}
            </div>
            <button onClick={() => updateConfig(p.id, { available: p.available ? 0 : 1 })} style={{
              ...S.btn(p.available ? S.green : S.red),
              fontSize: 12, padding: "6px 10px",
            }}>
              {p.available ? "Dostupno" : "Nedostupno"}
            </button>
            <button onClick={() => setEditing(p)} style={{
              ...S.btn("#fff", S.text), border: `1px solid ${S.border}`, fontSize: 12, padding: "6px 10px",
            }}>
              <Edit3 size={13} /> Uredi
            </button>
          </div>
        ))}
      </div>

      {editing && (
        <ProductConfigEditor
          product={editing}
          categories={categories}
          onClose={() => setEditing(null)}
          onSave={async (patch) => {
            await updateConfig(editing.id, patch);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

// ─── NetoBrutoEditor ─────────────────────────────────────────────────
// Compact admin price input with Neto/Bruto toggle (mirrors POS behavior).
// `value` is always stored in NETO (the persisted custom_price). The
// component lets the admin TYPE in either mode but always emits neto.
//
// In Bruto mode it tries 3 candidate netos (round/floor/ceil) so the
// computed bruto round-trips back to what was typed. If no candidate
// hits exactly (rare due to 2-decimal rounding), falls back to floor
// and shows a "snižena" warning explaining the actual bruto stored.
//
// Empty value → admin inherits POS base price (placeholder shows it).
function NetoBrutoEditor({ value, posPriceNeto, vatRate, onChange }) {
  const r = Number(vatRate) || 0;
  const factor = 1 + r / 100;
  const [mode, setMode] = useState("neto");
  const [brutoTyped, setBrutoTyped] = useState("");
  const [brutoSnizen, setBrutoSnizen] = useState(null);

  // Reset bruto-side helpers whenever the persisted neto changes externally
  // (e.g. modal opens with a different product) or vat changes.
  useEffect(() => {
    setBrutoTyped("");
    setBrutoSnizen(null);
  }, [vatRate]);

  const netoStr = value === "" || value === null || value === undefined
    ? ""
    : String(value);
  const netoNum = parseFloat(netoStr);
  const hasNeto = netoStr !== "" && !isNaN(netoNum);

  const onChangeNeto = (v) => {
    setBrutoSnizen(null);
    onChange(v);
  };

  const onChangeBruto = (v) => {
    if (v === "" || isNaN(parseFloat(v))) {
      onChange("");
      setBrutoTyped(v);
      setBrutoSnizen(null);
      return;
    }
    const typedBruto = parseFloat(v);
    const exactNeto = typedBruto / factor;
    const candidates = [
      Math.round(exactNeto * 100) / 100,
      Math.floor(exactNeto * 100) / 100,
      Math.ceil(exactNeto * 100) / 100,
    ];
    let chosen = null;
    for (const n of candidates) {
      const back = Math.round(n * factor * 100) / 100;
      if (back === typedBruto) { chosen = n; break; }
    }
    if (chosen !== null) {
      setBrutoTyped(v);
      setBrutoSnizen(null);
      onChange(chosen.toFixed(2));
    } else {
      const neto = Math.floor(exactNeto * 100) / 100;
      const finalBruto = Math.round(neto * factor * 100) / 100;
      setBrutoTyped(v);
      setBrutoSnizen(finalBruto);
      onChange(neto.toFixed(2));
    }
  };

  // Effective neto used for the "other side" hint
  const effNeto = hasNeto ? netoNum : null;
  const effBruto = effNeto !== null ? Math.round(effNeto * factor * 100) / 100 : null;

  return (
    <div>
      {/* Mode toggle */}
      <div style={{ display: "flex", gap: 0, border: `1px solid ${S.border}`, borderRadius: 8, overflow: "hidden", marginBottom: 8, width: "fit-content" }}>
        {[
          { v: "neto",  lbl: "Neto" },
          { v: "bruto", lbl: "Bruto" },
        ].map(o => (
          <button key={o.v} type="button"
            onClick={() => { setMode(o.v); setBrutoSnizen(null); }}
            style={{
              padding: "6px 14px",
              fontSize: 13, fontWeight: 600,
              background: mode === o.v ? S.green : "#fff",
              color:      mode === o.v ? "#fff"   : S.muted,
              border: "none",
              borderLeft: o.v === "bruto" ? `1px solid ${S.border}` : "none",
              cursor: "pointer", fontFamily: S.font,
            }}>
            {o.lbl}
          </button>
        ))}
      </div>

      {/* Active input */}
      {mode === "neto" ? (
        <NumpadInput
          value={netoStr}
          onChange={onChangeNeto}
          placeholder={`${Number(posPriceNeto || 0).toFixed(2)} (POS)`}
        />
      ) : (
        <input
          type="number"
          step="0.01"
          inputMode="decimal"
          value={brutoTyped !== "" ? brutoTyped : (effBruto !== null ? effBruto.toFixed(2) : "")}
          onChange={e => onChangeBruto(e.target.value)}
          placeholder={`${(Number(posPriceNeto || 0) * factor).toFixed(2)} (POS bruto)`}
          style={{
            width: "100%", padding: "10px 14px",
            border: `1.5px solid ${S.border}`, borderRadius: 8,
            fontSize: 16, fontFamily: "JetBrains Mono, monospace",
            color: S.dark, outline: "none", boxSizing: "border-box",
          }}
        />
      )}

      {/* Hint line — shows the OTHER side of the price + VAT */}
      {brutoSnizen !== null ? (
        <div style={{
          marginTop: 6, padding: "6px 10px", fontSize: 11, color: "#92400e",
          background: "#fef3c7", border: "1px solid #fbbf24", borderRadius: 6,
        }}>
          ⚠️ Bruto cijena nije moguća zbog PDV zaokruživanja. Snižena na{" "}
          <strong>{brutoSnizen.toFixed(2)} €</strong>{" "}
          (Neto: {effNeto !== null ? effNeto.toFixed(2) : "—"} €)
        </div>
      ) : (effNeto !== null ? (
        <div style={{ marginTop: 6, fontSize: 12, color: S.muted }}>
          {mode === "neto" ? (
            <>Bruto: <strong style={{ color: S.green }}>{effBruto.toFixed(2)} €</strong> (neto + {r}% PDV)</>
          ) : (
            <>Neto: <strong style={{ color: S.dark }}>{effNeto.toFixed(2)} €</strong></>
          )}
        </div>
      ) : (
        <div style={{ marginTop: 6, fontSize: 11, color: S.lightMuted }}>
          Prazno = koristi POS cijenu ({Number(posPriceNeto || 0).toFixed(2)} € neto / {(Number(posPriceNeto || 0) * factor).toFixed(2)} € bruto)
        </div>
      ))}
    </div>
  );
}

function ProductConfigEditor({ product, categories, onClose, onSave }) {
  const [form, setForm] = useState({
    custom_name: product.custom_name || "",
    custom_description: product.custom_description || "",
    custom_price: product.custom_price ?? "",
    photo_url: product.photo_url || "",
    self_category_id: product.self_category_id || "",
    display_order: product.display_order || 0,
  });

  // ─── Tags state ─────────────────────────────────────────────────
  // Initialize from custom_tags if present; otherwise auto-import from
  // POS-side kitchen_tags_preset (Q1 design decision — saves admin time).
  // Each row: { base, visible, translations: { en, al } }
  const [tags, setTags] = useState(() => {
    if (Array.isArray(product.custom_tags) && product.custom_tags.length > 0) {
      return product.custom_tags.map(t => ({
        base: t.base || "",
        visible: t.visible !== false,
        translations: t.translations || {},
      }));
    }
    // Fallback: import from POS kitchen_tags_preset (visible by default)
    if (Array.isArray(product.tags_preset) && product.tags_preset.length > 0) {
      return product.tags_preset.map(s => ({
        base: String(s),
        visible: true,
        translations: {},
      }));
    }
    return [];
  });

  // ─── Name + description translations ────────────────────────────
  const [nameTr, setNameTr] = useState(() => ({
    en: product.name_translations?.en || "",
    al: product.name_translations?.al || "",
  }));
  const [descTr, setDescTr] = useState(() => ({
    en: product.description_translations?.en || "",
    al: product.description_translations?.al || "",
  }));

  const change = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // ─── Tag operations ─────────────────────────────────────────────
  const addTag = () => setTags(prev => [...prev, { base: "", visible: true, translations: {} }]);
  const removeTag = (idx) => setTags(prev => prev.filter((_, i) => i !== idx));
  const updateTag = (idx, patch) => setTags(prev => prev.map((t, i) => i === idx ? { ...t, ...patch } : t));
  const updateTagTr = (idx, lang, val) => setTags(prev => prev.map((t, i) =>
    i === idx ? { ...t, translations: { ...(t.translations || {}), [lang]: val } } : t
  ));

  const handleSave = () => {
    // Strip empty tags before saving
    const cleanTags = tags
      .filter(t => t.base && t.base.trim())
      .map(t => ({
        base: t.base.trim(),
        visible: !!t.visible,
        translations: Object.fromEntries(
          Object.entries(t.translations || {})
            .filter(([_, v]) => typeof v === "string" && v.trim())
            .map(([k, v]) => [k, v.trim()])
        ),
      }));

    const cleanTr = (obj) => Object.fromEntries(
      Object.entries(obj).filter(([_, v]) => typeof v === "string" && v.trim()).map(([k, v]) => [k, v.trim()])
    );

    onSave({
      ...form,
      custom_tags: cleanTags,
      name_translations: cleanTr(nameTr),
      description_translations: cleanTr(descTr),
    });
  };

  return (
    <Modal open onClose={onClose} maxWidth={600}>
      <div style={{ padding: 20, maxHeight: "85vh", overflowY: "auto" }}>
        <h2 style={{ margin: "0 0 4px", fontSize: 18, fontWeight: 800 }}>Uredi: {product.name}</h2>
        <p style={{ margin: "0 0 16px", color: S.muted, fontSize: 13 }}>Ostavi prazno za fallback na POS vrijednost</p>

        <Row label="Prilagođen naziv (CG/podrazumevano)">
          <input value={form.custom_name} onChange={e => change("custom_name", e.target.value)} placeholder={product.name} style={S.input} />
        </Row>

        {/* Name translations */}
        <Row label="Prijevod naziva — English / Shqip">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <input
              value={nameTr.en}
              onChange={e => setNameTr(p => ({ ...p, en: e.target.value }))}
              placeholder="🇬🇧 English (opciono)"
              style={S.input}
            />
            <input
              value={nameTr.al}
              onChange={e => setNameTr(p => ({ ...p, al: e.target.value }))}
              placeholder="🇦🇱 Shqip (opciono)"
              style={S.input}
            />
          </div>
        </Row>

        <Row label="Opis (CG/podrazumevano)">
          <textarea value={form.custom_description} onChange={e => change("custom_description", e.target.value)} rows={3} style={{ ...S.input, fontFamily: "inherit", resize: "vertical" }} />
        </Row>

        {/* Description translations */}
        <Row label="Prijevod opisa — English / Shqip">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <textarea
              value={descTr.en}
              onChange={e => setDescTr(p => ({ ...p, en: e.target.value }))}
              placeholder="🇬🇧 English (opciono)"
              rows={2}
              style={{ ...S.input, fontFamily: "inherit", resize: "vertical" }}
            />
            <textarea
              value={descTr.al}
              onChange={e => setDescTr(p => ({ ...p, al: e.target.value }))}
              placeholder="🇦🇱 Shqip (opciono)"
              rows={2}
              style={{ ...S.input, fontFamily: "inherit", resize: "vertical" }}
            />
          </div>
        </Row>

        {/* Price editor — Neto/Bruto toggle mirrors POS behavior so admin
            doesn't have to do mental math. Internal-only state (_priceMode,
            _brutoTyped, _brutoSnizen) drives the UI; only `custom_price`
            (always neto) is persisted. Empty input = inherit POS price. */}
        <Row label="Prilagođena cijena (€)">
          <NetoBrutoEditor
            value={form.custom_price}
            posPriceNeto={product.pos_price}
            vatRate={product.vat_rate}
            onChange={(v) => change("custom_price", v)}
          />
        </Row>

        <Row label="Foto URL (slika proizvoda)">
          <input value={form.photo_url} onChange={e => change("photo_url", e.target.value)} placeholder="https://..." style={S.input} />
          {form.photo_url && <img src={form.photo_url} alt="" style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 8, marginTop: 6 }} onError={(e) => e.target.style.display = "none"} />}
        </Row>

        <Row label="Kategorija za Self Service">
          <select value={form.self_category_id} onChange={e => change("self_category_id", e.target.value)} style={S.input}>
            <option value="">(bez kategorije)</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Row>

        <Row label="Redoslijed prikaza">
          <NumpadInput value={form.display_order ?? ""} onChange={(v) => change("display_order", v)} decimal={false} placeholder="0" />
        </Row>

        {/* Tags section — full width */}
        <div style={{ marginTop: 22, marginBottom: 8, paddingTop: 16, borderTop: `1px solid ${S.border}` }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
            <label style={{ fontSize: 12, fontWeight: 700, color: S.muted, textTransform: "uppercase", letterSpacing: 1 }}>
              Tagovi (dodaci / opcije)
            </label>
            <button onClick={addTag} type="button"
              style={{ ...S.btn(S.accent), fontSize: 12, padding: "5px 10px" }}>
              <Plus size={13} /> Dodaj tag
            </button>
          </div>
          <div style={{ fontSize: 12, color: S.muted, marginBottom: 10 }}>
            Klijent vidi vidljive tagove pri narudžbi. Skriveni tagovi se ne pojavljuju u kiosk-u, ali ostaju za buduće aktiviranje. Prijevodi su opcioni — fallback na osnovni naziv.
          </div>

          {tags.length === 0 ? (
            <div style={{ padding: 16, background: "#f8fafc", borderRadius: 10, color: S.muted, fontSize: 13, textAlign: "center" }}>
              Nema tagova
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {tags.map((tag, idx) => (
                <div key={idx} style={{
                  background: tag.visible ? "#fff" : "#f8fafc",
                  border: `1.5px solid ${tag.visible ? S.border : "#e2e8f0"}`,
                  borderRadius: 10, padding: 10,
                  opacity: tag.visible ? 1 : 0.7,
                }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
                    {/* Visibility checkbox — labeled by eye icon */}
                    <button
                      type="button"
                      onClick={() => updateTag(idx, { visible: !tag.visible })}
                      title={tag.visible ? "Vidljivo klijentima" : "Skriveno"}
                      style={{
                        background: "transparent", border: "none", cursor: "pointer", padding: 4,
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0,
                      }}>
                      {tag.visible ? <Eye size={18} color={S.green} /> : <EyeOff size={18} color={S.lightMuted} />}
                    </button>

                    {/* Base label (CG / default) */}
                    <input
                      value={tag.base}
                      onChange={e => updateTag(idx, { base: e.target.value })}
                      placeholder="npr. bez luka"
                      style={{ ...S.input, flex: 1, fontWeight: 600 }}
                    />

                    <button
                      type="button"
                      onClick={() => removeTag(idx)}
                      title="Obriši tag"
                      style={{ ...S.btn("#fff", S.red), border: `1px solid ${S.red}40`, padding: "8px 10px", fontSize: 12 }}>
                      <Trash2 size={14} />
                    </button>
                  </div>

                  {/* Translations row */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, paddingLeft: 30 }}>
                    <input
                      value={tag.translations?.en || ""}
                      onChange={e => updateTagTr(idx, "en", e.target.value)}
                      placeholder="🇬🇧 English (opciono)"
                      style={{ ...S.input, fontSize: 13 }}
                    />
                    <input
                      value={tag.translations?.al || ""}
                      onChange={e => updateTagTr(idx, "al", e.target.value)}
                      placeholder="🇦🇱 Shqip (opciono)"
                      style={{ ...S.input, fontSize: 13 }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 22 }}>
          <button onClick={onClose} style={{ ...S.btn("#fff", S.text), flex: 1, border: `1px solid ${S.border}`, justifyContent: "center" }}>Otkaži</button>
          <button onClick={handleSave} style={{ ...S.btn(S.accent), flex: 1, justifyContent: "center" }}>
            <Save size={16} /> Sačuvaj
          </button>
        </div>
      </div>
    </Modal>
  );
}

function Row({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: S.muted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 5 }}>{label}</label>
      {children}
    </div>
  );
}

// ══════════════════════════════════════════════════
// CATEGORIES TAB
// ══════════════════════════════════════════════════
function CategoriesTab({ showToast }) {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await apiAuth("/self-settings/categories");
    if (r.success) setCats(r.data?.categories || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (form) => {
    const r = await apiAuth("/self-settings/categories", { method: "POST", body: JSON.stringify(form) });
    if (r.success) { showToast("Sačuvano", "success"); setEditing(null); load(); }
    else showToast(r.message || "Error", "error");
  };

  const del = async (id) => {
    if (!window.confirm("Obrisati kategoriju?")) return;
    const r = await apiAuth(`/self-settings/deleteCategory/${id}`, { method: "POST" });
    if (r.success) { showToast("Obrisano", "success"); load(); }
    else showToast(r.message || "Error", "error");
  };

  if (loading) return <div style={{ textAlign: "center", padding: 40 }}><Spinner /></div>;

  return (
    <div>
      <div style={{ marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>Kategorije</h2>
        <button onClick={() => setEditing({ id: 0, name: "", translations: {}, icon: "", sort_order: cats.length, is_active: 1 })} style={S.btn(S.accent)}>
          <Plus size={16} /> Nova kategorija
        </button>
      </div>

      {cats.length === 0 ? (
        <div style={{ background: "#fff", padding: 40, borderRadius: 12, textAlign: "center", color: S.muted }}>
          Nema kategorija. Kreirajte prvu.
        </div>
      ) : (
        <div style={{ background: "#fff", borderRadius: 12, border: `1px solid ${S.border}`, overflow: "hidden" }}>
          {cats.map((c, idx) => (
            <div key={c.id} style={{ padding: 12, borderBottom: idx < cats.length - 1 ? `1px solid ${S.border}` : "none", display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600 }}>{c.name}</div>
                <div style={{ fontSize: 12, color: S.muted }}>Redoslijed: {c.sort_order} · {c.is_active ? "Aktivna" : "Neaktivna"}</div>
              </div>
              <button onClick={() => setEditing(c)} style={{ ...S.btn("#fff", S.text), border: `1px solid ${S.border}`, fontSize: 12, padding: "6px 10px" }}>
                <Edit3 size={13} /> Uredi
              </button>
              <button onClick={() => del(c.id)} style={{ ...S.btn("#fff", S.red), border: `1px solid ${S.border}`, fontSize: 12, padding: "6px 10px" }}>
                <Trash2 size={13} /> Obriši
              </button>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <Modal open onClose={() => setEditing(null)}>
          <div style={{ padding: 20 }}>
            <h2 style={{ margin: "0 0 14px", fontSize: 18, fontWeight: 800 }}>{editing.id ? "Uredi kategoriju" : "Nova kategorija"}</h2>
            <Row label={<>Naziv (Crnogorski) {LANG_FLAGS.cg}</>}>
              <input value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} style={S.input} autoFocus placeholder="npr. Hrana" />
            </Row>

            <div style={{ padding: 10, background: "#f8fafc", borderRadius: 8, marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: S.muted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>
                Prevodi (opciono — prikazuju se klijentima po izboru jezika)
              </div>
              {["en", "al"].map(code => (
                <div key={code} style={{ marginBottom: 8 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: S.text, display: "block", marginBottom: 3 }}>
                    {LANG_FLAGS[code]} {LANG_NAMES[code]}
                  </label>
                  <input
                    value={editing.translations?.[code] || ""}
                    onChange={e => setEditing({
                      ...editing,
                      translations: { ...(editing.translations || {}), [code]: e.target.value }
                    })}
                    placeholder={code === "en" ? "e.g. Food" : "p.sh. Ushqime"}
                    style={S.input}
                  />
                </div>
              ))}
            </div>

            <Row label="Redoslijed">
              <NumpadInput value={String(editing.sort_order ?? 0)} onChange={(v) => setEditing({ ...editing, sort_order: parseInt(v || 0) })} decimal={false} placeholder="0" />
            </Row>
            <Row label="Status">
              <select value={editing.is_active} onChange={e => setEditing({ ...editing, is_active: parseInt(e.target.value) })} style={S.input}>
                <option value={1}>Aktivna</option>
                <option value={0}>Neaktivna</option>
              </select>
            </Row>
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <button onClick={() => setEditing(null)} style={{ ...S.btn("#fff", S.text), flex: 1, border: `1px solid ${S.border}`, justifyContent: "center" }}>Otkaži</button>
              <button onClick={() => save(editing)} disabled={!editing.name.trim()} style={{ ...S.btn(S.accent), flex: 1, justifyContent: "center", opacity: editing.name.trim() ? 1 : 0.5 }}>
                <Save size={16} /> Sačuvaj
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════
// TABLES & QR TAB
// ══════════════════════════════════════════════════
function TablesQRTab({ showToast }) {
  const [tableInput, setTableInput] = useState("");
  const [terminalInput, setTerminalInput] = useState("");
  const [numbers, setNumbers] = useState([]);
  const [terminals, setTerminals] = useState([]);
  const [slug, setSlug] = useState("");
  const [takeawayEnabled, setTakeawayEnabled] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await apiAuth("/self-settings/settings");
    if (r.success) {
      const s = r.data?.settings;
      setNumbers(s?.table_numbers || []);
      setTerminals(s?.terminal_numbers || []);
      setTableInput((s?.table_numbers || []).join(", "));
      setTerminalInput((s?.terminal_numbers || []).join(", "));
      setSlug(s?.company_slug || "");
      setTakeawayEnabled(!!s?.takeaway_enabled);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const nums = tableInput.split(/[\s,]+/).map(s => parseInt(s.trim())).filter(n => n > 0);
    const terms = terminalInput.split(/[\s,]+/).map(s => parseInt(s.trim())).filter(n => n > 0);
    const r = await apiAuth("/self-settings/tables", {
      method: "POST",
      body: JSON.stringify({ numbers: nums, terminals: terms }),
    });
    if (r.success) {
      setNumbers(r.data?.numbers || []);
      setTerminals(r.data?.terminals || []);
      showToast("Sačuvano", "success");
    } else {
      showToast(r.message || "Error", "error");
    }
  };

  const qrBase = `${window.location.protocol}//${window.location.host}`;

  const downloadQR = (urlPath, label) => {
    const svg = document.getElementById(`qr-${label}`);
    if (!svg) return;
    const serializer = new XMLSerializer();
    const src = serializer.serializeToString(svg);
    const svgBlob = new Blob([src], { type: "image/svg+xml" });
    const link = document.createElement("a");
    link.download = `qr-${label}.svg`;
    link.href = URL.createObjectURL(svgBlob);
    link.click();
  };

  if (loading) return <div style={{ textAlign: "center", padding: 40 }}><Spinner /></div>;

  if (!slug) {
    return (
      <div style={{ background: "#fff", padding: 40, borderRadius: 12, textAlign: "center" }}>
        <AlertCircle size={32} color={S.yellow} style={{ marginBottom: 10 }} />
        <h3 style={{ margin: "0 0 6px", fontSize: 16 }}>Postavite slug kompanije</h3>
        <p style={{ color: S.muted, fontSize: 13, margin: 0 }}>Idite na Podešavanja → URL slug, pa se vratite ovdje.</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ background: "#fff", padding: 16, borderRadius: 12, marginBottom: 16, border: `1px solid ${S.border}` }}>
        <h3 style={{ margin: "0 0 10px", fontSize: 16 }}>Konfiguracija stolova / terminala</h3>
        <Row label="Brojevi stolova (razdvoji zarezom ili razmakom)">
          <input value={tableInput} onChange={e => setTableInput(e.target.value)} placeholder="1, 2, 3, 5, 10" style={S.input} />
        </Row>
        <Row label="Brojevi terminala (opciono)">
          <input value={terminalInput} onChange={e => setTerminalInput(e.target.value)} placeholder="1, 2" style={S.input} />
        </Row>
        <button onClick={save} style={S.btn(S.accent)}>
          <Save size={16} /> Sačuvaj
        </button>
      </div>

      <div style={{ background: "#fff", padding: 16, borderRadius: 12, border: `1px solid ${S.border}` }}>
        <h3 style={{ margin: "0 0 10px", fontSize: 16 }}>QR kodovi</h3>
        <p style={{ color: S.muted, fontSize: 13, marginTop: 0 }}>
          Štampaj i postavi na svaki sto. Svaki QR vodi na: {qrBase}/{slug}/table/<b>N</b>
        </p>

        {takeawayEnabled && slug && (() => {
          const url = `${qrBase}/${slug}`;
          const label = "univerzalni";
          return (
            <div style={{ marginBottom: 18 }}>
              <h4 style={{ margin: "14px 0 10px", fontSize: 14, color: S.muted, textTransform: "uppercase", letterSpacing: 1 }}>Univerzalni QR (ponesi)</h4>
              <div style={{ display: "flex", gap: 14, alignItems: "center", background: "#fafafa", padding: 14, borderRadius: 10, border: `1px solid ${S.border}` }}>
                <QRCodeSVG id={`qr-${label}`} value={url} size={120} includeMargin={true} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 800, fontSize: 16 }}>Ponesi / Univerzalni</div>
                  <div style={{ fontSize: 12, color: S.muted, marginTop: 4, wordBreak: "break-all" }}>{url}</div>
                  <div style={{ fontSize: 12, color: S.muted, marginTop: 6, lineHeight: 1.4 }}>
                    Stalni QR koji ne zavisi od stola. Klijenti skeniraju, poručuju i nose. Narudžbe se prikazuju kao <b>PONESI</b>.
                  </div>
                  <button onClick={() => downloadQR(url, label)} style={{
                    marginTop: 8, background: "transparent", border: `1px solid ${S.border}`,
                    borderRadius: 6, padding: "5px 10px", cursor: "pointer", fontSize: 12, color: S.muted,
                    display: "inline-flex", alignItems: "center", gap: 4,
                  }}>
                    <Download size={12} /> SVG
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {numbers.length === 0 && terminals.length === 0 && !takeawayEnabled && (
          <div style={{ textAlign: "center", padding: 30, color: S.muted }}>Dodajte brojeve iznad.</div>
        )}

        {numbers.length > 0 && (
          <>
            <h4 style={{ margin: "14px 0 10px", fontSize: 14, color: S.muted, textTransform: "uppercase", letterSpacing: 1 }}>Stolovi</h4>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 12 }}>
              {numbers.map(n => {
                const url = `${qrBase}/${slug}/table/${n}`;
                const label = `table-${n}`;
                return (
                  <div key={n} style={{ background: "#fafafa", padding: 12, borderRadius: 10, textAlign: "center", border: `1px solid ${S.border}` }}>
                    <QRCodeSVG id={`qr-${label}`} value={url} size={120} includeMargin={true} />
                    <div style={{ fontWeight: 800, fontSize: 18, marginTop: 4 }}>Sto {n}</div>
                    <button onClick={() => downloadQR(url, label)} style={{
                      marginTop: 6, background: "transparent", border: `1px solid ${S.border}`,
                      borderRadius: 6, padding: "5px 10px", cursor: "pointer", fontSize: 12, color: S.muted,
                      display: "inline-flex", alignItems: "center", gap: 4,
                    }}>
                      <Download size={12} /> SVG
                    </button>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {terminals.length > 0 && (
          <>
            <h4 style={{ margin: "18px 0 10px", fontSize: 14, color: S.muted, textTransform: "uppercase", letterSpacing: 1 }}>Terminali</h4>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 12 }}>
              {terminals.map(n => {
                const url = `${qrBase}/${slug}/terminal/${n}`;
                const label = `terminal-${n}`;
                return (
                  <div key={n} style={{ background: "#fafafa", padding: 12, borderRadius: 10, textAlign: "center", border: `1px solid ${S.border}` }}>
                    <QRCodeSVG id={`qr-${label}`} value={url} size={120} includeMargin={true} />
                    <div style={{ fontWeight: 800, fontSize: 18, marginTop: 4 }}>Terminal {n}</div>
                    <button onClick={() => downloadQR(url, label)} style={{
                      marginTop: 6, background: "transparent", border: `1px solid ${S.border}`,
                      borderRadius: 6, padding: "5px 10px", cursor: "pointer", fontSize: 12, color: S.muted,
                      display: "inline-flex", alignItems: "center", gap: 4,
                    }}>
                      <Download size={12} /> SVG
                    </button>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════
// HISTORY TAB — beyond 24h, with filters
// ══════════════════════════════════════════════════
function HistoryTab({ showToast }) {
  const [days, setDays] = useState(7);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await apiAuth(`/self-admin/history?days=${days}`);
    if (r.success) setOrders(r.data?.orders || []);
    setLoading(false);
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => {
    const finished = orders.filter(o => o.status === "finished");
    return {
      total: orders.length,
      finished: finished.length,
      revenue: finished.reduce((s, o) => s + Number(o.total), 0),
      rejected: orders.filter(o => o.status === "rejected").length,
    };
  }, [orders]);

  return (
    <div>
      <div style={{ marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>Istorija narudžbi</h2>
        <select value={days} onChange={e => setDays(parseInt(e.target.value))} style={{ ...S.input, width: 160 }}>
          <option value={1}>Poslednja 24h</option>
          <option value={3}>3 dana</option>
          <option value={7}>7 dana</option>
          <option value={14}>14 dana</option>
          <option value={30}>30 dana</option>
        </select>
      </div>

      {/* Stat cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10, marginBottom: 14 }}>
        <StatCard label="Ukupno" value={stats.total} color={S.accent} />
        <StatCard label="Završeno" value={stats.finished} color={S.green} />
        <StatCard label="Odbijeno" value={stats.rejected} color={S.red} />
        <StatCard label="Promet" value={eur(stats.revenue)} color={S.dark} />
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40 }}><Spinner /></div>
      ) : orders.length === 0 ? (
        <div style={{ background: "#fff", padding: 40, borderRadius: 12, textAlign: "center", color: S.muted }}>Nema podataka</div>
      ) : (
        <div style={{ background: "#fff", borderRadius: 12, overflow: "hidden", border: `1px solid ${S.border}` }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f8fafc", textAlign: "left", fontSize: 12, color: S.muted, textTransform: "uppercase", letterSpacing: 1 }}>
                <th style={{ padding: 12 }}>Broj</th>
                <th style={{ padding: 12 }}>Sto</th>
                <th style={{ padding: 12 }}>Status</th>
                <th style={{ padding: 12 }}>Iznos</th>
                <th style={{ padding: 12 }}>Fiskal</th>
                <th style={{ padding: 12 }}>Vrijeme</th>
              </tr>
            </thead>
            <tbody>
              {orders.map(o => (
                <tr key={o.id} style={{ borderTop: `1px solid ${S.border}`, fontSize: 13 }}>
                  <td style={{ padding: 12, fontWeight: 700 }}>{o.order_number}</td>
                  <td style={{ padding: 12 }}>{o.source_type === "terminal" ? "T" : "S"}{o.table_number}</td>
                  <td style={{ padding: 12 }}>
                    <span style={{
                      padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600,
                      background: o.status === "finished" ? "#dcfce7" : o.status === "rejected" ? "#fee2e2" : "#f1f5f9",
                      color: o.status === "finished" ? "#166534" : o.status === "rejected" ? "#991b1b" : S.muted,
                    }}>
                      {o.status === "finished" ? "Završeno" : o.status === "rejected" ? "Odbijeno" : "Otkazano"}
                    </span>
                  </td>
                  <td style={{ padding: 12, fontWeight: 700 }}>{eur(o.total)}</td>
                  <td style={{ padding: 12 }}>
                    {o.handled_as === "fiscal" ? (
                      <span style={{ color: S.green, fontSize: 12 }}><Receipt size={12} style={{ display: "inline", verticalAlign: "middle" }} /> #{o.linked_invoice_id}</span>
                    ) : (
                      <span style={{ color: S.muted, fontSize: 12 }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: 12, color: S.muted, fontSize: 12 }}>{fmtDateTime(o.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div style={{ background: "#fff", padding: 14, borderRadius: 10, border: `1px solid ${S.border}`, borderLeft: `4px solid ${color}` }}>
      <div style={{ fontSize: 11, color: S.muted, textTransform: "uppercase", letterSpacing: 1, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: S.dark, marginTop: 4 }}>{value}</div>
    </div>
  );
}

// ══════════════════════════════════════════════════
// INVOICES TAB — fiscalized SELF orders
// Shows only orders where a POS invoice has been linked.
// For storno/corrections admin uses POS app.
// ══════════════════════════════════════════════════
function InvoicesTab({ showToast, activeOperator }) {
  const canReprint = hasPermission(activeOperator, "reprint_invoice");
  const [days, setDays] = useState(7);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [detailFor, setDetailFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await apiAuth(`/self-admin/fiscalizedOrders?days=${days}`);
    if (r.success) setRows(r.data?.orders || []);
    else showToast(r.message || "Error", "error");
    setLoading(false);
  }, [days, showToast]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!search) return rows;
    const s = search.toLowerCase();
    return rows.filter(r =>
      (r.order_number || "").toLowerCase().includes(s) ||
      (r.invoice_number || "").toLowerCase().includes(s) ||
      String(r.table_number || "").includes(s) ||
      (r.customer_name || "").toLowerCase().includes(s)
    );
  }, [rows, search]);

  const stats = useMemo(() => ({
    count: rows.length,
    revenue: rows.reduce((s, r) => s + Number(r.total || 0), 0),
  }), [rows]);

  const reprint = async (invoiceId) => {
    showToast("Štampam...", "info");
    await printReceipt(invoiceId);
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18 }}>Računi (Self Service)</h2>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: S.muted }}>
            Samo računi izdati preko Self aplikacije. Za storno ili druge radnje otvorite POS.
          </p>
        </div>
        <select value={days} onChange={e => setDays(parseInt(e.target.value))} style={{ ...S.input, width: 160 }}>
          <option value={1}>Danas</option>
          <option value={3}>3 dana</option>
          <option value={7}>7 dana</option>
          <option value={14}>14 dana</option>
          <option value={30}>30 dana</option>
        </select>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, marginBottom: 14 }}>
        <StatCard label={`Računa (${days}d)`} value={stats.count} color={S.accent} />
        <StatCard label="Ukupan promet" value={eur(stats.revenue)} color={S.green} />
      </div>

      <div style={{ position: "relative", marginBottom: 12 }}>
        <Search size={16} color={S.muted} style={{ position: "absolute", left: 12, top: 11 }} />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Pretraži po računu, narudžbi, stolu, klijentu..."
          style={{ ...S.input, paddingLeft: 38 }} />
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40 }}><Spinner /></div>
      ) : filtered.length === 0 ? (
        <div style={{ background: "#fff", padding: 40, borderRadius: 12, textAlign: "center", color: S.muted }}>
          {rows.length === 0 ? "Nema fiskalizovanih Self narudžbi za odabrani period." : "Nema rezultata za pretragu."}
        </div>
      ) : (
        <div style={{ background: "#fff", borderRadius: 12, overflow: "hidden", border: `1px solid ${S.border}` }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f8fafc", textAlign: "left", fontSize: 11, color: S.muted, textTransform: "uppercase", letterSpacing: 1 }}>
                <th style={{ padding: 10 }}>Račun</th>
                <th style={{ padding: 10 }}>Narudžba</th>
                <th style={{ padding: 10 }}>Sto</th>
                <th style={{ padding: 10 }}>Klijent</th>
                <th style={{ padding: 10 }}>Plaćanje</th>
                <th style={{ padding: 10 }}>Iznos</th>
                <th style={{ padding: 10 }}>Vrijeme</th>
                <th style={{ padding: 10 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.id} style={{ borderTop: `1px solid ${S.border}`, fontSize: 13 }}>
                  <td style={{ padding: 10, fontWeight: 700 }}>{r.invoice_number || `#${r.linked_invoice_id}`}</td>
                  <td style={{ padding: 10, color: S.muted }}>{r.order_number}</td>
                  <td style={{ padding: 10 }}>{r.source_type === "terminal" ? "T" : "S"}{r.table_number}</td>
                  <td style={{ padding: 10, color: S.muted }}>{r.customer_name || "—"}</td>
                  <td style={{ padding: 10, fontSize: 12, color: S.muted }}>{r.payment_method || "—"}</td>
                  <td style={{ padding: 10, fontWeight: 700 }}>{eur(r.total)}</td>
                  <td style={{ padding: 10, color: S.muted, fontSize: 12 }}>{fmtDateTime(r.created_at)}</td>
                  <td style={{ padding: 10, display: "flex", gap: 4, justifyContent: "flex-end" }}>
                    {canReprint && (
                      <button onClick={() => reprint(r.linked_invoice_id)} title="Reprint" style={{ ...S.btn("#fff", S.text), border: `1px solid ${S.border}`, padding: "5px 8px", fontSize: 11 }}>
                        <Printer size={12} />
                      </button>
                    )}
                    <button onClick={() => setDetailFor(r)} title="Detalji" style={{ ...S.btn(S.accent), padding: "5px 10px", fontSize: 11 }}>
                      Detalji
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detailFor && (
        <InvoiceDetailModal
          selfOrder={detailFor}
          onClose={() => setDetailFor(null)}
          onReprint={() => reprint(detailFor.linked_invoice_id)}
          canReprint={canReprint}
          showToast={showToast}
        />
      )}
    </div>
  );
}

function InvoiceDetailModal({ selfOrder, onClose, onReprint, canReprint = true, showToast }) {
  const [invoice, setInvoice] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiAuth(`/invoices/show/${selfOrder.linked_invoice_id}`).then(r => {
      if (r.success) {
        // Response shape: r.data = { invoice: {...}, items: [...], can_storno, ... }
        setInvoice(r.data?.invoice || null);
        setItems(r.data?.items || []);
      } else {
        showToast(r.message || "Error", "error");
      }
      setLoading(false);
    });
  }, [selfOrder, showToast]);

  return (
    <Modal open onClose={onClose} maxWidth={560}>
      <div style={{ padding: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div>
            <h2 style={{ margin: "0 0 4px", fontSize: 18, fontWeight: 800 }}>
              Račun {invoice?.invoice_number || `#${selfOrder.linked_invoice_id}`}
            </h2>
            <p style={{ margin: 0, fontSize: 13, color: S.muted }}>
              Self narudžba #{selfOrder.order_number} · Sto {selfOrder.table_number}
            </p>
          </div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", cursor: "pointer" }}>
            <X size={20} color={S.muted} />
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: 40 }}><Spinner /></div>
        ) : !invoice ? (
          <div style={{ color: S.red, textAlign: "center", padding: 20 }}>Račun nije pronađen</div>
        ) : (
          <>
            {/* Fiscal codes */}
            <div style={{ background: "#f8fafc", borderRadius: 10, padding: 12, marginBottom: 12, fontSize: 12 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <div>
                  <div style={{ color: S.muted, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>Status</div>
                  <div style={{ fontWeight: 700, color: invoice.status === "fiscalized" ? S.green : invoice.status === "failed" ? S.red : S.muted }}>
                    {invoice.status === "fiscalized" ? "✓ Fiskalizovan" : invoice.status === "failed" ? "✗ Neuspjeh" : invoice.status}
                  </div>
                </div>
                <div>
                  <div style={{ color: S.muted, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>Plaćanje</div>
                  <div style={{ fontWeight: 700 }}>{invoice.payment_method || "—"}</div>
                </div>
                {invoice.fic && (
                  <div style={{ gridColumn: "1 / -1" }}>
                    <div style={{ color: S.muted, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>FIC</div>
                    <div style={{ fontFamily: "monospace", fontSize: 11, wordBreak: "break-all" }}>{invoice.fic}</div>
                  </div>
                )}
                {invoice.iic && (
                  <div style={{ gridColumn: "1 / -1" }}>
                    <div style={{ color: S.muted, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>IIC</div>
                    <div style={{ fontFamily: "monospace", fontSize: 11, wordBreak: "break-all" }}>{invoice.iic}</div>
                  </div>
                )}
                <div>
                  <div style={{ color: S.muted, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>Vrijeme</div>
                  <div style={{ fontWeight: 700 }}>{fmtDateTime(invoice.invoice_date)}</div>
                </div>
                <div>
                  <div style={{ color: S.muted, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>TCR</div>
                  <div style={{ fontWeight: 700 }}>{invoice.tcr_code || "—"}</div>
                </div>
              </div>
            </div>

            {/* Items */}
            <div style={{ border: `1px solid ${S.border}`, borderRadius: 10, overflow: "hidden", marginBottom: 12 }}>
              {items.map((it, idx) => (
                <div key={idx} style={{ padding: "8px 12px", borderBottom: idx < items.length - 1 ? `1px solid ${S.border}` : "none", display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                  <span><b>{Number(it.quantity)}×</b> {it.item_name || it.name}</span>
                  <span style={{ fontWeight: 600 }}>{eur(it.total_amount != null ? it.total_amount : Number(it.quantity) * Number(it.unit_price))}</span>
                </div>
              ))}
            </div>

            {/* Totals */}
            <div style={{ background: "#f8fafc", borderRadius: 10, padding: 12, marginBottom: 14, fontSize: 13 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                <span style={{ color: S.muted }}>Osnovica</span>
                <span>{eur(invoice.subtotal)}</span>
              </div>
              {Number(invoice.discount_amount) > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3, color: S.red }}>
                  <span>Popust</span>
                  <span>-{eur(invoice.discount_amount)}</span>
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                <span style={{ color: S.muted }}>PDV</span>
                <span>{eur(invoice.vat_amount)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", borderTop: `1px dashed ${S.border}`, paddingTop: 6, marginTop: 6, fontSize: 15, fontWeight: 800 }}>
                <span>Ukupno</span>
                <span>{eur(invoice.total_amount)}</span>
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: "flex", gap: 8 }}>
              {canReprint && (
                <button onClick={onReprint} style={{ ...S.btn(S.accent), flex: 1, justifyContent: "center" }}>
                  <Printer size={16} /> Štampaj ponovo
                </button>
              )}
              <button onClick={onClose} style={{ ...S.btn("#fff", S.text), flex: 1, border: `1px solid ${S.border}`, justifyContent: "center" }}>
                Zatvori
              </button>
            </div>

            <div style={{ textAlign: "center", marginTop: 12, fontSize: 12, color: S.muted }}>
              Za storno ili druge radnje otvorite POS aplikaciju.
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

// ══════════════════════════════════════════════════
// OPERATORS TAB — PIN management (like POS StaffPinManager)
// ══════════════════════════════════════════════════
function OperatorsTab({ showToast, user, pinData, setPinData, activeOperator, setActiveOperator, onTriggerSetup }) {
  const [editOp, setEditOp] = useState(null);
  const [addModal, setAddModal] = useState(false);
  const [backendOps, setBackendOps] = useState([]);
  const [loadingOps, setLoadingOps] = useState(false);

  // Load backend operators (from Poreska Uprava registration) for picker
  const loadBackendOps = useCallback(async () => {
    setLoadingOps(true);
    try {
      const r = await apiAuth("/operators/list");
      if (r.success) setBackendOps(Array.isArray(r.data) ? r.data : (r.data?.data || []));
    } catch(e) { /* ignore */ }
    setLoadingOps(false);
  }, []);

  useEffect(() => { loadBackendOps(); }, [loadBackendOps]);

  // If PIN system not yet set up — show first-run prompt
  if (!pinData || !pinData.operators || pinData.operators.length === 0) {
    return (
      <div style={{ background: "#fff", borderRadius: 12, padding: 40, textAlign: "center", border: `1px solid ${S.border}`, maxWidth: 520, margin: "40px auto" }}>
        <div style={{ display: "inline-flex", width: 64, height: 64, borderRadius: 16, background: "rgba(59,130,246,0.1)", alignItems: "center", justifyContent: "center", marginBottom: 14 }}>
          <Users size={32} color={S.accent} />
        </div>
        <h2 style={{ margin: "0 0 8px", fontSize: 20 }}>PIN sistem nije aktiviran</h2>
        <p style={{ color: S.muted, fontSize: 14, marginBottom: 20, lineHeight: 1.5 }}>
          Aktivirajte PIN sistem da zaključavate aplikaciju kada niko ne radi i da dodijelite različite dozvole za svaku osobu (konobar, menadžer, admin...).
        </p>
        <button onClick={onTriggerSetup} style={{ ...S.btn(S.accent), padding: "12px 20px", fontSize: 15 }}>
          <Users size={18} /> Postavi Admin PIN
        </button>
      </div>
    );
  }

  const operators = pinData.operators || [];
  const isAdmin = !!activeOperator?.is_main_admin;

  // Operators from backend that don't yet have a local PIN assigned
  const availableBackendOps = backendOps.filter(bo =>
    !operators.find(lo => lo.operator_code === bo.operator_code)
  );

  const removeOp = (opCode) => {
    const op = operators.find(o => o.operator_code === opCode);
    if (!op) return;
    if (op.is_main_admin) { showToast("Glavni admin se ne može obrisati", "error"); return; }
    if (!window.confirm(`Obriši operatora "${op.name}"?`)) return;
    const updated = { ...pinData, operators: operators.filter(o => o.operator_code !== opCode) };
    savePinData(updated, user?.company_id);
    setPinData(updated);
    if (activeOperator?.operator_code === opCode) setActiveOperator(null);
    showToast("Operator uklonjen", "success");
  };

  const handleAddClick = () => {
    if (availableBackendOps.length === 0) {
      showToast("Svi registrovani operateri već imaju PIN. Dodajte nove operatore u POS → Podešavanja.", "info");
      return;
    }
    setAddModal(true);
  };

  const handleDisablePin = () => {
    const confirmText = "Ovo će obrisati SVE operatere i PIN sistem.\nSvi korisnici će imati pun pristup nakon login-a.\n\nNastaviti?";
    if (!window.confirm(confirmText)) return;
    if (!window.confirm("Stvarno? Ova radnja se ne može poništiti.")) return;
    if (user?.company_id) localStorage.removeItem(getPinKey(user.company_id));
    showToast("PIN sistem isključen.", "success");
    // Full reload — AdminApp re-mounts, synthesizes fresh activeOperator from
    // user (with real operator_code), no stale state lingering.
    setTimeout(() => window.location.reload(), 500);
  };

  return (
    <div style={{ maxWidth: 900 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18 }}>Operateri</h2>
          <p style={{ margin: "4px 0 0", color: S.muted, fontSize: 13 }}>
            Svaki operator ima svoj 4-cifreni PIN i dozvole. Aktivni: <b>{activeOperator?.name || "—"}</b>
          </p>
        </div>
        {isAdmin && (
          <button onClick={handleAddClick} style={S.btn(S.accent)} disabled={loadingOps}>
            <Plus size={16} /> {loadingOps ? "Učitavam..." : "Novi operator"}
          </button>
        )}
      </div>

      {!isAdmin && (
        <div style={{ background: "#fef3c7", border: "1px solid #fde68a", padding: 10, borderRadius: 8, marginBottom: 14, fontSize: 13, color: "#78350f" }}>
          Samo glavni admin može dodavati ili brisati operatore.
        </div>
      )}

      {isAdmin && availableBackendOps.length === 0 && !loadingOps && (
        <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", padding: 10, borderRadius: 8, marginBottom: 14, fontSize: 13, color: "#1e40af" }}>
          ℹ️ Svi registrovani operateri iz Poreske uprave već imaju PIN. Da biste dodali nove osobe, prvo ih registrujte u POS aplikaciji (Podešavanja → Operateri).
        </div>
      )}

      <div style={{ background: "#fff", borderRadius: 12, border: `1px solid ${S.border}`, overflow: "hidden" }}>
        {operators.map((op, idx) => (
          <div key={op.operator_code} style={{
            padding: 14, borderBottom: idx < operators.length - 1 ? `1px solid ${S.border}` : "none",
            display: "flex", alignItems: "center", gap: 12,
            background: op.is_main_admin ? "rgba(59,130,246,0.03)" : "#fff",
          }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: op.is_main_admin ? "rgba(59,130,246,0.1)" : "rgba(16,185,129,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <User size={18} color={op.is_main_admin ? S.accent : S.green} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: S.dark, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                {op.name}
                {op.is_main_admin && <span style={{ fontSize: 10, color: S.accent, fontWeight: 700, letterSpacing: 0.5 }}>ADMIN</span>}
                {activeOperator?.operator_code === op.operator_code && <span style={{ fontSize: 10, color: S.green, fontWeight: 700 }}>AKTIVAN</span>}
              </div>
              <div style={{ fontSize: 12, color: S.muted, fontFamily: "monospace" }}>
                {op.operator_code} · {op.is_main_admin ? "Pun pristup" : `${(op.permissions || []).length} dozvola`}
              </div>
            </div>
            {isAdmin && !op.is_main_admin && (
              <div style={{ display: "flex", gap: 6 }}>
                <button onClick={() => setEditOp(op)} style={{ ...S.btn("#fff", S.text), border: `1px solid ${S.border}`, fontSize: 12, padding: "6px 10px" }}>
                  <Edit3 size={13} /> Izmijeni
                </button>
                <button onClick={() => removeOp(op.operator_code)} style={{ ...S.btn("#fff", S.red), border: `1px solid ${S.border}`, fontSize: 12, padding: "6px 10px" }}>
                  <Trash2 size={13} />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Danger zone — disable PIN system (main admin only) */}
      {isAdmin && (
        <div style={{ marginTop: 30, background: "#fff", border: "1px solid #fecaca", borderRadius: 12, padding: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: S.red, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>
            Opasna zona
          </div>
          <div style={{ fontSize: 13, color: S.muted, marginBottom: 12, lineHeight: 1.5 }}>
            Isključivanje PIN sistema briše sve operatere i PIN-ove. Aplikacija će raditi bez zaključavanja — svi sa email loginom imaju pun pristup.
          </div>
          <button onClick={handleDisablePin} style={{ ...S.btn("#fff", S.red), border: `1px solid ${S.red}`, fontSize: 13 }}>
            <Trash2 size={14} /> Isključi PIN sistem
          </button>
        </div>
      )}

      {(addModal || editOp) && (
        <OperatorEditModal
          existing={editOp}
          pinData={pinData}
          setPinData={setPinData}
          availableBackendOps={availableBackendOps}
          companyId={user?.company_id}
          onClose={() => { setAddModal(false); setEditOp(null); }}
          showToast={showToast}
        />
      )}
    </div>
  );
}

// Modal to add/edit operator — PICK from backend list (Poreska Uprava-registered), then set PIN + permissions
function OperatorEditModal({ existing, pinData, setPinData, availableBackendOps, companyId, onClose, showToast }) {
  // When editing, the backend op is the one matching existing.operator_code (not in availableBackendOps)
  const [selectedBackendOp, setSelectedBackendOp] = useState(
    existing ? { operator_code: existing.operator_code, full_name: existing.name, id: existing.id } : null
  );
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [pinStep, setPinStep] = useState(1);
  const [selectedPreset, setSelectedPreset] = useState(existing ? "custom" : "cashier");
  const [customPerms, setCustomPerms] = useState(
    existing ? (existing.permissions || []) : PERMISSION_PRESETS.cashier.permissions
  );
  const [saving, setSaving] = useState(false);

  const handlePinDigit = (d) => {
    if (pinStep === 1) {
      const next = pin + d;
      setPin(next);
      if (next.length === 4) setTimeout(() => setPinStep(2), 200);
    } else {
      const next = confirmPin + d;
      setConfirmPin(next);
    }
  };
  const handlePinBack = () => {
    if (pinStep === 1) setPin(p => p.slice(0, -1));
    else setConfirmPin(p => p.slice(0, -1));
  };
  const resetPin = () => { setPin(""); setConfirmPin(""); setPinStep(1); };

  const handlePreset = (p) => {
    setSelectedPreset(p);
    if (p !== "custom") setCustomPerms(PERMISSION_PRESETS[p].permissions);
  };

  const togglePerm = (k) => {
    setSelectedPreset("custom");
    setCustomPerms(prev => prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k]);
  };

  const save = async () => {
    if (!selectedBackendOp) { showToast("Izaberite operatora", "error"); return; }

    let pin_hash = existing?.pin_hash || null;
    if (pin || !existing) {
      if (pin.length !== 4) { showToast("PIN mora biti 4 cifre", "error"); return; }
      if (pin !== confirmPin) { showToast("PIN-ovi se ne poklapaju", "error"); return; }
      pin_hash = await hashPin(pin);
      const dupPin = (pinData.operators || []).find(o =>
        o.pin_hash === pin_hash && o.operator_code !== (existing?.operator_code || "")
      );
      if (dupPin) { showToast(`PIN već koristi: ${dupPin.name}`, "error"); return; }
    }

    setSaving(true);

    const perms = selectedPreset === "custom" ? customPerms : (PERMISSION_PRESETS[selectedPreset]?.permissions || []);

    const newOp = {
      id: selectedBackendOp.id || existing?.id || Date.now(),
      name: selectedBackendOp.full_name || existing?.name,
      operator_code: selectedBackendOp.operator_code,
      pin_hash,
      permissions: perms,
      is_main_admin: false,
    };

    const updated = { ...pinData };
    if (existing) {
      updated.operators = updated.operators.map(o => o.operator_code === existing.operator_code ? newOp : o);
    } else {
      updated.operators = [...(updated.operators || []), newOp];
    }
    savePinData(updated, companyId);
    setPinData(updated);
    setSaving(false);
    showToast(existing ? "Operator ažuriran" : "Operator dodat", "success");
    onClose();
  };

  const currentPin = pinStep === 1 ? pin : confirmPin;
  const permsByGroup = useMemo(() => {
    const groups = {};
    for (const p of ALL_PERMISSIONS) {
      if (!groups[p.group]) groups[p.group] = [];
      groups[p.group].push(p);
    }
    return groups;
  }, []);

  return (
    <Modal open onClose={onClose} maxWidth={640}>
      <div style={{ padding: 20 }}>
        <h2 style={{ margin: "0 0 14px", fontSize: 18, fontWeight: 800 }}>
          {existing ? `Izmijeni: ${existing.name}` : "Novi operator"}
        </h2>

        {/* Operator picker — can only select from Poreska Uprava-registered list */}
        {!existing && (
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: S.text, marginBottom: 6 }}>
              Izaberite operatora <span style={{ color: S.red }}>*</span>
            </label>
            {availableBackendOps.length === 0 ? (
              <div style={{ padding: 12, background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 8, fontSize: 13, color: "#78350f" }}>
                Nema dostupnih operatora. Svi registrovani operateri već imaju PIN ili nisu registrovani u POS aplikaciji.
              </div>
            ) : (
              <select
                value={selectedBackendOp?.operator_code || ""}
                onChange={e => {
                  const found = availableBackendOps.find(o => o.operator_code === e.target.value);
                  setSelectedBackendOp(found || null);
                }}
                style={S.input}
                autoFocus
              >
                <option value="">— Izaberite —</option>
                {availableBackendOps.map(op => (
                  <option key={op.operator_code} value={op.operator_code}>
                    {op.full_name} ({op.operator_code})
                  </option>
                ))}
              </select>
            )}
            <div style={{ fontSize: 11, color: S.muted, marginTop: 6, lineHeight: 1.4 }}>
              Operateri se registruju u POS aplikaciji kao zvanični zaposleni (Poreska uprava kod).
              Ovdje im dodjeljujete 4-cifreni PIN za SELF pristup.
            </div>
          </div>
        )}

        {existing && (
          <div style={{ marginBottom: 14, padding: 10, background: "#f8fafc", borderRadius: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: S.dark }}>{existing.name}</div>
            <div style={{ fontSize: 12, color: S.muted, fontFamily: "monospace" }}>{existing.operator_code}</div>
          </div>
        )}

        {/* PIN entry */}
        <div style={{ background: "#f8fafc", borderRadius: 10, padding: 14, marginBottom: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: S.muted, textTransform: "uppercase", letterSpacing: 1 }}>
              {existing ? (pin ? `Novi PIN · ${pinStep === 1 ? "Unesite" : "Potvrdite"}` : "PIN (ostavi prazno da zadržiš postojeći)") : `PIN · ${pinStep === 1 ? "Unesite 4 cifre" : "Potvrdite PIN"}`}
            </div>
            {pin && <button onClick={resetPin} style={{ background: "transparent", border: "none", color: S.muted, fontSize: 11, cursor: "pointer", textDecoration: "underline" }}>reset</button>}
          </div>
          <div style={{ display: "flex", gap: 10, marginBottom: 10, justifyContent: "center" }}>
            {[0,1,2,3].map(i => (
              <div key={i} style={{ width: 14, height: 14, borderRadius: "50%", border: currentPin.length > i ? "none" : `2px solid ${S.border}`, background: currentPin.length > i ? S.accent : "transparent", transition: "all 0.15s" }} />
            ))}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6, maxWidth: 240, margin: "0 auto" }}>
            {[1,2,3,4,5,6,7,8,9].map(n => (
              <button key={n} onClick={() => handlePinDigit(String(n))} style={{ padding: 12, border: `1px solid ${S.border}`, background: "#fff", borderRadius: 8, fontSize: 18, fontWeight: 600, cursor: "pointer" }}>{n}</button>
            ))}
            <div />
            <button onClick={() => handlePinDigit("0")} style={{ padding: 12, border: `1px solid ${S.border}`, background: "#fff", borderRadius: 8, fontSize: 18, fontWeight: 600, cursor: "pointer" }}>0</button>
            <button onClick={handlePinBack} style={{ padding: 12, border: `1px solid ${S.border}`, background: "#fff", borderRadius: 8, fontSize: 16, cursor: "pointer", color: S.muted }}>⌫</button>
          </div>
        </div>

        {/* Permission preset */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: S.muted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>
            Tip operatora
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {Object.entries(PERMISSION_PRESETS).map(([k, v]) => (
              <button key={k} onClick={() => handlePreset(k)} style={{
                padding: "8px 14px", borderRadius: 20, fontSize: 13, fontWeight: 600,
                border: `1.5px solid ${selectedPreset === k ? S.accent : S.border}`,
                background: selectedPreset === k ? "#eff6ff" : "#fff",
                color: selectedPreset === k ? S.accent : S.text, cursor: "pointer",
              }}>{v.label}</button>
            ))}
          </div>
        </div>

        {/* Permissions list */}
        <div style={{ background: "#f8fafc", borderRadius: 10, padding: 12, marginBottom: 14, maxHeight: 260, overflowY: "auto" }}>
          {Object.entries(permsByGroup).map(([group, perms]) => (
            <div key={group} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: S.muted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>{group}</div>
              {perms.map(p => (
                <label key={p.key} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 2px", cursor: "pointer", fontSize: 13 }}>
                  <input type="checkbox" checked={customPerms.includes(p.key)} onChange={() => togglePerm(p.key)} />
                  {p.label}
                </label>
              ))}
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={onClose} disabled={saving} style={{ ...S.btn("#fff", S.text), flex: 1, border: `1px solid ${S.border}`, justifyContent: "center" }}>Otkaži</button>
          <button onClick={save} disabled={saving || (!existing && !selectedBackendOp)} style={{ ...S.btn(S.accent), flex: 1, justifyContent: "center", opacity: (saving || (!existing && !selectedBackendOp)) ? 0.5 : 1 }}>
            <Save size={16} /> {saving ? "..." : "Sačuvaj"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ══════════════════════════════════════════════════
// SETTINGS TAB
// ══════════════════════════════════════════════════
function SettingsTab({ showToast }) {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [slugInput, setSlugInput] = useState("");
  const [slugSaving, setSlugSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await apiAuth("/self-settings/settings");
    if (r.success) {
      setS(r.data?.settings);
      setSlugInput(r.data?.settings?.company_slug || "");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (patch = null) => {
    // If patch provided, apply it to local state first and save with merged values
    const next = patch ? { ...s, ...patch } : s;
    if (patch) setS(next);
    if (!patch) setSaving(true);
    const r = await apiAuth("/self-settings/settings", {
      method: "POST",
      body: JSON.stringify({
        enabled: next.enabled ? 1 : 0,
        auto_accept: next.auto_accept ? 1 : 0,
        takeaway_enabled: next.takeaway_enabled ? 1 : 0,
        display_name: next.display_name || "",
        welcome_text: next.welcome_text || "",
        banner_image: next.banner_image || "",
        languages: next.languages || "cg",
        branding_color: next.branding_color || "#3b82f6",
        bg_color: next.bg_color || "",
        estimated_prep_min: parseInt(next.estimated_prep_min || 15),
        sound_enabled: next.sound_enabled ? 1 : 0,
        opening_hours: next.opening_hours || null,
      }),
    });
    if (!patch) setSaving(false);
    if (r.success) showToast(patch ? "✓" : "Sačuvano", "success");
    else showToast(r.message || "Error", "error");
  };

  const saveSlug = async () => {
    setSlugSaving(true);
    const r = await apiAuth("/self-settings/slug", {
      method: "POST",
      body: JSON.stringify({ slug: slugInput }),
    });
    setSlugSaving(false);
    if (r.success) { showToast("Slug sačuvan", "success"); load(); }
    else showToast(r.message || "Error", "error");
  };

  if (loading || !s) return <div style={{ textAlign: "center", padding: 40 }}><Spinner /></div>;

  const toggleLang = (code) => {
    const cur = (s.languages || "cg").split(",").map(x => x.trim()).filter(Boolean);
    const next = cur.includes(code) ? cur.filter(c => c !== code) : [...cur, code];
    if (next.length === 0) return;
    setS({ ...s, languages: next.join(",") });
  };

  const activeLangs = (s.languages || "cg").split(",").map(x => x.trim()).filter(Boolean);

  return (
    <div style={{ maxWidth: 760 }}>
      <h2 style={{ margin: "0 0 14px", fontSize: 18 }}>Podešavanja</h2>

      {/* Slug */}
      <div style={{ background: "#fff", padding: 16, borderRadius: 12, marginBottom: 14, border: `1px solid ${S.border}` }}>
        <h3 style={{ margin: "0 0 6px", fontSize: 15 }}>URL slug</h3>
        <p style={{ margin: "0 0 10px", color: S.muted, fontSize: 13 }}>
          Kratak naziv za URL. Klijenti će skenirati QR i ići na <code>self.efis.pro/<b>{slugInput || "slug"}</b>/table/N</code>
        </p>
        <div style={{ display: "flex", gap: 8 }}>
          <input value={slugInput} onChange={e => setSlugInput(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
            placeholder="npr. square" maxLength={50} style={{ ...S.input, flex: 1 }} />
          <button onClick={saveSlug} disabled={slugSaving || !slugInput || slugInput === s.company_slug} style={{ ...S.btn(S.accent), opacity: (slugSaving || !slugInput) ? 0.5 : 1 }}>
            <Save size={16} /> {slugSaving ? "..." : "Sačuvaj"}
          </button>
        </div>
      </div>

      {/* Fiskalizacija */}
      <FiscalizationSection showToast={showToast} />

      {/* Terminali — self-service kiosk devices + live display URL */}
      <TerminalsSection showToast={showToast} />

      {/* Konobar — waiter devices, waiter-only tables, assignments */}
      <KonobarSection showToast={showToast} />

      {/* Main toggles */}
      <div style={{ background: "#fff", padding: 16, borderRadius: 12, marginBottom: 14, border: `1px solid ${S.border}` }}>
        <h3 style={{ margin: "0 0 14px", fontSize: 15 }}>Glavna podešavanja</h3>

        <ToggleRow label="Self Service aktivan" desc="Kada je uključen, klijenti mogu naručivati." checked={!!s.enabled} onChange={v => save({ enabled: v ? 1 : 0 })} />
        <ToggleRow label="Automatsko prihvatanje" desc="Narudžbe odmah idu u kuhinju bez admin odobrenja." checked={!!s.auto_accept} onChange={v => save({ auto_accept: v ? 1 : 0 })} />
        <ToggleRow label="Univerzalni QR (ponesi)" desc="Jedan stalni QR za poručivanje bez stola — za klijente koji nose hranu. Narudžbe se prikazuju kao PONESI." checked={!!s.takeaway_enabled} onChange={v => save({ takeaway_enabled: v ? 1 : 0 })} />

        <Row label="Naziv (prikazuje se klijentima u zaglavlju)">
          <input
            value={s.display_name || ""}
            onChange={e => setS({ ...s, display_name: e.target.value })}
            placeholder={s.company_name || "Naziv restorana"}
            maxLength={100}
            style={S.input}
          />
          <div style={{ fontSize: 12, color: S.muted, marginTop: 4 }}>
            Ostavite prazno da se koristi zvanični naziv firme ({s.company_name || "—"}).
          </div>
        </Row>

        <Row label="Tekst dobrodošlice (prikazuje se klijentima)">
          <textarea value={s.welcome_text || ""} onChange={e => setS({ ...s, welcome_text: e.target.value })} rows={2} maxLength={500} style={{ ...S.input, resize: "vertical", fontFamily: "inherit" }} />
        </Row>

        <Row label="Brending boja">
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input type="color" value={s.branding_color || "#3b82f6"} onChange={e => setS({ ...s, branding_color: e.target.value })} style={{ width: 46, height: 38, border: "none", cursor: "pointer" }} />
            <input value={s.branding_color || ""} onChange={e => setS({ ...s, branding_color: e.target.value })} placeholder="#3b82f6" style={{ ...S.input, width: 130 }} />
          </div>
        </Row>

        {/* Background color picker. Few presets covering common moods plus a
            free-form hex. NULL/empty = default light gray (existing look).
            All presets keep enough contrast with #ffffff product cards. */}
        <Row label="Pozadina aplikacije">
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {[
                { v: "",        lbl: "Svijetla (default)", swatch: "#f5f6f8" },
                { v: "#fef3c7", lbl: "Toplo žuta",           swatch: "#fef3c7" },
                { v: "#dcfce7", lbl: "Mint zelena",          swatch: "#dcfce7" },
                { v: "#dbeafe", lbl: "Nježno plava",         swatch: "#dbeafe" },
                { v: "#fce7f3", lbl: "Roze",                 swatch: "#fce7f3" },
                { v: "#1f2937", lbl: "Tamna",                swatch: "#1f2937" },
              ].map(p => {
                const active = (s.bg_color || "") === p.v;
                return (
                  <button key={p.v || "default"}
                    onClick={() => setS({ ...s, bg_color: p.v })}
                    title={p.lbl}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 6,
                      padding: "6px 10px", borderRadius: 8,
                      border: `2px solid ${active ? S.accent : S.border}`,
                      background: active ? "#eff6ff" : "#fff",
                      cursor: "pointer", fontSize: 12, fontWeight: 600,
                      color: S.dark, fontFamily: S.font,
                    }}>
                    <span style={{
                      width: 18, height: 18, borderRadius: 4,
                      background: p.swatch, border: `1px solid ${S.border}`,
                      flexShrink: 0,
                    }} />
                    {p.lbl}
                  </button>
                );
              })}
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input type="color"
                value={s.bg_color || "#f5f6f8"}
                onChange={e => setS({ ...s, bg_color: e.target.value })}
                style={{ width: 38, height: 32, border: "none", cursor: "pointer", padding: 0 }} />
              <input
                value={s.bg_color || ""}
                onChange={e => setS({ ...s, bg_color: e.target.value })}
                placeholder="prazno = default"
                style={{ ...S.input, width: 160 }} />
              <span style={{ fontSize: 11, color: S.muted }}>
                ili izaberi posebnu boju
              </span>
            </div>
          </div>
        </Row>

        <Row label="Procijenjeno vrijeme pripreme (minuta)">
          <div style={{ width: 180 }}>
            <NumpadInput value={String(s.estimated_prep_min ?? 15)} onChange={(v) => setS({ ...s, estimated_prep_min: parseInt(v || 15) })} decimal={false} placeholder="15" />
          </div>
        </Row>

        <Row label="Jezici (prvi je podrazumijevani)">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {Object.keys(T).map(l => (
              <button key={l} onClick={() => toggleLang(l)} style={{
                padding: "6px 12px", borderRadius: 16, fontSize: 13, fontWeight: 600,
                border: `1.5px solid ${activeLangs.includes(l) ? S.accent : S.border}`,
                background: activeLangs.includes(l) ? "#eff6ff" : "#fff",
                color: activeLangs.includes(l) ? S.accent : S.text,
                cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4,
              }}>
                {LANG_FLAGS[l]} {LANG_NAMES[l]}
              </button>
            ))}
          </div>
        </Row>

        <div style={{ marginTop: 16 }}>
          <button onClick={save} disabled={saving} style={{ ...S.btn(S.accent), padding: "10px 20px", opacity: saving ? 0.6 : 1 }}>
            {saving ? <><Spinner color="#fff" size={16} /> Čuvanje...</> : <><Save size={16} /> Sačuvaj podešavanja</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function FiscalizationSection({ showToast }) {
  // Read companyId from stored user (set at login). Needed for per-company TCR.
  const storedUser = (() => { try { return JSON.parse(localStorage.getItem("self_user") || "{}"); } catch { return {}; } })();
  const companyId = storedUser?.company_id;

  const [tcrs, setTcrs] = useState([]);
  const [selectedTcr, setSelectedTcr] = useState(getTcr(companyId));
  const [loading, setLoading] = useState(true);

  // Printer state — same shape as POS
  const [printerType, setPrinterType] = useState(() => localStorage.getItem("self_printer_type") || (isElectron ? "usb" : "system"));
  const [printerAddr, setPrinterAddr] = useState(() => localStorage.getItem("self_printer_addr") || "");
  const [paperWidth, setPaperWidth] = useState(() => localStorage.getItem("self_paper_width") || "80");
  const [serverStatus, setServerStatus] = useState(null);
  const [testing, setTesting] = useState(false);
  const [systemPrinters, setSystemPrinters] = useState([]);

  // ═══ WinUSB direct printing state (Windows desktop only) ═══
  // Detect Windows runtime — only show WinUSB UI there. macOS/Linux Electron
  // builds skip the toggle entirely. Browser sessions also don't show it.
  // The POS app uses the same gating; keeps the UI consistent.
  const isWindowsElectron = isElectron && /windows nt/i.test(navigator.userAgent || "");

  // useWinUsb: master toggle. When ON, all print jobs route through libusb,
  // bypassing node-thermal-printer entirely. Default OFF — existing flow runs.
  const [useWinUsb, setUseWinUsb] = useState(false);
  // winUsbVid/Pid: when null → auto-detect first printer-class device.
  // When set → use that exact device. Saved as integers in settings.
  const [winUsbVid, setWinUsbVid] = useState(null);
  const [winUsbPid, setWinUsbPid] = useState(null);
  const [detectedDevice, setDetectedDevice] = useState(null);     // result of last detect/test
  const [usbDevices, setUsbDevices] = useState([]);                // for advanced picker
  const [showAdvancedUsb, setShowAdvancedUsb] = useState(false);   // toggle Napredno section
  const [winUsbBusy, setWinUsbBusy] = useState(false);

  useEffect(() => {
    apiAuth("/auth/company-config").then(r => {
      if (r.success) {
        const all = r.data?.all_tcrs || [];
        setTcrs(all);
        if (!getTcr(companyId)) {
          const active = all.filter(t => t.status === "active");
          if (active.length > 0) {
            setTcr(active[0].tcr_code, companyId);
            setSelectedTcr(active[0].tcr_code);
          }
        }
      }
      setLoading(false);
    });
  }, [companyId]);

  // Load system printers + saved Electron settings
  useEffect(() => {
    if (isElectron && window.electronAPI?.listPrinters) {
      window.electronAPI.listPrinters().then(p => setSystemPrinters(p || []));
      window.electronAPI.getSettings().then(s => {
        if (!s) return;
        if (s.interface) setPrinterAddr(s.interface);
        // Load WinUSB settings (defaults applied by main.js getSettings()).
        // null/missing values stay null = auto-detect mode.
        setUseWinUsb(!!s.useWinUsb);
        setWinUsbVid(s.winUsbVid ?? null);
        setWinUsbPid(s.winUsbPid ?? null);
        // Load paperWidth from Electron settings as the source of truth on
        // desktop. localStorage is the fallback for browser sessions.
        if (s.paperWidth) setPaperWidth(String(s.paperWidth));
      });
    }
  }, []);

  const pickTcr = (code) => {
    setTcr(code, companyId);
    setSelectedTcr(code);
    showToast("TCR sačuvan", "success");
  };

  const savePrinter = async () => {
    localStorage.setItem("self_printer_type", printerType);
    if (printerAddr) localStorage.setItem("self_printer_addr", printerAddr);
    else localStorage.removeItem("self_printer_addr");
    localStorage.setItem("self_paper_width", paperWidth);

    // If Electron, save to main process so node-thermal-printer picks it up
    if (isElectron && window.electronAPI) {
      const widthChars = paperWidth === "58" || paperWidth === "57" ? 32 : 48;
      await window.electronAPI.saveSettings({
        type: "epson",
        interface: printerAddr,
        printerName: printerAddr,
        width: widthChars,
        // WinUSB fields — saved every time so toggling is persisted.
        useWinUsb: !!useWinUsb,
        winUsbVid: winUsbVid,
        winUsbPid: winUsbPid,
        paperWidth: parseInt(paperWidth) || 80,
      });
    }
    showToast("Postavke štampača sačuvane!", "success");
  };

  // ═══ WinUSB action handlers ═══

  // Auto-detect: scan USB and find a printer-class device. Saves found VID/PID
  // to state but does NOT persist until user clicks Sačuvaj.
  const winUsbAutoDetect = async () => {
    if (!window.electronAPI?.detectUsbPrinter) {
      showToast("WinUSB nije dostupan u ovoj verziji aplikacije", "error");
      return;
    }
    setWinUsbBusy(true);
    setDetectedDevice(null);
    try {
      const r = await window.electronAPI.detectUsbPrinter();
      if (r.success) {
        setDetectedDevice(r.device);
        setWinUsbVid(r.device.vid);
        setWinUsbPid(r.device.pid);
        showToast(`Pronađen: ${r.device.product || r.device.vendor || "USB printer"}`, "success");
      } else {
        showToast(r.message || "USB printer nije pronađen", "error");
      }
    } catch (e) {
      showToast("Greška: " + e.message, "error");
    }
    setWinUsbBusy(false);
  };

  // Test: verify the saved/picked VID/PID resolves to a connected device.
  // Doesn't print — just checks libusb can find the device. Saves first so
  // the test reflects the current selection (not whatever's persisted).
  const winUsbTestConnection = async () => {
    if (!window.electronAPI?.winUsbTest) {
      showToast("WinUSB nije dostupan u ovoj verziji aplikacije", "error");
      return;
    }
    setWinUsbBusy(true);
    try {
      await window.electronAPI.saveSettings({
        type: "epson",
        interface: printerAddr,
        printerName: printerAddr,
        width: paperWidth === "58" || paperWidth === "57" ? 32 : 48,
        useWinUsb: true,
        winUsbVid: winUsbVid,
        winUsbPid: winUsbPid,
        paperWidth: parseInt(paperWidth) || 80,
      });
      const r = await window.electronAPI.winUsbTest();
      if (r.success) showToast(r.message || "Povezan!", "success");
      else showToast(r.message || "Greška", "error");
    } catch (e) {
      showToast("Greška: " + e.message, "error");
    }
    setWinUsbBusy(false);
  };

  // Refresh USB device list for the advanced picker
  const winUsbRefreshList = async () => {
    if (!window.electronAPI?.listUsbDevices) {
      showToast("WinUSB nije dostupan u ovoj verziji aplikacije", "error");
      return;
    }
    setWinUsbBusy(true);
    try {
      const r = await window.electronAPI.listUsbDevices();
      if (r.success) {
        setUsbDevices(r.devices || []);
        if (!r.usbAvailable) {
          showToast("USB modul nije dostupan u ovoj instalaciji", "error");
        }
      } else {
        showToast(r.message || "Greška pri čitanju USB uređaja", "error");
      }
    } catch (e) {
      showToast("Greška: " + e.message, "error");
    }
    setWinUsbBusy(false);
  };

  const winUsbPickDevice = (dev) => {
    setWinUsbVid(dev.vid);
    setWinUsbPid(dev.pid);
    setDetectedDevice(dev);
    showToast(`Izabran: ${dev.product || dev.vendor || "USB device"}`, "success");
  };

  const testConnection = async () => {
    setTesting(true);
    setServerStatus(null);
    if (isElectron && window.electronAPI) {
      const r = await window.electronAPI.testConnection();
      setServerStatus({ ok: r.success, msg: r.message || (r.success ? "Povezan!" : "Greška") });
    } else {
      setServerStatus({ ok: true, msg: "Sistem za štampanje je dostupan." });
    }
    setTesting(false);
  };

  const testPrint = async () => {
    setTesting(true);
    const testText = "\n================================\n     EFIS.PRO SELF - Test\n================================\nPrinter: OK | Sirina: " + paperWidth + "mm\n" + new Date().toLocaleString("sr-Latn-ME") + "\n================================\n\n\n";
    if (isElectron && window.electronAPI) {
      const r = await window.electronAPI.printText(testText);
      if (r.success) showToast("Štampano!", "success"); else showToast(r.message || "Greška", "error");
    } else if (printerType === "network" && printerAddr) {
      try {
        const printUrl = (printerAddr || "").endsWith("/") ? printerAddr.slice(0, -1) + "/print/text" : printerAddr + "/print/text";
        const r = await fetch(printUrl, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text: testText }) });
        const result = await r.json();
        if (result.success) showToast("Štampano!", "success"); else showToast(result.message || "Greška", "error");
      } catch (e) { showToast("Ne mogu se povezati: " + e.message, "error"); }
    } else {
      const w = window.open("", "test", "width=350,height=400");
      if (w) {
        // eslint-disable-next-line no-useless-concat
        w.document.write('<html><head><style>@page{size:' + paperWidth + 'mm auto;margin:2mm;}body{font-family:monospace;text-align:center;padding:10mm;}</style></head><body><h2>EFIS.PRO SELF</h2><p>Test Print</p><p>Sirina: ' + paperWidth + 'mm</p><p>' + new Date().toLocaleString("sr-Latn-ME") + '</p><hr><p>Štampanje radi!</p><scr' + 'ipt>setTimeout(function(){window.print();},500);</scr' + 'ipt></body></html>');
        w.document.close();
      }
    }
    setTesting(false);
  };

  return (
    <div style={{ background: "#fff", padding: 16, borderRadius: 12, marginBottom: 14, border: `1px solid ${S.border}` }}>
      <h3 style={{ margin: "0 0 6px", fontSize: 15 }}>Fiskalizacija</h3>
      <p style={{ margin: "0 0 12px", color: S.muted, fontSize: 13 }}>
        Koji TCR koristi Self app za izdavanje računa. Ovo je isti TCR kao u POS-u.
      </p>

      {loading ? (
        <Spinner />
      ) : tcrs.length === 0 ? (
        <div style={{ background: "#fef2f2", color: S.red, padding: 10, borderRadius: 8, fontSize: 13 }}>
          Nema konfigurisanih TCR-ova. Dodajte TCR u POS → Podešavanja.
        </div>
      ) : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
          {tcrs.map(t => (
            <button key={t.tcr_code} onClick={() => pickTcr(t.tcr_code)} disabled={t.status !== "active"} style={{
              padding: "8px 14px", borderRadius: 8, fontSize: 13, fontWeight: 600,
              border: `1.5px solid ${selectedTcr === t.tcr_code ? S.accent : S.border}`,
              background: selectedTcr === t.tcr_code ? "#eff6ff" : "#fff",
              color: selectedTcr === t.tcr_code ? S.accent : (t.status === "active" ? S.text : S.lightMuted),
              cursor: t.status === "active" ? "pointer" : "not-allowed",
              opacity: t.status === "active" ? 1 : 0.5,
            }}>
              {t.tcr_code}
              {t.business_unit_code && <span style={{ fontSize: 11, color: S.muted, marginLeft: 4 }}>· {t.business_unit_code}</span>}
              {t.status !== "active" && <span style={{ fontSize: 10, color: S.muted, marginLeft: 4 }}>(neaktivan)</span>}
            </button>
          ))}
        </div>
      )}

      {/* Printer */}
      <h4 style={{ margin: "18px 0 10px", fontSize: 13, color: S.muted, textTransform: "uppercase", letterSpacing: 1 }}>Termalni štampač</h4>

      {/* Platform indicator */}
      <div style={{ padding: "8px 14px", background: "#f0fdf4", borderRadius: 8, marginBottom: 16, fontSize: 13, color: "#15803d", display: "flex", alignItems: "center", gap: 8 }}>
        <CheckCircle size={16} />
        {isElectron ? "Desktop aplikacija — direktno štampanje" : "Web pretraživač — sistemski dijalog"}
      </div>

      {isElectron ? (
        <>
          <label style={{ fontSize: 14, fontWeight: 600, display: "block", marginBottom: 6 }}>Printer adresa / interfejs</label>
          <input
            value={printerAddr}
            onChange={e => setPrinterAddr(e.target.value)}
            placeholder="tcp://192.168.1.100:9100 ili USB ime"
            style={{ ...S.input, marginBottom: 8, fontFamily: "monospace" }}
          />
          <div style={{ fontSize: 12, color: S.muted, marginBottom: 16, lineHeight: 1.5 }}>
            <b>Mrežni:</b> tcp://IP:9100 &nbsp;·&nbsp;
            <b>USB Windows:</b> \\localhost\ImeStampaca &nbsp;·&nbsp;
            <b>USB Linux:</b> /dev/usb/lp0
          </div>

          {systemPrinters.length > 0 && (
            <>
              <label style={{ fontSize: 14, fontWeight: 600, display: "block", marginBottom: 6 }}>Ili izaberite sistemski printer</label>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
                {systemPrinters.map(p => (
                  <button key={p.name} onClick={() => setPrinterAddr(p.name)} style={{
                    padding: "10px 14px", borderRadius: 8,
                    border: `2px solid ${printerAddr === p.name ? S.green : S.border}`,
                    background: printerAddr === p.name ? "#f0fdf4" : "#fff",
                    textAlign: "left", cursor: "pointer", fontFamily: S.font, fontSize: 13,
                  }}>
                    <span style={{ fontWeight: 600 }}>{p.name}</span>
                    {p.isDefault && <span style={{ marginLeft: 8, fontSize: 11, color: S.green }}>(default)</span>}
                  </button>
                ))}
              </div>
            </>
          )}
        </>
      ) : (
        <>
          <label style={{ fontSize: 14, fontWeight: 600, display: "block", marginBottom: 6 }}>Način štampanja</label>
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            <button onClick={() => setPrinterType("system")} style={{
              flex: 1, padding: "12px", borderRadius: 8,
              border: `2px solid ${printerType === "system" ? S.green : S.border}`,
              background: printerType === "system" ? "#f0fdf4" : "#fff",
              fontWeight: 600, fontSize: 13, cursor: "pointer",
              color: printerType === "system" ? S.green : S.muted,
            }}>Sistemski dijalog</button>
            <button onClick={() => setPrinterType("network")} style={{
              flex: 1, padding: "12px", borderRadius: 8,
              border: `2px solid ${printerType === "network" ? S.green : S.border}`,
              background: printerType === "network" ? "#f0fdf4" : "#fff",
              fontWeight: 600, fontSize: 13, cursor: "pointer",
              color: printerType === "network" ? S.green : S.muted,
            }}>Mrežni print server</button>
          </div>
          {printerType === "network" ? (
            <>
              <label style={{ fontSize: 14, fontWeight: 600, display: "block", marginBottom: 6 }}>Print server URL</label>
              <input value={printerAddr} onChange={e => setPrinterAddr(e.target.value)} placeholder="http://192.168.1.100:9100" style={{ ...S.input, marginBottom: 8, fontFamily: "monospace" }} />
              <div style={{ fontSize: 12, color: S.muted, marginBottom: 16 }}>
                URL print servera lokalno u mreži.
              </div>
            </>
          ) : (
            <div style={{ padding: 14, background: "#f0f9ff", borderRadius: 8, marginBottom: 16, fontSize: 13, color: "#1d4ed8" }}>
              Štampanje koristi sistemski dijalog — pri svakoj štampi se otvara prozor za izbor printera.
            </div>
          )}
        </>
      )}

      <label style={{ fontSize: 14, fontWeight: 600, display: "block", marginBottom: 6 }}>Širina papira</label>
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {["57", "58", "76", "80"].map(w => (
          <button key={w} onClick={() => setPaperWidth(w)} style={{
            padding: "10px 18px", borderRadius: 8,
            border: `2px solid ${paperWidth === w ? S.green : S.border}`,
            background: paperWidth === w ? "#f0fdf4" : "#fff",
            fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "monospace",
            color: paperWidth === w ? S.green : S.muted,
          }}>{w}mm</button>
        ))}
      </div>

      {/* ═══ WinUSB direct printing (Windows desktop only) ═══
          When toggle is ON, all print jobs go directly to the USB printer
          via libusb — no Windows printer driver needed. Bypasses the
          spooler entirely. Killer use-case: customer has POS using a USB
          printer; they install SELF on the same machine and want to share
          the printer without unplugging/swapping ports. WinUSB lets both
          apps drive the same physical printer. NOTE: requires the printer
          to expose libusb-compatible drivers (WinUSB or libusb-win32 via
          Zadig). Most cheap thermal printers work; some vendor-branded
          units don't. */}
      {isWindowsElectron && (
        <div style={{ marginBottom: 16, padding: 14, background: useWinUsb ? "#f0f9ff" : "#f9fafb", border: `1px solid ${useWinUsb ? "#bae6fd" : S.border}`, borderRadius: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: useWinUsb ? 12 : 4 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: useWinUsb ? "#0c4a6e" : S.dark }}>WinUSB direktno štampanje</div>
              <div style={{ fontSize: 12, color: S.muted, marginTop: 2 }}>
                Šalje ESC/POS direktno preko USB-a, bez Windows drajvera za printer.
              </div>
            </div>
            <button
              onClick={() => setUseWinUsb(v => !v)}
              style={{
                padding: "8px 14px",
                background: useWinUsb ? S.green : "#f3f4f6",
                color: useWinUsb ? "white" : S.muted,
                border: "none", borderRadius: 7, cursor: "pointer",
                fontWeight: 700, fontSize: 12,
                display: "flex", alignItems: "center", gap: 6,
                fontFamily: S.font,
              }}
            >
              {useWinUsb ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
              {useWinUsb ? "ON" : "OFF"}
            </button>
          </div>

          {useWinUsb && (
            <>
              {/* Status / detected device info */}
              {(detectedDevice || (winUsbVid && winUsbPid)) ? (
                <div style={{ padding: "10px 12px", background: "white", border: `1px solid ${S.border}`, borderRadius: 8, marginBottom: 10 }}>
                  <div style={{ fontSize: 11, color: S.muted, marginBottom: 2 }}>Aktivni printer:</div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>
                    {detectedDevice?.product || detectedDevice?.vendor || "USB Printer"}
                  </div>
                  <div style={{ fontSize: 10, color: S.muted, fontFamily: "monospace" }}>
                    VID:{(winUsbVid ?? 0).toString(16).padStart(4, "0").toUpperCase()} PID:{(winUsbPid ?? 0).toString(16).padStart(4, "0").toUpperCase()}
                  </div>
                </div>
              ) : (
                <div style={{ padding: "10px 12px", background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 8, marginBottom: 10, fontSize: 12, color: "#92400e" }}>
                  Printer još nije izabran. Kliknite "Auto-detekcija" ili izaberite ručno u Naprednim postavkama.
                </div>
              )}

              <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
                <button onClick={winUsbAutoDetect} disabled={winUsbBusy} style={{ ...S.btnSm(S.accent, "white"), flex: "1 1 calc(50% - 3px)", justifyContent: "center", opacity: winUsbBusy ? 0.5 : 1, padding: "8px 10px" }}>
                  {winUsbBusy ? <Loader2 size={13} className="spin" /> : <RefreshCw size={13} />}
                  Auto-detekcija
                </button>
                <button onClick={winUsbTestConnection} disabled={winUsbBusy || !winUsbVid} style={{ ...S.btnSm(S.green, "white"), flex: "1 1 calc(50% - 3px)", justifyContent: "center", opacity: (winUsbBusy || !winUsbVid) ? 0.5 : 1, padding: "8px 10px" }}>
                  <CheckCircle size={13} />
                  Testiraj
                </button>
              </div>

              <button
                onClick={() => { const next = !showAdvancedUsb; setShowAdvancedUsb(next); if (next && usbDevices.length === 0) winUsbRefreshList(); }}
                style={{ width: "100%", padding: "8px 12px", background: "transparent", border: `1px dashed ${S.border}`, borderRadius: 7, cursor: "pointer", fontSize: 12, color: S.muted, fontWeight: 600, fontFamily: S.font }}
              >
                {showAdvancedUsb ? "▾" : "▸"} Napredno (ručni izbor uređaja)
              </button>

              {showAdvancedUsb && (
                <div style={{ marginTop: 10, padding: 10, background: "white", border: `1px solid ${S.border}`, borderRadius: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <div style={{ fontSize: 12, color: S.muted }}>Svi USB uređaji ({usbDevices.length})</div>
                    <button onClick={winUsbRefreshList} disabled={winUsbBusy} style={{ ...S.btnSm("#f3f4f6", S.muted), padding: "5px 10px" }}>
                      <RefreshCw size={11} />Osvježi
                    </button>
                  </div>
                  {usbDevices.length === 0 && !winUsbBusy && (
                    <div style={{ fontSize: 12, color: S.muted, padding: 12, textAlign: "center" }}>
                      Nema dostupnih USB uređaja. Provjerite drajver (WinUSB / libusb).
                    </div>
                  )}
                  {usbDevices.map((d, i) => (
                    <button
                      key={`${d.vid}-${d.pid}-${i}`}
                      onClick={() => winUsbPickDevice(d)}
                      style={{
                        width: "100%", padding: "8px 10px",
                        background: (winUsbVid === d.vid && winUsbPid === d.pid) ? "#f0fdf4" : "white",
                        border: `1px solid ${(winUsbVid === d.vid && winUsbPid === d.pid) ? S.green : S.border}`,
                        borderRadius: 6, marginBottom: 4, cursor: "pointer",
                        textAlign: "left", fontFamily: S.font, fontSize: 12,
                        display: "flex", alignItems: "center", gap: 8,
                      }}
                    >
                      {d.isPrinter && <span title="Printer-class device" style={{ fontSize: 11 }}>🖨️</span>}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {d.product || d.vendor || "Unknown device"}
                        </div>
                        <div style={{ fontSize: 10, color: S.muted, fontFamily: "monospace" }}>
                          VID:{d.vidHex.toUpperCase()} PID:{d.pidHex.toUpperCase()}
                          {d.isPrinter && <span style={{ marginLeft: 6, color: S.green }}>· printer-class</span>}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              <div style={{ marginTop: 10, fontSize: 11, color: S.muted, lineHeight: 1.4 }}>
                ⚠️ Za WinUSB potreban je libusb drajver. Ako auto-detekcija ne nalazi printer, instalirajte WinUSB drajver pomoću alata <b>Zadig</b> (zadig.akeo.ie) i pokušajte ponovo.
              </div>
            </>
          )}
        </div>
      )}

      <button onClick={savePrinter} style={{ ...S.btn(S.accent), width: "100%", justifyContent: "center", padding: 12, fontSize: 14, marginBottom: 14 }}>
        <Save size={16} /> Sačuvaj postavke štampača
      </button>

      {/* Test */}
      <div style={{ padding: 14, background: "#f8fafc", borderRadius: 10, border: `1px solid ${S.border}` }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: S.muted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>Testiranje</div>
        <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
          <button onClick={testConnection} disabled={testing} style={{ ...S.btn("#fff", S.dark), border: `1px solid ${S.border}`, flex: 1, justifyContent: "center" }}>
            {testing ? "..." : "Provjeri konekciju"}
          </button>
          <button onClick={testPrint} disabled={testing} style={{ ...S.btn(S.green), flex: 1, justifyContent: "center" }}>
            <Printer size={14} /> Test print
          </button>
        </div>
        {serverStatus && (
          <div style={{
            padding: 12, borderRadius: 8, fontSize: 13, display: "flex", alignItems: "center", gap: 8,
            background: serverStatus.ok ? "#f0fdf4" : "#fef2f2",
            border: `1px solid ${serverStatus.ok ? "#bbf7d0" : "#fecaca"}`,
            color: serverStatus.ok ? "#15803d" : S.red,
          }}>
            {serverStatus.ok ? <CheckCircle size={16} /> : <XCircle size={16} />}
            <span>{serverStatus.msg}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// TerminalsSection — admin-side terminal device management
// Lives inside SettingsTab → renders below FiscalizationSection.
// Three sub-cards:
//   1) Live Display URL (public TV screen)
//   2) Active Terminals list (paired devices, with rename/revoke)
//   3) Pending Pairings list (unredeemed codes with countdown)
// ════════════════════════════════════════════════════════════════
function TerminalsSection({ showToast }) {
  const [terminals, setTerminals] = useState([]);
  const [pendingCodes, setPendingCodes] = useState([]);
  const [liveUrl, setLiveUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [pairingModal, setPairingModal] = useState(null); // { code, terminalNumber, expiresInSec }
  const [editLabelId, setEditLabelId] = useState(null);
  const [editLabelValue, setEditLabelValue] = useState("");
  const [regenerating, setRegenerating] = useState(false);

  const load = useCallback(async () => {
    const [tr, lr] = await Promise.all([
      apiAuth("/self-admin/terminals-list"),
      apiAuth("/self-admin/live-slug"),
    ]);
    if (tr.success) {
      setTerminals(tr.data?.terminals || []);
      setPendingCodes(tr.data?.pendingCodes || []);
    }
    if (lr.success) {
      setLiveUrl(lr.data?.url || "");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreatePairing = async () => {
    setCreating(true);
    const r = await apiAuth("/self-admin/terminals-create-pairing", {
      method: "POST",
      body: JSON.stringify({}),
    });
    setCreating(false);
    if (r.success) {
      setPairingModal({
        code: r.data.code,
        terminalNumber: r.data.terminalNumber,
        expiresInSec: r.data.expiresInSec || 600,
      });
      load();
    } else {
      showToast(r.message || "Greška pri kreiranju koda", "error");
    }
  };

  const handleRevoke = async (terminal) => {
    const label = terminal.device_label || `Terminal ${terminal.terminal_number}`;
    if (!window.confirm(`Opozvati ${label}? Uređaj više neće moći da prima narudžbe.`)) return;
    const r = await apiAuth(`/self-admin/terminals-revoke/${terminal.id}`, { method: "POST" });
    if (r.success) {
      showToast("Terminal opozvan", "success");
      load();
    } else {
      showToast(r.message || "Greška", "error");
    }
  };

  const handleCancelPairing = async (pairing) => {
    if (!window.confirm(`Otkazati neiskorišćen kod za Terminal ${pairing.terminal_number}?`)) return;
    const r = await apiAuth(`/self-admin/terminals-cancel-pairing/${pairing.id}`, { method: "POST" });
    if (r.success) {
      showToast("Kod otkazan", "success");
      load();
    } else {
      showToast(r.message || "Greška", "error");
    }
  };

  const startEditLabel = (t) => {
    setEditLabelId(t.id);
    setEditLabelValue(t.device_label || "");
  };

  const saveLabel = async (id) => {
    const r = await apiAuth(`/self-admin/terminals-relabel/${id}`, {
      method: "POST",
      body: JSON.stringify({ deviceLabel: editLabelValue }),
    });
    if (r.success) {
      showToast("Naziv izmijenjen", "success");
      setEditLabelId(null);
      load();
    } else {
      showToast(r.message || "Greška", "error");
    }
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(liveUrl);
      showToast("URL kopiran", "success");
    } catch {
      // Fallback: show prompt for manual copy
      window.prompt("Kopirajte URL:", liveUrl);
    }
  };

  const regenerateLiveSlug = async () => {
    if (!window.confirm("Generisati novi URL? Stari URL će prestati da radi.")) return;
    setRegenerating(true);
    const r = await apiAuth("/self-admin/live-slug-regenerate", { method: "POST" });
    setRegenerating(false);
    if (r.success) {
      setLiveUrl(r.data.url);
      showToast("Novi URL kreiran", "success");
    } else {
      showToast(r.message || "Greška", "error");
    }
  };

  if (loading) {
    return (
      <div style={{ background: "#fff", padding: 16, borderRadius: 12, marginBottom: 14, border: `1px solid ${S.border}`, textAlign: "center" }}>
        <Spinner />
      </div>
    );
  }

  return (
    <>
      {/* ═══ Live Display URL ═══ */}
      <div style={{ background: "#fff", padding: 16, borderRadius: 12, marginBottom: 14, border: `1px solid ${S.border}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <Tv size={18} color={S.accent} />
          <h3 style={{ margin: 0, fontSize: 15 }}>Live Display — javni ekran statusa</h3>
        </div>
        <p style={{ margin: "0 0 12px", color: S.muted, fontSize: 13 }}>
          Otvorite ovaj URL na velikom ekranu (TV) u restoranu. Klijenti će vidjeti brojeve narudžbi i njihov status. URL je javan ali nepogodljiv — niko ga ne može pogoditi. Ako URL procuri, generišite novi.
        </p>
        <div style={{ display: "flex", gap: 6, alignItems: "stretch", flexWrap: "wrap" }}>
          <div style={{
            flex: "1 1 300px",
            display: "flex", alignItems: "center",
            padding: "10px 12px",
            background: "#f8fafc",
            border: `1px solid ${S.border}`,
            borderRadius: 8,
            fontFamily: "JetBrains Mono, monospace",
            fontSize: 13,
            color: liveUrl ? S.text : S.muted,
            wordBreak: "break-all",
          }}>
            {liveUrl || "URL će biti generisan..."}
          </div>
          <button onClick={copyUrl} disabled={!liveUrl}
            style={{ ...S.btn("#fff"), border: `1px solid ${S.border}`, padding: "10px 14px" }}>
            <Copy size={14} /> Kopiraj
          </button>
          <button onClick={() => liveUrl && window.open(liveUrl, "_blank")} disabled={!liveUrl}
            style={{ ...S.btn("#fff"), border: `1px solid ${S.border}`, padding: "10px 14px" }}>
            <ExternalLink size={14} /> Otvori
          </button>
          <button onClick={regenerateLiveSlug} disabled={regenerating}
            style={{ ...S.btn("#fff"), border: `1px solid ${S.red}`, color: S.red, padding: "10px 14px", opacity: regenerating ? 0.6 : 1 }}>
            {regenerating ? <Spinner size={14} color={S.red} /> : <RefreshCw size={14} />} Novi URL
          </button>
        </div>
      </div>

      {/* ═══ Terminals (paired devices + pending) ═══ */}
      <div style={{ background: "#fff", padding: 16, borderRadius: 12, marginBottom: 14, border: `1px solid ${S.border}` }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Monitor size={18} color={S.accent} />
            <h3 style={{ margin: 0, fontSize: 15 }}>Terminali (samouslužni kiosci)</h3>
          </div>
          <button onClick={handleCreatePairing} disabled={creating}
            style={{ ...S.btn(S.accent), padding: "8px 14px", opacity: creating ? 0.6 : 1 }}>
            {creating ? <Spinner size={14} color="#fff" /> : <Plus size={14} />}
            Dodaj novi terminal
          </button>
        </div>

        <p style={{ margin: "0 0 14px", color: S.muted, fontSize: 13 }}>
          Terminal je fizički uređaj (kiosk sa touchscreenom) na kojem klijent samostalno pravi narudžbu. Da biste dodali novi, kliknite "Dodaj" — dobićete 6-cifreni kod koji unesete u Terminal aplikaciju.
        </p>

        {/* Pending pairings */}
        {pendingCodes.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: S.muted, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.4 }}>
              Čeka uparivanje ({pendingCodes.length})
            </div>
            {pendingCodes.map(p => (
              <PendingCodeRow key={p.id} pairing={p} onCancel={() => handleCancelPairing(p)} onTick={load} />
            ))}
          </div>
        )}

        {/* Active terminals */}
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: S.muted, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.4 }}>
            Uparen ({terminals.filter(t => !t.is_revoked).length})
          </div>
          {terminals.length === 0 ? (
            <div style={{ padding: 24, textAlign: "center", color: S.muted, fontSize: 13, background: "#f8fafc", borderRadius: 8 }}>
              Još uvijek nema upareneh terminala.
            </div>
          ) : (
            terminals.map(t => (
              <div key={t.id} style={{
                display: "flex", alignItems: "center", gap: 12,
                padding: "10px 12px",
                borderRadius: 8,
                border: `1px solid ${S.border}`,
                marginBottom: 6,
                background: t.is_revoked ? "#fafafa" : "#fff",
                opacity: t.is_revoked ? 0.6 : 1,
              }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 8,
                  background: t.is_revoked ? "#f1f5f9" : "#eff6ff",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0,
                }}>
                  <Monitor size={18} color={t.is_revoked ? S.muted : S.accent} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {editLabelId === t.id ? (
                    <div style={{ display: "flex", gap: 4 }}>
                      <input
                        value={editLabelValue}
                        onChange={e => setEditLabelValue(e.target.value)}
                        autoFocus
                        placeholder={`Terminal ${t.terminal_number}`}
                        maxLength={120}
                        style={{ ...S.input, padding: "6px 8px", fontSize: 13, flex: 1 }}
                        onKeyDown={e => { if (e.key === "Enter") saveLabel(t.id); if (e.key === "Escape") setEditLabelId(null); }}
                      />
                      <button onClick={() => saveLabel(t.id)} style={S.btnSm(S.accent, "#fff")}><Check size={13} /></button>
                      <button onClick={() => setEditLabelId(null)} style={S.btnSm("#f3f4f6", S.muted)}><X size={13} /></button>
                    </div>
                  ) : (
                    <div style={{ fontSize: 14, fontWeight: 600, color: S.text, display: "flex", alignItems: "center", gap: 6 }}>
                      {t.device_label || `Terminal ${t.terminal_number}`}
                      <span style={{ fontSize: 11, color: S.muted, fontWeight: 400, fontFamily: "JetBrains Mono, monospace" }}>
                        #{t.terminal_number}
                      </span>
                      {t.is_revoked && <span style={{ fontSize: 10, color: S.red, padding: "1px 6px", background: "#fef2f2", borderRadius: 4 }}>OPOZVAN</span>}
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: S.muted, marginTop: 2 }}>
                    {t.last_used_at
                      ? `Posljednja aktivnost: ${formatRelative(t.last_used_at)}`
                      : "Nikad nije korišten"}
                  </div>
                </div>
                {!t.is_revoked && editLabelId !== t.id && (
                  <div style={{ display: "flex", gap: 4 }}>
                    <button onClick={() => startEditLabel(t)} title="Preimenuj"
                      style={S.btnSm("#f3f4f6", S.muted)}><Edit3 size={13} /></button>
                    <button onClick={() => handleRevoke(t)} title="Opozovi"
                      style={S.btnSm("#fef2f2", S.red)}><Power size={13} /></button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* ═══ Pairing modal ═══ */}
      {pairingModal && (
        <PairingCodeModal
          code={pairingModal.code}
          terminalNumber={pairingModal.terminalNumber}
          expiresInSec={pairingModal.expiresInSec}
          onClose={() => { setPairingModal(null); load(); }}
          onPaired={() => {
            showToast(`Terminal ${pairingModal.terminalNumber} uspješno uparen!`, "success");
            setPairingModal(null);
            load();
          }}
        />
      )}
    </>
  );
}

// ════════════════════════════════════════════════════════════════
// KonobarSection — admin-side waiter (konobar) management.
// On/off toggle, waiter-only table set, add/manage waiters with
// pairing codes and per-waiter table assignment.
// Mirrors TerminalsSection patterns. Waiters pair a phone once at
// self.efis.pro/{slug}/konobar with a 6-digit code.
// ════════════════════════════════════════════════════════════════
function KonobarSection({ showToast }) {
  const [enabled, setEnabled] = useState(false);
  const [waiterTables, setWaiterTables] = useState([]);
  const [tableInput, setTableInput] = useState("");
  const [waiters, setWaiters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingTables, setSavingTables] = useState(false);

  // add-waiter form
  const [newName, setNewName] = useState("");
  const [newTables, setNewTables] = useState([]); // assigned table numbers
  const [creating, setCreating] = useState(false);

  // pairing code modal: { name, code }
  const [codeModal, setCodeModal] = useState(null);

  // per-waiter edit of assigned tables: { id, tables:[] }
  const [editAssign, setEditAssign] = useState(null);

  const load = useCallback(async () => {
    const r = await apiAuth("/self-admin/waiters");
    if (r.success) {
      setEnabled(!!r.data?.waiterEnabled);
      const wt = r.data?.waiterTables || [];
      setWaiterTables(wt);
      setTableInput(wt.join(", "));
      setWaiters(r.data?.waiters || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleEnabled = async (v) => {
    setEnabled(v);
    const r = await apiAuth("/self-admin/waiter-toggle", {
      method: "POST",
      body: JSON.stringify({ enabled: v ? 1 : 0 }),
    });
    if (!r.success) {
      showToast(r.message || "Greška", "error");
      setEnabled(!v);
    } else {
      showToast(v ? "Konobar uključen" : "Konobar isključen", "success");
    }
  };

  const saveTables = async () => {
    const nums = tableInput.split(/[\s,]+/).map(s => parseInt(s.trim())).filter(n => n > 0);
    setSavingTables(true);
    const r = await apiAuth("/self-admin/waiter-tables", {
      method: "POST",
      body: JSON.stringify({ numbers: nums }),
    });
    setSavingTables(false);
    if (r.success) {
      setWaiterTables(r.data?.numbers || []);
      setTableInput((r.data?.numbers || []).join(", "));
      showToast("Stolovi sačuvani", "success");
    } else {
      showToast(r.message || "Greška", "error");
    }
  };

  const toggleNewTable = (n) => {
    setNewTables(prev => prev.includes(n) ? prev.filter(x => x !== n) : [...prev, n].sort((a, b) => a - b));
  };

  const createWaiter = async () => {
    if (!newName.trim()) { showToast("Unesite ime konobara", "error"); return; }
    setCreating(true);
    const r = await apiAuth("/self-admin/waiter-create", {
      method: "POST",
      body: JSON.stringify({ name: newName.trim(), assignedTables: newTables }),
    });
    setCreating(false);
    if (r.success) {
      setCodeModal({ name: r.data.name, code: r.data.pairingCode });
      setNewName("");
      setNewTables([]);
      load();
    } else {
      showToast(r.message || "Greška", "error");
    }
  };

  const regenCode = async (w) => {
    const r = await apiAuth(`/self-admin/waiter-pair/${w.id}`, { method: "POST" });
    if (r.success) {
      setCodeModal({ name: w.name, code: r.data.pairingCode });
    } else {
      showToast(r.message || "Greška", "error");
    }
  };

  const unpair = async (w) => {
    if (!window.confirm(`Opozvati uređaj konobara ${w.name}? Moraće ponovo da se uparuje.`)) return;
    const r = await apiAuth(`/self-admin/waiter-unpair/${w.id}`, { method: "POST" });
    if (r.success) { showToast("Uređaj opozvan", "success"); load(); }
    else showToast(r.message || "Greška", "error");
  };

  const removeWaiter = async (w) => {
    if (!window.confirm(`Obrisati konobara ${w.name}? Ovo je trajno.`)) return;
    const r = await apiAuth(`/self-admin/waiter-delete/${w.id}`, { method: "POST" });
    if (r.success) { showToast("Konobar obrisan", "success"); load(); }
    else showToast(r.message || "Greška", "error");
  };

  const saveAssign = async (id) => {
    const r = await apiAuth(`/self-admin/waiter-update/${id}`, {
      method: "POST",
      body: JSON.stringify({ assignedTables: editAssign.tables }),
    });
    if (r.success) { showToast("Stolovi dodijeljeni", "success"); setEditAssign(null); load(); }
    else showToast(r.message || "Greška", "error");
  };

  const konobarUrl = `${window.location.protocol}//${window.location.host}`;

  if (loading) {
    return (
      <div style={{ background: "#fff", padding: 16, borderRadius: 12, marginBottom: 14, border: `1px solid ${S.border}`, textAlign: "center" }}>
        <Spinner />
      </div>
    );
  }

  return (
    <>
      <div style={{ background: "#fff", padding: 16, borderRadius: 12, marginBottom: 14, border: `1px solid ${S.border}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <Users size={18} color={S.accent} />
          <h3 style={{ margin: 0, fontSize: 15 }}>Konobar (konobarska usluga)</h3>
        </div>
        <p style={{ margin: "0 0 12px", color: S.muted, fontSize: 13 }}>
          Konobar na svom telefonu (preko browsera) prima narudžbe za stolove. Otvori <code>{konobarUrl}/<b>slug</b>/konobar</code>, unese 6-cifreni kod jednom, i uređaj ostaje uparen. Narudžbe idu direktno u Aktivne i u kuhinju kao <b>K-STOL N</b>.
        </p>

        <ToggleRow
          label="Konobar aktivan"
          desc="Kada je uključeno, konobari mogu da se uparuju i primaju narudžbe."
          checked={enabled}
          onChange={toggleEnabled}
        />
      </div>

      {enabled && (
        <>
          {/* Waiter tables config */}
          <div style={{ background: "#fff", padding: 16, borderRadius: 12, marginBottom: 14, border: `1px solid ${S.border}` }}>
            <h3 style={{ margin: "0 0 10px", fontSize: 15 }}>Stolovi za konobare</h3>
            <p style={{ margin: "0 0 10px", color: S.muted, fontSize: 13 }}>
              Zaseban skup stolova samo za konobare (nezavisno od QR stolova). Razdvojite brojeve zarezom.
            </p>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                value={tableInput}
                onChange={e => setTableInput(e.target.value)}
                placeholder="1, 2, 3, 4, 5"
                style={{ ...S.input, flex: 1 }}
              />
              <button onClick={saveTables} disabled={savingTables} style={{ ...S.btn(S.accent), opacity: savingTables ? 0.6 : 1 }}>
                {savingTables ? <Spinner size={14} color="#fff" /> : <Save size={16} />} Sačuvaj
              </button>
            </div>
            {waiterTables.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
                {waiterTables.map(n => (
                  <span key={n} style={{ background: "#eff6ff", color: S.accent, padding: "4px 10px", borderRadius: 6, fontSize: 13, fontWeight: 700 }}>
                    Sto {n}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Add waiter */}
          <div style={{ background: "#fff", padding: 16, borderRadius: 12, marginBottom: 14, border: `1px solid ${S.border}` }}>
            <h3 style={{ margin: "0 0 10px", fontSize: 15 }}>Dodaj konobara</h3>
            <Row label="Ime konobara">
              <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="npr. Marko" maxLength={100} style={S.input} />
            </Row>
            {waiterTables.length > 0 ? (
              <Row label="Dodijeljeni stolovi">
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {waiterTables.map(n => (
                    <button key={n} onClick={() => toggleNewTable(n)} style={{
                      padding: "6px 12px", borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: "pointer",
                      background: newTables.includes(n) ? S.accent : "#fff",
                      color: newTables.includes(n) ? "#fff" : S.dark,
                      border: `1.5px solid ${newTables.includes(n) ? S.accent : S.border}`,
                    }}>
                      Sto {n}
                    </button>
                  ))}
                </div>
              </Row>
            ) : (
              <p style={{ color: S.muted, fontSize: 13, margin: "0 0 10px" }}>Prvo dodajte stolove za konobare iznad.</p>
            )}
            <button onClick={createWaiter} disabled={creating || !newName.trim()} style={{ ...S.btn(S.accent), opacity: (creating || !newName.trim()) ? 0.5 : 1 }}>
              {creating ? <Spinner size={14} color="#fff" /> : <Plus size={16} />} Kreiraj i generiši kod
            </button>
          </div>

          {/* Waiter list */}
          <div style={{ background: "#fff", padding: 16, borderRadius: 12, marginBottom: 14, border: `1px solid ${S.border}` }}>
            <h3 style={{ margin: "0 0 10px", fontSize: 15 }}>Konobari ({waiters.length})</h3>
            {waiters.length === 0 ? (
              <div style={{ padding: 24, textAlign: "center", color: S.muted, fontSize: 13, background: "#f8fafc", borderRadius: 8 }}>
                Još nema konobara.
              </div>
            ) : (
              waiters.map(w => (
                <div key={w.id} style={{
                  border: `1px solid ${S.border}`, borderRadius: 8, padding: "10px 12px", marginBottom: 8,
                  background: w.is_active ? "#fff" : "#fafafa",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 8, background: w.paired ? "#ecfdf5" : "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <User size={18} color={w.paired ? "#059669" : S.muted} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: S.dark, display: "flex", alignItems: "center", gap: 6 }}>
                        {w.name}
                        {w.paired
                          ? <span style={{ fontSize: 10, color: "#059669", padding: "1px 6px", background: "#ecfdf5", borderRadius: 4 }}>UPAREN</span>
                          : <span style={{ fontSize: 10, color: S.muted, padding: "1px 6px", background: "#f1f5f9", borderRadius: 4 }}>NIJE UPAREN</span>}
                      </div>
                      <div style={{ fontSize: 11, color: S.muted, marginTop: 2 }}>
                        Stolovi: {w.assignedTables.length > 0 ? w.assignedTables.join(", ") : "—"}
                      </div>
                    </div>
                  </div>

                  {editAssign && editAssign.id === w.id ? (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontSize: 12, color: S.muted, marginBottom: 6 }}>Izaberite stolove:</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                        {waiterTables.map(n => (
                          <button key={n} onClick={() => setEditAssign(ea => ({ ...ea, tables: ea.tables.includes(n) ? ea.tables.filter(x => x !== n) : [...ea.tables, n].sort((a, b) => a - b) }))} style={{
                            padding: "6px 12px", borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: "pointer",
                            background: editAssign.tables.includes(n) ? S.accent : "#fff",
                            color: editAssign.tables.includes(n) ? "#fff" : S.dark,
                            border: `1.5px solid ${editAssign.tables.includes(n) ? S.accent : S.border}`,
                          }}>Sto {n}</button>
                        ))}
                      </div>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button onClick={() => saveAssign(w.id)} style={S.btn(S.accent)}><Check size={14} /> Sačuvaj</button>
                        <button onClick={() => setEditAssign(null)} style={{ ...S.btn("#f3f4f6"), color: S.muted }}><X size={14} /> Otkaži</button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
                      <button onClick={() => setEditAssign({ id: w.id, tables: [...w.assignedTables] })} style={{ ...S.btnSm("#fff", S.dark), border: `1px solid ${S.border}` }}>
                        <Edit3 size={12} /> Stolovi
                      </button>
                      <button onClick={() => regenCode(w)} style={{ ...S.btnSm("#fff", S.accent), border: `1px solid ${S.accent}` }}>
                        <Hash size={12} /> {w.paired ? "Novi kod" : "Kod za uparivanje"}
                      </button>
                      {w.paired && (
                        <button onClick={() => unpair(w)} style={{ ...S.btnSm("#fff", "#b45309"), border: `1px solid #fcd34d` }}>
                          <Power size={12} /> Opozovi
                        </button>
                      )}
                      <button onClick={() => removeWaiter(w)} style={{ ...S.btnSm("#fef2f2", S.red), border: `1px solid #fecaca` }}>
                        <Trash2 size={12} /> Obriši
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </>
      )}

      {/* Pairing code modal */}
      {codeModal && (
        <Modal open onClose={() => setCodeModal(null)}>
          <div style={{ padding: 24, textAlign: "center" }}>
            <div style={{ width: 56, height: 56, borderRadius: 14, background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
              <User size={28} color={S.accent} />
            </div>
            <h2 style={{ margin: "0 0 4px", fontSize: 18, fontWeight: 800 }}>Kod za {codeModal.name}</h2>
            <p style={{ margin: "0 0 16px", color: S.muted, fontSize: 13 }}>
              Konobar otvara <b>/konobar</b> na telefonu i unosi ovaj kod. Vrijedi 10 minuta.
            </p>
            <div style={{ fontSize: 44, fontWeight: 800, letterSpacing: 8, fontFamily: "JetBrains Mono, monospace", color: S.dark, marginBottom: 18 }}>
              {codeModal.code}
            </div>
            <button onClick={() => setCodeModal(null)} style={{ ...S.btn(S.accent), width: "100%", justifyContent: "center" }}>
              Zatvori
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}


// ────────────────────────────────────────────────────
// PendingCodeRow — single pending pairing in the list
// (above the modal — for codes the owner created previously)
// Uses a local timer to update the countdown every second.
// ────────────────────────────────────────────────────
function PendingCodeRow({ pairing, onCancel, onTick }) {
  const [secondsLeft, setSecondsLeft] = useState(pairing.expires_in_sec ?? 600);

  useEffect(() => {
    setSecondsLeft(pairing.expires_in_sec ?? 600);
  }, [pairing.expires_in_sec, pairing.id]);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const t = setInterval(() => {
      setSecondsLeft(s => {
        if (s <= 1) {
          // Trigger parent reload — code has expired, list will refresh
          if (onTick) onTick();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [secondsLeft, onTick]);

  const mm = Math.floor(secondsLeft / 60);
  const ss = secondsLeft % 60;

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12,
      padding: "10px 12px",
      borderRadius: 8,
      border: `1px solid #fde68a`,
      background: "#fffbeb",
      marginBottom: 6,
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: 8,
        background: "#fef3c7",
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
      }}>
        <Clock size={18} color="#92400e" />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#92400e" }}>
          Terminal {pairing.terminal_number} — kod: <span style={{ fontFamily: "JetBrains Mono, monospace", fontSize: 16 }}>{pairing.code}</span>
        </div>
        <div style={{ fontSize: 11, color: "#92400e", marginTop: 2 }}>
          Vrijedi još {String(mm).padStart(2, "0")}:{String(ss).padStart(2, "0")}
        </div>
      </div>
      <button onClick={onCancel} title="Otkaži"
        style={S.btnSm("#fef2f2", S.red)}><X size={13} /></button>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// PairingCodeModal — full-screen modal showing the 6-digit code
// after owner clicks "Dodaj novi terminal".
//
// - Big readable digits (font-size 48px)
// - Live countdown timer (10 minutes → 0)
// - Auto-polls /self-admin/terminals-list every 3s while open
// - Detects when this terminal has been redeemed (used_at is set)
//   → fires onPaired() (which shows success + closes)
// - "Zatvori" just closes; pending code stays valid in the list
//   below until expiry. "Otkaži" invalidates immediately.
// ════════════════════════════════════════════════════════════════
function PairingCodeModal({ code, terminalNumber, expiresInSec, onClose, onPaired }) {
  const [secondsLeft, setSecondsLeft] = useState(expiresInSec || 600);
  const onPairedRef = useRef(onPaired);
  useEffect(() => { onPairedRef.current = onPaired; }, [onPaired]);

  // Countdown
  useEffect(() => {
    if (secondsLeft <= 0) return;
    const t = setInterval(() => setSecondsLeft(s => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [secondsLeft]);

  // Poll for redemption — every 3 seconds, check if our code disappeared
  // from the pendingCodes list (meaning it was redeemed).
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      const r = await apiAuth("/self-admin/terminals-list");
      if (cancelled) return;
      if (r.success) {
        const stillPending = (r.data?.pendingCodes || []).some(p => p.code === code);
        const justPaired = (r.data?.terminals || []).some(t =>
          t.terminal_number === terminalNumber && !t.is_revoked && !t.last_used_at && new Date(t.created_at).getTime() > Date.now() - 30000
        );
        if (!stillPending && justPaired) {
          if (onPairedRef.current) onPairedRef.current();
          return; // Stop polling
        }
      }
    };
    const interval = setInterval(poll, 5000); // setup-only, self-stops on pair (was 3s)
    return () => { cancelled = true; clearInterval(interval); };
  }, [code, terminalNumber]);

  const mm = Math.floor(secondsLeft / 60);
  const ss = secondsLeft % 60;
  const expired = secondsLeft <= 0;

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 16, zIndex: 1000,
    }}>
      <div style={{
        background: "#fff", borderRadius: 16, padding: 28,
        maxWidth: 460, width: "100%",
        boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
        textAlign: "center",
      }}>
        <div style={{ display: "inline-flex", padding: 10, background: "#eff6ff", borderRadius: 12, marginBottom: 12 }}>
          <Monitor size={28} color={S.accent} />
        </div>
        <h2 style={{ margin: "0 0 6px", fontSize: 20 }}>
          Novi terminal #{terminalNumber}
        </h2>
        <p style={{ margin: "0 0 22px", color: S.muted, fontSize: 14, lineHeight: 1.5 }}>
          Otvorite EFIS SELF Terminal aplikaciju na novom uređaju i unesite kod ispod.
        </p>

        {/* Big code display */}
        <div style={{
          display: "flex", justifyContent: "center", gap: 8,
          marginBottom: 18,
          fontFamily: "JetBrains Mono, monospace",
          fontSize: 48,
          fontWeight: 700,
          letterSpacing: 4,
          color: expired ? S.muted : S.text,
          opacity: expired ? 0.5 : 1,
        }}>
          {code.split("").map((d, i) => (
            <span key={i} style={{
              display: "inline-block",
              minWidth: 44,
              padding: "8px 4px",
              background: "#f8fafc",
              border: `2px solid ${S.border}`,
              borderRadius: 8,
            }}>{d}</span>
          ))}
        </div>

        {/* Countdown */}
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "6px 14px",
          background: expired ? "#fef2f2" : (secondsLeft < 60 ? "#fffbeb" : "#f0fdf4"),
          color: expired ? S.red : (secondsLeft < 60 ? "#92400e" : "#15803d"),
          borderRadius: 20, fontSize: 13, fontWeight: 600,
          marginBottom: 22,
        }}>
          <Clock size={14} />
          {expired ? "Kod je istekao" : `Vrijedi još ${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`}
        </div>

        <div style={{
          padding: 12, background: "#f8fafc", borderRadius: 10,
          fontSize: 12, color: S.muted, lineHeight: 1.5,
          marginBottom: 18, textAlign: "left",
        }}>
          <div style={{ fontWeight: 600, color: S.text, marginBottom: 4 }}>Šta dalje?</div>
          1. Otvorite EFIS SELF Terminal na novom uređaju<br/>
          2. Unesite 6-cifreni kod iznad<br/>
          3. Uređaj će biti uparen i spreman za rad
        </div>

        <button onClick={onClose} style={{
          ...S.btn("#f3f4f6"),
          color: S.muted,
          border: `1px solid ${S.border}`,
          padding: "10px 24px",
          width: "100%", justifyContent: "center",
        }}>
          Zatvori
        </button>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────
// formatRelative — "Prije 5 min", "Prije 2 sata", "Juče" etc.
// Used in terminals list to show last_used_at timestamp.
// ────────────────────────────────────────────────────
function formatRelative(timestamp) {
  if (!timestamp) return "";
  const date = new Date(timestamp.replace(" ", "T") + (timestamp.endsWith("Z") ? "" : "Z"));
  const diffSec = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diffSec < 60) return "Upravo sad";
  if (diffSec < 3600) return `Prije ${Math.floor(diffSec / 60)} min`;
  if (diffSec < 86400) return `Prije ${Math.floor(diffSec / 3600)} h`;
  if (diffSec < 86400 * 2) return "Juče";
  if (diffSec < 86400 * 7) return `Prije ${Math.floor(diffSec / 86400)} d`;
  return date.toLocaleDateString("sr-Latn-ME");
}

// ════════════════════════════════════════════════════════════════════════
// ════════════════════════════════════════════════════════════════════════
//  TERMINAL KIOSK APP (Phase 3A)
//
//  Mounted at /terminal (no slug, no number). The device authenticates
//  itself via Bearer terminal_token in localStorage. On first launch (no
//  token), shows PairingScreen — owner enters 6-digit code generated in
//  /admin → Podešavanja → Terminali.
//
//  State machine:
//    "loading"   → checking token validity at /api/self-terminal/me
//    "pairing"   → no token, show numpad
//    "landing"   → token valid, idle screen ("Tap to order")
//    "menu"      → customer browsing categories + products
//    (cart, confirmation come in Phase 3B)
//
//  Auto-resets to "landing" after 90s of no interaction (clears cart).
// ════════════════════════════════════════════════════════════════════════
// ════════════════════════════════════════════════════════════════════════

const TERMINAL_TOKEN_KEY = "self_terminal_token";
const TERMINAL_LANG_KEY = "self_terminal_lang";
const TERMINAL_INACTIVITY_MS = 90 * 1000;

// ─── Terminal-scoped API helper (Bearer terminal_token) ─────────────
async function terminalApi(path, opts = {}) {
  const token = localStorage.getItem(TERMINAL_TOKEN_KEY);
  const h = { "Content-Type": "application/json" };
  if (token) h["Authorization"] = `Bearer ${token}`;
  try {
    const r = await fetch(`${API_BASE}${path}`, { ...opts, headers: { ...h, ...(opts.headers || {}) } });
    if (r.status === 401) {
      // Token revoked or invalid — wipe and force re-pair
      localStorage.removeItem(TERMINAL_TOKEN_KEY);
      window.location.reload();
      return { success: false, message: "Unauthorized", _unauth: true };
    }
    return await r.json();
  } catch (e) {
    return { success: false, message: e.message || "Network error" };
  }
}

// ─── useOrientation — auto-detects portrait vs landscape ────────────
// Returns 'portrait' if window is taller than wide, otherwise 'landscape'.
// Re-checks on resize so a rotated display adapts in real time.
function useOrientation() {
  const compute = () => (window.innerHeight > window.innerWidth ? "portrait" : "landscape");
  const [orient, setOrient] = useState(compute);
  useEffect(() => {
    const onResize = () => setOrient(compute());
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);
  return orient;
}

// ─── useInactivityReset — fires onTimeout after `ms` of no input ────
// Listens for touchstart/mousedown/keydown anywhere in the document.
// Pause by passing enabled=false (e.g. on Landing — there's nothing to reset).
function useInactivityReset(onTimeout, ms, enabled = true) {
  const onTimeoutRef = useRef(onTimeout);
  useEffect(() => { onTimeoutRef.current = onTimeout; }, [onTimeout]);
  useEffect(() => {
    if (!enabled) return;
    let timer = setTimeout(() => onTimeoutRef.current && onTimeoutRef.current(), ms);
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => onTimeoutRef.current && onTimeoutRef.current(), ms);
    };
    const events = ["touchstart", "mousedown", "keydown", "wheel"];
    events.forEach(e => window.addEventListener(e, reset, { passive: true }));
    return () => {
      clearTimeout(timer);
      events.forEach(e => window.removeEventListener(e, reset));
    };
  }, [ms, enabled]);
}

// ════════════════════════════════════════════════════════════════════
// TerminalKioskApp — top-level state machine for the kiosk
// ════════════════════════════════════════════════════════════════════
function TerminalKioskApp() {
  const [stage, setStage] = useState("loading"); // loading | pairing | landing | menu | cart | confirmation
  const [device, setDevice] = useState(null); // { terminalNumber, deviceLabel, company, settings }
  const [menuData, setMenuData] = useState(null); // { categories, products }
  const [cart, setCart] = useState([]); // [{ productId, name, price, qty, ... }]
  const [lang, setLang] = useState(() => localStorage.getItem(TERMINAL_LANG_KEY) || "cg");
  const [orderResult, setOrderResult] = useState(null); // { orderNumber, publicToken, total, estimatedPrepMin }

  // ─── On mount: try to validate existing token ───
  useEffect(() => {
    const token = localStorage.getItem(TERMINAL_TOKEN_KEY);
    if (!token) {
      setStage("pairing");
      return;
    }
    (async () => {
      const r = await terminalApi("/self-terminal/me");
      if (r._unauth) return; // reload already triggered
      if (r.success) {
        setDevice(r.data);
        // If saved language isn't enabled for this company, use the first available
        const enabled = (r.data.settings?.languages || "cg").split(",").map(s => s.trim());
        if (!enabled.includes(lang)) {
          const first = enabled[0] || "cg";
          setLang(first);
          localStorage.setItem(TERMINAL_LANG_KEY, first);
        }
        setStage("landing");
      } else {
        // Token might be revoked but reload didn't happen (network blip etc.)
        localStorage.removeItem(TERMINAL_TOKEN_KEY);
        setStage("pairing");
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Persist language changes ───
  useEffect(() => {
    localStorage.setItem(TERMINAL_LANG_KEY, lang);
  }, [lang]);

  // ─── After pairing succeeds ───
  const handlePaired = useCallback((data) => {
    setDevice(data);
    const enabled = (data.settings?.languages || "cg").split(",").map(s => s.trim());
    const first = enabled[0] || "cg";
    setLang(first);
    setStage("landing");
  }, []);

  // ─── Inactivity reset (active in menu + cart only — landing has no
  // session, confirmation has its own 15s countdown) ───
  const resetToLanding = useCallback(() => {
    setCart([]);
    setOrderResult(null);
    setStage("landing");
  }, []);
  useInactivityReset(resetToLanding, TERMINAL_INACTIVITY_MS, stage === "menu" || stage === "cart");

  // ─── Start a new order: load menu, jump to menu screen ───
  const startNewOrder = async () => {
    setStage("loading"); // brief loading flash
    const r = await terminalApi("/self-terminal/menu");
    if (r._unauth) return;
    if (r.success) {
      setMenuData(r.data);
      setCart([]);
      setStage("menu");
    } else {
      // Show error then bounce back
      alert(r.message || "Greška pri učitavanju menija");
      setStage("landing");
    }
  };

  // ─── Cart operations ───
  const addToCart = (product, qty = 1, tags = [], note = "") => {
    setCart(prev => {
      // Same product + same tags + same note → merge qty
      const sig = (p) => `${p.productId}|${(p.tags || []).join(",")}|${p.note || ""}`;
      const sigNew = `${product.id}|${tags.join(",")}|${note}`;
      const idx = prev.findIndex(it => sig(it) === sigNew);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: next[idx].qty + qty };
        return next;
      }
      return [...prev, {
        productId: product.id,
        name: product.name,
        price: product.price,
        qty,
        tags,
        note,
        photo_url: product.photo_url,
      }];
    });
  };
  const updateCartQty = (idx, newQty) => {
    setCart(prev => {
      if (newQty <= 0) return prev.filter((_, i) => i !== idx);
      return prev.map((it, i) => i === idx ? { ...it, qty: newQty } : it);
    });
  };
  const removeFromCart = (idx) => {
    setCart(prev => prev.filter((_, i) => i !== idx));
  };

  // ─── Place order: POST to backend, print receipt, jump to confirmation ───
  const placeOrder = async ({ customerName, note }) => {
    const r = await terminalApi("/self-terminal/order", {
      method: "POST",
      body: JSON.stringify({
        customerName: customerName || "",
        note: note || "",
        language: lang,
        items: cart.map(it => ({
          productId: it.productId,
          qty: it.qty,
          tags: it.tags,
          note: it.note,
        })),
      }),
    });
    if (!r.success) return { success: false, message: r.message };

    const result = {
      orderNumber: r.data.orderNumber,
      publicToken: r.data.publicToken,
      total: r.data.total,
      estimatedPrepMin: r.data.estimatedPrepMin || 15,
    };
    setOrderResult(result);

    // Fire-and-forget receipt printing if running in Electron .exe
    printPickupTicket({
      device,
      cart,
      orderNumber: result.orderNumber,
      publicToken: result.publicToken,
      total: result.total,
      lang,
    }).catch(() => { /* silent — print failure shouldn't block UX */ });

    setStage("confirmation");
    return { success: true };
  };

  // ─── After confirmation: clear and return to landing ───
  const finishOrder = () => {
    setCart([]);
    setOrderResult(null);
    setStage("landing");
  };

  // ─── Render by stage ───
  if (stage === "loading") {
    return <KioskLoadingScreen />;
  }
  if (stage === "pairing") {
    return <PairingScreen onPaired={handlePaired} lang={lang} setLang={setLang} />;
  }
  if (stage === "landing") {
    return (
      <LandingScreen
        device={device}
        lang={lang}
        setLang={setLang}
        onStart={startNewOrder}
      />
    );
  }
  if (stage === "menu") {
    return (
      <MenuScreen
        device={device}
        menuData={menuData}
        cart={cart}
        lang={lang}
        setLang={setLang}
        onAddToCart={addToCart}
        onCancel={resetToLanding}
        onViewCart={() => setStage("cart")}
      />
    );
  }
  if (stage === "cart") {
    return (
      <CartScreen
        device={device}
        cart={cart}
        lang={lang}
        setLang={setLang}
        menuData={menuData}
        onUpdateQty={updateCartQty}
        onRemove={removeFromCart}
        onBack={() => setStage("menu")}
        onCancel={resetToLanding}
        onPlaceOrder={placeOrder}
      />
    );
  }
  if (stage === "confirmation") {
    return (
      <ConfirmationScreen
        device={device}
        orderResult={orderResult}
        lang={lang}
        onFinish={finishOrder}
      />
    );
  }
  return null;
}

// ════════════════════════════════════════════════════════════════════
// KioskLoadingScreen — initial token validation / menu fetch
// ════════════════════════════════════════════════════════════════════
function KioskLoadingScreen() {
  return (
    <div style={{
      minHeight: "100vh", background: S.bg,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontFamily: S.font,
    }}>
      <Spinner size={48} color={S.accent} />
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// PairingScreen — first-launch UI, big numpad
// ════════════════════════════════════════════════════════════════════
function PairingScreen({ onPaired, lang, setLang }) {
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const press = (digit) => {
    if (submitting) return;
    setError("");
    if (code.length < 6) setCode(code + String(digit));
  };
  const back = () => {
    setError("");
    setCode(code.slice(0, -1));
  };
  const clear = () => {
    setError("");
    setCode("");
  };

  // Auto-submit when 6 digits entered
  useEffect(() => {
    if (code.length !== 6 || submitting) return;
    (async () => {
      setSubmitting(true);
      const r = await terminalApi("/self-terminal/redeem-pairing", {
        method: "POST",
        body: JSON.stringify({ code, deviceLabel: "" }),
      });
      setSubmitting(false);
      if (r.success) {
        localStorage.setItem(TERMINAL_TOKEN_KEY, r.data.token);
        onPaired(r.data);
      } else {
        setError(r.message || t(lang, "kiosk_pair_invalid"));
        setTimeout(() => setCode(""), 800);
      }
    })();
  }, [code, submitting, lang, onPaired]);

  const digits = ["1","2","3","4","5","6","7","8","9"];

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(135deg, #0F1B2E 0%, #1E3A5F 100%)",
      color: "#fff",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 32,
      fontFamily: S.font,
      userSelect: "none",
      WebkitTapHighlightColor: "transparent",
    }}>
      <div style={{ maxWidth: 480, width: "100%", textAlign: "center" }}>
        <div style={{
          display: "inline-flex", padding: 16, background: "rgba(255,255,255,0.1)",
          borderRadius: 20, marginBottom: 18,
        }}>
          <Monitor size={48} color="#fff" />
        </div>
        <h1 style={{ margin: "0 0 10px", fontSize: 32, fontWeight: 700 }}>
          {t(lang, "kiosk_pair_title")}
        </h1>
        <p style={{ margin: "0 0 32px", color: "rgba(255,255,255,0.7)", fontSize: 15, lineHeight: 1.5 }}>
          {t(lang, "kiosk_pair_intro")}
        </p>

        {/* Code display */}
        <div style={{
          display: "flex", justifyContent: "center", gap: 8, marginBottom: 24,
        }}>
          {[0,1,2,3,4,5].map(i => (
            <div key={i} style={{
              width: 50, height: 64,
              borderRadius: 10,
              background: code[i] ? "rgba(255,255,255,0.15)" : "rgba(255,255,255,0.05)",
              border: `2px solid ${code[i] ? S.accent : "rgba(255,255,255,0.15)"}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 32, fontWeight: 700, fontFamily: "JetBrains Mono, monospace",
              color: error ? "#ef4444" : "#fff",
              transition: "all .15s",
            }}>
              {code[i] || ""}
            </div>
          ))}
        </div>

        {error && (
          <div style={{
            color: "#ef4444", fontSize: 14, fontWeight: 600, marginBottom: 16,
            minHeight: 20,
          }}>
            {error}
          </div>
        )}
        {!error && submitting && (
          <div style={{ marginBottom: 16, minHeight: 20 }}>
            <Spinner size={18} color="#fff" />
          </div>
        )}
        {!error && !submitting && <div style={{ minHeight: 36 }} />}

        {/* Numpad */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 12,
          maxWidth: 360,
          margin: "0 auto",
        }}>
          {digits.map(d => (
            <button key={d} onClick={() => press(d)} disabled={submitting}
              style={kioskNumpadBtnStyle(submitting)}>
              {d}
            </button>
          ))}
          <button onClick={clear} disabled={submitting}
            style={{ ...kioskNumpadBtnStyle(submitting), fontSize: 16, color: "rgba(255,255,255,0.6)" }}>
            C
          </button>
          <button onClick={() => press(0)} disabled={submitting}
            style={kioskNumpadBtnStyle(submitting)}>
            0
          </button>
          <button onClick={back} disabled={submitting}
            style={{ ...kioskNumpadBtnStyle(submitting), fontSize: 18 }}>
            ⌫
          </button>
        </div>

        {/* Language picker — bottom corner */}
        <div style={{ marginTop: 36, display: "flex", justifyContent: "center", gap: 8 }}>
          {Object.keys(LANG_FLAGS).map(l => (
            <button key={l} onClick={() => setLang(l)}
              style={{
                padding: "8px 14px", borderRadius: 20,
                border: `1.5px solid ${lang === l ? "#fff" : "rgba(255,255,255,0.2)"}`,
                background: lang === l ? "rgba(255,255,255,0.15)" : "transparent",
                color: "#fff", cursor: "pointer", fontSize: 13,
                display: "inline-flex", alignItems: "center", gap: 5,
              }}>
              {LANG_FLAGS[l]} {LANG_NAMES[l]}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

const kioskNumpadBtnStyle = (disabled) => ({
  padding: "20px 0",
  fontSize: 28,
  fontWeight: 600,
  fontFamily: "JetBrains Mono, monospace",
  background: "rgba(255,255,255,0.08)",
  color: "#fff",
  border: "1.5px solid rgba(255,255,255,0.12)",
  borderRadius: 14,
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.5 : 1,
  transition: "all .1s",
  WebkitTapHighlightColor: "transparent",
  userSelect: "none",
});

// ════════════════════════════════════════════════════════════════════
// LandingScreen — idle "tap to order" screen
// Adapts to portrait/landscape automatically.
// Language icon top-right.
// ════════════════════════════════════════════════════════════════════
function LandingScreen({ device, lang, setLang, onStart }) {
  const orient = useOrientation();
  const [showLangPicker, setShowLangPicker] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const company = device?.company || {};
  const settings = device?.settings || {};
  const accent = settings.branding_color || S.accent;
  const enabledLangs = (settings.languages || "cg").split(",").map(s => s.trim()).filter(Boolean);

  // ─── Long-press handler for hidden owner settings ─────────────
  // Held 2.5s on logo → opens KioskSettingsPanel. Tap-cancels normally
  // (the user is just exploring or accidentally touching). stopPropagation
  // on the touchstart so the parent's onClick={onStart} doesn't fire when
  // the press completes.
  const longPressTimer = useRef(null);
  const longPressFired = useRef(false);
  const startLongPress = (e) => {
    e.stopPropagation();
    longPressFired.current = false;
    longPressTimer.current = setTimeout(() => {
      longPressFired.current = true;
      setShowSettings(true);
      // Haptic feedback if device supports it
      if (navigator.vibrate) navigator.vibrate(80);
    }, 2500);
  };
  const cancelLongPress = (e) => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
    longPressTimer.current = null;
    if (longPressFired.current) {
      e.stopPropagation();
      e.preventDefault();
    }
  };

  // ─── Settings handlers ─────────────────────────────────────────
  const handleUnpair = () => {
    if (!window.confirm(t(lang, "kset_unpair_confirm"))) return;
    localStorage.removeItem(TERMINAL_TOKEN_KEY);
    window.location.reload();
  };

  return (
    <div
      onClick={onStart}
      style={{
        position: "fixed", inset: 0,
        background: `linear-gradient(135deg, ${accent} 0%, ${shadeColor(accent, -25)} 100%)`,
        color: "#fff", fontFamily: S.font,
        display: "flex", flexDirection: "column",
        cursor: "pointer", userSelect: "none",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      {/* Top bar — language icon top-right */}
      <div style={{
        position: "absolute", top: 0, right: 0, padding: 20,
        display: "flex", gap: 8, zIndex: 2,
      }}>
        {enabledLangs.length > 1 && (
          <button
            onClick={(e) => { e.stopPropagation(); setShowLangPicker(true); }}
            style={{
              padding: "10px 16px", borderRadius: 24,
              background: "rgba(255,255,255,0.15)",
              border: "1px solid rgba(255,255,255,0.25)",
              color: "#fff", cursor: "pointer",
              display: "inline-flex", alignItems: "center", gap: 8,
              fontSize: 16, fontWeight: 600,
              WebkitBackdropFilter: "blur(10px)", backdropFilter: "blur(10px)",
            }}
          >
            {LANG_FLAGS[lang]} {LANG_NAMES[lang]}
          </button>
        )}
      </div>

      {/* Center content */}
      <div style={{
        flex: 1, display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        padding: orient === "portrait" ? "20px 28px" : "20px 60px",
        textAlign: "center",
      }}>
        {/* Logo — long-press 2.5s opens hidden owner settings */}
        <div
          onTouchStart={startLongPress}
          onTouchEnd={cancelLongPress}
          onTouchCancel={cancelLongPress}
          onMouseDown={startLongPress}
          onMouseUp={cancelLongPress}
          onMouseLeave={cancelLongPress}
          style={{ display: "inline-block", cursor: "pointer" }}
        >
          {company.logo_path ? (
            <img
              src={`https://efis.pro/${company.logo_path}`}
              alt={company.name}
              draggable={false}
              style={{
                maxWidth: orient === "portrait" ? 220 : 280,
                maxHeight: orient === "portrait" ? 160 : 200,
                marginBottom: 20,
                filter: "drop-shadow(0 4px 16px rgba(0,0,0,0.2))",
                pointerEvents: "none", // events bubble up to wrapper div
              }}
              onError={(e) => { e.target.style.display = "none"; }}
            />
          ) : (
            <div style={{
              width: orient === "portrait" ? 120 : 140,
              height: orient === "portrait" ? 120 : 140,
              borderRadius: 30, marginBottom: 20,
              background: "rgba(255,255,255,0.15)",
              display: "flex", alignItems: "center", justifyContent: "center",
              pointerEvents: "none",
            }}>
              <Utensils size={orient === "portrait" ? 60 : 70} color="#fff" />
            </div>
          )}
        </div>

        {/* Restaurant name */}
        <h1 style={{
          margin: "0 0 12px",
          fontSize: orient === "portrait" ? 42 : 56,
          fontWeight: 700, letterSpacing: -1,
        }}>
          {company.name || "EFIS.PRO"}
        </h1>

        {/* Welcome message */}
        {settings.welcome_text && (
          <p style={{
            margin: "0 0 40px",
            fontSize: orient === "portrait" ? 18 : 22,
            color: "rgba(255,255,255,0.85)",
            maxWidth: 600, lineHeight: 1.5,
          }}>
            {settings.welcome_text}
          </p>
        )}

        {/* Big "Tap to order" prompt */}
        <div style={{
          padding: orient === "portrait" ? "20px 36px" : "24px 56px",
          background: "rgba(255,255,255,0.95)",
          color: accent,
          borderRadius: 100,
          fontSize: orient === "portrait" ? 22 : 28,
          fontWeight: 700,
          display: "inline-flex", alignItems: "center", gap: 14,
          boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
          animation: "kioskPulse 2s ease-in-out infinite",
        }}>
          <ShoppingCart size={orient === "portrait" ? 28 : 36} />
          {t(lang, "kiosk_landing_tap")}
        </div>

        <style>{`@keyframes kioskPulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.04); } }`}</style>
      </div>

      {/* Footer — terminal number */}
      <div style={{
        padding: 16, textAlign: "center",
        fontSize: 12, color: "rgba(255,255,255,0.5)",
        fontFamily: "JetBrains Mono, monospace",
      }}>
        {device?.deviceLabel ? `${device.deviceLabel} • ` : ""}TERMINAL #{device?.terminalNumber ?? "?"}
        <div style={{ fontSize: 9, opacity: 0.4, marginTop: 4, fontFamily: S.font, letterSpacing: 0.5 }}>
          {t(lang, "kset_long_press_hint")}
        </div>
      </div>

      {/* Language picker overlay */}
      {showLangPicker && (
        <LangPickerOverlay
          enabledLangs={enabledLangs}
          current={lang}
          onPick={(l) => { setLang(l); setShowLangPicker(false); }}
          onClose={() => setShowLangPicker(false)}
          accent={accent}
        />
      )}

      {/* Owner settings overlay (long-press logo to open) */}
      {showSettings && (
        <KioskSettingsPanel
          device={device}
          lang={lang}
          accent={accent}
          onClose={() => setShowSettings(false)}
          onUnpair={handleUnpair}
        />
      )}
    </div>
  );
}

// ─── LangPickerOverlay — full-screen language chooser ───────────────
function LangPickerOverlay({ enabledLangs, current, onPick, onClose, accent }) {
  return (
    <div
      onClick={(e) => { e.stopPropagation(); onClose(); }}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.6)",
        WebkitBackdropFilter: "blur(8px)", backdropFilter: "blur(8px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 100, fontFamily: S.font,
      }}
    >
      <div onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 24, padding: 28,
          minWidth: 320, maxWidth: 480, width: "90%",
          boxShadow: "0 30px 80px rgba(0,0,0,0.4)",
        }}
      >
        <h2 style={{ margin: "0 0 18px", fontSize: 22, color: S.dark, textAlign: "center" }}>
          {t(current, "kiosk_choose_lang")}
        </h2>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {enabledLangs.map(l => (
            <button key={l} onClick={() => onPick(l)}
              style={{
                padding: "16px 20px", borderRadius: 14,
                border: `2px solid ${current === l ? accent : S.border}`,
                background: current === l ? `${accent}10` : "#fff",
                cursor: "pointer", fontSize: 18, fontWeight: 600,
                display: "flex", alignItems: "center", gap: 14,
                color: S.dark, textAlign: "left",
                fontFamily: S.font,
              }}
            >
              <span style={{ fontSize: 28 }}>{LANG_FLAGS[l]}</span>
              {LANG_NAMES[l]}
              {current === l && <Check size={20} color={accent} style={{ marginLeft: "auto" }} />}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// KioskSettingsPanel — hidden owner config overlay
//
// Opened by long-pressing the logo on LandingScreen for 2.5 seconds.
// Lets the owner:
//   - See terminal number + paired device label
//   - Edit/save device label (calls /self-terminal/relabel — TODO)
//   - Configure printer (calls existing electronAPI IPC handlers from
//     preload.js: getSettings/saveSettings/testConnection/listPrinters)
//   - Print a test receipt (uses the same printReceipt IPC the kiosk
//     uses for real pickup tickets, with sample data)
//   - Unpair the terminal (clears localStorage token → forces re-pair
//     on next refresh)
//
// In a browser (no Electron), the printer section shows a notice that
// the feature requires the .exe version. Everything else still works.
// ════════════════════════════════════════════════════════════════════
function KioskSettingsPanel({ device, lang, accent, onClose, onUnpair }) {
  const isElectron = !!(window.electronAPI && window.electronAPI.isElectron);
  const [printerSettings, setPrinterSettings] = useState(null);
  const [printers, setPrinters] = useState([]);
  const [busy, setBusy] = useState(false);
  const [statusMsg, setStatusMsg] = useState(null); // { type: "ok"|"err", text }
  const [deviceLabel, setDeviceLabel] = useState(device?.deviceLabel || "");

  // ─── Load printer settings on mount ───
  useEffect(() => {
    if (!isElectron) return;
    (async () => {
      try {
        const s = await window.electronAPI.getSettings();
        setPrinterSettings(s || {
          type: "epson",
          interface: "",
          characterSet: "PC852_LATIN2",
          width: 48,
        });
      } catch (e) {
        setPrinterSettings({ type: "epson", interface: "", characterSet: "PC852_LATIN2", width: 48 });
      }
      try {
        const list = await window.electronAPI.listPrinters();
        setPrinters(list || []);
      } catch (e) {
        // listPrinters not critical; ignore failures
      }
    })();
  }, [isElectron]);

  const showStatus = (type, text, ms = 3000) => {
    setStatusMsg({ type, text });
    setTimeout(() => setStatusMsg(null), ms);
  };

  const updatePrinter = (k, v) => setPrinterSettings(s => ({ ...s, [k]: v }));

  const savePrinter = async () => {
    if (!isElectron) return;
    setBusy(true);
    try {
      const r = await window.electronAPI.saveSettings(printerSettings);
      if (r?.success) showStatus("ok", t(lang, "kset_printer_saved"));
      else showStatus("err", r?.message || t(lang, "kset_printer_test_fail"));
    } catch (e) {
      showStatus("err", e.message);
    }
    setBusy(false);
  };

  const testPrinter = async () => {
    if (!isElectron) return;
    setBusy(true);
    try {
      const r = await window.electronAPI.testConnection();
      if (r?.success) showStatus("ok", t(lang, "kset_printer_test_ok"));
      else showStatus("err", r?.message || t(lang, "kset_printer_test_fail"));
    } catch (e) {
      showStatus("err", e.message);
    }
    setBusy(false);
  };

  // Print a sample pickup ticket so owner can verify receipt formatting
  // and print quality from the settings panel without placing a real order.
  const testPrint = async () => {
    if (!isElectron) return;
    setBusy(true);
    try {
      const company = device?.company || {};
      const sampleData = {
        company_name: (company.name || "EFIS.PRO").toUpperCase(),
        address: [company.address, company.city].filter(Boolean).join(", "),
        pib: company.pib || "",
        title: lang === "en" ? "TEST PRINT" : (lang === "al" ? "PRINTIM TEST" : "TEST ŠTAMPE"),
        invoice_number: "TEST-001",
        date: new Date().toLocaleString("sr-Latn-ME", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }),
        items: [
          { name: "Test Item A", qty: 1, price: 5.00, total: 5.00 },
          { name: "Test Item B", qty: 2, price: 2.50, total: 5.00 },
        ],
        total: 10.00,
        payment_method: lang === "en" ? "Pay at counter" : (lang === "al" ? "Paguaj në arkë" : "Plati na kasi"),
        iic: "",
        fic: "",
        qr_url: "https://self.efis.pro",
      };
      const r = await window.electronAPI.printReceipt(sampleData);
      if (r?.success) showStatus("ok", "OK");
      else showStatus("err", r?.message || t(lang, "kset_printer_test_fail"));
    } catch (e) {
      showStatus("err", e.message);
    }
    setBusy(false);
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.7)",
        WebkitBackdropFilter: "blur(8px)", backdropFilter: "blur(8px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 200, fontFamily: S.font,
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 22,
          maxWidth: 640, width: "100%", maxHeight: "92vh",
          display: "flex", flexDirection: "column",
          boxShadow: "0 30px 80px rgba(0,0,0,0.5)",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div style={{
          padding: "20px 24px",
          borderBottom: `1px solid ${S.border}`,
          background: "#0F1B2E", color: "#fff",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800 }}>{t(lang, "kset_title")}</div>
            <div style={{ fontSize: 12, opacity: 0.7, marginTop: 3 }}>
              {t(lang, "kset_subtitle")}
            </div>
          </div>
          <button onClick={onClose}
            style={{
              padding: "10px 16px", borderRadius: 10,
              background: "rgba(255,255,255,0.15)", color: "#fff",
              border: "none", cursor: "pointer",
              fontSize: 14, fontWeight: 600,
              display: "inline-flex", alignItems: "center", gap: 6,
              fontFamily: S.font,
            }}>
            <X size={16} /> {t(lang, "kset_close")}
          </button>
        </div>

        {/* Body — scrollable */}
        <div style={{ overflowY: "auto", flex: 1, padding: 22 }}>
          {/* Status toast */}
          {statusMsg && (
            <div style={{
              padding: "12px 16px", borderRadius: 12,
              marginBottom: 16,
              background: statusMsg.type === "ok" ? "#dcfce7" : "#fee2e2",
              border: `1px solid ${statusMsg.type === "ok" ? "#86efac" : "#fecaca"}`,
              color: statusMsg.type === "ok" ? "#166534" : "#991b1b",
              fontSize: 14, fontWeight: 600,
              display: "flex", alignItems: "center", gap: 8,
            }}>
              {statusMsg.type === "ok" ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
              {statusMsg.text}
            </div>
          )}

          {/* ── Section: Terminal Info ── */}
          <SectionHeader title={t(lang, "kset_terminal_info")} icon={<Monitor size={18} />} />
          <div style={kSettingsCard}>
            <div style={kSettingsRow}>
              <div style={kSettingsLabel}>{t(lang, "kset_terminal_number")}</div>
              <div style={{ ...kSettingsValue, fontFamily: "JetBrains Mono, monospace" }}>
                #{device?.terminalNumber ?? "?"}
              </div>
            </div>
            <div style={kSettingsRow}>
              <div style={kSettingsLabel}>{t(lang, "kset_device_label")}</div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flex: 1, justifyContent: "flex-end" }}>
                <input
                  value={deviceLabel}
                  onChange={(e) => setDeviceLabel(e.target.value)}
                  placeholder="Kasa 1"
                  maxLength={40}
                  style={kSettingsInput}
                />
              </div>
            </div>
            <div style={{ padding: "12px 18px", borderTop: `1px solid ${S.border}` }}>
              <button
                onClick={onUnpair}
                style={{
                  padding: "10px 16px", borderRadius: 10,
                  background: "#fee2e2", color: "#991b1b",
                  border: "1px solid #fecaca", cursor: "pointer",
                  fontSize: 14, fontWeight: 600,
                  fontFamily: S.font,
                  display: "inline-flex", alignItems: "center", gap: 6,
                }}>
                <Power size={14} /> {t(lang, "kset_unpair")}
              </button>
            </div>
          </div>

          {/* ── Section: Printer ── */}
          <SectionHeader title={t(lang, "kset_printer")} icon={<Printer size={18} />} />

          {!isElectron ? (
            <div style={{
              ...kSettingsCard,
              padding: 18, color: S.muted, fontSize: 14,
              display: "flex", alignItems: "center", gap: 10,
            }}>
              <AlertCircle size={18} />
              {t(lang, "kset_printer_no_electron")}
            </div>
          ) : !printerSettings ? (
            <div style={{ padding: 24, textAlign: "center" }}>
              <Spinner size={20} color={accent} />
            </div>
          ) : (
            <div style={kSettingsCard}>
              <div style={kSettingsRow}>
                <div style={kSettingsLabel}>{t(lang, "kset_printer_type")}</div>
                <select
                  value={printerSettings.type || "epson"}
                  onChange={(e) => updatePrinter("type", e.target.value)}
                  style={kSettingsSelect}
                >
                  <option value="epson">Epson (ESC/POS)</option>
                  <option value="star">Star</option>
                  <option value="tanca">Tanca</option>
                  <option value="daruma">Daruma</option>
                  <option value="brother">Brother</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
              <div style={kSettingsRow}>
                <div style={kSettingsLabel}>{t(lang, "kset_printer_interface")}</div>
                <input
                  value={printerSettings.interface || ""}
                  onChange={(e) => updatePrinter("interface", e.target.value)}
                  placeholder={t(lang, "kset_printer_iface_ph")}
                  style={{ ...kSettingsInput, fontFamily: "JetBrains Mono, monospace", fontSize: 13 }}
                />
              </div>
              {printers.length > 0 && (
                <div style={kSettingsRow}>
                  <div style={kSettingsLabel}>System printers</div>
                  <div style={{ flex: 1, fontSize: 12, color: S.muted, textAlign: "right", fontFamily: "JetBrains Mono, monospace" }}>
                    {printers.slice(0, 4).map(p => (
                      <div key={p.name || p}>{p.name || p}</div>
                    ))}
                  </div>
                </div>
              )}
              <div style={kSettingsRow}>
                <div style={kSettingsLabel}>{t(lang, "kset_printer_charset")}</div>
                <select
                  value={printerSettings.characterSet || "PC852_LATIN2"}
                  onChange={(e) => updatePrinter("characterSet", e.target.value)}
                  style={kSettingsSelect}
                >
                  <option value="PC852_LATIN2">PC852_LATIN2 (CG/AL)</option>
                  <option value="PC437_USA">PC437_USA</option>
                  <option value="WPC1252">WPC1252</option>
                  <option value="PC850_MULTILINGUAL">PC850</option>
                  <option value="ISO8859_2_LATIN2">ISO8859_2</option>
                </select>
              </div>
              <div style={kSettingsRow}>
                <div style={kSettingsLabel}>{t(lang, "kset_printer_width")}</div>
                <input
                  type="number"
                  value={printerSettings.width || 48}
                  onChange={(e) => updatePrinter("width", parseInt(e.target.value, 10) || 48)}
                  min="24" max="80"
                  style={{ ...kSettingsInput, maxWidth: 100 }}
                />
              </div>
              <div style={{
                padding: "14px 18px", borderTop: `1px solid ${S.border}`,
                display: "flex", gap: 8, flexWrap: "wrap",
              }}>
                <button onClick={savePrinter} disabled={busy}
                  style={{ ...kSettingsBtn(accent, "primary"), flex: 1 }}>
                  {busy ? <Spinner size={14} color="#fff" /> : <Check size={14} />}
                  {t(lang, "kset_printer_save")}
                </button>
                <button onClick={testPrinter} disabled={busy}
                  style={kSettingsBtn(accent, "secondary")}>
                  {t(lang, "kset_printer_test")}
                </button>
                <button onClick={testPrint} disabled={busy}
                  style={kSettingsBtn(accent, "secondary")}>
                  <Printer size={14} />
                  {t(lang, "kset_printer_test_print")}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Settings panel UI primitives ─────────────────────────────────
function SectionHeader({ title, icon }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 8,
      fontSize: 13, fontWeight: 700, color: S.muted,
      textTransform: "uppercase", letterSpacing: 0.6,
      marginBottom: 10, marginTop: 12,
    }}>
      {icon}{title}
    </div>
  );
}
const kSettingsCard = {
  background: "#f8fafc",
  border: `1px solid ${S.border}`,
  borderRadius: 14,
  marginBottom: 18,
  overflow: "hidden",
};
const kSettingsRow = {
  display: "flex", alignItems: "center", gap: 12,
  padding: "12px 18px",
  borderBottom: `1px solid ${S.border}`,
};
const kSettingsLabel = {
  fontSize: 13, color: S.muted, fontWeight: 600,
  flexShrink: 0, minWidth: 120,
};
const kSettingsValue = {
  fontSize: 14, color: S.dark, fontWeight: 600,
  textAlign: "right", flex: 1,
};
const kSettingsInput = {
  flex: 1, padding: "8px 12px",
  border: `1.5px solid ${S.border}`, borderRadius: 8,
  background: "#fff",
  fontSize: 14, fontFamily: `"DM Sans", sans-serif`, color: S.dark,
  outline: "none",
  textAlign: "right",
};
const kSettingsSelect = {
  flex: 1, padding: "8px 12px",
  border: `1.5px solid ${S.border}`, borderRadius: 8,
  background: "#fff",
  fontSize: 14, fontFamily: `"DM Sans", sans-serif`, color: S.dark,
  outline: "none",
};
function kSettingsBtn(accent, kind) {
  if (kind === "primary") return {
    padding: "10px 16px", borderRadius: 10,
    background: accent, color: "#fff",
    border: "none", cursor: "pointer",
    fontSize: 14, fontWeight: 700, fontFamily: `"DM Sans", sans-serif`,
    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
  };
  return {
    padding: "10px 16px", borderRadius: 10,
    background: "#fff", color: S.dark,
    border: `1.5px solid ${S.border}`, cursor: "pointer",
    fontSize: 14, fontWeight: 600, fontFamily: `"DM Sans", sans-serif`,
    display: "inline-flex", alignItems: "center", gap: 6,
  };
}

// ════════════════════════════════════════════════════════════════════
// MenuScreen — categories + product tiles + cart bar
// Phase 3A: tap-to-add (no customization modal yet — Phase 3B)
// Phase 3A: cart bar shows count + total but checkout opens in 3B
// ════════════════════════════════════════════════════════════════════
function MenuScreen({ device, menuData, cart, lang, setLang, onAddToCart, onCancel, onViewCart }) {
  const orient = useOrientation();
  const settings = device?.settings || {};
  const accent = settings.branding_color || S.accent;
  const enabledLangs = (settings.languages || "cg").split(",").map(s => s.trim()).filter(Boolean);
  const [activeCat, setActiveCat] = useState(null); // null = all
  const [showLangPicker, setShowLangPicker] = useState(false);
  const [productModal, setProductModal] = useState(null); // product being customized
  const [pulseSig, setPulseSig] = useState(0); // re-triggers cart bar animation

  const categories = menuData?.categories || [];
  const products = menuData?.products || [];

  const filtered = activeCat === null
    ? products
    : products.filter(p => p.category_id === activeCat);

  const cartCount = cart.reduce((s, it) => s + it.qty, 0);
  // Bruto totals — what customer actually pays. Items store neto.
  const cartTotal = cart.reduce((s, it) => s + netoToBruto(it.price, it.vat_rate) * it.qty, 0);

  // Decide whether to open the customization modal or add directly:
  // - If product has preset tags OR description longer than 30 chars → modal
  // - Otherwise → add directly to cart with qty 1
  const productNeedsModal = (p) => {
    // Use rich-tags visibility filter when present, fall back to flat preset
    const visibleTags = Array.isArray(p.tags_rich) && p.tags_rich.length > 0
      ? p.tags_rich.filter(t => t && t.base && t.visible !== false)
      : (Array.isArray(p.tags_preset) ? p.tags_preset : []);
    const hasTags = p.tags_enabled && visibleTags.length > 0;
    const hasDesc = p.description && p.description.length > 30;
    return hasTags || hasDesc;
  };

  const handleProductTap = (product) => {
    if (!product.available) return;
    if (productNeedsModal(product)) {
      setProductModal(product);
    } else {
      onAddToCart(product, 1, [], "");
      setPulseSig(s => s + 1);
    }
  };

  const handleModalAdd = (product, qty, tags, note) => {
    onAddToCart(product, qty, tags, note);
    setProductModal(null);
    setPulseSig(s => s + 1);
  };

  // Translate category name to current language
  const catName = (c) => {
    if (c.translations && typeof c.translations === "object") {
      return c.translations[lang] || c.name;
    }
    return c.name;
  };

  return (
    <div style={{
      position: "fixed", inset: 0,
      background: (menuData?.settings?.bg_color && /^#[0-9a-fA-F]{3,8}$/.test(menuData.settings.bg_color)) ? menuData.settings.bg_color : S.bg,
      display: "flex", flexDirection: "column",
      fontFamily: S.font,
      WebkitTapHighlightColor: "transparent",
      userSelect: "none",
    }}>
      {/* Top bar */}
      <div style={{
        background: "#fff",
        padding: "14px 20px",
        borderBottom: `1px solid ${S.border}`,
        display: "flex", alignItems: "center", gap: 12,
        flexShrink: 0,
      }}>
        <button onClick={onCancel}
          style={{
            padding: "10px 14px", borderRadius: 12,
            background: "#f1f5f9", border: "none", cursor: "pointer",
            display: "inline-flex", alignItems: "center", gap: 6,
            fontSize: 15, fontWeight: 600, color: S.muted,
            fontFamily: S.font,
          }}>
          <X size={18} /> {t(lang, "close")}
        </button>
        <div style={{ flex: 1, fontSize: 18, fontWeight: 700, color: S.dark }}>
          {t(lang, "menu")}
        </div>
        {enabledLangs.length > 1 && (
          <button onClick={() => setShowLangPicker(true)}
            style={{
              padding: "10px 14px", borderRadius: 12,
              background: "#f1f5f9", border: "none", cursor: "pointer",
              display: "inline-flex", alignItems: "center", gap: 6,
              fontSize: 15, fontWeight: 600, color: S.dark,
              fontFamily: S.font,
            }}>
            {LANG_FLAGS[lang]} {LANG_NAMES[lang]}
          </button>
        )}
      </div>

      {/* Category strip */}
      <div style={{
        display: "flex", gap: 8, padding: "12px 16px",
        overflowX: "auto", background: "#fff",
        borderBottom: `1px solid ${S.border}`,
        flexShrink: 0,
      }}>
        <CategoryChip active={activeCat === null} onClick={() => setActiveCat(null)}
          accent={accent} label={t(lang, "kiosk_categories_all")} />
        {categories.map(c => (
          <CategoryChip key={c.id} active={activeCat === c.id}
            onClick={() => setActiveCat(c.id)} accent={accent} label={catName(c)} />
        ))}
      </div>

      {/* Product grid */}
      <div style={{
        flex: 1, overflowY: "auto",
        padding: 14,
        paddingBottom: cartCount > 0 ? 110 : 14, // leave room for cart bar
      }}>
        {filtered.length === 0 ? (
          <div style={{
            textAlign: "center", padding: 60,
            color: S.muted, fontSize: 18,
          }}>
            {t(lang, "no_items")}
          </div>
        ) : (
          <div style={{
            display: "grid",
            gridTemplateColumns: orient === "portrait"
              ? "repeat(2, 1fr)"
              : "repeat(auto-fill, minmax(220px, 1fr))",
            gap: 14,
          }}>
            {filtered.map(p => (
              <ProductTile key={p.id} product={p} accent={accent} lang={lang} onAdd={() => handleProductTap(p)} />
            ))}
          </div>
        )}
      </div>

      {/* Cart bar — only shown when cart has items */}
      {cartCount > 0 && (
        <CartBar
          key={pulseSig}
          count={cartCount}
          total={cartTotal}
          accent={accent}
          lang={lang}
          onView={onViewCart}
        />
      )}

      {/* Product customization modal */}
      {productModal && (
        <ProductModal
          product={productModal}
          accent={accent}
          lang={lang}
          onClose={() => setProductModal(null)}
          onAdd={handleModalAdd}
        />
      )}

      {/* Language picker overlay */}
      {showLangPicker && (
        <LangPickerOverlay
          enabledLangs={enabledLangs}
          current={lang}
          onPick={(l) => { setLang(l); setShowLangPicker(false); }}
          onClose={() => setShowLangPicker(false)}
          accent={accent}
        />
      )}
    </div>
  );
}

// ─── CategoryChip ────────────────────────────────────────────────────
function CategoryChip({ active, onClick, accent, label }) {
  return (
    <button onClick={onClick}
      style={{
        padding: "10px 18px",
        borderRadius: 22,
        border: `1.5px solid ${active ? accent : S.border}`,
        background: active ? accent : "#fff",
        color: active ? "#fff" : S.dark,
        fontSize: 15, fontWeight: 600,
        cursor: "pointer", whiteSpace: "nowrap",
        flexShrink: 0,
        fontFamily: S.font,
      }}>
      {label}
    </button>
  );
}

// ─── ProductTile ─────────────────────────────────────────────────────
// Big tap target with photo (or fallback), name, price, [+] button.
// Phase 3A: tapping anywhere on the tile adds the product directly to
// the cart with no customization. In Phase 3B, products with tags or
// descriptions will open a customization modal instead.
function ProductTile({ product, accent, lang, onAdd }) {
  const isAvailable = product.available !== 0;
  return (
    <button
      onClick={onAdd}
      disabled={!isAvailable}
      style={{
        background: "#fff", borderRadius: 18,
        border: `1px solid ${S.border}`,
        overflow: "hidden",
        textAlign: "left", padding: 0,
        cursor: isAvailable ? "pointer" : "not-allowed",
        opacity: isAvailable ? 1 : 0.5,
        display: "flex", flexDirection: "column",
        fontFamily: S.font,
        transition: "transform .1s",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      {/* Photo or placeholder */}
      <div style={{
        width: "100%", aspectRatio: "4 / 3",
        background: product.photo_url ? `url(${product.photo_url}) center/cover` : "#f1f5f9",
        position: "relative",
      }}>
        {!product.photo_url && (
          <div style={{
            position: "absolute", inset: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: S.lightMuted,
          }}>
            <Coffee size={48} />
          </div>
        )}
        {!isAvailable && (
          <div style={{
            position: "absolute", inset: 0,
            background: "rgba(255,255,255,0.85)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 14, fontWeight: 700, color: S.red,
          }}>
            {t(lang, "unavailable_now")}
          </div>
        )}
        {/* Floating add button */}
        {isAvailable && (
          <div style={{
            position: "absolute", bottom: 10, right: 10,
            width: 44, height: 44, borderRadius: "50%",
            background: accent, color: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 6px 18px rgba(0,0,0,0.15)",
          }}>
            <Plus size={24} />
          </div>
        )}
      </div>
      {/* Name + price */}
      <div style={{ padding: 12, flex: 1, display: "flex", flexDirection: "column" }}>
        <div style={{
          fontSize: 16, fontWeight: 700, color: S.dark,
          marginBottom: 4, lineHeight: 1.2,
          display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}>
          {pickT(product.name_translations, lang, product.name)}
        </div>
        {(() => {
          const desc = pickT(product.description_translations, lang, product.description);
          return desc ? (
            <div style={{
              fontSize: 12, color: S.muted, marginBottom: 8,
              display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}>
              {desc}
            </div>
          ) : null;
        })()}
        <div style={{ marginTop: "auto", fontSize: 18, fontWeight: 700, color: accent }}>
          {netoToBruto(product.price, product.vat_rate).toFixed(2)} €
        </div>
      </div>
    </button>
  );
}

// ─── CartBar — sticky bottom bar showing count + total ───────────────
// Phase 3A: tapping the bar is a placeholder (alert). Phase 3B will
// open the full CartScreen for review + customer name + place order.
function CartBar({ count, total, accent, lang, onView }) {
  return (
    <button onClick={onView}
      style={{
        position: "absolute", bottom: 14, left: 14, right: 14,
        padding: "16px 20px",
        background: accent, color: "#fff",
        border: "none", borderRadius: 16,
        cursor: "pointer", fontFamily: S.font,
        display: "flex", alignItems: "center", gap: 14,
        fontSize: 17, fontWeight: 700,
        boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
        animation: "cartPop .25s ease-out",
        WebkitTapHighlightColor: "transparent",
      }}>
      <div style={{
        width: 36, height: 36, borderRadius: 10,
        background: "rgba(255,255,255,0.2)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <ShoppingCart size={20} />
      </div>
      <div style={{ flex: 1, textAlign: "left" }}>
        <div style={{ fontSize: 13, opacity: 0.85 }}>{count} {t(lang, "items")}</div>
        <div style={{ fontSize: 19 }}>{total.toFixed(2)} €</div>
      </div>
      <div style={{ fontSize: 15, fontWeight: 600, opacity: 0.9 }}>
        {t(lang, "cart")} →
      </div>
      <style>{`@keyframes cartPop { 0% { transform: translateY(20px); opacity: 0; } 100% { transform: translateY(0); opacity: 1; } }`}</style>
    </button>
  );
}

// ════════════════════════════════════════════════════════════════════
// ProductModal — full-screen product customization
//
// Shown when user taps a product that has preset tags OR a description
// longer than ~30 chars. Allows: qty +/-, toggle preset tags, free-text
// special note, then "Add to cart" with the customization applied.
// ════════════════════════════════════════════════════════════════════
function ProductModal({ product, accent, lang, onClose, onAdd }) {
  const [qty, setQty] = useState(1);
  const [selectedTags, setSelectedTags] = useState([]);
  const [note, setNote] = useState("");
  const [kbActive, setKbActive] = useState(false);

  // Tags rich-or-fallback. Filter hidden ones — kiosk customer never sees
  // those. Order line stores tag.base (CG) so kitchen reads native lang.
  const visibleTags = useMemo(() => {
    const source = Array.isArray(product.tags_rich) && product.tags_rich.length > 0
      ? product.tags_rich
      : (Array.isArray(product.tags_preset) ? product.tags_preset : []).map(s => ({
          base: String(s), visible: true, translations: {},
        }));
    return source.filter(t => t && t.base && t.visible !== false);
  }, [product]);
  const hasTags = product.tags_enabled && visibleTags.length > 0;

  const toggleTag = (base) => {
    setSelectedTags(prev => prev.includes(base) ? prev.filter(t => t !== base) : [...prev, base]);
  };

  const submit = () => {
    onAdd(product, qty, selectedTags, note.trim());
  };

  const lineTotal = (netoToBruto(product.price, product.vat_rate) * qty).toFixed(2);
  const displayName = pickT(product.name_translations, lang, product.name);
  const displayDesc = pickT(product.description_translations, lang, product.description);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.55)",
        WebkitBackdropFilter: "blur(8px)", backdropFilter: "blur(8px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 50, fontFamily: S.font, padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 22,
          maxWidth: 560, width: "100%", maxHeight: "90vh",
          display: "flex", flexDirection: "column",
          boxShadow: "0 30px 80px rgba(0,0,0,0.4)",
          overflow: "hidden",
        }}
      >
        {/* Photo */}
        {product.photo_url ? (
          <div style={{
            width: "100%", aspectRatio: "16 / 9",
            background: `url(${product.photo_url}) center/cover`,
            position: "relative", flexShrink: 0,
          }}>
            <button onClick={onClose} style={modalCloseBtnStyle}>
              <X size={22} />
            </button>
          </div>
        ) : (
          <div style={{
            padding: "24px 28px 12px", position: "relative", flexShrink: 0,
            background: `linear-gradient(135deg, ${accent}15, ${accent}05)`,
          }}>
            <Coffee size={48} color={accent} />
            <button onClick={onClose} style={modalCloseBtnStyle}>
              <X size={22} />
            </button>
          </div>
        )}

        {/* Body — scrollable */}
        <div style={{ overflowY: "auto", flex: 1, padding: "20px 24px" }}>
          <h2 style={{ margin: "0 0 6px", fontSize: 24, color: S.dark, lineHeight: 1.2 }}>
            {displayName}
          </h2>
          <div style={{ fontSize: 22, fontWeight: 700, color: accent, marginBottom: 14 }}>
            {netoToBruto(product.price, product.vat_rate).toFixed(2)} €
          </div>
          {displayDesc && (
            <p style={{ margin: "0 0 18px", fontSize: 15, color: S.muted, lineHeight: 1.5 }}>
              {displayDesc}
            </p>
          )}

          {/* Preset tags — translated labels, base stored on order */}
          {hasTags && (
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: S.muted, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>
                {t(lang, "tags")}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {visibleTags.map((tagObj, i) => {
                  const active = selectedTags.includes(tagObj.base);
                  const label = pickT(tagObj.translations, lang, tagObj.base);
                  return (
                    <button key={i} onClick={() => toggleTag(tagObj.base)}
                      style={{
                        padding: "10px 16px",
                        borderRadius: 22,
                        border: `1.5px solid ${active ? accent : S.border}`,
                        background: active ? `${accent}15` : "#fff",
                        color: active ? accent : S.dark,
                        fontSize: 14, fontWeight: 600,
                        cursor: "pointer", fontFamily: S.font,
                        display: "inline-flex", alignItems: "center", gap: 6,
                      }}>
                      {active && <Check size={14} />}
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Special note */}
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: S.muted, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>
              {t(lang, "kiosk_special_note")}
            </div>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onFocus={() => setKbActive(true)}
              maxLength={200}
              rows={2}
              placeholder={t(lang, "kiosk_special_note_ph")}
              style={{
                width: "100%", padding: "12px 16px",
                borderRadius: 12, border: `1.5px solid ${kbActive ? accent : S.border}`,
                fontSize: 16, fontFamily: `"DM Sans", sans-serif`,
                resize: "none", outline: "none",
                color: S.dark,
                boxSizing: "border-box",
              }}
            />
          </div>

          {/* Quantity */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: S.dark }}>{t(lang, "qty")}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <button onClick={() => setQty(q => Math.max(1, q - 1))}
                style={qtyBtnStyle(accent)}>
                <Minus size={20} />
              </button>
              <div style={{ minWidth: 40, textAlign: "center", fontSize: 24, fontWeight: 700, color: S.dark }}>
                {qty}
              </div>
              <button onClick={() => setQty(q => Math.min(99, q + 1))}
                style={qtyBtnStyle(accent)}>
                <Plus size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Footer — Add button */}
        <div style={{
          padding: 16, borderTop: `1px solid ${S.border}`,
          background: "#fff", flexShrink: 0,
        }}>
          <button onClick={submit}
            style={{
              width: "100%", padding: "16px 20px",
              background: accent, color: "#fff",
              border: "none", borderRadius: 14,
              fontSize: 17, fontWeight: 700,
              cursor: "pointer", fontFamily: S.font,
              display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 10,
            }}>
            <Plus size={20} />
            {t(lang, "add_to_cart")} — {lineTotal} €
          </button>
        </div>
      </div>

      {/* On-screen keyboard for the note field */}
      {kbActive && (
        <TouchKeyboard
          value={note}
          onChange={setNote}
          onClose={() => setKbActive(false)}
          lang={lang}
          accent={accent}
        />
      )}
    </div>
  );
}

const modalCloseBtnStyle = {
  position: "absolute", top: 12, right: 12,
  width: 40, height: 40, borderRadius: "50%",
  background: "rgba(0,0,0,0.5)", color: "#fff",
  border: "none", cursor: "pointer",
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  WebkitBackdropFilter: "blur(6px)", backdropFilter: "blur(6px)",
};

const qtyBtnStyle = (accent) => ({
  width: 44, height: 44, borderRadius: "50%",
  background: `${accent}15`, color: accent,
  border: "none", cursor: "pointer",
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  fontFamily: S.font,
});

// ════════════════════════════════════════════════════════════════════
// CartScreen — full-screen cart review + checkout
//
// Replaces the menu while open. Lists each item with qty +/-, remove
// button, optional customer name + general note, and "Place order"
// button at the bottom. On submit: posts order, switches to confirmation.
// ════════════════════════════════════════════════════════════════════
function CartScreen({ device, cart, lang, setLang, menuData, onUpdateQty, onRemove, onBack, onCancel, onPlaceOrder }) {
  const settings = device?.settings || {};
  const accent = settings.branding_color || S.accent;
  const enabledLangs = (settings.languages || "cg").split(",").map(s => s.trim()).filter(Boolean);
  const [showLangPicker, setShowLangPicker] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [activeKbField, setActiveKbField] = useState(null); // "name" | "note" | null

  // Bruto totals — what customer pays. Items store neto.
  const total = cart.reduce((s, it) => s + netoToBruto(it.price, it.vat_rate) * it.qty, 0);
  const itemCount = cart.reduce((s, it) => s + it.qty, 0);

  const handleSubmit = async () => {
    if (cart.length === 0) return;
    setSubmitting(true);
    setError("");
    setActiveKbField(null);
    const r = await onPlaceOrder({ customerName: customerName.trim(), note: note.trim() });
    if (!r.success) {
      setError(r.message || t(lang, "kiosk_place_failed"));
      setSubmitting(false);
    }
    // On success: TerminalKioskApp switches stage; this component unmounts.
  };

  // ─── On-screen keyboard handlers ───
  const kbValue = activeKbField === "name" ? customerName : (activeKbField === "note" ? note : "");
  const handleKbChange = (newValue) => {
    if (activeKbField === "name") setCustomerName(newValue);
    else if (activeKbField === "note") setNote(newValue);
  };

  return (
    <div style={{
      position: "fixed", inset: 0,
      background: (menuData?.settings?.bg_color && /^#[0-9a-fA-F]{3,8}$/.test(menuData.settings.bg_color)) ? menuData.settings.bg_color : S.bg,
      display: "flex", flexDirection: "column",
      fontFamily: S.font,
      WebkitTapHighlightColor: "transparent",
    }}>
      {/* Top bar */}
      <div style={{
        background: "#fff",
        padding: "16px 24px",
        borderBottom: `1px solid ${S.border}`,
        display: "flex", alignItems: "center", gap: 14,
        flexShrink: 0,
      }}>
        <button onClick={onBack}
          style={{
            padding: "12px 18px", borderRadius: 14,
            background: "#f1f5f9", border: "none", cursor: "pointer",
            display: "inline-flex", alignItems: "center", gap: 6,
            fontSize: 16, fontWeight: 600, color: S.muted,
            fontFamily: S.font,
          }}>
          ← {t(lang, "back")}
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: S.dark, lineHeight: 1.1 }}>
            {t(lang, "kiosk_review_order")}
          </div>
          {itemCount > 0 && (
            <div style={{ marginTop: 2, color: S.muted, fontSize: 14 }}>
              {itemCount} {t(lang, "items")}
            </div>
          )}
        </div>
        {enabledLangs.length > 1 && (
          <button onClick={() => setShowLangPicker(true)}
            style={{
              padding: "12px 16px", borderRadius: 14,
              background: "#f1f5f9", border: "none", cursor: "pointer",
              display: "inline-flex", alignItems: "center", gap: 8,
              fontSize: 16, fontWeight: 600, color: S.dark,
              fontFamily: S.font,
            }}>
            {LANG_FLAGS[lang]} {LANG_NAMES[lang]}
          </button>
        )}
      </div>

      {/* Items + form — scrollable, centered, max-width container */}
      <div style={{
        flex: 1, overflowY: "auto",
        padding: "20px 16px",
        paddingBottom: activeKbField ? 380 : 140, // leave room for keyboard or footer
      }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          {cart.length === 0 ? (
            <div style={{ textAlign: "center", padding: 80, color: S.muted, fontSize: 20 }}>
              <ShoppingCart size={64} color={S.lightMuted} style={{ marginBottom: 18 }} />
              <div>{t(lang, "empty_cart")}</div>
            </div>
          ) : (
            <>
              {/* Cart items — bigger, with shadow */}
              <div style={{
                background: "#fff", borderRadius: 20, marginBottom: 18,
                overflow: "hidden",
                boxShadow: "0 4px 16px rgba(15,23,42,0.06)",
                border: `1px solid ${S.border}`,
              }}>
                {cart.map((item, idx) => (
                  <CartItemRow key={idx} item={item} accent={accent} isLast={idx === cart.length - 1}
                    onIncrement={() => onUpdateQty(idx, item.qty + 1)}
                    onDecrement={() => onUpdateQty(idx, item.qty - 1)}
                    onRemove={() => onRemove(idx)}
                    lang={lang}
                  />
                ))}
              </div>

              {/* Customer name */}
              <div style={{ marginBottom: 16 }}>
                <label style={kioskFormLabelStyle}>
                  {t(lang, "name_optional")}
                </label>
                <input
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  onFocus={() => setActiveKbField("name")}
                  maxLength={60}
                  style={{
                    ...kioskInputStyle,
                    borderColor: activeKbField === "name" ? accent : S.border,
                  }}
                />
              </div>

              {/* General note */}
              <div style={{ marginBottom: 18 }}>
                <label style={kioskFormLabelStyle}>
                  {t(lang, "note_optional")}
                </label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  onFocus={() => setActiveKbField("note")}
                  maxLength={200}
                  rows={2}
                  style={{
                    ...kioskInputStyle,
                    borderColor: activeKbField === "note" ? accent : S.border,
                    resize: "none",
                    minHeight: 70,
                  }}
                />
              </div>

              {/* Pay-at-counter notice — bigger, more prominent */}
              <div style={{
                padding: "16px 20px", background: "#fef3c7",
                border: "1.5px solid #fde68a", borderRadius: 14,
                fontSize: 16, color: "#92400e",
                display: "flex", alignItems: "center", gap: 12,
                fontWeight: 600,
              }}>
                <CreditCard size={22} />
                {t(lang, "kiosk_pay_at_counter")}
              </div>

              {error && (
                <div style={{
                  marginTop: 14, padding: "14px 18px",
                  background: "#fee2e2", border: "1px solid #fecaca",
                  borderRadius: 12, color: S.red, fontSize: 15, fontWeight: 600,
                }}>
                  {error}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Sticky footer — total + place order. Hides when keyboard open. */}
      {cart.length > 0 && !activeKbField && (
        <div style={{
          position: "absolute", bottom: 0, left: 0, right: 0,
          background: "#fff", borderTop: `1px solid ${S.border}`,
          padding: "16px 20px",
          boxShadow: "0 -8px 24px rgba(0,0,0,0.06)",
        }}>
          <div style={{ maxWidth: 720, margin: "0 auto", display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ flex: "0 0 auto" }}>
              <div style={{ fontSize: 12, color: S.muted, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 600 }}>
                {t(lang, "total")}
              </div>
              <div style={{ fontSize: 28, fontWeight: 800, color: S.dark, fontFamily: "JetBrains Mono, monospace", lineHeight: 1 }}>
                {total.toFixed(2)} €
              </div>
            </div>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              style={{
                flex: 1, padding: "18px 24px",
                background: submitting ? S.muted : accent, color: "#fff",
                border: "none", borderRadius: 16,
                fontSize: 18, fontWeight: 700,
                cursor: submitting ? "default" : "pointer",
                fontFamily: S.font,
                display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 10,
                opacity: submitting ? 0.85 : 1,
                boxShadow: submitting ? "none" : `0 8px 20px ${accent}40`,
              }}>
              {submitting ? (
                <><Spinner size={20} color="#fff" /> {t(lang, "kiosk_placing")}</>
              ) : (
                <><Send size={20} /> {t(lang, "place_order")}</>
              )}
            </button>
          </div>
        </div>
      )}

      {/* On-screen keyboard */}
      {activeKbField && (
        <TouchKeyboard
          value={kbValue}
          onChange={handleKbChange}
          onClose={() => setActiveKbField(null)}
          lang={lang}
          accent={accent}
        />
      )}

      {/* Language picker overlay */}
      {showLangPicker && (
        <LangPickerOverlay
          enabledLangs={enabledLangs}
          current={lang}
          onPick={(l) => { setLang(l); setShowLangPicker(false); }}
          onClose={() => setShowLangPicker(false)}
          accent={accent}
        />
      )}
    </div>
  );
}

const kioskFormLabelStyle = {
  display: "block",
  fontSize: 13, fontWeight: 700, color: S.muted,
  marginBottom: 8,
  textTransform: "uppercase", letterSpacing: 0.6,
};
const kioskInputStyle = {
  width: "100%", padding: "16px 18px",
  borderRadius: 14, border: `1.5px solid ${S.border}`,
  background: "#fff",
  fontSize: 17, fontFamily: `"DM Sans", sans-serif`, color: S.dark,
  outline: "none", boxSizing: "border-box",
  transition: "border-color .15s",
};

// ─── CartItemRow — single line in the cart with qty controls ────────
function CartItemRow({ item, accent, isLast, onIncrement, onDecrement, onRemove, lang }) {
  return (
    <div style={{
      padding: "18px 18px",
      display: "flex", alignItems: "center", gap: 16,
      borderBottom: isLast ? "none" : `1px solid ${S.border}`,
    }}>
      {/* Photo */}
      <div style={{
        width: 84, height: 84, borderRadius: 14, flexShrink: 0,
        background: item.photo_url ? `url(${item.photo_url}) center/cover` : "#f1f5f9",
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: item.photo_url ? "0 2px 6px rgba(0,0,0,0.08)" : "none",
      }}>
        {!item.photo_url && <Coffee size={32} color={S.lightMuted} />}
      </div>
      {/* Name + tags + note */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: S.dark, lineHeight: 1.25 }}>
          {item.name}
        </div>
        {(item.tags || []).length > 0 && (
          <div style={{ fontSize: 13, color: S.muted, marginTop: 4 }}>
            {item.tags.join(" · ")}
          </div>
        )}
        {item.note && (
          <div style={{ fontSize: 13, color: S.muted, marginTop: 4, fontStyle: "italic" }}>
            "{item.note}"
          </div>
        )}
        <div style={{ fontSize: 17, fontWeight: 700, color: accent, marginTop: 6 }}>
          {(netoToBruto(item.price, item.vat_rate) * item.qty).toFixed(2)} €
        </div>
      </div>
      {/* Qty controls — bigger */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <button onClick={onDecrement}
          style={cartQtyBtnStyle(accent)}
          aria-label={t(lang, "remove")}>
          {item.qty === 1 ? <Trash2 size={20} /> : <Minus size={22} />}
        </button>
        <div style={{ minWidth: 32, textAlign: "center", fontSize: 22, fontWeight: 700, color: S.dark }}>
          {item.qty}
        </div>
        <button onClick={onIncrement} style={cartQtyBtnStyle(accent)}>
          <Plus size={22} />
        </button>
      </div>
    </div>
  );
}

const cartQtyBtnStyle = (accent) => ({
  width: 48, height: 48, borderRadius: "50%",
  background: `${accent}15`, color: accent,
  border: "none", cursor: "pointer",
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  fontFamily: `"DM Sans", sans-serif`,
});

// ════════════════════════════════════════════════════════════════════
// ConfirmationScreen — order placed, shows order#, QR, ETA, 15s countdown
//
// 15-second auto-return to landing. "Nova narudžba" button skips the wait.
// QR encodes /order/{publicToken} → customer phone can track status.
// ════════════════════════════════════════════════════════════════════
function ConfirmationScreen({ device, orderResult, lang, onFinish }) {
  const settings = device?.settings || {};
  const accent = settings.branding_color || S.accent;
  const orient = useOrientation();
  const [secondsLeft, setSecondsLeft] = useState(15);

  // 15-second auto-return countdown
  useEffect(() => {
    if (secondsLeft <= 0) {
      onFinish();
      return;
    }
    const t = setTimeout(() => setSecondsLeft(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secondsLeft, onFinish]);

  const statusUrl = `https://self.efis.pro/order/${orderResult.publicToken}`;

  return (
    <div style={{
      position: "fixed", inset: 0,
      background: `linear-gradient(135deg, ${accent} 0%, ${shadeColor(accent, -25)} 100%)`,
      color: "#fff", fontFamily: S.font,
      display: "flex", flexDirection: "column",
      WebkitTapHighlightColor: "transparent",
      userSelect: "none",
    }}>
      <div style={{
        flex: 1, display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        padding: orient === "portrait" ? "20px 24px" : "20px 60px",
        textAlign: "center",
      }}>
        {/* Success check */}
        <div style={{
          width: orient === "portrait" ? 80 : 96,
          height: orient === "portrait" ? 80 : 96,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.95)",
          display: "flex", alignItems: "center", justifyContent: "center",
          marginBottom: 18,
          animation: "checkPop .4s ease-out",
        }}>
          <Check size={orient === "portrait" ? 48 : 56} color={accent} strokeWidth={3.5} />
        </div>

        <h1 style={{
          margin: "0 0 6px",
          fontSize: orient === "portrait" ? 30 : 38,
          fontWeight: 700, letterSpacing: -0.5,
        }}>
          {t(lang, "kiosk_order_placed")}
        </h1>

        <div style={{ fontSize: 15, color: "rgba(255,255,255,0.85)", marginBottom: 24 }}>
          {t(lang, "kiosk_your_order_no")}
        </div>

        {/* Big order number */}
        <div style={{
          padding: orient === "portrait" ? "14px 36px" : "18px 48px",
          background: "rgba(255,255,255,0.95)",
          color: accent,
          borderRadius: 20,
          fontSize: orient === "portrait" ? 60 : 76,
          fontWeight: 800, letterSpacing: -2,
          fontFamily: "JetBrains Mono, monospace",
          marginBottom: 28,
          boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
        }}>
          #{orderResult.orderNumber}
        </div>

        {/* QR code */}
        <div style={{
          background: "#fff", padding: 14, borderRadius: 14,
          marginBottom: 14,
          boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
        }}>
          <QRCodeSVG value={statusUrl} size={orient === "portrait" ? 140 : 160} level="M" />
        </div>
        <div style={{ fontSize: 13, color: "rgba(255,255,255,0.85)", marginBottom: 24, maxWidth: 320 }}>
          {t(lang, "kiosk_scan_qr")}
        </div>

        {/* ETA */}
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          padding: "8px 18px", borderRadius: 20,
          background: "rgba(255,255,255,0.15)",
          fontSize: 14, fontWeight: 600,
          marginBottom: 28,
        }}>
          <Clock size={16} />
          {t(lang, "kiosk_estimated_time")}: ~{orderResult.estimatedPrepMin} {t(lang, "kiosk_minutes_short")}
        </div>

        {/* Pay at counter notice */}
        <div style={{
          fontSize: 14, color: "rgba(255,255,255,0.9)",
          fontWeight: 600, letterSpacing: 0.5,
          padding: "8px 16px",
          border: "1.5px dashed rgba(255,255,255,0.4)",
          borderRadius: 10,
        }}>
          💳 {t(lang, "kiosk_pay_at_counter")}
        </div>

        <style>{`@keyframes checkPop { 0% { transform: scale(0); } 60% { transform: scale(1.15); } 100% { transform: scale(1); } }`}</style>
      </div>

      {/* Footer — countdown + new order button */}
      <div style={{
        padding: 20,
        display: "flex", alignItems: "center", justifyContent: "center", gap: 12,
        flexWrap: "wrap",
      }}>
        <div style={{
          fontSize: 13, color: "rgba(255,255,255,0.7)",
          fontFamily: "JetBrains Mono, monospace",
        }}>
          {t(lang, "kiosk_returning_in")} {secondsLeft}{t(lang, "kiosk_seconds_short")}
        </div>
        <button onClick={onFinish}
          style={{
            padding: "12px 24px",
            background: "rgba(255,255,255,0.95)",
            color: accent,
            border: "none", borderRadius: 24,
            fontSize: 15, fontWeight: 700,
            cursor: "pointer", fontFamily: S.font,
            display: "inline-flex", alignItems: "center", gap: 8,
          }}>
          <Plus size={16} /> {t(lang, "place_new")}
        </button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// TouchKeyboard — on-screen keyboard for kiosk touch input
//
// Slides up from bottom when an input is focused. Layout:
//   Row 1: 1 2 3 4 5 6 7 8 9 0
//   Row 2: q w e r t y u i o p
//   Row 3: a s d f g h j k l
//   Row 4: SHIFT z x c v b n m ⌫
//   Row 5: 123/ABC special-chars space DONE
//
// Special chars row toggles between latin diacritics and symbols.
// CG keyboard adds: č ć ž š đ
// AL keyboard adds: ë ç (and shares the latin row)
// Shift toggles uppercase for one keypress (auto-releases) — long-press
// to lock would be nicer but skipped for v1 simplicity.
//
// Always-append model: keys append to end of value, backspace removes
// last char. Cursor positioning skipped — kiosk inputs are short.
//
// Physical keyboard still works — the controlled input below this
// keyboard accepts native keystrokes too. Both methods coexist.
// ════════════════════════════════════════════════════════════════════
function TouchKeyboard({ value, onChange, onClose, lang = "cg", accent = "#3b82f6" }) {
  const [shift, setShift] = useState(false);
  const [showSymbols, setShowSymbols] = useState(false);

  // Diacritic chars per language (added as a 4th row when relevant)
  const diacritics = {
    cg: ["č", "ć", "ž", "š", "đ"],
    al: ["ë", "ç"],
    en: [],
  };
  const langDiacritics = diacritics[lang] || [];

  const numbersRow = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"];
  const row1 = ["q", "w", "e", "r", "t", "y", "u", "i", "o", "p"];
  const row2 = ["a", "s", "d", "f", "g", "h", "j", "k", "l"];
  const row3 = ["z", "x", "c", "v", "b", "n", "m"];

  const symbolsRow1 = ["@", ".", ",", "?", "!", "-", "_", "(", ")", "/"];
  const symbolsRow2 = ["#", "&", "*", "+", "=", "$", "€", ":", ";", "\""];
  const symbolsRow3 = ["%", "<", ">", "[", "]", "{", "}", "'", "`", "~"];

  const press = (key) => {
    const ch = shift ? key.toUpperCase() : key;
    onChange((value || "") + ch);
    if (shift) setShift(false); // auto-release after one keypress
  };
  const pressRaw = (key) => onChange((value || "") + key);
  const backspace = () => onChange((value || "").slice(0, -1));
  const space = () => onChange((value || "") + " ");
  const toggleShift = () => setShift(s => !s);
  const toggleSymbols = () => setShowSymbols(s => !s);

  const renderKey = (key, opts = {}) => {
    const display = opts.raw ? key : (shift ? key.toUpperCase() : key);
    return (
      <button
        key={key + (opts.suffix || "")}
        onClick={opts.onPress || (() => (opts.raw ? pressRaw(key) : press(key)))}
        style={{
          ...kbKeyStyle,
          flex: opts.flex || 1,
          background: opts.bg || "#fff",
          color: opts.color || S.dark,
          fontWeight: opts.weight || 500,
          fontSize: opts.size || 22,
        }}
      >
        {opts.label || display}
      </button>
    );
  };

  return (
    <div style={{
      position: "fixed", bottom: 0, left: 0, right: 0,
      background: "#e5e7ea",
      padding: "10px 8px 14px",
      boxShadow: "0 -8px 28px rgba(0,0,0,0.18)",
      borderTop: `1px solid ${S.border}`,
      zIndex: 80,
      animation: "kbSlideUp .2s ease-out",
      WebkitTapHighlightColor: "transparent",
      userSelect: "none",
    }}>
      {/* Top toolbar: preview + Done */}
      <div style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "4px 8px 10px",
      }}>
        <div style={{
          flex: 1, padding: "8px 14px",
          background: "#fff", borderRadius: 10,
          fontSize: 16, color: S.muted,
          minHeight: 36,
          maxHeight: 60, overflowY: "auto",
          fontFamily: `"DM Sans", sans-serif`,
          wordBreak: "break-word",
        }}>
          {value || <span style={{ opacity: 0.4 }}>—</span>}
        </div>
        <button onClick={onClose}
          style={{
            padding: "10px 18px",
            background: accent, color: "#fff",
            border: "none", borderRadius: 10,
            fontSize: 15, fontWeight: 700,
            cursor: "pointer", fontFamily: `"DM Sans", sans-serif`,
          }}>
          ✓
        </button>
      </div>

      {showSymbols ? (
        <>
          <div style={kbRowStyle}>{symbolsRow1.map(k => renderKey(k, { raw: true }))}</div>
          <div style={kbRowStyle}>{symbolsRow2.map(k => renderKey(k, { raw: true }))}</div>
          <div style={kbRowStyle}>{symbolsRow3.map(k => renderKey(k, { raw: true }))}</div>
        </>
      ) : (
        <>
          <div style={kbRowStyle}>{numbersRow.map(k => renderKey(k, { raw: true }))}</div>
          <div style={kbRowStyle}>{row1.map(k => renderKey(k))}</div>
          <div style={kbRowStyle}>{row2.map(k => renderKey(k))}</div>
          {langDiacritics.length > 0 && (
            <div style={kbRowStyle}>
              {langDiacritics.map(k => renderKey(k, { bg: "#fef3c7", color: "#92400e" }))}
            </div>
          )}
          <div style={kbRowStyle}>
            {renderKey("shift", {
              suffix: "shift", flex: 1.5,
              bg: shift ? accent : "#cbd5e1",
              color: shift ? "#fff" : S.dark,
              weight: 700, size: 14,
              onPress: toggleShift,
              label: "⇧",
            })}
            {row3.map(k => renderKey(k))}
            {renderKey("bs", {
              suffix: "bs", flex: 1.5,
              bg: "#cbd5e1", weight: 700, size: 18,
              onPress: backspace,
              label: "⌫",
            })}
          </div>
        </>
      )}

      {/* Bottom row: 123/ABC, space, special toggle, lang flag (info) */}
      <div style={kbRowStyle}>
        {renderKey("toggleSym", {
          suffix: "tg", flex: 1.5,
          bg: "#cbd5e1", weight: 700, size: 14,
          onPress: toggleSymbols,
          label: showSymbols ? "ABC" : "123#",
        })}
        {renderKey("space", {
          suffix: "sp", flex: 5,
          bg: "#fff", weight: 500, size: 14,
          onPress: space,
          label: " ",
        })}
      </div>
      <style>{`@keyframes kbSlideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }`}</style>
    </div>
  );
}

const kbRowStyle = {
  display: "flex", gap: 5, marginBottom: 5,
  justifyContent: "center",
};
const kbKeyStyle = {
  flex: 1, minWidth: 0,
  padding: "14px 0",
  border: "none", borderRadius: 8,
  cursor: "pointer",
  fontFamily: `"DM Sans", sans-serif`,
  boxShadow: "0 1px 0 rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.05)",
  transition: "transform .05s",
  WebkitTapHighlightColor: "transparent",
};

// ════════════════════════════════════════════════════════════════════
// printPickupTicket — fire-and-forget receipt printing via Electron IPC.
//
// In browser (no electronAPI): silently no-ops. In .exe: builds the
// structured payload and calls the existing printer:printReceipt IPC
// handler in main.js (which already supports title/qr_url/no fiscal
// fields). Failures are caught and logged but never block UX — customer
// has already seen their order on screen and can scan the on-screen QR.
//
// Receipt format:
//   - Company name / address / PIB (header)
//   - Title: "TIKET ZA PREUZIMANJE" (or translated)
//   - Order number (Br. S007)
//   - Items with qty / price
//   - Total
//   - "Plati na kasi" / "Pay at counter" notice
//   - QR code → /order/{publicToken}
//   - Footer ("Hvala na posjeti!")
// ════════════════════════════════════════════════════════════════════
async function printPickupTicket({ device, cart, orderNumber, publicToken, total, lang }) {
  if (!window.electronAPI?.printReceipt) return; // browser fallback — no-op

  const company = device?.company || {};
  const data = {
    company_name: (company.name || "").toUpperCase(),
    address: [company.address, company.city].filter(Boolean).join(", "),
    pib: company.pib || "",
    title: t(lang, "kiosk_review_order").toUpperCase() === "RISHIKO PORISIJË"
      ? "TIKET I MARRJES" // shouldn't happen, fallback
      : (lang === "en" ? "PICKUP TICKET" : (lang === "al" ? "TIKET I MARRJES" : "TIKET ZA PREUZIMANJE")),
    invoice_number: orderNumber,
    date: new Date().toLocaleString("sr-Latn-ME", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }),
    items: cart.map(it => ({
      name: it.tags && it.tags.length > 0 ? `${it.name} (${it.tags.join(", ")})` : it.name,
      qty: it.qty,
      // Pickup ticket shows BRUTO (what customer pays at counter). Prices
      // stored in cart are neto; convert per-line.
      price: netoToBruto(it.price, it.vat_rate),
      total: netoToBruto(it.price, it.vat_rate) * it.qty,
    })),
    total: total,
    payment_method: lang === "en" ? "Pay at counter" : (lang === "al" ? "Paguaj në arkë" : "Plati na kasi"),
    iic: "", // intentionally empty — this is NOT a fiscal receipt
    fic: "",
    qr_url: `https://self.efis.pro/order/${publicToken}`,
  };

  try {
    await window.electronAPI.printReceipt(data);
  } catch (e) {
    console.warn("Print failed (non-fatal):", e);
  }
}

// ─── shadeColor — darken/lighten a hex color by percent (-100..100) ─
function shadeColor(color, percent) {
  if (!color || !color.startsWith("#")) return color || "#3b82f6";
  let r = parseInt(color.slice(1, 3), 16);
  let g = parseInt(color.slice(3, 5), 16);
  let b = parseInt(color.slice(5, 7), 16);
  r = Math.max(0, Math.min(255, Math.round(r + (r * percent) / 100)));
  g = Math.max(0, Math.min(255, Math.round(g + (g * percent) / 100)));
  b = Math.max(0, Math.min(255, Math.round(b + (b * percent) / 100)));
  return "#" + [r, g, b].map(v => v.toString(16).padStart(2, "0")).join("");
}

// ════════════════════════════════════════════════════════════════════
//  KONOBAR (WAITER) APP — mobile-first, opened on a waiter's phone at
//  self.efis.pro/{slug}/konobar.
//
//  Flow:
//    pairing  → enter 6-digit code → permanent waiter_token in localStorage
//    tables   → grid of assigned tables (free vs busy + order count/total)
//    menu     → pick products for the chosen table → cart → send
//  Order lands in Aktivne (preparing) + kitchen as K-STOL N. No fiscalize.
// ════════════════════════════════════════════════════════════════════
const WAITER_TOKEN_KEY = "self_waiter_token";
const WAITER_LANG_KEY = "self_waiter_lang";

async function waiterApi(path, opts = {}) {
  const token = localStorage.getItem(WAITER_TOKEN_KEY);
  const h = { "Content-Type": "application/json" };
  if (token) h["Authorization"] = `Bearer ${token}`;
  try {
    const r = await fetch(`${API_BASE}${path}`, { ...opts, headers: { ...h, ...(opts.headers || {}) } });
    if (r.status === 401) {
      localStorage.removeItem(WAITER_TOKEN_KEY);
      window.location.reload();
      return { success: false, message: "Unauthorized", _unauth: true };
    }
    return await r.json();
  } catch (e) {
    return { success: false, message: e.message || "Network error" };
  }
}

function KonobarApp({ slug }) {
  const [stage, setStage] = useState("loading"); // loading | pairing | tables | menu | confirm
  const [waiter, setWaiter] = useState(null);     // { id, name, assignedTables }
  const [company, setCompany] = useState(null);
  const [settings, setSettings] = useState(null);
  const [tables, setTables] = useState([]);
  const [menuData, setMenuData] = useState(null); // { categories, products }
  const [activeTable, setActiveTable] = useState(null);
  const [cart, setCart] = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const [lang, setLang] = useState(() => localStorage.getItem(WAITER_LANG_KEY) || "cg");
  const [toast, setToast] = useState(null);
  const [lastOrder, setLastOrder] = useState(null);
  const [sending, setSending] = useState(false);

  const brand = settings?.branding_color || "#3b82f6";

  const showToast = (msg, type = "info") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2600);
  };

  // ── Boot: verify token if present ──
  useEffect(() => {
    (async () => {
      const token = localStorage.getItem(WAITER_TOKEN_KEY);
      if (!token) { setStage("pairing"); return; }
      const r = await waiterApi("/self-waiter/me");
      if (r.success) {
        setWaiter(r.data.waiter);
        setCompany(r.data.company);
        setSettings(r.data.settings);
        setStage("tables");
      } else if (!r._unauth) {
        setStage("pairing");
      }
    })();
  }, []);

  // ── Load tables when entering the tables stage ──
  const loadTables = useCallback(async () => {
    const r = await waiterApi("/self-waiter/tables");
    if (r.success) setTables(r.data.tables || []);
    return r.success;
  }, []);

  useEffect(() => {
    if (stage !== "tables") return;
    // Smart poller (jitter/pause/backoff). Kept at 8s — already reasonable and
    // only active on the tables stage.
    return makeSmartPoller(() => loadTables(), 8000, { key: "waiter-tables" });
  }, [stage, loadTables]);

  // ── Load menu once ──
  const ensureMenu = useCallback(async () => {
    if (menuData) return menuData;
    const r = await waiterApi("/self-waiter/menu");
    if (r.success) { setMenuData(r.data); return r.data; }
    showToast("Greška pri učitavanju menija", "error");
    return null;
  }, [menuData]);

  const pickTr = (obj, fallback) => {
    if (obj && typeof obj === "object" && obj[lang]) return obj[lang];
    return fallback;
  };

  // ── Pairing ──
  const handlePair = async (code) => {
    const r = await waiterApi("/self-waiter/redeemPairing", {
      method: "POST",
      body: JSON.stringify({ code, deviceLabel: navigator.userAgent.slice(0, 80) }),
    });
    if (r.success) {
      localStorage.setItem(WAITER_TOKEN_KEY, r.data.token);
      setWaiter(r.data.waiter);
      setCompany(r.data.company);
      setSettings(r.data.settings);
      setStage("tables");
      return true;
    }
    return false;
  };

  // ── Cart ops ──
  const addToCart = (p) => {
    setCart(prev => {
      const ex = prev.find(it => it.id === p.id);
      if (ex) return prev.map(it => it === ex ? { ...it, qty: it.qty + 1 } : it);
      return [...prev, { id: p.id, name: p.name, price: p.price, qty: 1 }];
    });
  };
  const decFromCart = (id) => {
    setCart(prev => prev.flatMap(it => {
      if (it.id !== id) return [it];
      if (it.qty <= 1) return [];
      return [{ ...it, qty: it.qty - 1 }];
    }));
  };
  const cartCount = cart.reduce((s, it) => s + it.qty, 0);
  const cartTotal = cart.reduce((s, it) => s + it.price * it.qty, 0);

  const openTable = async (tn) => {
    setActiveTable(tn);
    setCart([]);
    setActiveCat("all");
    await ensureMenu();
    setStage("menu");
  };

  const sendOrder = async () => {
    if (cart.length === 0) return;
    setSending(true);
    const r = await waiterApi("/self-waiter/order", {
      method: "POST",
      body: JSON.stringify({
        tableNumber: activeTable,
        language: lang,
        items: cart.map(it => ({ productId: it.id, qty: it.qty })),
      }),
    });
    setSending(false);
    if (r.success) {
      setLastOrder({ table: activeTable, total: r.data.total, orderNumber: r.data.orderNumber });
      setCart([]);
      setStage("confirm");
      loadTables();
    } else {
      showToast(r.message || "Greška pri slanju", "error");
    }
  };

  // ════ RENDER ════
  if (stage === "loading") {
    return <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f8fafc" }}><Spinner /></div>;
  }

  if (stage === "pairing") {
    return <KonobarPairing slug={slug} onPair={handlePair} brand={brand} />;
  }

  // Shared header
  const Header = ({ title, onBack, right }) => (
    <div style={{ background: brand, color: "#fff", padding: "14px 16px", paddingTop: "max(14px, env(safe-area-inset-top))", display: "flex", alignItems: "center", gap: 12, position: "sticky", top: 0, zIndex: 20 }}>
      {onBack && (
        <button onClick={onBack} style={{ background: "rgba(255,255,255,0.2)", border: "none", borderRadius: 8, width: 34, height: 34, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff", flexShrink: 0 }}>
          <X size={18} />
        </button>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 16, fontWeight: 800, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{title}</div>
        {company && <div style={{ fontSize: 11, opacity: 0.85 }}>{company.name} · {waiter?.name}</div>}
      </div>
      {right}
    </div>
  );

  if (stage === "tables") {
    return (
      <div style={{ minHeight: "100vh", background: "#f8fafc" }}>
        <Header title="Stolovi" />
        <div style={{ padding: 16 }}>
          {tables.length === 0 ? (
            <div style={{ textAlign: "center", padding: 40, color: "#64748b" }}>
              <Coffee size={40} style={{ opacity: 0.4 }} />
              <div style={{ marginTop: 12, fontSize: 14 }}>Nemate dodijeljenih stolova. Obratite se administratoru.</div>
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))", gap: 12 }}>
              {tables.map(t => (
                <button key={t.number} onClick={() => openTable(t.number)} style={{
                  aspectRatio: "1", borderRadius: 14, cursor: "pointer", padding: 10,
                  border: t.busy ? `2px solid ${brand}` : "1px solid #e2e8f0",
                  background: t.busy ? `${brand}10` : "#fff",
                  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4,
                  boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                }}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: t.busy ? brand : "#0f172a" }}>{t.number}</div>
                  {t.busy ? (
                    <>
                      <div style={{ fontSize: 11, fontWeight: 700, color: brand }}>{t.orderCount} {t.orderCount === 1 ? "narudžba" : "narudžbe"}</div>
                      <div style={{ fontSize: 13, fontWeight: 800, color: "#0f172a" }}>{eur(t.total)}</div>
                    </>
                  ) : (
                    <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600 }}>Slobodan</div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
        {toast && <KonobarToast toast={toast} />}
      </div>
    );
  }

  if (stage === "menu") {
    const cats = menuData?.categories || [];
    const prods = (menuData?.products || []).filter(p => {
      if (activeCat === "all") return true;
      return Number(p.self_category_id) === Number(activeCat);
    });
    return (
      <div style={{ minHeight: "100vh", background: "#f8fafc", paddingBottom: cartCount > 0 ? 120 : 20 }}>
        <Header title={`Sto ${activeTable}`} onBack={() => { setStage("tables"); setCart([]); }} />
        {/* Category chips */}
        <div style={{ display: "flex", gap: 8, overflowX: "auto", padding: "12px 16px", position: "sticky", top: 60, background: "#f8fafc", zIndex: 10 }}>
          <KCatChip active={activeCat === "all"} onClick={() => setActiveCat("all")} label="Sve" brand={brand} />
          {cats.map(c => (
            <KCatChip key={c.id} active={String(activeCat) === String(c.id)} onClick={() => setActiveCat(c.id)} label={pickTr(c.translations, c.name)} brand={brand} />
          ))}
        </div>
        {/* Products */}
        <div style={{ padding: "0 16px" }}>
          {prods.map(p => {
            const inCart = cart.find(it => it.id === p.id);
            return (
              <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 12, background: "#fff", borderRadius: 12, padding: 12, marginBottom: 8, border: "1px solid #eef2f7" }}>
                {p.photo_url && (
                  <img src={p.photo_url.startsWith("http") ? p.photo_url : `https://efis.pro/${p.photo_url}`} alt="" style={{ width: 52, height: 52, borderRadius: 10, objectFit: "cover", flexShrink: 0 }} onError={e => { e.target.style.display = "none"; }} />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>{pickTr(p.name_translations, p.name)}</div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: brand, marginTop: 2 }}>{eur(p.price)}</div>
                </div>
                {inCart ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <button onClick={() => decFromCart(p.id)} style={qtyBtn(brand)}><Minus size={16} /></button>
                    <span style={{ fontSize: 16, fontWeight: 800, minWidth: 18, textAlign: "center" }}>{inCart.qty}</span>
                    <button onClick={() => addToCart(p)} style={qtyBtn(brand)}><Plus size={16} /></button>
                  </div>
                ) : (
                  <button onClick={() => addToCart(p)} style={{ ...qtyBtn(brand), width: "auto", padding: "0 14px", borderRadius: 10 }}><Plus size={16} /></button>
                )}
              </div>
            );
          })}
          {prods.length === 0 && <div style={{ textAlign: "center", padding: 30, color: "#94a3b8", fontSize: 14 }}>Nema proizvoda.</div>}
        </div>
        {/* Cart bar */}
        {cartCount > 0 && (
          <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, padding: "12px 16px", paddingBottom: "max(12px, env(safe-area-inset-bottom))", background: "#fff", borderTop: "1px solid #e2e8f0", boxShadow: "0 -2px 12px rgba(0,0,0,0.06)" }}>
            <button onClick={sendOrder} disabled={sending} style={{ width: "100%", background: brand, color: "#fff", border: "none", borderRadius: 12, padding: "14px 18px", fontSize: 16, fontWeight: 800, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", opacity: sending ? 0.7 : 1 }}>
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {sending ? <Spinner size={16} color="#fff" /> : <Send size={18} />}
                Pošalji ({cartCount})
              </span>
              <span>{eur(cartTotal)}</span>
            </button>
          </div>
        )}
        {toast && <KonobarToast toast={toast} />}
      </div>
    );
  }

  if (stage === "confirm") {
    return (
      <div style={{ minHeight: "100vh", background: "#f8fafc", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24, textAlign: "center" }}>
        <div style={{ width: 76, height: 76, borderRadius: 24, background: "#ecfdf5", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 18 }}>
          <CheckCircle size={42} color="#059669" />
        </div>
        <h2 style={{ margin: "0 0 6px", fontSize: 22, fontWeight: 800, color: "#0f172a" }}>Narudžba poslata!</h2>
        <p style={{ margin: "0 0 4px", color: "#64748b", fontSize: 15 }}>Sto {lastOrder?.table} · {lastOrder?.orderNumber}</p>
        <p style={{ margin: "0 0 24px", color: "#0f172a", fontSize: 18, fontWeight: 800 }}>{eur(lastOrder?.total || 0)}</p>
        <button onClick={() => { setActiveTable(null); setStage("tables"); }} style={{ background: brand, color: "#fff", border: "none", borderRadius: 12, padding: "14px 28px", fontSize: 16, fontWeight: 800, cursor: "pointer", width: "100%", maxWidth: 320 }}>
          Nazad na stolove
        </button>
      </div>
    );
  }

  return null;
}

// ── Konobar pairing screen ──
function KonobarPairing({ slug, onPair, brand }) {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (code.length !== 6) return;
    setBusy(true);
    setError("");
    const ok = await onPair(code);
    setBusy(false);
    if (!ok) { setError("Kod je netačan ili je istekao."); setCode(""); }
  };

  return (
    <div style={{ minHeight: "100vh", background: brand, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ background: "#fff", borderRadius: 20, padding: 28, width: "100%", maxWidth: 380, textAlign: "center", boxShadow: "0 10px 40px rgba(0,0,0,0.18)" }}>
        <div style={{ width: 60, height: 60, borderRadius: 16, background: `${brand}15`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
          <Users size={30} color={brand} />
        </div>
        <h1 style={{ margin: "0 0 6px", fontSize: 22, fontWeight: 800, color: "#0f172a" }}>Konobar</h1>
        <p style={{ margin: "0 0 20px", color: "#64748b", fontSize: 14, lineHeight: 1.5 }}>
          Unesite 6-cifreni kod iz<br />/admin → Podešavanja → Konobar
        </p>
        <input
          value={code}
          onChange={e => setCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
          onKeyDown={e => { if (e.key === "Enter") submit(); }}
          inputMode="numeric"
          autoFocus
          placeholder="000000"
          style={{
            width: "100%", textAlign: "center", fontSize: 34, fontWeight: 800,
            letterSpacing: 12, fontFamily: "JetBrains Mono, monospace",
            padding: "14px 0", border: `2px solid ${code.length === 6 ? brand : "#e2e8f0"}`,
            borderRadius: 12, outline: "none", color: "#0f172a", boxSizing: "border-box",
            marginBottom: 14,
          }}
        />
        {error && <div style={{ color: "#dc2626", fontSize: 13, marginBottom: 12 }}>{error}</div>}
        <button onClick={submit} disabled={code.length !== 6 || busy} style={{
          width: "100%", background: brand, color: "#fff", border: "none", borderRadius: 12,
          padding: "14px", fontSize: 16, fontWeight: 800, cursor: "pointer",
          opacity: (code.length !== 6 || busy) ? 0.5 : 1,
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        }}>
          {busy ? <Spinner size={16} color="#fff" /> : <Check size={18} />} Upari uređaj
        </button>
      </div>
    </div>
  );
}

function KCatChip({ active, onClick, label, brand }) {
  return (
    <button onClick={onClick} style={{
      flexShrink: 0, padding: "8px 16px", borderRadius: 20, cursor: "pointer", fontSize: 13, fontWeight: 700,
      whiteSpace: "nowrap",
      background: active ? brand : "#fff",
      color: active ? "#fff" : "#475569",
      border: `1.5px solid ${active ? brand : "#e2e8f0"}`,
    }}>{label}</button>
  );
}

function KonobarToast({ toast }) {
  const bg = toast.type === "error" ? "#dc2626" : toast.type === "success" ? "#059669" : "#0f172a";
  return (
    <div style={{ position: "fixed", bottom: 90, left: "50%", transform: "translateX(-50%)", background: bg, color: "#fff", padding: "12px 20px", borderRadius: 10, fontSize: 14, fontWeight: 600, zIndex: 100, boxShadow: "0 4px 16px rgba(0,0,0,0.2)", maxWidth: "90%" }}>
      {toast.msg}
    </div>
  );
}

function qtyBtn(brand) {
  return {
    width: 36, height: 36, borderRadius: 10, border: "none", cursor: "pointer",
    background: `${brand}15`, color: brand, display: "flex", alignItems: "center", justifyContent: "center",
    flexShrink: 0,
  };
}

// ════════════════════════════════════════════════════════════════════
// ════════════════════════════════════════════════════════════════════
//  ORDER STATUS PAGE (Phase 4) — /order/{publicToken}
//
//  Customer scans the QR code on their pickup ticket → lands here on
//  their phone → sees their order's live status. Polls every 4s.
//
//  Public read-only — the publicToken is the secret. No login needed.
//  Backend endpoint: GET /api/self/order-by-token/{publicToken}
//
//  Stops polling when status reaches a terminal state (finished/
//  rejected/cancelled) or after 30 minutes (whichever comes first).
//
//  Mobile-first design — this is opened on customer's phone.
// ════════════════════════════════════════════════════════════════════
// ════════════════════════════════════════════════════════════════════
function OrderStatusPage({ publicToken }) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Customer's phone may not have any pre-set language. Detect from browser
  // or default to "cg". This is independent of the kiosk's language.
  const [lang, setLang] = useState(() => {
    const browser = (navigator.language || "cg").slice(0, 2).toLowerCase();
    if (browser === "sq" || browser === "al") return "al";
    if (browser === "en") return "en";
    return "cg";
  });

  // ─── Polling ───
  const orderRef = useRef(order);
  useEffect(() => { orderRef.current = order; }, [order]);

  useEffect(() => {
    let cancelled = false;
    const TERMINAL_STATUSES = ["finished", "rejected", "cancelled"];

    const fetchStatus = async () => {
      try {
        const r = await fetch(`${API_BASE}/self/order-by-token/${encodeURIComponent(publicToken)}`);
        const json = await r.json();
        if (cancelled) return;
        if (json.success) {
          setOrder(json.data);
          setError(null);
        } else {
          setError(json.message || t(lang, "order_status_not_found"));
        }
      } catch (e) {
        if (!cancelled) setError(e.message || "Network error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchStatus(); // initial load
    const interval = setInterval(() => {
      if (cancelled) return;
      // Stop polling once status is terminal — read latest from ref so we
      // don't have to put `order` in deps (which would re-create the effect
      // on every poll and leak intervals).
      const cur = orderRef.current;
      if (cur && TERMINAL_STATUSES.includes(cur.status)) return;
      fetchStatus();
    }, 5000); // customer-facing, self-stops on terminal status + 30min cap (was 4s)

    // Stop polling after 30 minutes regardless
    const stopTimer = setTimeout(() => clearInterval(interval), 30 * 60 * 1000);

    return () => {
      cancelled = true;
      clearInterval(interval);
      clearTimeout(stopTimer);
    };
  }, [publicToken, lang]);

  // Edge-triggered ready alarm (sound + vibrate + 6s green banner)
  const readyFlash = useReadyAlarm(order?.status);

  if (loading) {
    return (
      <div style={{
        minHeight: "100vh", background: S.bg,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontFamily: S.font,
      }}>
        <Spinner size={36} color={S.accent} />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div style={{
        minHeight: "100vh", background: S.bg, fontFamily: S.font,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20,
      }}>
        <div style={{
          background: "#fff", padding: "32px 28px", borderRadius: 18,
          maxWidth: 420, width: "100%", textAlign: "center",
          boxShadow: "0 6px 24px rgba(0,0,0,0.06)",
        }}>
          <div style={{ display: "inline-flex", padding: 14, background: "#fef2f2", borderRadius: 14, marginBottom: 14 }}>
            <AlertCircle size={36} color={S.red} />
          </div>
          <h2 style={{ margin: "0 0 8px", fontSize: 20, color: S.dark }}>
            {error || t(lang, "order_status_not_found")}
          </h2>
        </div>
      </div>
    );
  }

  // ─── Status decoration ───
  const styling = orderStatusStyling(order.status);
  const statusLabel = t(lang, {
    pending: "order_waiting_label",
    preparing: "order_preparing_label",
    ready_pickup: "order_ready_pickup_label",
    ready_table: "order_ready_table_label",
    finished: "order_finished_label",
    rejected: "order_rejected_label",
    cancelled: "order_cancelled_label",
  }[order.status] || "order_status_loading");

  // Time elapsed since order placed (in minutes)
  const placedDate = order.createdAt ? new Date(order.createdAt.replace(" ", "T") + "Z") : null;
  const elapsedMin = placedDate ? Math.max(0, Math.floor((Date.now() - placedDate.getTime()) / 60000)) : null;
  const remainingMin = order.estimatedPrepMin ? Math.max(0, order.estimatedPrepMin - (elapsedMin || 0)) : null;

  // Customer-page bg from settings (sent in orderByToken response).
  // Falls back to S.bg when admin hasn't customized.
  const pageBg = (order.bgColor && /^#[0-9a-fA-F]{3,8}$/.test(order.bgColor))
    ? order.bgColor
    : S.bg;

  return (
    <div style={{
      minHeight: "100vh",
      background: `linear-gradient(180deg, ${styling.bg} 0%, ${styling.bg}dd 60%, ${pageBg} 100%)`,
      fontFamily: S.font,
      paddingBottom: 40,
    }}>
      <style>{`
        @keyframes ready-pulse-banner {
          0%, 100% { transform: scale(1); box-shadow: 0 4px 18px rgba(22,163,74,0.55); }
          50% { transform: scale(1.025); box-shadow: 0 4px 28px rgba(22,163,74,0.95); }
        }
      `}</style>
      {readyFlash && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 1500,
          background: "#16a34a", color: "#fff",
          padding: "16px 16px",
          paddingTop: "max(16px, env(safe-area-inset-top))",
          textAlign: "center", fontWeight: 800, fontSize: 18,
          boxShadow: "0 4px 18px rgba(22,163,74,0.55)",
          animation: "ready-pulse-banner 1.0s ease-in-out infinite",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
        }}>
          <CheckCircle size={26} />
          {order.status === "ready_pickup"
            ? t(lang, "status_ready_pickup")
            : t(lang, "status_ready_table")}
        </div>
      )}
      {/* Header card */}
      <div style={{
        padding: "32px 20px 28px",
        textAlign: "center", color: "#fff",
      }}>
        <div style={{
          display: "inline-flex",
          width: 86, height: 86, borderRadius: "50%",
          background: "rgba(255,255,255,0.18)",
          alignItems: "center", justifyContent: "center",
          marginBottom: 14,
          animation: order.status === "preparing" ? "pulse 1.6s ease-in-out infinite" : "none",
        }}>
          <styling.Icon size={44} color="#fff" />
        </div>

        <div style={{ fontSize: 13, fontWeight: 600, opacity: 0.85, marginBottom: 6, letterSpacing: 0.6, textTransform: "uppercase" }}>
          {t(lang, "order_number")}
        </div>
        <div style={{
          fontSize: 56, fontWeight: 800, letterSpacing: -1.5,
          fontFamily: "JetBrains Mono, monospace",
          marginBottom: 14,
        }}>
          #{order.orderNumber}
        </div>

        <div style={{
          display: "inline-block",
          padding: "10px 20px",
          background: "rgba(255,255,255,0.2)",
          borderRadius: 30,
          fontSize: 15, fontWeight: 600,
          maxWidth: "90%",
        }}>
          {statusLabel}
        </div>
      </div>

      {/* Main content card */}
      <div style={{ padding: "0 16px" }}>
        <div style={{
          background: "#fff", borderRadius: 20,
          boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
          overflow: "hidden",
          maxWidth: 540, margin: "0 auto",
        }}>
          {/* ETA panel — only when preparing/pending */}
          {(order.status === "pending" || order.status === "preparing") && remainingMin !== null && (
            <div style={{
              padding: "16px 20px",
              background: `${styling.bg}10`,
              borderBottom: `1px solid ${S.border}`,
              display: "flex", alignItems: "center", gap: 12,
            }}>
              <div style={{
                width: 44, height: 44, borderRadius: "50%",
                background: `${styling.bg}20`,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <Clock size={22} color={styling.bg} />
              </div>
              <div>
                <div style={{ fontSize: 12, color: S.muted, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>
                  {t(lang, "order_eta_about")}
                </div>
                <div style={{ fontSize: 18, fontWeight: 700, color: S.dark }}>
                  ~{remainingMin > 0 ? remainingMin : "<1"} {t(lang, "kiosk_minutes_short")}
                </div>
              </div>
            </div>
          )}

          {/* Reject reason — only when rejected */}
          {order.status === "rejected" && order.rejectReason && (
            <div style={{
              padding: "16px 20px",
              background: "#fef2f2",
              borderBottom: `1px solid ${S.border}`,
            }}>
              <div style={{ fontSize: 12, color: S.red, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, marginBottom: 4 }}>
                {t(lang, "status_rejected")}
              </div>
              <div style={{ fontSize: 15, color: S.dark }}>
                {order.rejectReason}
              </div>
            </div>
          )}

          {/* Items list — with per-item kitchen readiness (same pattern as
              the QR-table customer page: ✓ done = strikethrough green,
              ⏰ pending = yellow clock, no icon = unknown/not yet sent). */}
          <div style={{ padding: "8px 0" }}>
            {(order.items || []).map((it, idx) => {
              const isDone = it.kitchen_status === "done";
              const isPending = it.kitchen_status === "pending";
              return (
                <div key={idx} style={{
                  padding: "12px 20px",
                  borderBottom: idx === order.items.length - 1 ? "none" : `1px solid #f1f5f9`,
                  opacity: isDone ? 0.55 : 1,
                  transition: "opacity .2s",
                }}>
                  <div style={{
                    display: "flex", alignItems: "center", gap: 12,
                  }}>
                    {/* Qty bubble */}
                    <div style={{
                      minWidth: 28, height: 28, borderRadius: 8,
                      background: "#f1f5f9", color: S.muted,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 13, fontWeight: 700,
                    }}>
                      {fmtQty(it.qty)}
                    </div>
                    {/* Status icon (kitchen-driven, per-item) */}
                    {isDone && <CheckCircle size={16} color={S.green} style={{ flexShrink: 0 }} />}
                    {isPending && <Clock size={16} color="#eab308" style={{ flexShrink: 0 }} />}
                    {/* Name */}
                    <div style={{
                      flex: 1, fontSize: 15, color: S.dark,
                      textDecoration: isDone ? "line-through" : "none",
                    }}>
                      {it.product_name}
                    </div>
                    {/* Line total */}
                    <div style={{ fontSize: 14, color: S.muted, fontWeight: 600, fontFamily: "JetBrains Mono, monospace" }}>
                      {Number(it.line_total).toFixed(2)} €
                    </div>
                  </div>
                  {/* Tags (under name, indented) */}
                  {it.tags?.length > 0 && (
                    <div style={{ fontSize: 12, color: S.muted, marginTop: 4, paddingLeft: 40 }}>
                      + {it.tags.join(", ")}
                    </div>
                  )}
                  {/* Special note */}
                  {it.note && (
                    <div style={{ fontSize: 12, color: S.muted, fontStyle: "italic", marginTop: 2, paddingLeft: 40 }}>
                      "{it.note}"
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Total */}
          <div style={{
            padding: "16px 20px",
            background: "#f8fafc",
            display: "flex", justifyContent: "space-between", alignItems: "center",
            borderTop: `1px solid ${S.border}`,
          }}>
            <div style={{ fontSize: 13, color: S.muted, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>
              {t(lang, "total")}
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: S.dark, fontFamily: "JetBrains Mono, monospace" }}>
              {Number(order.total).toFixed(2)} €
            </div>
          </div>
        </div>

        {/* Source / table info — small footer */}
        <div style={{
          textAlign: "center", marginTop: 14,
          color: S.muted, fontSize: 12,
          fontFamily: "JetBrains Mono, monospace",
        }}>
          {order.sourceType === "terminal" ? `TERMINAL #${order.tableNumber}` : `STOL #${order.tableNumber}`}
        </div>

        {/* Lang toggle (small, centered, bottom) */}
        <div style={{
          marginTop: 18,
          display: "flex", justifyContent: "center", gap: 6,
        }}>
          {Object.keys(LANG_FLAGS).map(l => (
            <button key={l} onClick={() => setLang(l)}
              style={{
                padding: "6px 12px", borderRadius: 16,
                border: `1px solid ${lang === l ? S.dark : S.border}`,
                background: lang === l ? "#fff" : "transparent",
                color: lang === l ? S.dark : S.muted,
                fontSize: 12, cursor: "pointer",
                fontFamily: S.font,
              }}>
              {LANG_FLAGS[l]} {LANG_NAMES[l]}
            </button>
          ))}
        </div>
      </div>

      <style>{`@keyframes pulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.08); } }`}</style>
    </div>
  );
}

// Returns visual styling for each order status — color + icon component
function orderStatusStyling(status) {
  const map = {
    pending:      { bg: "#f59e0b", Icon: Clock },
    preparing:    { bg: "#3b82f6", Icon: Utensils },
    ready_pickup: { bg: "#16a34a", Icon: CheckCircle },
    ready_table:  { bg: "#16a34a", Icon: CheckCircle },
    finished:     { bg: "#6b7280", Icon: Check },
    rejected:     { bg: "#dc2626", Icon: XCircle },
    cancelled:    { bg: "#6b7280", Icon: X },
  };
  return map[status] || map.pending;
}

// ════════════════════════════════════════════════════════════════════
// ════════════════════════════════════════════════════════════════════
//  LIVE DISPLAY PAGE (Phase 5) — /live/{slug}
//
//  Public wall-TV display showing today's active order numbers grouped
//  into 3 columns: ČEKA / U PRIPREMI / SPREMNO.
//
//  - No login. Slug is the secret. Anyone with the URL can watch.
//  - Polls every 3s — endpoint already exempt from rate limiting.
//  - "READY" column gets visual emphasis: brand color, bigger flex weight.
//  - New entries in READY pulse for 8 seconds when they first appear, so
//    customers notice their order is up.
//  - Auto-recovers from network errors (small "Reconnecting..." indicator).
//  - Landscape-only design (TV is always landscape).
//  - Empty state: columns visible with their headers, no special UI.
// ════════════════════════════════════════════════════════════════════
// ════════════════════════════════════════════════════════════════════
function LiveDisplayPage({ slug }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [now, setNow] = useState(new Date());
  // Map of orderNumber → timestamp(ms) when it first appeared in READY.
  // Used to drive the pulse animation; entries older than 8s are removed
  // from the map but stay in the data.
  const [freshReady, setFreshReady] = useState({});
  const prevReadyRef = useRef(new Set());
  // Customer-facing display, browser-detected language is fine
  const lang = useMemo(() => {
    const browser = (navigator.language || "cg").slice(0, 2).toLowerCase();
    if (browser === "sq" || browser === "al") return "al";
    if (browser === "en") return "en";
    return "cg";
  }, []);

  // ─── Live TV display poll: smart poller (jitter/pause/backoff), 5s → 7s ──
  // Deps [slug] only (stable), so it sets up once. fetchData returns a boolean
  // so the poller can back off on a failed/blocked request instead of hammering.
  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        const r = await fetch(`${API_BASE}/self/live/${encodeURIComponent(slug)}`);
        const json = await r.json();
        if (cancelled) return true;
        if (json.success) { setData(json.data); setError(false); return true; }
        setError(true); return false;
      } catch (e) {
        if (!cancelled) setError(true);
        return false;
      }
    };
    const stop = makeSmartPoller(fetchData, 7000, { key: `live-tv-${slug}` });
    return () => { cancelled = true; stop(); };
  }, [slug]);

  // ─── Track which numbers just appeared in READY (for pulse) ─────
  useEffect(() => {
    if (!data) return;
    const nowMs = Date.now();
    const prevSet = prevReadyRef.current;
    setFreshReady(prev => {
      const next = { ...prev };
      // Mark new entries
      for (const num of data.ready) {
        if (!prevSet.has(num) && !next[num]) next[num] = nowMs;
      }
      // Drop expired (>8s old) and any not-currently-ready entries
      const stillReady = new Set(data.ready);
      for (const k of Object.keys(next)) {
        if (!stillReady.has(k) || nowMs - next[k] > 8000) delete next[k];
      }
      return next;
    });
    prevReadyRef.current = new Set(data.ready);
  }, [data]);

  // ─── Re-render every 1s for the clock + to expire stale `freshReady` entries ─
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  // Auto-cleanup of expired fresh entries on each clock tick
  useEffect(() => {
    setFreshReady(prev => {
      const nowMs = Date.now();
      const next = {};
      let changed = false;
      for (const k of Object.keys(prev)) {
        if (nowMs - prev[k] <= 8000) next[k] = prev[k];
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [now]);

  if (!data && !error) {
    // Initial load
    return (
      <div style={liveBgStyle("#0F1115")}>
        <div style={{ color: "#fff", fontSize: 24, opacity: 0.6 }}>
          {t(lang, "connecting")}
        </div>
      </div>
    );
  }

  const company = data?.company || {};
  const accent = company.brandingColor || "#3b82f6";
  const totalActive = (data?.pending?.length || 0) + (data?.preparing?.length || 0) + (data?.ready?.length || 0);
  const clockStr = now.toLocaleTimeString("sr-Latn-ME", { hour: "2-digit", minute: "2-digit" });
  const dateStr = now.toLocaleDateString("sr-Latn-ME", { day: "2-digit", month: "long", weekday: "long" });

  return (
    <div style={liveBgStyle("#0F1115")}>
      {/* Header bar — logo + name (left), clock + date (right) ───── */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "20px 36px",
        borderBottom: "1px solid rgba(255,255,255,0.08)",
        flexShrink: 0,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
          {company.logo_path ? (
            <img
              src={`https://efis.pro/${company.logo_path}`}
              alt={company.name || ""}
              style={{ height: 56, maxWidth: 200, objectFit: "contain", filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.3))" }}
              onError={(e) => { e.target.style.display = "none"; }}
            />
          ) : (
            <div style={{
              width: 56, height: 56, borderRadius: 14,
              background: accent,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <Utensils size={30} color="#fff" />
            </div>
          )}
          <div>
            <div style={{ fontSize: 28, fontWeight: 800, color: "#fff", letterSpacing: -0.5, lineHeight: 1.1 }}>
              {company.name || "EFIS.PRO"}
            </div>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,0.55)", marginTop: 2, letterSpacing: 0.4, textTransform: "uppercase", fontWeight: 600 }}>
              {dateStr}
            </div>
          </div>
        </div>

        <div style={{ textAlign: "right" }}>
          <div style={{
            fontSize: 52, fontWeight: 700, color: "#fff",
            fontFamily: "JetBrains Mono, monospace", letterSpacing: -1, lineHeight: 1,
          }}>
            {clockStr}
          </div>
          {error && (
            <div style={{
              marginTop: 6, fontSize: 12, color: "#fbbf24",
              display: "inline-flex", alignItems: "center", gap: 6,
              fontWeight: 600,
            }}>
              <span style={{
                width: 8, height: 8, borderRadius: "50%",
                background: "#fbbf24", animation: "blink 1.2s infinite",
              }} />
              {t(lang, "live_reconnecting")}
            </div>
          )}
        </div>
      </div>

      {/* 3-column body ──────────────────────────────────────────── */}
      <div style={{
        flex: 1, display: "flex",
        padding: "28px 28px 32px",
        gap: 20,
        overflow: "hidden",
      }}>
        <LiveColumn
          title={t(lang, "live_pending")}
          numbers={data?.pending || []}
          color="#a3a8b3"
          bg="rgba(255,255,255,0.04)"
          flex={1}
          fontFamily={S.font}
        />
        <LiveColumn
          title={t(lang, "live_preparing")}
          numbers={data?.preparing || []}
          color="#60a5fa"
          bg="rgba(96, 165, 250, 0.08)"
          flex={1}
          fontFamily={S.font}
        />
        <LiveColumn
          title={t(lang, "live_ready")}
          numbers={data?.ready || []}
          color="#fff"
          accentBg={accent}
          freshReady={freshReady}
          bg={`linear-gradient(180deg, ${accent}25 0%, ${accent}08 100%)`}
          borderColor={accent}
          flex={1.3}
          fontFamily={S.font}
          isReady
        />
      </div>

      {/* Empty state — small caption when no orders at all */}
      {totalActive === 0 && (
        <div style={{
          position: "absolute", bottom: 20, left: 0, right: 0,
          textAlign: "center", color: "rgba(255,255,255,0.3)",
          fontSize: 14, letterSpacing: 0.5, textTransform: "uppercase", fontWeight: 600,
        }}>
          {t(lang, "live_no_orders")}
        </div>
      )}

      <style>{`
        @keyframes blink { 0%,100% { opacity: 1; } 50% { opacity: 0.3; } }
        @keyframes liveFreshPulse {
          0%   { transform: scale(1);    box-shadow: 0 0 0 0 rgba(255,255,255,0.7); }
          40%  { transform: scale(1.05); box-shadow: 0 0 0 28px rgba(255,255,255,0); }
          100% { transform: scale(1);    box-shadow: 0 0 0 0 rgba(255,255,255,0); }
        }
      `}</style>
    </div>
  );
}

// ─── Single column for the live display ────────────────────────────
function LiveColumn({ title, numbers, color, bg, accentBg, borderColor, freshReady, flex, fontFamily, isReady }) {
  // Auto-fit font size based on entry count. Numbers are ~4 chars
  // ("S007"). Generous when sparse, drop hard when dense so the column
  // never overflows even at peak.
  const count = numbers.length;
  let fontSize;
  if (count === 0) fontSize = 0;
  else if (count <= 2) fontSize = isReady ? 130 : 110;
  else if (count <= 4) fontSize = isReady ? 96 : 80;
  else if (count <= 7) fontSize = isReady ? 70 : 60;
  else if (count <= 10) fontSize = 50;
  else if (count <= 14) fontSize = 38;
  else if (count <= 20) fontSize = 28;
  else fontSize = 22; // 21+ — extreme; still legible on a TV from 2-3m

  return (
    <div style={{
      flex,
      display: "flex", flexDirection: "column",
      background: bg,
      borderRadius: 22,
      border: borderColor ? `2px solid ${borderColor}55` : "1px solid rgba(255,255,255,0.06)",
      overflow: "hidden",
      fontFamily,
    }}>
      {/* Column header */}
      <div style={{
        padding: "18px 28px",
        borderBottom: `1px solid rgba(255,255,255,0.08)`,
        display: "flex", justifyContent: "space-between", alignItems: "center",
        background: isReady ? `${accentBg}25` : "transparent",
      }}>
        <div style={{
          fontSize: 20, fontWeight: 800, color,
          letterSpacing: 1.5, textTransform: "uppercase",
        }}>
          {title}
        </div>
        <div style={{
          fontSize: 18, fontWeight: 700, color,
          minWidth: 44, height: 44, borderRadius: 12,
          background: "rgba(255,255,255,0.08)",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          fontFamily: "JetBrains Mono, monospace",
        }}>
          {count}
        </div>
      </div>

      {/* Numbers */}
      <div style={{
        flex: 1, overflow: "auto", // last-resort scroll if 30+ entries
        padding: count > 0 ? "16px 12px" : 0,
        display: "flex", flexDirection: "column",
        gap: count <= 4 ? 16 : (count <= 14 ? 10 : 6),
        alignItems: "stretch", justifyContent: count <= 2 ? "center" : "flex-start",
        scrollbarWidth: "thin",
      }}>
        {numbers.map(num => {
          const isFresh = isReady && freshReady && freshReady[num];
          return (
            <div key={num}
              style={{
                fontSize, fontWeight: 800, lineHeight: 1,
                fontFamily: "JetBrains Mono, monospace",
                color: isReady ? "#fff" : color,
                textAlign: "center",
                padding: count <= 4 ? "12px 0" : "8px 0",
                borderRadius: 14,
                background: isReady ? accentBg : "transparent",
                letterSpacing: -2,
                // Pulse ripple for newly-arrived READY entries — animates
                // scale + outward glow, never touches the text color.
                // Previous version made text color = bg color during the
                // pulse, which hid the number for ~5-6s.
                ...(isFresh && {
                  animation: "liveFreshPulse 1.2s ease-out 4",
                }),
              }}>
              {num}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Background wrapper used by Live page ──────────────────────────
function liveBgStyle(bg) {
  return {
    position: "fixed", inset: 0,
    background: bg,
    color: "#fff",
    fontFamily: `"DM Sans", sans-serif`,
    display: "flex", flexDirection: "column",
    overflow: "hidden",
    WebkitTapHighlightColor: "transparent",
    userSelect: "none",
  };
}

function ToggleRow({ label, desc, checked, onChange }) {
  return (
    <div style={{ display: "flex", alignItems: "center", padding: "10px 0", borderBottom: `1px solid ${S.border}`, gap: 12, marginBottom: 8 }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>{label}</div>
        {desc && <div style={{ fontSize: 12, color: S.muted, marginTop: 2 }}>{desc}</div>}
      </div>
      <button onClick={() => onChange(!checked)} style={{
        width: 46, height: 26, borderRadius: 13, background: checked ? S.green : "#cbd5e1",
        border: "none", cursor: "pointer", padding: 0, position: "relative", transition: "background .15s",
      }}>
        <div style={{
          position: "absolute", top: 3, left: checked ? 23 : 3, width: 20, height: 20,
          background: "#fff", borderRadius: "50%", transition: "left .15s",
          boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
        }} />
      </button>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// MAIN APP — routes based on URL
// ══════════════════════════════════════════════════════════════════════
export default function App() {
  const [mode, setMode] = useState(() => detectMode());
  const [token, setToken] = useState(() => localStorage.getItem("self_token"));
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("self_user") || "null"); } catch { return null; }
  });

  useEffect(() => {
    const onPop = () => setMode(detectMode());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // ── Maintenance gate ────────────────────────────────────────────────────
  // Placed AFTER all hooks (Rules of Hooks satisfied). When on, we return the
  // "back soon" screen instead of any child component — so none of the child
  // apps (CustomerApp, LiveDisplayPage, AdminView, etc.) mount, meaning ZERO
  // pollers run and ZERO SELF API traffic is generated. Flip SELF_MAINTENANCE
  // to false to restore the full app exactly as before.
  if (SELF_MAINTENANCE) {
    return (
      <div style={{
        minHeight: "100vh", display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", textAlign: "center",
        background: "#0f0f0f", color: "#e5e5e5", fontFamily: "'JetBrains Mono','DM Sans',monospace",
        padding: "24px", gap: "18px"
      }}>
        <div style={{ fontSize: "56px", lineHeight: 1 }}>🍽️</div>
        <div style={{ fontSize: "26px", fontWeight: 700, letterSpacing: "0.5px" }}>EFIS.PRO SELF</div>
        <div style={{ fontSize: "20px", color: "#f59e0b", fontWeight: 600 }}>{SELF_MAINTENANCE_MSG}</div>
        <div style={{ fontSize: "14px", color: "#777", maxWidth: 360 }}>
          Sistem je privremeno u održavanju. Hvala na strpljenju.
        </div>
      </div>
    );
  }

  // Customer mode
  if (mode.mode === "customer") {
    return <CustomerApp slug={mode.slug} kind={mode.kind} number={mode.number} />;
  }

  // Terminal kiosk mode (standalone .exe or browser at /terminal)
  if (mode.mode === "kiosk") {
    return <TerminalKioskApp />;
  }

  // Order status page — opened by customer scanning receipt QR
  if (mode.mode === "orderStatus") {
    return <OrderStatusPage publicToken={mode.publicToken} />;
  }

  // Public live display for wall TV
  if (mode.mode === "liveDisplay") {
    return <LiveDisplayPage slug={mode.slug} />;
  }

  // Waiter (konobar) app
  if (mode.mode === "konobar") {
    return <KonobarApp slug={mode.slug} />;
  }

  // Admin mode
  if (mode.mode === "admin") {
    if (!token) {
      return <AdminLogin onLogin={(tok, usr) => { setToken(tok); setUser(usr); }} />;
    }
    return <AdminApp user={user} onLogout={() => {
      localStorage.removeItem("self_token");
      localStorage.removeItem("self_user");
      setToken(null);
      setUser(null);
    }} />;
  }

  // Landing
  return (
    <div style={{ minHeight: "100vh", background: S.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, fontFamily: S.font }}>
      <div style={{ background: "#fff", padding: 40, borderRadius: 16, maxWidth: 480, textAlign: "center", boxShadow: "0 10px 40px rgba(0,0,0,0.05)" }}>
        <div style={{ display: "inline-flex", width: 64, height: 64, borderRadius: 16, background: S.accent, alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
          <Utensils size={32} color="#fff" />
        </div>
        <h1 style={{ margin: "0 0 8px", fontSize: 24, color: S.dark }}>EFIS.PRO SELF</h1>
        <p style={{ color: S.muted, margin: "0 0 24px", fontSize: 14 }}>
          Self-service sistem za naručivanje u restoranu.
        </p>
        <div style={{ textAlign: "left", background: S.bg, padding: 14, borderRadius: 10, fontSize: 13, color: S.text, lineHeight: 1.7 }}>
          <div><b>Klijenti:</b> skenirajte QR kod na stolu.</div>
          <div><b>Admin:</b> <a href="/admin" style={{ color: S.accent, fontWeight: 600 }}>/admin</a></div>
        </div>
      </div>
    </div>
  );
}
